import { emergencyContactEmailChoices } from '../../shared/nursingEmergencyContacts';
import { otherDiagnosisValidationMessage, serializeMedicationRows, type MedicationRow } from '../../shared/nursingClinicalForm';
import { hasUnavailableSecondPerson, requiresSecondPersonAssist } from '../../shared/nursingMobilityValues';
import { parsePcpOffice, pcpOffices, samePcpOffice } from '../../shared/linkPcpOffice';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ChangeEvent, type ComponentProps, type DragEvent, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tab,
  Tabs,
  Step,
  StepButton,
  StepLabel,
  StepIcon,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MicIcon from '@mui/icons-material/Mic';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import CancelIcon from '@mui/icons-material/Cancel';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency';
import SecurityIcon from '@mui/icons-material/Security';
import ShieldIcon from '@mui/icons-material/Shield';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import MedicationIcon from '@mui/icons-material/Medication';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import HomeIcon from '@mui/icons-material/Home';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AddIcon from '@mui/icons-material/Add';
import HealingIcon from '@mui/icons-material/Healing';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderIcon from '@mui/icons-material/Folder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LoginIcon from '@mui/icons-material/Login';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import SummarizeIcon from '@mui/icons-material/Summarize';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { DataGridPremium, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid-premium';
import InputMask from '@mona-health/react-input-mask';
import SignatureCanvas from 'react-signature-canvas';
import {
  AuthExpiredError,
  authFetch,
  getAccessToken,
  getRefreshToken,
  getStoredUser,
  hasRole,
  NURSE_ADMIN_SESSION_KEY,
  NURSING_SELECTED_PATIENT_KEY,
  storeAuthSession,
  type AuthUser,
} from '../utils/auth';
import { downloadBlob } from '../utils/caregiverApi';
import AddressAutocomplete from '../components/AddressAutocomplete';
import MultiSelectPicklist from '../components/MultiSelectPicklist';
import ReauthDialog from '../components/ReauthDialog';
import {
  hasPpClusterContract,
  isRnOnlyClusterAssessment,
  shouldRequireClusterPackagesOnPlanOfCare,
} from '../lib/nursingAssessmentClusterPackageRules';
import { canEditCompletedNursingAssessment } from '../../shared/nursingAssessmentEditRules';

const NURSE_TICKET_DESCRIPTION_LIMIT = 49_973;

interface FieldDef {
  apiName: string;
  label: string;
  dataType: string;
  picklistValues?: string[];
  ui?: {
    multiline?: boolean;
  };
}

interface CardDef {
  title: string;
  fields: string[];
}

interface StepDef {
  title: string;
  cards: Record<string, CardDef>;
}

interface Metadata {
  steps: Record<string, StepDef>;
  defaultFormData: Record<string, unknown>;
  fields: Record<string, FieldDef>;
}

interface NursingDocument {
  id: number;
  documentName: string;
  formName: string | null;
  createdAt: string;
}

type CaregiverRecord = Record<string, unknown>;

interface CaregiverIdPicture {
  fileId: string;
  url: string;
  fileName: string;
  contentType: string;
}

interface ManualNursingDocumentConfig {
  key: string;
  label: string;
  description: string;
  flagField: string;
  parent: 'patient' | 'caregiver';
}

interface ManualNursingDocumentUploadResponse {
  document?: {
    documentKey?: string;
    label?: string;
    fileName?: string;
    nexusFileId?: string;
    uploadedAt?: string;
  };
  flagField?: string;
}

interface PlanOfCareTaskRow {
  task: string;
  min: string;
  max: string;
  instructions: string;
  required: boolean;
  assist: boolean;
  supervision?: string;
}

type PlanOfCareTaskField = keyof Pick<PlanOfCareTaskRow, 'task' | 'min' | 'max' | 'instructions' | 'required' | 'assist'>;

interface PlanOfCareTaskValidationIssue {
  rowIndex: number;
  task: string;
  fields: PlanOfCareTaskField[];
  missingLabels: string[];
}

interface ConsentServiceConfig {
  contractTypes: string[];
  services: string;
  frequency: string;
  fee: string;
  cannotChoose: string[];
  requiresFrequencyInput: boolean;
  isPrivatePay: boolean;
}

interface ConsentRow {
  id: string;
  version: number;
  service: string;
  frequency: string;
  fee: string;
  weekendFee: string;
  expectedCoverage: string;
  patientResponsibility: string;
  requiresFrequencyInput: boolean;
  requiresFeeInput: boolean;
  isPrivatePay: boolean;
}

const MANUAL_NURSING_DOCUMENTS: ManualNursingDocumentConfig[] = [
  {
    key: 'poc',
    label: 'Handwritten POC',
    description: 'Upload the signed handwritten plan of care for this patient.',
    flagField: 'pocDocUploaded',
    parent: 'patient',
  },
  {
    key: 'consent',
    label: 'Consents',
    description: 'Upload the signed Consent for LHCSA Services packet.',
    flagField: 'consentForLhcsaServicesUploaded',
    parent: 'patient',
  },
  {
    key: 'advancedDirectives',
    label: 'Advanced Directives',
    description: 'Upload advance directive documents obtained during assessment.',
    flagField: 'advancedDirectivesDocUploaded',
    parent: 'patient',
  },
  {
    key: 'medicalRecords',
    label: 'Medical Records',
    description: 'Upload clinical records collected for this patient.',
    flagField: 'medicalRecordsDocUploaded',
    parent: 'patient',
  },
  {
    key: 'aideEvaluation',
    label: 'Aide Evaluation',
    description: 'Upload the evaluation document for the selected caregiver.',
    flagField: 'evaluationDocUploaded',
    parent: 'caregiver',
  },
];

interface DiagnosisRow {
  id: number;
  icdCode: string;
  description: string;
  date: string;
}

interface DiagnosisOption {
  code: string;
  description: string;
}

const PRIMARY_DIAGNOSIS_OPTIONS: Array<DiagnosisOption & { label: string }> = [
  { code: 'F03', description: 'Unspecified Dementia', label: 'Unspecified Dementia (ICD10: F03)' },
  { code: 'R26.9', description: 'Unspecified abnormalities of gait and mobility', label: 'Unspecified abnormalities of gait and mobility (ICD10: R26.9)' },
];

const ICD10_DIAGNOSIS_OPTIONS: DiagnosisOption[] = [
  { code: 'R10.9', description: 'Abdominal pain' },
  { code: 'E11.9', description: 'Type 2 diabetes mellitus unspecified' },
  { code: 'R53.81', description: 'Malaise' },
  { code: 'F10.20', description: 'Alcohol dependence uncomplicated' },
  { code: 'R19.7', description: 'Diarrhea unspecified' },
  { code: 'R41.3', description: 'Memory loss unspecified' },
  { code: 'R41.82', description: 'Altered mental status unspecified' },
  { code: 'R26.2', description: 'Difficulty in walking not elsewhere classified' },
  { code: 'G43.909', description: 'Migraine unspecified not intractable without status migrainosus' },
  { code: 'G30.9', description: "Alzheimer's disease unspecified" },
  { code: 'H04.123', description: 'Dry eye syndrome bilateral' },
  { code: 'R11.2', description: 'Nausea with vomiting unspecified' },
  { code: 'D64.9', description: 'Anemia unspecified' },
  { code: 'K57.92', description: 'Diverticulitis of intestine part unspecified without perforation or abscess' },
  { code: 'N31.9', description: 'Neuromuscular dysfunction of bladder unspecified' },
  { code: 'F41.9', description: 'Anxiety disorder unspecified' },
  { code: 'R60.9', description: 'Edema unspecified' },
  { code: 'G60.9', description: 'Hereditary and idiopathic neuropathy unspecified' },
  { code: 'M19.90', description: 'Osteoarthritis unspecified site unspecified whether generalized or localized' },
  { code: 'W19', description: 'Fall initial encounter' },
  { code: 'F17.210', description: 'Nicotine dependence cigarettes uncomplicated' },
  { code: 'J45.909', description: 'Unspecified asthma uncomplicated' },
  { code: 'R53.83', description: 'Other fatigue' },
  { code: 'E66.9', description: 'Obesity unspecified' },
  { code: 'I48.91', description: 'Unspecified atrial fibrillation' },
  { code: 'R50.9', description: 'Fever unspecified' },
  { code: 'Z99.81', description: 'Dependence on supplemental oxygen' },
  { code: 'F31.9', description: 'Bipolar disorder unspecified' },
  { code: 'R26.9', description: 'Unspecified abnormalities of gait and mobility' },
  { code: 'G82.20', description: 'Paraplegia unspecified' },
  { code: 'N40.0', description: 'Benign prostatic hyperplasia without lower urinary tract symptoms' },
  { code: 'K25.9', description: 'Gastric ulcer unspecified as acute or chronic without hemorrhage or perforation' },
  { code: 'L89.90', description: 'Pressure ulcer unspecified site unspecified stage' },
  { code: 'H26.9', description: 'Cataract unspecified' },
  { code: 'K21.9', description: 'Gastroesophageal reflux disease without esophagitis' },
  { code: 'F43.10', description: 'Post traumatic stress disorder unspecified' },
  { code: 'I63.9', description: 'Cerebral infarction unspecified' },
  { code: 'H40.9', description: 'Glaucoma unspecified' },
  { code: 'C61', description: 'Malignant neoplasm of prostate' },
  { code: 'R07.9', description: 'Chest pain unspecified' },
  { code: 'I50.9', description: 'Heart failure unspecified' },
  { code: 'I26.99', description: 'Other pulmonary embolism without acute cor pulmonale' },
  { code: 'N18.9', description: 'Chronic kidney disease unspecified' },
  { code: 'E78.5', description: 'Hyperlipidemia unspecified' },
  { code: 'G82.50', description: 'Quadriplegia unspecified' },
  { code: 'J44.9', description: 'Chronic obstructive pulmonary disease unspecified' },
  { code: 'I10', description: 'Essential primary hypertension' },
  { code: 'M06.9', description: 'Rheumatoid arthritis unspecified' },
  { code: 'G89.4', description: 'Chronic pain syndrome' },
  { code: 'E05.9', description: 'Thyrotoxicosis unspecified' },
  { code: 'F20.9', description: 'Schizophrenia unspecified' },
  { code: 'K74.60', description: 'Unspecified cirrhosis of liver' },
  { code: 'E03.9', description: 'Hypothyroidism unspecified' },
  { code: 'G40.909', description: 'Epilepsy unspecified not intractable without status epilepticus' },
  { code: 'K59.00', description: 'Constipation unspecified' },
  { code: 'R32', description: 'Unspecified urinary incontinence' },
  { code: 'R06.02', description: 'Shortness of breath' },
  { code: 'I25.10', description: 'Atherosclerotic heart disease of native coronary artery without angina pectoris' },
  { code: 'G47.00', description: 'Insomnia unspecified' },
  { code: 'M48.00', description: 'Spinal stenosis site unspecified' },
  { code: 'U07.1', description: 'COVID19' },
  { code: 'K58.9', description: 'Irritable bowel syndrome without diarrhea' },
  { code: 'R00.0', description: 'Tachycardia unspecified' },
  { code: 'F03.9', description: 'Dementia unspecified' },
  { code: 'S81.809A', description: 'Unspecified open wound of lower leg initial encounter' },
  { code: 'N39.0', description: 'Urinary tract infection site not specified' },
  { code: 'Z74.1', description: 'Need for assistance with personal care' },
  { code: 'Z79.01', description: 'Long term current use of anticoagulants' },
  { code: 'F32.9', description: 'Major depressive disorder single episode unspecified' },
  { code: 'Z79.891', description: 'Long term current use of opiate analgesic' },
];

interface NurseUserRecord {
  id: string;
  username?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  portalRoles?: string[] | string;
}

interface NurseAdminSessionBackup {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
  nurseName: string;
}

interface DelegatedTicketResult {
  ticketId: string;
  route: string;
  subject: string;
  assessmentTagged: boolean;
  warnings: string[];
}

interface PendingTicketAttempt {
  description: string;
  portalRequestId: string;
  error: string;
}

const PLAN_OF_CARE_TASKS: Record<string, { defaultDescription: string }> = {
  '100 Tub': { defaultDescription: '' },
  '101 Shower': { defaultDescription: '' },
  '102 Bed / Sponge': { defaultDescription: '' },
  '103 Total care / perineal care': { defaultDescription: '' },
  '104 Mouth Care': { defaultDescription: '' },
  '105 Comb': { defaultDescription: '' },
  '106 Shampoo': { defaultDescription: '' },
  '107 Shave': { defaultDescription: '' },
  '108 File Nails': { defaultDescription: '' },
  '109 Dressing': { defaultDescription: '' },
  '110 Skin Care': { defaultDescription: 'Report wound & bruises' },
  '111 Foot Care': { defaultDescription: '' },
  '112 Toileting - Diaper': { defaultDescription: '' },
  '113 Toileting - Commode': { defaultDescription: '' },
  '114 Toileting - Bedpan': { defaultDescription: '' },
  '115 Toileting - Toilet': { defaultDescription: '' },
  '200 Prepare Prescribed Diet': { defaultDescription: '' },
  '201 Prepare Breakfast': { defaultDescription: '' },
  '202 Prepare Lunch': { defaultDescription: '' },
  '203 Prepare Dinner': { defaultDescription: '' },
  '204 Prepare Snacks': { defaultDescription: '' },
  '205 Assist with Feeding': { defaultDescription: '' },
  '206 Record Food Intake': { defaultDescription: '' },
  '207 Record Fluid Intake': { defaultDescription: '' },
  '300 Assist patient to move / change position': { defaultDescription: '' },
  '301 Assist with Walking': { defaultDescription: '' },
  '302 Use device to transfer/walk': { defaultDescription: '' },
  '303 Assist with Home Exercise Program (HHA only)': { defaultDescription: '' },
  '305 Turn & Reposition (every 2 Hrs)': { defaultDescription: '' },
  '306 Transfer with Hoyer': { defaultDescription: '' },
  '400 Check Temperature (HHA only)': { defaultDescription: '' },
  '401 Check Pulse (HHA only)': { defaultDescription: '' },
  '402 Check Blood Pressure (HHA only)': { defaultDescription: '' },
  '403 Check Respirations (HHA only)': { defaultDescription: '' },
  '404 Weigh Patient': { defaultDescription: '' },
  '405 Record Output (Urine / BM)': { defaultDescription: '' },
  '406 Assist With Catheter Care': { defaultDescription: '' },
  '407 Empty Foley Bag': { defaultDescription: '' },
  '408 Assist With Ostomy Care': { defaultDescription: '' },
  '409 Remind To Take Medications': { defaultDescription: '' },
  '410 Assist With Treatments (HHA only)': { defaultDescription: '' },
  '500 Change Bed Linen': { defaultDescription: '' },
  '501 Wash, Fold & put away Patient Laundry': { defaultDescription: '' },
  '502 Sweep, vacuum or mop patient area': { defaultDescription: '' },
  '503 Clean Patient Care Equipment': { defaultDescription: '' },
  '504 Do Patient Shopping & Errand': { defaultDescription: '' },
  '505 Accompany Patient To Medical Appt': { defaultDescription: '' },
  '506 Diversional Activities': { defaultDescription: '' },
  '507 Monitor Patient\'s Safety': { defaultDescription: '' },
  '510 Do Patient\'s dishes': { defaultDescription: '' },
  '511 Clean up after meal preparation': { defaultDescription: '' },
  '512 Empty Patient Trash': { defaultDescription: '' },
  '513 Wipe patient spills / front use surface': { defaultDescription: '' },
  '514 Dust/Wipe patient areas': { defaultDescription: '' },
  '515 Remove Patient\'s spoilt food from fridge': { defaultDescription: '' },
  '539 Follow Fall Precautions': { defaultDescription: 'Use device, stay close, keep area clear' },
  '540 Follow Seizure Precautions': { defaultDescription: 'reduce stress, bright lights, remind meds' },
  '541 Follow Bleeding precautions': { defaultDescription: '' },
  '542 Follow Infection control measures': { defaultDescription: 'wash hands, wear PPE, keep area clean' },
  '543 Follow Oxygen safety precautions': { defaultDescription: 'No smoking, no flames, store upright' },
  '544 Report wound/bruises': { defaultDescription: '' },
  '545 Follow Needles/sharps precautions': { defaultDescription: 'Use sharps box, do not recap' },
  '546 Follow choking/aspiration precautions': { defaultDescription: 'Sit upright, feed small bites, slow pace' },
  '547 Follow allergy precautions': { defaultDescription: '' },
  '600 Patient is unable to sign timesheet': { defaultDescription: '' },
};

const HHA_CONTRACTS = new Set([
  'archcare senior life (pace)',
  'centerlight healthcare (pace) (001)',
  'private pay',
]);

const DEFAULT_POC_TASKS = new Set([
  '506 Diversional Activities',
  '539 Follow Fall Precautions',
  '542 Follow Infection control measures',
]);
const SYSTEM_REQUIRED_POC_TASKS = new Set([
  ...DEFAULT_POC_TASKS,
  '544 Report wound/bruises',
  '545 Follow Needles/sharps precautions',
  '546 Follow choking/aspiration precautions',
  '547 Follow allergy precautions',
]);

const TIMESHEET_REASON_OPTIONS = ['Blind', 'Cognitively impaired', 'Dominant hand limited mobility', 'Other'];
const MEDICATION_ROUTE_OPTIONS = [
  'Oral', 'Sublingual', 'Buccal', 'Inhalation', 'Nasal', 'Ophthalmic', 'Otic (ear)', 'Topical', 'Transdermal',
  'Rectal', 'Vaginal', 'Intramuscular (IM)', 'Subcutaneous (SQ)', 'Other',
];
const MEDICATION_FREQUENCY_OPTIONS = [
  'Daily', 'Twice daily', 'Three times daily', 'Four times daily', 'Every other day', 'Every 4 hours',
  'Every 6 hours', 'Every 8 hours', 'Every 12 hours', 'Once weekly', 'Monthly', 'PRN',
  'Every 4 hours PRN', 'Every 6 hours PRN', 'Every 8 hours PRN', 'Every 12 hours PRN', 'Other',
];
const FALL_RISK_DESCRIPTIONS: Record<string, string> = {
  'Diagnosis (3 Or More Co-Existing)': 'Includes only documented medical diagnosis',
  'Prior History of Falls Within 6 Months (1-2)': 'An unintentional change in position resulting in coming to rest on the ground or at a lower level',
  'Prior History of Falls Within 6 Months (3+)': 'An unintentional change in position resulting in coming to rest on the ground or at a lower level',
  Incontinence: 'Inability to make it to the bathroom or commode in time includes frequency, urgency, and/or nocturia',
  'Visual Impairment': 'Includes but not limited to, macular degeneration, diabetic retinopathies, visual field loss, age related changes, decline in visual acuity, accommodation, glare tolerance, depth perception, night vision or not wearing prescribed glasses or the correct prescription',
  'Impaired Functional Mobility': 'May include patients who need help with iADLS or ADLs or have gait or transfer problems, arthritis, pain, fear of falling, foot problems, impaired sensation, impaired coordination, or improper use of assistive devices',
  'Environmental Hazards': 'Includes but not limited to, poor illumination, equipment, substrates, inappropriate footwear, pets, hard to reach items, floor surfaces that are uneven or cluttered, or outdoor entry exits',
  'Polypharmacy (4 Or More Prescriptions)': 'All prescriptions including OTC meds. Drugs highly associated with fall risk include but not limited to, sedatives, anti-depressants, tranquilizers, narcotics, antihypertensives, cardiac meds, corticosteroids, anti-anxiety, anticholinergic, and hypoglycemic',
  'Pain Affecting Level of Function': 'Pain often affects an individual\'s desire or ability to move, or pain can be a factor in depression or compliance with safety recommendations',
  'Cognitive Impairment': 'Could include patients with dementia, Alzheimer\'s or stroke or patients who are confused, use poor judgment, have decreased comprehension, impulsivity, memory. Consider patients ability to adhere to the POC',
};
const FALL_RISK_EMBEDDED_FIELDS = new Set([
  'fallRiskScore',
  'anyFallPreventionRecommendations',
  'requiresPtReferralDueTo',
  'fallPreventionDmeSupplyNeeded',
  'fallPreventionDmeSupplyNeedsRepair',
  'needsIncreaseInServiceHoursDueTo',
  'fallPreventionRecOther',
]);
const INTEGUMENT_WOUND_DETAIL_FIELDS = new Set([
  'wound1PressureUlcerStage',
  'wound2PressureUlcerStage',
  'wound1Location',
  'wound1LesionType',
  'wound1LesionTypeOther',
  'wound1Assessed',
  'wound1AssessedNoDueTo',
  'wound1AssessedNoDueToOther',
  'wound1CareOrder',
  'wound1CareOrderOther',
  'wound1CarePerformedBy',
  'wound1CarePerformedByOther',
  'wound1UnderMdCare',
  'wound2Location',
  'wound2LesionType',
  'wound2LesionTypeOther',
  'wound2Assessed',
  'wound2AssessedNoDueTo',
  'wound2AssessedNoDueToOther',
  'wound2CareOrder',
  'wound2CareOrderOther',
  'wound2CarePerformedBy',
  'wound2CarePerformedByOther',
  'wound2UnderMdCare',
  'additionalWounds',
]);
const PAIN_LOCATION_DETAIL_FIELDS = new Set([
  'pain1Location',
  'pain1Intensity',
  'pain1Type',
  'pain1TypeOther',
  'pain1Frequency',
  'pain1MakesBetter',
  'pain1MakesBetterOther',
  'pain1MakesWorse',
  'pain1MakesWorseOther',
  'pain2Location',
  'pain2Intensity',
  'pain2Type',
  'pain2TypeOther',
  'pain2Frequency',
  'pain2MakesBetter',
  'pain2MakesBetterOther',
  'pain2MakesWorse',
  'pain2MakesWorseOther',
]);
const PLAN_OF_CARE_SCHEDULE_FIELDS = new Set([
  'clusterPackages',
  'daysPerWeek',
  'hoursPerDay',
  'shiftType1',
  'daysPerWeek2',
  'hoursPerDay2',
  'shiftType2',
  'daysPerWeek3',
  'hoursPerDay3',
  'shiftType3',
]);
const MAX_CONSENT_ROWS = 6;
const CONSENT_COVERAGE_OPTIONS = ['100%', '50%', '0%', 'Waived'];
const NARRATIVE_STANDARD_FIELDS = new Set([
  'palliativeCare',
  'palliativeCareProviderInfo',
  'supplementalHomeServices',
  'supplementalHomeServicesChha',
  'supplementalHomeServicesHospice',
  'supplementalHomeServicesOther',
  'safetyPrecautions',
  'safetyPrecautionsOther',
  'functionalLimitation',
  'prognosis',
  'servicesProvided',
  'nextVisit',
  'rnOrders',
  'goals',
  'dischargePlan',
]);
const CONSENT_EMBEDDED_FIELDS = new Set([
  'consentSignedBy',
  'proxyName',
  'proxyRelationship',
  'proxyPhone',
  'proxyEmail',
  'patientEmail',
  'patientSelfDirecting',
  'patientHasDementia',
  'financeClearanceReceived',
]);
const COMPLETED_SUMMARY_HIDDEN_FIELDS = new Set([
  'Signature',
  'ConsentSignature',
]);
const PHONE_MASK_FIELDS = new Set([
  'phone',
  'mobile',
  'emergencyContactPhone',
  'emergencyContactPhone2',
  'emergencyContact2Phone',
  'emergencyContact2Phone2',
  'pharmacyPhone',
  'pharmacyPhone2',
  'proxyPhone',
]);
const ADDRESS_AUTOCOMPLETE_FIELD = '__addressAutocomplete';
const PETS_IN_HOME_READONLY_FIELD = '__petsInHomeReadonly';
const HOYER_CONTRACTURE_READONLY_FIELD = '__hoyerContractureReadonly';
const UNABLE_TO_ASSESS_VITALS_CHECKED_FIELD = '__unableToAssessVitalsChecked';
const IADL_FOLLOWUP_FIELDS = new Set([
  'mealPlanningAssistedBy',
  'mealPlanningAssistedOther',
  'mealPreppingAssistedBy',
  'mealPreppingAssistedOther',
  'manageMedicationAssistedBy',
  'manageMedicationAssistedOther',
  'laundryErrandsSafelyLeftAlone',
  'laundryErrandsSafetyPlan',
  'laundryErrandsSafetyPlanOther',
  'manageMoneyAssistedBy',
  'manageMoneyAssistedOther',
]);

const CONSENT_SERVICE_CONFIG: ConsentServiceConfig[] = [
  { contractTypes: ['Contract'], services: 'PCA/HHA', frequency: 'PCA/HHA', fee: '', cannotChoose: [], requiresFrequencyInput: true, isPrivatePay: false },
  { contractTypes: ['PP', 'PP (1 to 1)', 'PP (Cluster)'], services: 'PCA/HHA', frequency: 'PCA/HHA (PP)', fee: 'Private Pay', cannotChoose: [], requiresFrequencyInput: true, isPrivatePay: true },
  { contractTypes: ['All'], services: 'RN', frequency: 'Initial visit', fee: '', cannotChoose: [], requiresFrequencyInput: false, isPrivatePay: false },
  { contractTypes: ['All'], services: 'RN', frequency: 'Recertification', fee: '', cannotChoose: [], requiresFrequencyInput: false, isPrivatePay: false },
  { contractTypes: ['PP (1 to 1)', 'PP (Cluster)'], services: 'RN', frequency: 'Routine Visit', fee: '$75/visit', cannotChoose: [], requiresFrequencyInput: true, isPrivatePay: false },
  { contractTypes: ['PP (Cluster)'], services: 'RN', frequency: 'Med 1', fee: '$430/month', cannotChoose: ['Med 2', 'Med 3', 'LEAP 1-4'], requiresFrequencyInput: false, isPrivatePay: false },
  { contractTypes: ['PP (Cluster)'], services: 'HHA', frequency: 'Med 2', fee: '$625/month', cannotChoose: ['Med 1', 'Med 3', 'CC', 'LEAP 1-4'], requiresFrequencyInput: false, isPrivatePay: false },
  { contractTypes: ['PP (Cluster)'], services: 'HHA & RN', frequency: 'Med 3', fee: '$965/month', cannotChoose: ['Med 1', 'Med 2', 'CC', 'LEAP 1-4'], requiresFrequencyInput: false, isPrivatePay: false },
  { contractTypes: ['PP (Cluster)'], services: 'HHA', frequency: 'Groom 1', fee: '$150/month', cannotChoose: ['Groom 3', 'CC', 'LEAP 1-4'], requiresFrequencyInput: false, isPrivatePay: false },
  { contractTypes: ['PP (Cluster)'], services: 'HHA', frequency: 'Groom 2', fee: '$325/month', cannotChoose: ['Groom 3', 'CC', 'LEAP 1-4'], requiresFrequencyInput: false, isPrivatePay: false },
  { contractTypes: ['PP (Cluster)'], services: 'HHA', frequency: 'Groom 3', fee: '$745/month', cannotChoose: ['Groom 1', 'Groom 2', 'CC', 'LEAP 1-4'], requiresFrequencyInput: false, isPrivatePay: false },
  { contractTypes: ['PP (Cluster)'], services: 'HHA', frequency: 'AM/PM', fee: '$1,100/month', cannotChoose: ['Groom 1', 'Groom 2', 'Groom 3', 'CC', 'LEAP 1-4'], requiresFrequencyInput: false, isPrivatePay: false },
  { contractTypes: ['PP (Cluster)'], services: 'HHA', frequency: 'Escort', fee: '$600/month', cannotChoose: ['CC', 'LEAP 1-4'], requiresFrequencyInput: false, isPrivatePay: false },
  { contractTypes: ['PP (Cluster)'], services: 'HHA', frequency: 'Day Safety Checks & Toileting', fee: '$850/month', cannotChoose: ['CC', 'LEAP 1-4'], requiresFrequencyInput: false, isPrivatePay: false },
  { contractTypes: ['PP (Cluster)'], services: 'HHA', frequency: 'Night Safety Checks & Toileting', fee: '$850/month', cannotChoose: ['LEAP 1-4'], requiresFrequencyInput: false, isPrivatePay: false },
  { contractTypes: ['PP (Cluster)'], services: 'HHA', frequency: 'CC - Combined Care', fee: '$3,000/month', cannotChoose: ['Med 2', 'Med 3', 'Groom 1', 'Groom 2', 'Groom 3', 'AM/PM', 'Escort', 'Day Safety', 'LEAP 1-4'], requiresFrequencyInput: false, isPrivatePay: false },
  { contractTypes: ['PP (Cluster)'], services: 'HHA & RN', frequency: 'LEAP 1', fee: '$4,000/month', cannotChoose: ['Any cluster, except RN Routine'], requiresFrequencyInput: false, isPrivatePay: false },
  { contractTypes: ['PP (Cluster)'], services: 'HHA & RN', frequency: 'LEAP 2', fee: '$4,500/month', cannotChoose: ['Any cluster, except RN Routine'], requiresFrequencyInput: false, isPrivatePay: false },
  { contractTypes: ['PP (Cluster)'], services: 'HHA & RN', frequency: 'LEAP 3', fee: '$5,500/month', cannotChoose: ['Any cluster, except RN Routine'], requiresFrequencyInput: false, isPrivatePay: false },
  { contractTypes: ['PP (Cluster)'], services: 'HHA & RN', frequency: 'LEAP 4', fee: '$6,750/month', cannotChoose: ['Any cluster, except RN Routine'], requiresFrequencyInput: false, isPrivatePay: false },
];

const VISIT_TYPE_VISIBLE_STEPS: Record<string, number[]> = {
  Initial: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  'Triage Assessment': [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  Revisit: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  Recert: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  'Aide Supervision/Evaluation': [1, 11, 12],
  'Patient Evaluation': [1, 2, 3, 11, 12],
};

const REVISIT_CARD_GATES: Record<string, { step: number; label: string; fields: string[] }> = {
  anyNewConditionsImpactingHomeCare: {
    step: 2,
    label: 'Any new conditions impacting home care?',
    fields: ['primaryDx', 'otherPertinentDiagnosis', 'diagnosisCommentsTeaching'],
  },
  anyNewOrDiscontinuedMedication: {
    step: 2,
    label: 'Any new or discontinued medication?',
    fields: ['medications', 'medicationCommentsTeaching'],
  },
  anyAdlOrIadlFunctionStatusChanges: {
    step: 3,
    label: 'Any ADLs or IADLs function status changes?',
    fields: [
      'bathing', 'grooming', 'dressingUpper', 'dressingLower', 'toileting', 'eatingFeeding',
      'secondPersonAvailable', 'mealPlanning', 'mealPreppingCooking', 'manageMedication',
      'remindMedication', 'laundry', 'shopping', 'housekeeping', 'useThePhone',
      'manageMoney', 'safetyAwareness', 'mealPlanningAssistedBy', 'mealPlanningAssistedOther',
      'mealPreppingAssistedBy', 'mealPreppingAssistedOther', 'manageMedicationAssistedBy',
      'manageMedicationAssistedOther', 'laundryErrandsSafelyLeftAlone', 'laundryErrandsSafetyPlan',
      'laundryErrandsSafetyPlanOther', 'manageMoneyAssistedBy', 'manageMoneyAssistedOther',
    ],
  },
  anyNewOrDiscontinuedDmeOrSupplies: {
    step: 4,
    label: 'Any new or discontinued DMEs or supplies?',
    fields: ['dmeSuppliesInHome', 'dmeSuppliesOther', 'repairReplacementNeededFor'],
  },
  anyNutritionalChanges: {
    step: 5,
    label: 'Any nutritional changes?',
    fields: [
      'prescribedDiet', 'prescribedDietOther', 'consistency', 'consistencyOther', 'appetite',
      'changeInAppetite', 'changeInAppetiteDescription', 'fluids', 'fluidRestrictionAmount',
      'fluidEncourageAmount', 'fluidConsistency', 'hydrationAdequate', 'enteralFeedings',
      'enteralFeedingPerformedBy', 'enteralFeedingPerformedByOther',
    ],
  },
  anyMentalStatusChanges: {
    step: 5,
    label: 'Any mental status changes?',
    fields: [
      'orientationCognition', 'levelOfConsciousness', 'moodBehavior', 'moodBehaviorOther',
      'frequencyOfBehavior', 'frequencyOfBehaviorOther', 'communication', 'communicationOther',
      'neuro', 'cva', 'cvaExtremities', 'tremors', 'tremorsOther', 'paralysis', 'paralysisOther',
    ],
  },
  anyCardioPulmonaryStatusChanges: {
    step: 5,
    label: 'Any cardio-pulmonary status changes?',
    fields: [
      'cardioPulmonarySymptoms', 'cardioPulmonarySymptomsOther', 'orthopneaPillows', 'coughType',
      'chestPain', 'chestPainLocation', 'chestPainLocationOther', 'chestPainFrequency',
      'chestPainFrequencyOther', 'chestPainDuration', 'chestPainDurationOther', 'chestPainQuality',
      'chestPainQualityOther', 'chestPainSeverity', 'chestPainPrecipitatingFactors',
      'chestPainPrecipitatingFactorsOther', 'chestPainAssociatedSymptoms',
      'chestPainAssociatedSymptomsOther', 'chestPainRelievedBy', 'chestPainRelievedByOther',
      'oxygenRequired', 'oxygenFrequency', 'oxygenPrnPercentage', 'oxygenFlowRate',
      'oxygenDeliveryVia', 'oxygenDeliveryViaOther', 'oxygenEquipment',
    ],
  },
  anyEentStatusChanges: {
    step: 6,
    label: 'Any EENT status changes?',
    fields: ['eent', 'eentOther', 'dentures', 'denturesFit'],
  },
  anyFunctionStatusChanges: {
    step: 6,
    label: 'Any function status changes?',
    fields: [
      'musculoskeletal', 'musculoskeletalOther', 'amputation', 'cast', 'castOther',
      'rom', 'limitedRom', 'contracture', 'ambulationStatus', 'ambulatory', 'ambulatoryOther',
      'transferStatus', 'transferStatusOther', 'nonAmbulatory', 'bedMobility', 'bedMobilityOther',
      'gait', 'gaitOther', 'activitiesPermittedInHome', 'activitiesPermittedInHomeOther',
      'hoyerAgitationCombativeness', 'hoyerBedMobilityAssistMoreThanOne', 'hoyerSevereContractures',
      'hoyerPoorTrunkControl', 'hoyerRelatedAccidentHistory', 'hoyerSafeManeuveringSpaceInHome',
      'ambulationAssist', 'ambulationAssistOther', 'secondPersonAssistWhenNeeded',
      'canPatientSelfPropelWheelchair',
    ],
  },
  anyGuStatusChanges: {
    step: 7,
    label: 'Any GU status changes?',
    fields: [
      'guSymptoms', 'guSymptomsOther', 'dialysis', 'dialysisDays', 'dialysisAccessType',
      'dialysisLocation', 'urinaryCatheter', 'catheterChangedBy', 'catheterChangedByOther',
      'catheterChangedByChhaMdInfo', 'urineOutput', 'urineOutputOther', 'urinaryDiversion',
      'urinaryDiversionOther', 'urinaryStomaSite', 'urinaryStomaCondition',
      'urinaryStomaConditionOther', 'urinaryOstomyCareBy', 'urinaryOstomyCareByOther',
      'reproductiveSymptoms', 'reproductiveSymptomsOther',
    ],
  },
  anyGiStatusChanges: {
    step: 7,
    label: 'Any GI status changes?',
    fields: [
      'giSymptoms', 'giSymptomsOther', 'giCurrentTreatment', 'giCurrentTreatmentDescription',
      'bowelFrequency', 'bowelFrequencyOther', 'lastBmDate', 'giOstomy', 'giOstomyOther',
      'giStomaSite', 'giStomaCondition', 'giStomaConditionOther', 'giOutput', 'giOutputOther',
      'giOstomyCareBy', 'giOstomyCareByOther',
    ],
  },
  anyPeripheralVascularStatusChanges: {
    step: 7,
    label: 'Any peripheral vascular status changes?',
    fields: [
      'peripheralVascularSensation', 'peripheralVascularSensationOther',
      'peripheralVascularColor', 'peripheralVascularColorOther',
      'peripheralVascularTemperature', 'peripheralVascularTemperatureOther',
    ],
  },
  anyEndocrineStatusChanges: {
    step: 7,
    label: 'Any endocrine status changes?',
    fields: [
      'endocrineSymptoms', 'endocrineSymptomsOther', 'diabetesStatus', 'insulinAdministration',
      'bloodSugarTaken', 'bloodSugarValue', 'bloodSugarCondition', 'bloodSugarPostPrandialHours',
    ],
  },
  anySkinChanges: {
    step: 8,
    label: 'Any skin changes?',
    fields: [
      'integumentColor', 'integumentTurgor', 'integumentTemp', 'integumentCondition', 'skinProblems',
      'wound1PressureUlcerStage', 'wound1Location', 'wound1LesionType', 'wound1LesionTypeOther',
      'wound1Assessed', 'wound1AssessedNoDueTo', 'wound1AssessedNoDueToOther', 'wound1CareOrder',
      'wound1CareOrderOther', 'wound1CarePerformedBy', 'wound1CarePerformedByOther',
      'wound1UnderMdCare', 'wound2Location', 'wound2LesionType', 'wound2LesionTypeOther',
      'wound2Assessed', 'wound2AssessedNoDueTo', 'wound2AssessedNoDueToOther', 'wound2CareOrder',
      'wound2CareOrderOther', 'wound2CarePerformedBy', 'wound2CarePerformedByOther',
      'wound2UnderMdCare', 'wound2PressureUlcerStage', 'additionalWounds',
    ],
  },
  anyNewPain: {
    step: 8,
    label: 'Any new pain?',
    fields: [
      'painHistory', 'pain1Location', 'pain1Intensity', 'pain1Type', 'pain1TypeOther',
      'pain1Frequency', 'pain1MakesBetter', 'pain1MakesBetterOther', 'pain1MakesWorse',
      'pain1MakesWorseOther', 'pain2Location', 'pain2Intensity', 'pain2Type', 'pain2TypeOther',
      'pain2Frequency', 'pain2MakesBetter', 'pain2MakesBetterOther', 'pain2MakesWorse',
      'pain2MakesWorseOther',
    ],
  },
  isPocUpdateNeeded: {
    step: 10,
    label: 'Is POC update needed?',
    fields: [
      'clusterPackages',
      'pocStartDate', 'pocEndDate', 'planOfCareReviewedWith', 'planOfCareReviewedWithOther',
      'planOfCareNote', 'planOfCareTasks', 'shiftType1', 'shiftType2', 'shiftType3',
    ],
  },
};

const REVISIT_FIELD_TO_GATE = Object.entries(REVISIT_CARD_GATES).reduce<Record<string, string>>((acc, [gateField, config]) => {
  config.fields.forEach((field) => {
    acc[field] = gateField;
  });
  return acc;
}, {});

const LEGACY_RADIO_PICKLIST_FIELDS = new Set([
  'adequateCookingFacility',
  'adequateMedSupplyAtHome',
  'adequatePlumbingSanitation',
  'adequateVentilationHeating',
  'areServicesContinuing',
  'bathing',
  'buildingHasSecurityDeskDoorman',
  'buildingHasSuper',
  'clutteredPathway',
  'dressingLower',
  'dressingUpper',
  'eatingFeeding',
  'fireEscapeSecondExit',
  'fireExtinguisher',
  'grooming',
  'hasElectric',
  'hasInjections',
  'hasRunningWater',
  'hasWorkingPhone',
  'hhaPcaCompliesWithPoc',
  'homeDementiaSafe',
  'housekeeping',
  'laundry',
  'lightedPathway',
  'manageMedication',
  'manageMoney',
  'mealPlanning',
  'mealPreppingCooking',
  'medPrefillReferralRequired',
  'medicationSideEffects',
  'medicationTeachingReviewed',
  'medicationsStoredProperly',
  'oxygenSafelyStored',
  'patientFamilyCooperative',
  'patientSatisfiedWithServices',
  'patientTreatedWithRespect',
  'pcaHhaCommunicatesEffectively',
  'remindMedication',
  'rnIntervention',
  'roachRodentBedBugInfestation',
  'safetyAwareness',
  'scatterRugsHazards',
  'shopping',
  'structuralHazards',
  'takesMedicationsAsOrdered',
  'toileting',
  'useThePhone',
  'wcAccessible',
  'workingSmokeDetectors',
]);

function getFieldLabel(fieldName: string, fields: Record<string, FieldDef>): string {
  if (fieldName === 'Signature') return 'Signature';
  if (fieldName === 'ConsentSignature') return 'Consent Signature';
  if (LEGACY_FIELD_LABEL_OVERRIDES[fieldName]) return LEGACY_FIELD_LABEL_OVERRIDES[fieldName];
  const nexusField = fields[fieldName];
  if (nexusField?.label) return nexusField.label;
  return fieldName
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function isLongText(fieldName: string, field?: FieldDef): boolean {
  if (fieldName === 'planOfCareNote') return true;
  return field?.ui?.multiline === true;
}

function isExactNoneSelection(value: unknown): boolean {
  if (Array.isArray(value)) return value.length === 1 && fieldValueEquals(value[0], 'None');
  return fieldValueEquals(value, 'None');
}

function fieldGridSx(cardKey: string, fieldName: string, field?: FieldDef, data: Record<string, unknown> = {}) {
  if (fieldName === ADDRESS_AUTOCOMPLETE_FIELD) return { gridColumn: '1 / -1', mt: 1 };
  if (fieldName === PETS_IN_HOME_READONLY_FIELD) return { gridColumn: { xs: '1 / -1', md: 'span 1' } };
  if (fieldName === HOYER_CONTRACTURE_READONLY_FIELD) return { gridColumn: '1 / -1' };
  if (cardKey === 'visitDetails') return { gridColumn: { xs: '1 / -1', md: 'span 1' } };
  if (cardKey === 'medicalDiagnosis') return { gridColumn: '1 / -1' };
  if (cardKey === 'planOfCare') {
    if (['pocStartDate', 'pocEndDate'].includes(fieldName)) {
      return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    }
    if (fieldName === 'planOfCareReviewedWithOther') {
      return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    }
    return { gridColumn: '1 / -1' };
  }
  if (cardKey === 'patientInfo') {
    if (['patientSelfDirecting', 'patientPhoneForEvv'].includes(fieldName)) return { gridColumn: '1 / -1' };
    if (fieldName === 'petsInHome') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.petsInHome, 'Yes') ? 'span 1' : '1 / -1' } };
    if (fieldName === 'petsInHomeList') return { gridColumn: { xs: '1 / -1', md: 'span 1' } };
    if (fieldName === 'pertinentHouseRules') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.pertinentHouseRules, 'Other') ? 'span 1' : '1 / -1' } };
    if (fieldName === 'pertinentHouseRulesOther') return { gridColumn: { xs: '1 / -1', md: 'span 1' } };
  }
  if (cardKey === 'emergencyContact' && fieldName === 'hasNoEmergencyContact') {
    return { gridColumn: '1 / -1' };
  }
  if (cardKey === 'physicianInfo' && ['pcp', 'pcpSelectedAddress', 'pcpInfo', 'note'].includes(fieldName)) {
    return { gridColumn: '1 / -1' };
  }
  if (cardKey === 'advancedDirectives') {
    if (fieldName === 'advanceDirectives') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.advanceDirectives, 'No, patient/HCP was educated') ? '1 / -1' : 'span 1' } };
    if (fieldName === 'copyObtained') return { gridColumn: { xs: '1 / -1', md: 'span 1' } };
  }
  if (cardKey === 'emergencyPreparedness') {
    if (['missingAidePoseSafetyRisk', 'backupAideAvailable', 'backupAideDetails', 'priorityCode', 'mobilityStatus'].includes(fieldName)) return { gridColumn: '1 / -1' };
    if (fieldName === 'electricDependency') return { gridColumn: { xs: '1 / -1', md: !isExactNoneSelection(data.electricDependency) && hasStoredValue(data.electricDependency) ? 'span 1' : '1 / -1' } };
    if (fieldName === 'lifeSustainingDmeCompanyInfo') return { gridColumn: { xs: '1 / -1', md: 'span 1' } };
    if (fieldName === 'medicalSuppliesPetsInfo') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.medicalSuppliesPetsInfo, 'Pets') ? 'span 1' : '1 / -1' } };
    if (fieldName === 'evacuationLocation') return { gridColumn: { xs: '1 / -1', md: '1 / span 1' } };
    if (fieldName === 'relativeFriendOtherContact') return { gridColumn: { xs: '1 / -1', md: 'span 1' } };
  }
  if (cardKey === 'adls') return { gridColumn: '1 / -1' };
  if (cardKey === 'iadls') {
    const assistedByOtherField: Record<string, string> = {
      mealPlanningAssistedBy: 'mealPlanningAssistedOther',
      mealPreppingAssistedBy: 'mealPreppingAssistedOther',
      manageMedicationAssistedBy: 'manageMedicationAssistedOther',
      laundryAssistedBy: 'laundryAssistedOther',
      shoppingAssistedBy: 'shoppingAssistedOther',
      manageMoneyAssistedBy: 'manageMoneyAssistedOther',
      laundryErrandsSafetyPlan: 'laundryErrandsSafetyPlanOther',
    };
    if (fieldName === 'laundryErrandsSafelyLeftAlone') return { gridColumn: '1 / -1' };
    if (assistedByOtherField[fieldName]) {
      return {
        gridColumn: {
          xs: '1 / -1',
          md: isFieldVisible(assistedByOtherField[fieldName], data) ? 'span 1' : '1 / -1',
        },
      };
    }
    if ([
      'mealPlanningAssistedOther',
      'mealPreppingAssistedOther',
      'manageMedicationAssistedOther',
      'laundryAssistedOther',
      'shoppingAssistedOther',
      'laundryErrandsSafetyPlanOther',
      'manageMoneyAssistedOther',
    ].includes(fieldName)) return { gridColumn: { xs: '1 / -1', md: 'span 1' } };
  }
  if (cardKey === 'environment') {
    if (['patientLivesIn', 'aptSleepSpaceForLiveIn'].includes(fieldName)) {
      return { gridColumn: '1 / -1' };
    }
    if ([
      'wcAccessible',
      'buildingHasSuper',
      'buildingHasSecurityDeskDoorman',
      'adequateVentilationHeating',
      'adequatePlumbingSanitation',
      'roachRodentBedBugInfestation',
      'homeDementiaSafe',
      'hasElectric',
      'hasRunningWater',
      'hasWorkingPhone',
      'clutteredPathway',
      'lightedPathway',
      'scatterRugsHazards',
      'structuralHazards',
      'adequateCookingFacility',
      'oxygenSafelyStored',
      'fireExtinguisher',
      'fireEscapeSecondExit',
      'workingSmokeDetectors',
    ].includes(fieldName)) {
      return { gridColumn: { xs: '1 / -1', md: 'span 4' } };
    }
    if (fieldName === 'environmentalIssuesAddressed') {
      return { gridColumn: { xs: '1 / -1', md: isFieldVisible('environmentalIssuesAddressedDescribe', data) ? '1 / span 6' : '1 / -1' } };
    }
    if (fieldName === 'environmentalIssuesAddressedDescribe') {
      return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    }
    if (['environmentOther', 'requiresReferralFor'].includes(fieldName)) {
      return { gridColumn: '1 / -1' };
    }
    if (fieldName === 'substanceUse') {
      return { gridColumn: { xs: '1 / -1', md: isFieldVisible('substanceUseOther', data) ? '1 / span 8' : '1 / -1' } };
    }
    if (fieldName === 'signsOfAbuseNeglect') {
      return { gridColumn: { xs: '1 / -1', md: isFieldVisible('signsOfAbuseNeglectOther', data) ? '1 / span 8' : '1 / -1' } };
    }
    if (['supportSystem', 'otherSupportSystem', 'livingSituation'].includes(fieldName)) {
      return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    }
    if (['substanceUseOther', 'signsOfAbuseNeglectOther'].includes(fieldName)) {
      return { gridColumn: { xs: '1 / -1', md: 'span 4' } };
    }
  }
  if (cardKey === 'dmeSupplies') {
    if (['dmeSuppliesInHome', 'dmeSuppliesOther', 'dmeSupplyKnowledge'].includes(fieldName)) return { gridColumn: '1 / -1' };
    if (fieldName === 'equipmentCondition') {
      return { gridColumn: { xs: '1 / -1', md: isFieldVisible('repairReplacementNeededFor', data) ? 'span 6' : '1 / -1' } };
    }
    if (fieldName === 'repairReplacementNeededFor') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'dmeSuppliesNeeded') {
      return { gridColumn: { xs: '1 / -1', md: isFieldVisible('neededDmeSupplies', data) ? 'span 6' : '1 / -1' } };
    }
    if (fieldName === 'neededDmeSupplies') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
  }
  if (cardKey === 'vitalSigns') {
    if (['bloodPressure', 'pulseRate', 'respirationRate', 'temp'].includes(fieldName)) {
      return { gridColumn: { xs: '1 / -1', md: 'span 3' } };
    }
    if (['heightFt', 'heightIn'].includes(fieldName)) {
      return { gridColumn: { xs: 'span 6', md: 'span 2' } };
    }
    if (['weight', 'pulseOxygen'].includes(fieldName)) {
      return { gridColumn: { xs: '1 / -1', md: 'span 4' } };
    }
    if (fieldName === 'anyChangeToPatientWeight') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.anyChangeToPatientWeight, 'Yes') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'anyChangeToPatientWeightExplain') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'unableToAssessVitals') return { gridColumn: '1 / -1' };
    if (fieldName === 'bowelSounds') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.bowelSounds, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'bowelSoundsOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (['lungSounds', 'pedalPulsePresent'].includes(fieldName)) return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'pedalEdema') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.pedalEdema, 'Yes') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'pedalEdemaDescription') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'hasAllergies') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.hasAllergies, 'Yes') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'allergies') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    return { gridColumn: '1 / -1' };
  }
  if (cardKey === 'pharmacy') {
    if (['pharmacyName', 'pharmacyPhone', 'pharmacyName2', 'pharmacyPhone2'].includes(fieldName)) {
      return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    }
    if ([
      'adequateMedSupplyAtHome',
      'medicationsStoredProperly',
      'takesMedicationsAsOrdered',
      'medicationTeachingReviewed',
      'medicationSideEffects',
      'hasInjections',
      'medPrefillReferralRequired',
      'rnIntervention',
    ].includes(fieldName)) {
      const explanationByField: Record<string, string> = {
        adequateMedSupplyAtHome: 'adequateMedSupplyAtHomeExplain',
        medicationsStoredProperly: 'medicationsStoredProperlyExplain',
        takesMedicationsAsOrdered: 'takesMedicationsAsOrderedExplain',
        medicationTeachingReviewed: 'medicationTeachingReviewedExplain',
        medicationSideEffects: 'sideEffectsExplanation',
        hasInjections: 'injectionsAdministeredBy',
        rnIntervention: 'rnPrePouredDays',
      };
      const explanationField = explanationByField[fieldName];
      return { gridColumn: { xs: '1 / -1', md: explanationField && isFieldVisible(explanationField, data) ? 'span 7' : fieldName === 'medPrefillReferralRequired' ? 'span 7' : '1 / -1' } };
    }
    if ([
      'adequateMedSupplyAtHomeExplain',
      'medicationsStoredProperlyExplain',
      'takesMedicationsAsOrderedExplain',
      'medicationTeachingReviewedExplain',
      'sideEffectsExplanation',
      'injectionsAdministeredBy',
      'rnPrePouredDays',
    ].includes(fieldName)) {
      return { gridColumn: { xs: '1 / -1', md: 'span 5' } };
    }
    return { gridColumn: '1 / -1' };
  }
  if (cardKey === 'nutrition') {
    if (fieldName === 'prescribedDiet') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.prescribedDiet, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'prescribedDietOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'consistency') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.consistency, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'consistencyOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (['appetite', 'changeInAppetite'].includes(fieldName)) return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.changeInAppetite, 'Yes') ? 'span 4' : 'span 6' } };
    if (fieldName === 'changeInAppetiteDescription') return { gridColumn: { xs: '1 / -1', md: 'span 4' } };
    if (['fluids', 'fluidConsistency'].includes(fieldName)) {
      const hasFluidAmount = fieldValueEquals(data.fluids, 'Restriction') || fieldValueEquals(data.fluids, 'Encourage');
      return { gridColumn: { xs: '1 / -1', md: hasFluidAmount ? 'span 4' : 'span 6' } };
    }
    if (['fluidRestrictionAmount', 'fluidEncourageAmount'].includes(fieldName)) return { gridColumn: { xs: '1 / -1', md: 'span 4' } };
    if (fieldName === 'hydrationAdequate') return { gridColumn: '1 / -1' };
    if (fieldName === 'enteralFeedings') {
      const enteralPerformedBy = data.enteralFeedingPerformedBy;
      const enteralValue = data.enteralFeedings;
      return {
        gridColumn: {
          xs: '1 / -1',
          md: fieldValueEquals(enteralValue, 'No')
            ? '1 / -1'
            : fieldValueEquals(enteralPerformedBy, 'Other')
              ? 'span 4'
              : enteralValue ? 'span 6' : '1 / -1',
        },
      };
    }
    if (fieldName === 'enteralFeedingPerformedBy') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.enteralFeedingPerformedBy, 'Other') ? 'span 4' : 'span 6' } };
    if (fieldName === 'enteralFeedingPerformedByOther') return { gridColumn: { xs: '1 / -1', md: 'span 4' } };
  }
  if (cardKey === 'neurological') {
    if (['orientationCognition', 'levelOfConsciousness'].includes(fieldName)) return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'moodBehavior') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.moodBehavior, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'moodBehaviorOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'frequencyOfBehavior') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.frequencyOfBehavior, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'frequencyOfBehaviorOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'neuro') return { gridColumn: '1 / -1' };
    if (fieldName === 'communication') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.communication, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'communicationOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'cva') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.cva, 'Residual weakness') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'cvaExtremities') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'tremors') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.tremors, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'tremorsOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'paralysis') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.paralysis, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'paralysisOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
  }
  if (cardKey === 'cardiovascularPulmonary') {
    if (fieldName === 'cardioPulmonarySymptoms') return { gridColumn: '1 / -1' };
    if (['cardioPulmonarySymptomsOther', 'orthopneaPillows'].includes(fieldName)) return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'coughType') return { gridColumn: '1 / -1' };
  }
  if (cardKey === 'chestPain') {
    if ([
      'chestPainFrequency',
      'chestPainDuration',
      'chestPainLocation',
      'chestPainQuality',
      'chestPainPrecipitatingFactors',
      'chestPainRelievedBy',
      'chestPainAssociatedSymptoms',
    ].includes(fieldName)) {
      const hasOther = fieldName === 'chestPainAssociatedSymptoms'
        ? fieldValueIncludes(data[fieldName], 'Other')
        : fieldValueEquals(data[fieldName], 'Other');
      return { gridColumn: { xs: '1 / -1', md: hasOther ? 'span 6' : '1 / -1' } };
    }
    if (fieldName.endsWith('Other')) return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'chestPainSeverity') return { gridColumn: '1 / -1' };
  }
  if (cardKey === 'oxygen') {
    if (['oxygenEquipment', 'oxygenFlowRate'].includes(fieldName)) return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'oxygenFrequency') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.oxygenFrequency, 'PRN for SOB') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'oxygenPrnPercentage') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'oxygenDeliveryVia') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.oxygenDeliveryVia, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'oxygenDeliveryViaOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
  }
  if (cardKey === 'eent') {
    if (['eent', 'eentOther'].includes(fieldName)) return { gridColumn: '1 / -1' };
    if (['dentures', 'denturesFit'].includes(fieldName)) return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
  }
  if (cardKey === 'musculoskeletal') {
    if (fieldName === 'musculoskeletal') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.musculoskeletal, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'musculoskeletalOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'amputation') {
      const hasCast = fieldValueIncludes(data.musculoskeletal, 'Cast');
      return { gridColumn: { xs: '1 / -1', md: hasCast ? 'span 6' : '1 / -1' } };
    }
    if (fieldName === 'cast') {
      const hasAmputation = fieldValueIncludes(data.musculoskeletal, 'Amputation');
      const hasCastOther = fieldValueIncludes(data.cast, 'Other');
      return { gridColumn: { xs: '1 / -1', md: hasAmputation && hasCastOther ? 'span 3' : hasAmputation || hasCastOther ? 'span 6' : '1 / -1' } };
    }
    if (fieldName === 'castOther') {
      const hasAmputation = fieldValueIncludes(data.musculoskeletal, 'Amputation');
      return { gridColumn: { xs: '1 / -1', md: hasAmputation ? 'span 3' : 'span 6' } };
    }
    if (fieldName === 'rom') {
      const hasLimited = fieldValueIncludes(data.rom, 'Limited ROM');
      const hasContracture = fieldValueIncludes(data.rom, 'Contracture');
      return { gridColumn: { xs: '1 / -1', md: hasLimited && hasContracture ? 'span 4' : hasLimited || hasContracture ? 'span 6' : '1 / -1' } };
    }
    if (['limitedRom', 'contracture'].includes(fieldName)) {
      const hasLimited = fieldValueIncludes(data.rom, 'Limited ROM');
      const hasContracture = fieldValueIncludes(data.rom, 'Contracture');
      return { gridColumn: { xs: '1 / -1', md: hasLimited && hasContracture ? 'span 4' : 'span 6' } };
    }
    if (['ambulationStatus', 'nonAmbulatory'].includes(fieldName)) return { gridColumn: '1 / -1' };
    if (fieldName === 'ambulatory') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.ambulatory, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'ambulatoryOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'transferStatus') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.transferStatus, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'transferStatusOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'ambulationAssist') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.ambulationAssist, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'ambulationAssistOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'bedMobility') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.bedMobility, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'bedMobilityOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (['canPatientSelfPropelWheelchair', 'secondPersonAssistWhenNeeded'].includes(fieldName)) return { gridColumn: '1 / -1' };
    if (fieldName === 'gait') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.gait, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'gaitOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'activitiesPermittedInHome') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.activitiesPermittedInHome, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'activitiesPermittedInHomeOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
  }
  if (cardKey === 'hoyerEvaluation' || cardKey === 'fallRisk') return { gridColumn: '1 / -1' };
  if (cardKey === 'guReproductive') {
    if (fieldName === 'guSymptoms') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.guSymptoms, 'Other') ? 'span 8' : '1 / -1' } };
    if (fieldName === 'guSymptomsOther') return { gridColumn: { xs: '1 / -1', md: 'span 4' } };
    if (fieldName === 'dialysis') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.dialysis, 'Hemodialysis') ? 'span 4' : '1 / -1' } };
    if (['dialysisAccessType', 'dialysisLocation'].includes(fieldName)) return { gridColumn: { xs: '1 / -1', md: 'span 4' } };
    if (['dialysisDays', 'urinaryCatheter', 'catheterChangedByChhaMdInfo'].includes(fieldName)) return { gridColumn: '1 / -1' };
    if (fieldName === 'catheterChangedBy') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.catheterChangedBy, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'catheterChangedByOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'urineOutput') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.urineOutput, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'urineOutputOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'urinaryDiversion') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.urinaryDiversion, 'Other') ? 'span 8' : '1 / -1' } };
    if (fieldName === 'urinaryDiversionOther') return { gridColumn: { xs: '1 / -1', md: 'span 4' } };
    if (['urinaryStomaSite', 'urinaryStomaCondition'].includes(fieldName)) return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.urinaryStomaCondition, 'Other') ? 'span 4' : 'span 6' } };
    if (fieldName === 'urinaryStomaConditionOther') return { gridColumn: { xs: '1 / -1', md: 'span 4' } };
    if (fieldName === 'urinaryOstomyCareBy') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.urinaryOstomyCareBy, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'urinaryOstomyCareByOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'reproductiveSymptoms') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.reproductiveSymptoms, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'reproductiveSymptomsOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
  }
  if (cardKey === 'peripheralVascular') {
    if (fieldName === 'peripheralVascularSensation') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.peripheralVascularSensation, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'peripheralVascularSensationOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'peripheralVascularColor') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.peripheralVascularColor, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'peripheralVascularColorOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'peripheralVascularTemperature') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.peripheralVascularTemperature, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'peripheralVascularTemperatureOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
  }
  if (cardKey === 'endocrineHematopoietic') {
    if (fieldName === 'endocrineSymptoms') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.endocrineSymptoms, 'Other') ? 'span 8' : '1 / -1' } };
    if (fieldName === 'endocrineSymptomsOther') return { gridColumn: { xs: '1 / -1', md: 'span 4' } };
    if (fieldName === 'diabetesStatus') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.diabetesStatus, 'Insulin') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'insulinAdministration') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'bloodSugarTaken') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.bloodSugarTaken, 'Yes') ? 'span 4' : '1 / -1' } };
    if (fieldName === 'bloodSugarValue') return { gridColumn: { xs: '1 / -1', md: 'span 4' } };
    if (fieldName === 'bloodSugarCondition') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.bloodSugarCondition, 'Post prandial') ? 'span 2' : 'span 4' } };
    if (fieldName === 'bloodSugarPostPrandialHours') return { gridColumn: { xs: '1 / -1', md: 'span 2' } };
    if (fieldName === 'intervention') return { gridColumn: '1 / -1' };
  }
  if (cardKey === 'gi') {
    if (fieldName === 'giSymptoms') return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data.giSymptoms, 'Other') ? 'span 8' : '1 / -1' } };
    if (fieldName === 'giSymptomsOther') return { gridColumn: { xs: '1 / -1', md: 'span 4' } };
    if (fieldName === 'giCurrentTreatment') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.giCurrentTreatment, 'Yes') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'giCurrentTreatmentDescription') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'bowelFrequency') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.bowelFrequency, 'Other') ? 'span 4' : 'span 6' } };
    if (fieldName === 'bowelFrequencyOther') return { gridColumn: { xs: '1 / -1', md: 'span 2' } };
    if (fieldName === 'lastBmDate') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'giOstomy') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.giOstomy, 'Other') ? 'span 8' : '1 / -1' } };
    if (fieldName === 'giOstomyOther') return { gridColumn: { xs: '1 / -1', md: 'span 4' } };
    if (['giStomaSite', 'giStomaCondition'].includes(fieldName)) return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.giStomaCondition, 'Other') ? 'span 4' : 'span 6' } };
    if (fieldName === 'giStomaConditionOther') return { gridColumn: { xs: '1 / -1', md: 'span 4' } };
    if (fieldName === 'giOutput') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.giOutput, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'giOutputOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName === 'giOstomyCareBy') return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data.giOstomyCareBy, 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName === 'giOstomyCareByOther') return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
  }
  if (cardKey === 'integument') {
    if (['integumentColor', 'integumentTurgor', 'integumentTemp', 'integumentCondition'].includes(fieldName)) return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    return { gridColumn: '1 / -1' };
  }
  if (cardKey.startsWith('wound')) {
    if (fieldName.endsWith('Location') || fieldName.endsWith('PressureUlcerStage') || fieldName.endsWith('UnderMdCare')) return { gridColumn: '1 / -1' };
    if (fieldName.includes('LesionType') && !fieldName.endsWith('Other')) return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data[fieldName], 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName.includes('Assessed') && !fieldName.endsWith('Other') && !fieldName.includes('NoDueTo')) return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data[fieldName], 'No, due to') ? fieldValueEquals(data[fieldName.replace('Assessed', 'AssessedNoDueTo')], 'Other') ? 'span 4' : 'span 6' : '1 / -1' } };
    if (fieldName.includes('AssessedNoDueTo') && !fieldName.endsWith('Other')) return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data[fieldName], 'Other') ? 'span 4' : 'span 6' } };
    if (fieldName.includes('AssessedNoDueTo') && fieldName.endsWith('Other')) return { gridColumn: { xs: '1 / -1', md: 'span 4' } };
    if (fieldName.includes('CareOrder') && !fieldName.endsWith('Other')) return { gridColumn: { xs: '1 / -1', md: fieldValueEquals(data[fieldName], 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName.includes('CarePerformedBy') && !fieldName.endsWith('Other')) return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data[fieldName], 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName.endsWith('Other')) return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
  }
  if (cardKey === 'additionalWounds') return { gridColumn: '1 / -1' };
  if (cardKey === 'painManagement') return { gridColumn: '1 / -1' };
  if (cardKey.startsWith('painLocation')) {
    if (['pain1Location', 'pain1Intensity', 'pain2Location', 'pain2Intensity'].includes(fieldName)) return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    if (fieldName.endsWith('Type') || fieldName.endsWith('MakesBetter') || fieldName.endsWith('MakesWorse')) return { gridColumn: { xs: '1 / -1', md: fieldValueIncludes(data[fieldName], 'Other') ? 'span 6' : '1 / -1' } };
    if (fieldName.endsWith('Other')) return { gridColumn: { xs: '1 / -1', md: 'span 6' } };
    return { gridColumn: '1 / -1' };
  }
  if (cardKey === 'narrative') return { gridColumn: '1 / -1' };
  if (field?.dataType === 'MULTIPICKLIST' || isLongText(fieldName, field)) return { gridColumn: '1 / -1' };
  return undefined;
}

const readOnlyTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    cursor: 'default',
    backgroundColor: '#f5f5f5',
  },
  '& .MuiInputBase-input': {
    cursor: 'default !important',
    backgroundColor: 'transparent !important',
    pointerEvents: 'none',
  },
  '& .MuiInputBase-inputMultiline': {
    cursor: 'default !important',
    backgroundColor: 'transparent !important',
    pointerEvents: 'none',
  },
};

const nursingDateFieldSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: 56,
    alignItems: 'center',
  },
  '& .MuiInputBase-input': {
    minHeight: 23,
    py: 2,
  },
  '& .MuiInputLabel-root': {
    mt: 0,
  },
};

function cardGridTemplateColumns(cardKey: string) {
  if ([
    'environment',
    'dmeSupplies',
    'vitalSigns',
    'pharmacy',
    'nutrition',
    'neurological',
    'cardiovascularPulmonary',
    'chestPain',
    'oxygen',
    'eent',
    'musculoskeletal',
    'hoyerEvaluation',
    'fallRisk',
    'guReproductive',
    'peripheralVascular',
    'endocrineHematopoietic',
    'gi',
    'integument',
    'wound1',
    'wound2',
    'additionalWounds',
    'painManagement',
    'painLocation1',
    'painLocation2',
    'narrative',
    'planOfCare',
  ].includes(cardKey)) {
    return { xs: '1fr', md: 'repeat(12, minmax(0, 1fr))' };
  }
  return { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' };
}

function iconForCard(cardKey: string) {
  switch (cardKey) {
    case 'visitDetails':
      return CalendarTodayIcon;
    case 'patientInfo':
      return PersonIcon;
    case 'physicianInfo':
      return LocalHospitalIcon;
    case 'emergencyContact':
    case 'emergencyContact2':
    case 'additionalEmergencyContact':
      return ContactEmergencyIcon;
    case 'advancedDirectives':
      return SecurityIcon;
    case 'consent':
    case 'emergencyPreparedness':
      return ShieldIcon;
    case 'vitalSigns':
      return MonitorHeartIcon;
    case 'medicalDiagnosis':
      return MedicalInformationIcon;
    case 'medications':
    case 'pharmacy':
      return MedicationIcon;
    case 'adls':
      return PersonIcon;
    case 'iadls':
    case 'environment':
      return HomeWorkIcon;
    case 'dmeSupplies':
      return HomeIcon;
    case 'nutrition':
      return RestaurantIcon;
    case 'neurological':
    case 'cardioPulmonary':
    case 'cardiovascularPulmonary':
    case 'guReproductive':
    case 'peripheralVascular':
    case 'endocrineHematopoietic':
    case 'gi':
      return MedicalServicesIcon;
    case 'chestPain':
    case 'oxygen':
    case 'musculoskeletal':
    case 'hoyerEvaluation':
      return HealingIcon;
    case 'eent':
    case 'fallRisk':
      return ShieldIcon;
    case 'integument':
      return AccessibilityIcon;
    case 'painManagement':
    case 'painLocation1':
    case 'painLocation2':
      return HealingIcon;
    case 'wound1':
    case 'wound2':
      return HealingIcon;
    case 'additionalWounds':
      return AssignmentIcon;
    case 'narrativeAssessment':
    case 'planOfCare':
    case 'expandedHandOff':
      return AssignmentIcon;
    default:
      return CheckCircleOutlineIcon;
  }
}

function orderedCardEntries(stepKey: string, cards: Record<string, CardDef>) {
  const entries = Object.entries(cards);
  if (stepKey === 'step7') {
    const legacyStep7Order = ['guReproductive', 'gi', 'peripheralVascular', 'endocrineHematopoietic'];
    const orderedKeys = new Set(legacyStep7Order);
    return [
      ...legacyStep7Order
        .map((cardKey) => cards[cardKey] ? [cardKey, cards[cardKey]] as [string, CardDef] : null)
        .filter((entry): entry is [string, CardDef] => Boolean(entry)),
      ...entries.filter(([cardKey]) => !orderedKeys.has(cardKey)),
    ];
  }
  if (stepKey !== 'step1') return entries;

  const legacyStep1Order = [
    'patientInfo',
    'emergencyContact',
    'emergencyContact2',
    'additionalEmergencyContact',
    'physicianInfo',
    'advancedDirectives',
    'emergencyPreparedness',
  ];
  const orderedKeys = new Set(legacyStep1Order);
  return [
    ...legacyStep1Order
      .map((cardKey) => cards[cardKey] ? [cardKey, cards[cardKey]] as [string, CardDef] : null)
      .filter((entry): entry is [string, CardDef] => Boolean(entry)),
    ...entries.filter(([cardKey]) => cardKey !== 'consent' && !orderedKeys.has(cardKey)),
  ];
}

function orderedFieldNames(cardKey: string, fields: string[], data: Record<string, unknown> = {}) {
  if (cardKey === 'physicianInfo') {
    const legacyPhysicianInfoOrder = [
      'requiresPcpReferral',
      'pcpNotInSystem',
      'pcp',
      'pcpInfo',
      'pcpSelectedAddress',
      'note',
    ];
    const fieldSet = new Set(fields);
    const orderedSet = new Set(legacyPhysicianInfoOrder);
    return [
      ...legacyPhysicianInfoOrder.filter((fieldName) => fieldSet.has(fieldName)),
      ...fields.filter((fieldName) => !orderedSet.has(fieldName)),
    ];
  }

  if (cardKey === 'patientInfo' && fields.includes('street')) {
    const nextFields = fields.filter((fieldName) => fieldName !== ADDRESS_AUTOCOMPLETE_FIELD);
    const streetIndex = nextFields.indexOf('street');
    return [
      ...nextFields.slice(0, streetIndex),
      ADDRESS_AUTOCOMPLETE_FIELD,
      ...nextFields.slice(streetIndex),
    ];
  }
  if (cardKey === 'emergencyPreparedness' && fields.includes('medicalSuppliesPetsInfo')) {
    const nextFields = fields.filter((fieldName) => fieldName !== PETS_IN_HOME_READONLY_FIELD);
    const medicalSuppliesIndex = nextFields.indexOf('medicalSuppliesPetsInfo');
    return [
      ...nextFields.slice(0, medicalSuppliesIndex + 1),
      PETS_IN_HOME_READONLY_FIELD,
      ...nextFields.slice(medicalSuppliesIndex + 1),
    ];
  }
  if (cardKey === 'hoyerEvaluation' && fields.includes('hoyerSevereContractures')) {
    const nextFields = fields.filter((fieldName) => fieldName !== HOYER_CONTRACTURE_READONLY_FIELD);
    const severeIndex = nextFields.indexOf('hoyerSevereContractures');
    return [
      ...nextFields.slice(0, severeIndex + 1),
      HOYER_CONTRACTURE_READONLY_FIELD,
      ...nextFields.slice(severeIndex + 1),
    ];
  }

  if (cardKey === 'chestPain') {
    const legacyChestPainOrder = [
      'chestPainFrequency',
      'chestPainFrequencyOther',
      'chestPainDuration',
      'chestPainDurationOther',
      'chestPainLocation',
      'chestPainLocationOther',
      'chestPainQuality',
      'chestPainQualityOther',
      'chestPainSeverity',
      'chestPainPrecipitatingFactors',
      'chestPainPrecipitatingFactorsOther',
      'chestPainRelievedBy',
      'chestPainRelievedByOther',
      'chestPainAssociatedSymptoms',
      'chestPainAssociatedSymptomsOther',
    ];
    const fieldSet = new Set(fields);
    const orderedSet = new Set(legacyChestPainOrder);
    return [
      ...legacyChestPainOrder.filter((fieldName) => fieldSet.has(fieldName)),
      ...fields.filter((fieldName) => !orderedSet.has(fieldName)),
    ];
  }

  if (cardKey === 'oxygen') {
    const legacyOxygenOrder = [
      'oxygenEquipment',
      'oxygenFlowRate',
      'oxygenFrequency',
      'oxygenPrnPercentage',
      'oxygenDeliveryVia',
      'oxygenDeliveryViaOther',
    ];
    const fieldSet = new Set(fields);
    const orderedSet = new Set(legacyOxygenOrder);
    return [
      ...legacyOxygenOrder.filter((fieldName) => fieldSet.has(fieldName)),
      ...fields.filter((fieldName) => !orderedSet.has(fieldName)),
    ];
  }

  if (cardKey === 'gi') {
    const legacyGiOrder = [
      'giSymptoms',
      'giSymptomsOther',
      'giCurrentTreatment',
      'giCurrentTreatmentDescription',
      'bowelFrequency',
      'bowelFrequencyOther',
      'lastBmDate',
      'giOstomy',
      'giOstomyOther',
      'giStomaSite',
      'giStomaCondition',
      'giStomaConditionOther',
      'giOutput',
      'giOutputOther',
      'giOstomyCareBy',
      'giOstomyCareByOther',
    ];
    const fieldSet = new Set(fields);
    const orderedSet = new Set(legacyGiOrder);
    return [
      ...legacyGiOrder.filter((fieldName) => fieldSet.has(fieldName)),
      ...fields.filter((fieldName) => !orderedSet.has(fieldName)),
    ];
  }

  if (cardKey === 'guReproductive') {
    const hasCatheterUrineOutput = ['Indwelling', 'Suprapubic', 'Condom Cath', 'External Device (Pure Wick, etc.)']
      .some((type) => fieldValueEquals(data.urinaryCatheter, type));
    const hasUrinaryOstomy = fieldValueIncludes(data.guSymptoms, 'Urinary Ostomy');
    const legacyGuOrder = [
      'guSymptoms',
      'guSymptomsOther',
      'dialysis',
      'dialysisAccessType',
      'dialysisLocation',
      'dialysisDays',
      'urinaryCatheter',
      'catheterChangedBy',
      'catheterChangedByOther',
      'catheterChangedByChhaMdInfo',
      ...(hasUrinaryOstomy && !hasCatheterUrineOutput ? [] : ['urineOutput', 'urineOutputOther']),
      'urinaryDiversion',
      'urinaryDiversionOther',
      'urinaryStomaSite',
      'urinaryStomaCondition',
      'urinaryStomaConditionOther',
      ...(hasUrinaryOstomy && !hasCatheterUrineOutput ? ['urineOutput', 'urineOutputOther'] : []),
      'urinaryOstomyCareBy',
      'urinaryOstomyCareByOther',
      'reproductiveSymptoms',
      'reproductiveSymptomsOther',
    ];
    const fieldSet = new Set(fields);
    const orderedSet = new Set(legacyGuOrder);
    return [
      ...legacyGuOrder.filter((fieldName) => fieldSet.has(fieldName)),
      ...fields.filter((fieldName) => !orderedSet.has(fieldName)),
    ];
  }

  if (cardKey === 'planOfCare') {
    const legacyPocOrder = [
      'daysPerWeek',
      'hoursPerDay',
      'shiftType1',
      'daysPerWeek2',
      'hoursPerDay2',
      'shiftType2',
      'daysPerWeek3',
      'hoursPerDay3',
      'shiftType3',
      'pocStartDate',
      'pocEndDate',
      'planOfCareTasks',
      'planOfCareReviewedWith',
      'planOfCareReviewedWithOther',
      'planOfCareNote',
    ];
    const fieldSet = new Set(fields);
    const orderedSet = new Set(legacyPocOrder);
    return [
      ...legacyPocOrder.filter((fieldName) => fieldSet.has(fieldName)),
      ...fields.filter((fieldName) => !orderedSet.has(fieldName)),
    ];
  }

  if (cardKey !== 'environment') return fields;

  const legacyEnvironmentOrder = [
    'patientLivesIn',
    'aptSleepSpaceForLiveIn',
    'wcAccessible',
    'hasElectric',
    'structuralHazards',
    'buildingHasSuper',
    'hasRunningWater',
    'adequateCookingFacility',
    'buildingHasSecurityDeskDoorman',
    'hasWorkingPhone',
    'oxygenSafelyStored',
    'adequateVentilationHeating',
    'clutteredPathway',
    'fireExtinguisher',
    'adequatePlumbingSanitation',
    'lightedPathway',
    'fireEscapeSecondExit',
    'roachRodentBedBugInfestation',
    'scatterRugsHazards',
    'workingSmokeDetectors',
    'homeDementiaSafe',
    'environmentalIssuesAddressed',
    'environmentalIssuesAddressedDescribe',
    'environmentOther',
    'supportSystem',
    'otherSupportSystem',
    'livingSituation',
    'substanceUse',
    'substanceUseOther',
    'signsOfAbuseNeglect',
    'signsOfAbuseNeglectOther',
    'requiresReferralFor',
  ];
  const fieldSet = new Set(fields);
  const orderedSet = new Set(legacyEnvironmentOrder);
  return [
    ...legacyEnvironmentOrder.filter((fieldName) => fieldSet.has(fieldName)),
    ...fields.filter((fieldName) => !orderedSet.has(fieldName)),
  ];
}

function cardDisplayTitle(cardKey: string, fallbackTitle: string) {
  switch (cardKey) {
    case 'neurological':
      return 'Neuro';
    case 'cardiovascularPulmonary':
      return 'Cardiovascular / Pulmonary';
    case 'chestPain':
      return 'Chest Pain';
    case 'oxygen':
      return 'Oxygen';
    case 'eent':
      return 'EENT';
    case 'musculoskeletal':
      return 'Musculoskeletal';
    case 'guReproductive':
      return 'GU/Reproductive';
    case 'gi':
      return 'GI';
    case 'narrative':
      return 'Narrative';
    case 'pharmacy':
      return 'Pharmacy/Medications';
    default:
      return fallbackTitle;
  }
}

function fieldValueIncludes(value: unknown, expected: string): boolean {
  if (Array.isArray(value)) return value.some((item) => fieldValueIncludes(item, expected));
  return fieldMatchKey(value).includes(fieldMatchKey(expected));
}

function fieldValueEquals(value: unknown, expected: string): boolean {
  return fieldMatchKey(value) === fieldMatchKey(expected);
}

function fieldMatchKey(value: unknown): string {
  return String(value || '').trim().replace(/[^a-z0-9]/gi, '').toLowerCase();
}

function isRequiresAssistance(value: unknown): boolean {
  return fieldValueEquals(value, 'Requires Assistance') || fieldValueEquals(value, 'Requiere asistencia');
}

function isNoSelection(value: unknown): boolean {
  return fieldValueEquals(value, 'No');
}

function visitReasonText(data: Record<string, unknown>): string {
  return String(data.visitReason || data.assessmentVisitReason || '').toLowerCase();
}

function isRevisitReason(data: Record<string, unknown>, ...needles: string[]): boolean {
  if (!fieldValueEquals(data.visitType, 'Revisit')) return false;
  const reason = visitReasonText(data);
  return needles.some((needle) => reason.includes(needle));
}

function selectedValues(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  return String(value || '').split(';').map((item) => item.trim()).filter(Boolean);
}

function valuesWithLockedDefaults(value: unknown, defaults: string[]): string[] {
  const values = selectedValues(value);
  const next = [...values];
  defaults.forEach((defaultValue) => {
    if (!next.some((value) => fieldValueEquals(value, defaultValue))) next.push(defaultValue);
  });
  return next;
}

function picklistOptionForValue(value: unknown, options: string[]): string {
  const stringValue = String(value ?? '').trim();
  if (!stringValue) return '';
  return options.find((option) => fieldValueEquals(option, stringValue)) || stringValue;
}

function picklistSelectedValues(value: unknown, options: string[]): string[] {
  const values = selectedValues(value).map((item) => picklistOptionForValue(item, options)).filter(Boolean);
  return values.filter((item, index) => values.findIndex((other) => fieldValueEquals(other, item)) === index);
}

function addUnique(values: string[], value: string): void {
  if (value && !values.includes(value)) values.push(value);
}

function deriveHandOffBuckets(data: Record<string, unknown>) {
  const needsReferralFor: string[] = [];
  const safetyConcerns: string[] = [];
  const needsFollowUp: string[] = [];
  const specialRequestUsedForScheduling: string[] = [];
  const specialRequestOther: string[] = [];
  const specialRequestNotUsedForScheduling: string[] = [];

  if (data.requiresPcpReferral === true || fieldValueEquals(data.requiresPcpReferral, 'Yes')) {
    addUnique(needsReferralFor, 'Requires Referral for PCP');
  }
  if (fieldValueEquals(data.medPrefillReferralRequired, 'Yes')) addUnique(needsReferralFor, 'Requires Referral for Med Prefills');
  if (
    selectedValues(data.wound1CarePerformedBy).includes('Needs referral for Skilled RN for wound care')
    || selectedValues(data.wound2CarePerformedBy).includes('Needs referral for Skilled RN for wound care')
  ) {
    addUnique(needsReferralFor, 'Requires Referral for Wound Care');
  }
  if (
    selectedValues(data.wound1UnderMdCare).includes('Needs referral for in-home wound care specialist (ex: Revive Care)')
    || selectedValues(data.wound2UnderMdCare).includes('Needs referral for in-home wound care specialist (ex: Revive Care)')
  ) {
    addUnique(needsReferralFor, 'Requires Referral for Wound Care Specialist');
  }
  if (fieldValueEquals(data.palliativeCare, 'Patient interested, requires PCP follow-up and referral')) {
    addUnique(needsReferralFor, 'Requires Referral for Palliative Care');
  }
  selectedValues(data.requiresReferralFor).forEach((value) => {
    if (value === 'Social Work') addUnique(needsReferralFor, 'Requires Referral for Social Work');
    if (value === 'Adult/Child Protective Service') addUnique(needsReferralFor, 'Requires Referral for APS');
    if (value === 'Guardianship') addUnique(needsReferralFor, 'Requires Referral for Guardianship');
    if (value === 'Meals on Wheels') addUnique(needsReferralFor, 'Requires Referral for Wheels');
    if (value === 'Heavy Duty Cleaning') addUnique(needsReferralFor, 'Requires Referral for Heavy Duty Cleaning');
    if (value === 'Other') addUnique(needsReferralFor, 'Requires Referral for Other');
  });
  selectedValues(data.anyFallPreventionRecommendations).forEach((value) => {
    if (value === 'Requires Life Alert/PERS referral') addUnique(needsReferralFor, 'Requires Referral for Life Alert/PERS');
    if (value === 'Requires PT referral due to') addUnique(needsReferralFor, 'Requires Referral for PT');
    if (value === 'Other') addUnique(needsReferralFor, 'Fall Prevention Recommendation - Other');
    if (value === 'DME/Supply needed') addUnique(safetyConcerns, 'DME/Supply Needed');
    if (value === 'DME/Supply needs repair') addUnique(safetyConcerns, 'DME/Supply Needs Repair');
    if (value === 'Needs increase in service hours due to') addUnique(safetyConcerns, 'Needs Increase in Service Hours');
  });

  if (hasUnavailableSecondPerson(data)) addUnique(safetyConcerns, '2nd Person Not Available');
  if (fieldValueEquals(data.aptSleepSpaceForLiveIn, 'No')) addUnique(safetyConcerns, 'Not Suitable for Live-in Aide');
  if (fieldValueEquals(data.adequateVentilationHeating, 'No')) addUnique(safetyConcerns, 'Inadequate Heat/Ventilation');
  if (fieldValueEquals(data.adequatePlumbingSanitation, 'No')) addUnique(safetyConcerns, 'No Adequate Plumbing/Sanitation');
  if (fieldValueEquals(data.hasElectric, 'No')) addUnique(safetyConcerns, 'No Electricity');
  if (fieldValueEquals(data.hasRunningWater, 'No')) addUnique(safetyConcerns, 'No Running Water');
  if (fieldValueEquals(data.roachRodentBedBugInfestation, 'Yes')) addUnique(safetyConcerns, 'Roach/Rodent/Bed Bug Infestation');
  if (fieldValueEquals(data.clutteredPathway, 'Yes')) addUnique(safetyConcerns, 'Cluttered Pathways/Hoarding');
  if (fieldValueEquals(data.structuralHazards, 'Yes')) addUnique(safetyConcerns, 'Structural Hazards');

  if (fieldValueEquals(data.hasInjections, 'Yes, but unable to self-administer and no backup available, requires physician follow-up')) {
    addUnique(needsFollowUp, 'Unable to Self-Administer Injection');
  }
  if (fieldValueEquals(data.copyObtained, 'Yes')) addUnique(needsFollowUp, 'Advance Directive - Copy Obtained');
  if (fieldValueEquals(data.aideRequiresAgencyFollowUp, 'Yes')) addUnique(needsFollowUp, 'Requires Agency Follow-up');
  if (fieldValueEquals(data.maskMandateCompliance, 'Non-adherence to mask mandate (do not address in the home, HR will follow up)')) {
    addUnique(needsFollowUp, 'Mask Mandate Non-Adherence');
  }

  const pertinentRules = selectedValues(data.pertinentHouseRules);
  const nonAmbulatory = selectedValues(data.nonAmbulatory);
  const ambulatory = selectedValues(data.ambulatory);
  const eent = selectedValues(data.eent);
  const safetyPrecautions = selectedValues(data.safetyPrecautions);
  const prescribedDiet = selectedValues(data.prescribedDiet);
  const dmeSupplies = selectedValues(data.dmeSuppliesInHome);
  const electricDependency = selectedValues(data.electricDependency);
  const skinProblems = selectedValues(data.skinProblems);
  const orientation = selectedValues(data.orientationCognition);
  const supplementalServices = selectedValues(data.supplementalHomeServices);
  const guSymptoms = selectedValues(data.guSymptoms);

  if (fieldValueEquals(data.petsInHome, 'Yes')) addUnique(specialRequestUsedForScheduling, 'Pets in Home');
  if (fieldValueEquals(data.smokerInHome, 'Yes')) addUnique(specialRequestUsedForScheduling, 'Smoker in Home');
  if (nonAmbulatory.includes('Bedbound')) addUnique(specialRequestUsedForScheduling, 'Bedbound');
  if (nonAmbulatory.includes('Chair bound')) addUnique(specialRequestUsedForScheduling, 'Chair Bound');
  if (fieldValueEquals(data.giOstomy, 'Colostomy')) addUnique(specialRequestUsedForScheduling, 'Colostomy');
  if (orientation.includes('Disoriented')) addUnique(specialRequestUsedForScheduling, 'Disoriented');
  if (dmeSupplies.includes('Glucometer/Strips')) addUnique(specialRequestUsedForScheduling, 'Glucometer/Strips');
  if (fieldValueEquals(data.transferStatus, 'Hoyer')) addUnique(specialRequestUsedForScheduling, 'Hoyer Transfer');
  if (prescribedDiet.includes('Kosher')) addUnique(specialRequestUsedForScheduling, 'Kosher Home');
  if (electricDependency.includes('Oxygen')) addUnique(specialRequestUsedForScheduling, 'Oxygen Dependency');
  if (guSymptoms.includes('Urinary Catheter') || data.urinaryCatheter) addUnique(specialRequestUsedForScheduling, 'Urinary Catheter');

  if (data.doorCode) addUnique(specialRequestOther, 'Door Code');
  if (pertinentRules.some((value) => value && value !== 'None')) addUnique(specialRequestOther, 'Pertinent House Rules/Special Request');

  if (fieldValueEquals(data.patientSelfDirecting, 'No')) addUnique(specialRequestNotUsedForScheduling, 'Patient is not self-directing');
  if (fieldValueEquals(data.patientSelfDirecting, 'Yes')) addUnique(specialRequestNotUsedForScheduling, 'Patient is self-directing');
  if (['shiftType1', 'shiftType2', 'shiftType3'].some((field) => fieldValueEquals(data[field], 'Split Shift'))) {
    addUnique(specialRequestNotUsedForScheduling, 'Aide Hours - Split Shift');
  }
  if (['shiftType1', 'shiftType2', 'shiftType3'].some((field) => fieldValueEquals(data[field], 'Live-In'))) {
    addUnique(specialRequestNotUsedForScheduling, 'Aide Hours - Live-In');
  }
  if (data.dialysis) addUnique(specialRequestNotUsedForScheduling, 'Dialysis');
  if (fieldValueEquals(data.livingSituation, 'Lives alone')) addUnique(specialRequestNotUsedForScheduling, 'Lives Alone');
  if (eent.includes('Legally Blind')) addUnique(specialRequestNotUsedForScheduling, 'Legally Blind');
  if (eent.some((value) => ['Hearing aid', 'Deaf/hearing loss', 'Hard of hearing L', 'Hard of hearing R'].includes(value))) {
    addUnique(specialRequestNotUsedForScheduling, 'Hearing Aid/Hard of Hearing');
  }
  if (eent.includes('Speech impairment')) addUnique(specialRequestNotUsedForScheduling, 'Speech Impairment');
  if (ambulatory.includes('Standard Cane') || ambulatory.includes('Quad Cane')) addUnique(specialRequestNotUsedForScheduling, 'Uses Cane');
  if (ambulatory.includes('No Device')) addUnique(specialRequestNotUsedForScheduling, 'No Device');
  if (ambulatory.includes('Standard Walker') || ambulatory.includes('Rolling Walker') || ambulatory.includes('Hemi Walker')) {
    addUnique(specialRequestNotUsedForScheduling, 'Uses Walker');
  }
  if (ambulatory.includes('Uses wheelchair for long distance')) addUnique(specialRequestNotUsedForScheduling, 'Wheelchair for Long Distance');
  if (supplementalServices.includes('Hospice/Palliative Care') || data.supplementalHomeServicesHospice) {
    addUnique(specialRequestNotUsedForScheduling, 'Receiving Hospice/Palliative Care');
  }
  if (electricDependency.includes('Feeding Pump')) addUnique(specialRequestNotUsedForScheduling, 'Feeding Pump');
  if (electricDependency.includes('Ventilator')) addUnique(specialRequestNotUsedForScheduling, 'Ventilator');
  if (skinProblems.includes('Wound')) addUnique(specialRequestNotUsedForScheduling, 'Wound');
  if (safetyPrecautions.includes('Seizure Precaution')) addUnique(specialRequestNotUsedForScheduling, 'Seizure Precaution');
  if (safetyPrecautions.includes('Anticoagulant/Bleeding Precaution')) addUnique(specialRequestNotUsedForScheduling, 'Anticoagulant/Bleeding Precaution');
  if (safetyPrecautions.includes('Fall Precaution')) addUnique(specialRequestNotUsedForScheduling, 'Fall Precaution');
  if (safetyPrecautions.includes('Needle/Sharps Precaution')) addUnique(specialRequestNotUsedForScheduling, 'Needle/Sharps Precaution');

  if (fieldValueEquals(data.patientPhoneForEvv, 'Yes, Patient allows use of their phone for EVV at start and end of shift')) {
    addUnique(specialRequestNotUsedForScheduling, 'Patient Allows EVV Phone Use');
  }
  if (fieldValueEquals(data.patientPhoneForEvv, 'Pt adamantly refuses use of their phone despite education about EVV mandate requirement')) {
    addUnique(specialRequestNotUsedForScheduling, 'Patient Refuses EVV Phone Use');
  }
  if (fieldValueEquals(data.patientPhoneForEvv, 'Pt does not have a phone')) addUnique(specialRequestNotUsedForScheduling, 'Patient Has No Phone');
  if (fieldValueEquals(data.patientPhoneForEvv, "Pt's phone does not work (technical issue)")) addUnique(specialRequestNotUsedForScheduling, 'Patient Phone Not Working');
  if (fieldValueEquals(data.patientPhoneForEvv, "Pt's phone line is disconnected")) addUnique(specialRequestNotUsedForScheduling, 'Patient Phone Disconnected');

  return {
    needsReferralFor,
    safetyConcerns,
    needsFollowUp,
    specialRequestUsedForScheduling,
    specialRequestOther,
    specialRequestNotUsedForScheduling,
  };
}

function isValidEmail(value: unknown): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function positiveNumber(value: unknown): number {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
}

function formatShiftPart(days: number, hours: number, shiftType?: unknown): string {
  let part = `${days}D x ${hours}H`;
  if (hours === 24 && shiftType) part += ` (${String(shiftType) === 'Live-In' ? 'live-in' : 'split'})`;
  return part;
}

function generatePcaHhaFrequency(data: Record<string, unknown>, privatePay = false): string {
  const fields = privatePay
    ? [
        ['ppDaysPerWeek', 'ppHoursPerDay', 'ppShiftType1'],
        ['ppDaysPerWeek2', 'ppHoursPerDay2', 'ppShiftType2'],
      ]
    : [
        ['daysPerWeek', 'hoursPerDay', 'shiftType1'],
        ['daysPerWeek2', 'hoursPerDay2', 'shiftType2'],
        ['daysPerWeek3', 'hoursPerDay3', 'shiftType3'],
      ];
  return fields
    .map(([daysField, hoursField, shiftField]) => {
      const days = Number.parseInt(String(data[daysField] || '0'), 10);
      const hours = Number(data[hoursField]);
      return days > 0 && hours > 0 ? formatShiftPart(days, hours, data[shiftField]) : '';
    })
    .filter(Boolean)
    .join(', ');
}

function privatePayPcaHhaFee(data: Record<string, unknown>): string {
  const hourlyRate = data.ppMutualCase ? '$39.50/hr' : '$29.50/hr';
  const liveInRate = data.ppMutualCase ? '$500/day' : '$375/day';
  const shifts = [
    ['ppDaysPerWeek', 'ppHoursPerDay', 'ppShiftType1'],
    ['ppDaysPerWeek2', 'ppHoursPerDay2', 'ppShiftType2'],
  ];
  const hasLiveIn = shifts.some(([daysField, hoursField, shiftField]) => Number(data[daysField]) > 0 && Number(data[hoursField]) === 24 && data[shiftField] === 'Live-In');
  const hasHourly = shifts.some(([daysField, hoursField, shiftField]) => Number(data[daysField]) > 0 && Number(data[hoursField]) > 0 && !(Number(data[hoursField]) === 24 && data[shiftField] === 'Live-In'));
  if (hasLiveIn && hasHourly) return `${hourlyRate}, ${liveInRate} (live-in)`;
  if (hasLiveIn) return `${liveInRate} (live-in)`;
  return hourlyRate;
}

function consentFrequencyKey(frequency: string): string {
  if (frequency.includes('LEAP')) return 'LEAP 1-4';
  if (frequency.includes('CC - Combined Care') || frequency.includes('CC - Combine Care')) return 'CC';
  if (frequency.includes('Day Safety')) return 'Day Safety';
  for (const key of ['Med 1', 'Med 2', 'Med 3', 'Groom 1', 'Groom 2', 'Groom 3', 'AM/PM', 'Escort']) {
    if (frequency.includes(key)) return key;
  }
  return frequency;
}

function isProtectedConsentFrequency(frequency: string): boolean {
  return frequency === 'Initial visit' || frequency === 'Recertification';
}

function complementaryConsentCoverage(value: string): string {
  const match = value.match(/^(\d+(?:\.\d+)?)%$/);
  if (!match) return '';
  const numericValue = Number(match[1]);
  if (!Number.isFinite(numericValue)) return '';
  return `${Math.max(0, Math.min(100, 100 - numericValue))}%`;
}

function normalizeConsentRow(row: Partial<ConsentRow> | null | undefined, index = 0, data: Record<string, unknown> = {}, contractName = ''): ConsentRow {
  let service = String(row?.service || '');
  let frequency = String(row?.frequency || '');
  let fee = String(row?.fee || '');
  const isPrivatePay = Boolean(row?.isPrivatePay);

  if (service === 'PCA/HHA') {
    const dynamicFrequency = generatePcaHhaFrequency(data, isPrivatePay);
    if (dynamicFrequency) frequency = dynamicFrequency;
    if (isPrivatePay) fee = privatePayPcaHhaFee(data);
    if (!isPrivatePay && (!fee || ['Contract name', 'MLTC contract name', 'User input required'].includes(fee))) fee = contractName;
  }

  if (service === 'RN' && (frequency === 'Initial visit' || frequency === 'Recertification')) {
    const contractTypes = selectedValues(data.contractTypes);
    const hasContract = contractTypes.includes('Contract');
    if (hasContract && contractName) fee = contractName;
    if (!hasContract && !fee) fee = frequency === 'Initial visit' ? '$325' : '$165/visit';
  }

  return {
    id: String(row?.id || `consent-row-${index}`),
    version: 2,
    service,
    frequency,
    fee,
    weekendFee: String(row?.weekendFee || ''),
    expectedCoverage: String(row?.expectedCoverage || ''),
    patientResponsibility: String(row?.patientResponsibility || ''),
    requiresFrequencyInput: Boolean(row?.requiresFrequencyInput),
    requiresFeeInput: Boolean(row?.requiresFeeInput),
    isPrivatePay,
  };
}

function parseConsentRows(value: unknown, data: Record<string, unknown> = {}, contractName = ''): ConsentRow[] {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (!Array.isArray(parsed)) return [];
    return parsed.map((row, index) => normalizeConsentRow(row, index, data, contractName));
  } catch {
    return [];
  }
}

function serializeConsentRows(rows: ConsentRow[]): string {
  return rows.length ? JSON.stringify(rows.map((row, index) => normalizeConsentRow(row, index))) : '';
}

function normalizeIsoDate(value: unknown): string {
  if (!value) return '';
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
}

function parseDiagnosisRows(value: unknown, includeBlankPrimary = false): DiagnosisRow[] {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (!Array.isArray(parsed)) return includeBlankPrimary ? [{ id: 1, icdCode: '', description: '', date: '' }] : [];
    const rows = parsed.map((row, index) => ({
      id: Number(row?.id) || index + 1,
      icdCode: String(row?.icdCode || ''),
      description: String(row?.description || ''),
      date: normalizeIsoDate(row?.date),
    }));
    return rows.length > 0 ? rows : (includeBlankPrimary ? [{ id: 1, icdCode: '', description: '', date: '' }] : []);
  } catch {
    return includeBlankPrimary ? [{ id: 1, icdCode: '', description: '', date: '' }] : [];
  }
}

function serializeDiagnosisRows(rows: DiagnosisRow[]): Record<string, unknown>[] {
  return rows
    .filter((row) => row.icdCode || row.description || row.date)
    .map((row) => ({ icdCode: row.icdCode, description: row.description, date: row.date || null }));
}

function parseMedicationRows(value: unknown): MedicationRow[] {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (!Array.isArray(parsed)) return [];
    return parsed.map((row, index) => {
      const status = row?.status || (row?.dcd === true ? 'Discontinued' : 'Existing');
      return {
        id: Number(row?.id) || index + 1,
        clinicalRowId: String(row?.clinicalRowId || ''),
        orderDate: normalizeIsoDate(row?.orderDate),
        medication: String(row?.medication || ''),
        dose: String(row?.dose || ''),
        route: String(row?.route || ''),
        frequency: String(row?.frequency || ''),
        purposeDx: String(row?.purposeDx || ''),
        status: String(status || 'Existing'),
        dcdDate: normalizeIsoDate(row?.dcdDate),
        isFromPreviousAssessment: Boolean(row?.isFromPreviousAssessment),
      };
    });
  } catch {
    return [];
  }
}

function fallRiskDisplayLabel(riskFactor: string): string {
  if (riskFactor === 'Prior History of Falls Within 6 Months (1-2)') return 'Prior History of Falls Within 6 Months (1 - 2 falls)';
  if (riskFactor === 'Prior History of Falls Within 6 Months (3+)') return 'Prior History of Falls Within 6 Months (3 or more falls)';
  return riskFactor;
}

function calculateFallRiskScore(factors: string[]): number {
  return factors.reduce((score, factor) => {
    if (factor.includes('Prior History of Falls Within 6 Months (1-2)')) return score + 2;
    if (factor.includes('Prior History of Falls Within 6 Months (3+)')) return score + 4;
    return score + 1;
  }, 0);
}

function isFilled(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'number') return value !== 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'boolean') return true;
  return Boolean(value);
}

const WOUND_2_FIELDS = [
  'wound2Location',
  'wound2LesionType',
  'wound2LesionTypeOther',
  'wound2Assessed',
  'wound2AssessedNoDueTo',
  'wound2AssessedNoDueToOther',
  'wound2CareOrder',
  'wound2CareOrderOther',
  'wound2CarePerformedBy',
  'wound2CarePerformedByOther',
  'wound2UnderMdCare',
  'wound2PressureUlcerStage',
];

function isWound2Touched(data: Record<string, unknown>): boolean {
  return WOUND_2_FIELDS.some((field) => isFilled(data[field]));
}

function hasEnvironmentalIssue(data: Record<string, unknown>): boolean {
  return [
    'roachRodentBedBugInfestation',
    'clutteredPathway',
    'scatterRugsHazards',
    'structuralHazards',
  ].some((field) => fieldValueEquals(data[field], 'Yes')) || [
    'adequateVentilationHeating',
    'adequatePlumbingSanitation',
    'hasElectric',
    'hasRunningWater',
    'hasWorkingPhone',
    'oxygenSafelyStored',
    'homeDementiaSafe',
    'workingSmokeDetectors',
    'aptSleepSpaceForLiveIn',
  ].some((field) => fieldValueEquals(data[field], 'No'));
}

function isEnvironmentalIssueFieldOut(fieldName: string, data: Record<string, unknown>): boolean {
  if ([
    'roachRodentBedBugInfestation',
    'clutteredPathway',
    'scatterRugsHazards',
    'structuralHazards',
  ].includes(fieldName)) {
    return fieldValueEquals(data[fieldName], 'Yes');
  }
  if ([
    'adequateVentilationHeating',
    'adequatePlumbingSanitation',
    'hasElectric',
    'hasRunningWater',
    'hasWorkingPhone',
    'oxygenSafelyStored',
    'homeDementiaSafe',
    'workingSmokeDetectors',
    'aptSleepSpaceForLiveIn',
  ].includes(fieldName)) {
    return fieldValueEquals(data[fieldName], 'No');
  }
  return false;
}

function consentServiceFee(config: ConsentServiceConfig, contractTypes: string[], contractName: string, data: Record<string, unknown>): string {
  const hasContract = contractTypes.includes('Contract');
  if (config.services === 'RN' && (config.frequency === 'Initial visit' || config.frequency === 'Recertification')) {
    return hasContract ? contractName : (config.frequency === 'Initial visit' ? '$325' : '$165/visit');
  }
  if (config.services === 'PCA/HHA' && config.isPrivatePay) return privatePayPcaHhaFee(data);
  if (config.services === 'PCA/HHA') return contractName;
  return config.fee;
}

function consentServiceOptions(data: Record<string, unknown>, existingRows: ConsentRow[], contractName: string) {
  const contractTypes = selectedValues(data.contractTypes);
  if (contractTypes.length === 0) return [];
  return CONSENT_SERVICE_CONFIG
    .filter((config) => config.contractTypes.includes('All') || config.contractTypes.some((type) => contractTypes.includes(type)))
    .filter((config) => {
      if (config.services === 'PCA/HHA') {
        return !existingRows.some((row) => row.service === 'PCA/HHA' && row.isPrivatePay === config.isPrivatePay);
      }
      if (existingRows.some((row) => row.frequency === config.frequency)) return false;
      const currentKey = consentFrequencyKey(config.frequency);
      return !existingRows.some((row) => {
        const existingConfig = CONSENT_SERVICE_CONFIG.find((item) => item.frequency === row.frequency);
        return existingConfig?.cannotChoose.some((blocked) => currentKey === blocked || currentKey.includes(blocked)) ?? false;
      });
    })
    .map((config) => {
      const frequency = config.services === 'PCA/HHA'
        ? generatePcaHhaFrequency(data, config.isPrivatePay)
        : config.frequency;
      const disabled = config.services === 'PCA/HHA' && !frequency;
      const serviceLabel = config.isPrivatePay ? `${config.services} (Private Pay)` : config.services;
      return {
        label: `${serviceLabel} - ${frequency || (config.isPrivatePay ? '(Fill PP schedule hours)' : '(Fill schedule hours)')}`,
        config,
        disabled,
        fee: consentServiceFee(config, contractTypes, contractName, data),
        frequency: frequency || config.frequency,
        weekendFee: config.services.includes('HHA') || config.services.includes('PCA') ? 'Daily Rate x 1.5' : 'Daily Rate',
      };
    });
}

const supervisionCompletedOnlyFields = new Set([
  'nameOfProfessional',
  'patientInitials',
  'identificationBadge',
  'uniformScrubAdequateAttire',
  'changeInPocNeeded',
  'supervisionBathing',
  'supervisionGrooming',
  'groomingOther',
  'supervisionToileting',
  'toiletingOther',
  'supervisionTransfers',
  'transfersOther',
  'supervisionAmbulation',
  'ambulationOther',
  'otherTasks',
  'otherTasksOther',
  'vitalSignsReadRecord',
  'instructionProvided',
  'useOfDmeSpecify',
  'instructionProvidedOther',
  'paraprofessionalResponse',
  'hhaPcaCompliesWithPoc',
  'pcaHhaCommunicatesEffectively',
  'patientFamilyCooperative',
  'patientSatisfiedWithServices',
  'patientTreatedWithRespect',
  'areServicesContinuing',
  'aideRequiresAgencyFollowUp',
  'comments',
]);

const supervisionIncompleteClearedFields = new Set([
  ...supervisionCompletedOnlyFields,
  'maskMandateCompliance',
]);

const aideSupervisionHiddenStep1Fields = new Set([
  'advanceDirectives',
  'copyObtained',
  'priorityCode',
  'missingAidePoseSafetyRisk',
  'backupAideAvailable',
  'backupAideDetails',
  'mobilityStatus',
  'electricDependency',
  'lifeSustainingDmeCompanyInfo',
  'medicalSuppliesPetsInfo',
  'evacuationLocation',
  'relativeFriendOtherContact',
]);

const patientEvaluationHiddenStep1Fields = new Set([
  'priorityCode',
  'missingAidePoseSafetyRisk',
  'backupAideAvailable',
  'backupAideDetails',
  'mobilityStatus',
  'electricDependency',
  'lifeSustainingDmeCompanyInfo',
  'medicalSuppliesPetsInfo',
  'evacuationLocation',
  'relativeFriendOtherContact',
]);

const revisitHiddenStep4EnvironmentFields = new Set([
  'patientLivesIn',
  'aptSleepSpaceForLiveIn',
  'wcAccessible',
  'buildingHasSuper',
  'buildingHasSecurityDeskDoorman',
  'adequateVentilationHeating',
  'adequatePlumbingSanitation',
  'roachRodentBedBugInfestation',
  'hasElectric',
  'hasRunningWater',
  'hasWorkingPhone',
  'clutteredPathway',
  'lightedPathway',
  'scatterRugsHazards',
  'structuralHazards',
  'adequateCookingFacility',
  'oxygenSafelyStored',
  'fireExtinguisher',
  'fireEscapeSecondExit',
  'workingSmokeDetectors',
  'environmentalIssuesAddressed',
  'environmentalIssuesAddressedDescribe',
  'homeDementiaSafe',
  'environmentOther',
  'supportSystem',
  'otherSupportSystem',
  'livingSituation',
  'substanceUse',
  'substanceUseOther',
  'signsOfAbuseNeglect',
  'signsOfAbuseNeglectOther',
  'requiresReferralFor',
]);

const noEmergencyContactFields = new Set([
  'emergencyContactName',
  'emergencyContactPhone',
  'emergencyContactPhone2',
  'emergencyContactRelationship',
  'emergencyContactEmail',
  'emergencyContact2Name',
  'emergencyContact2Phone',
  'emergencyContact2Phone2',
  'emergencyContact2Relationship',
  'emergencyContact2Email',
  'additionalEmergencyContact',
]);

const DEFAULT_SAFETY_PRECAUTIONS = [
  'Infection Control Measures',
  'Proper labelling of Meds',
  'Proper Waste Disposal',
  'Standard Precautions',
  'Emergency Preparedness',
];

const DEFAULT_GOALS = [
  'Patient will remain safely at home',
  'Personal care needs met by the Home Care Services and achieves optimal degree of comfort',
];

const DEFAULT_RN_ORDERS = [
  'Nurse to assess patient every 6 months and as needed to instruct and supervise',
  'Nurse to Supervise and Instruct Aide to Plan of Care. every 6 months and as needed.',
  'Nurse to assess and instruct: Safety, Mental Status & Judgement, Infection control, Personal Care needs, Medication Regimen, Nutrition And Supervision Of PCA/HHA',
  'Nurse to Instruct Symptoms to report to Nurse/MD.',
  'Nurse to assess need for Palliative Care',
];

const DEFAULT_DISCHARGE_PLAN = [
  "To self/HCP at patient/HCP's request",
  'Requires continued homecare services',
  'To facility when LHC can no longer care for patient',
  "To MLTC at vendor/contract's request",
];

const LOCKED_MULTISELECT_DEFAULTS: Record<string, string[]> = {
  safetyPrecautions: DEFAULT_SAFETY_PRECAUTIONS,
  goals: DEFAULT_GOALS,
  rnOrders: DEFAULT_RN_ORDERS,
  dischargePlan: DEFAULT_DISCHARGE_PLAN,
};

const MULTISELECT_MAX_SELECTIONS: Record<string, number> = {
  prescribedDiet: 2,
  dialysisDays: 3,
};

const READ_ONLY_MULTISELECT_FIELDS = new Set(['servicesProvided', 'goals', 'dischargePlan']);
const IADL_RADIO_FIELDS = new Set([
  'mealPlanning',
  'mealPreppingCooking',
  'manageMedication',
  'remindMedication',
  'laundry',
  'shopping',
  'housekeeping',
  'useThePhone',
  'manageMoney',
  'safetyAwareness',
]);
const LEGACY_FIELD_LABEL_OVERRIDES: Record<string, string> = {
  mobile: 'Mobile Phone',
  petsInHomeList: 'Pets In Home (Details)',
  pertinentHouseRules: 'Pertinent House Rules & Special Requests',
  pertinentHouseRulesOther: 'Other House Rules',
  patientSelfDirecting: 'Is patient self-directing (able to make decisions about their care and can direct the caregiver/nurse)?',
  patientPhoneForEvv: "Aides are required to clock in and out electronically. If the app is not working on the aide's phone, is there a phone in the patient's home that can be used to clock in and out?",
  contractTypes: 'Contract Type',
  advanceDirectives: 'Does patient have any advance directives?',
  backupAideAvailable: 'Is there a backup caregiver available if the aide cannot be present?',
  backupAideDetails: 'Backup caregiver details',
  priorityCode: 'Priority Code (Auto-calculated)',
  relativeFriendOtherContact: 'Relative/Friend/Other (obtain name & Tel)',
  notTakingMedications: 'Patient is not taking any prescribed or PRN medications',
  pcpNotInSystem: 'PCP is not in the system',
  pcpSelectedAddress: 'PCP Office',
  missingAidePoseSafetyRisk: 'Missing a day of care poses a patient safety risk (i.e: needs help for mobility, transfers, meds, toileting)?',
  lifeSustainingDmeCompanyInfo: 'Life Sustaining DME company info',
  medicalSuppliesPetsInfo: 'Pertinent Information & Other Medical Supplies and Pets That Will Need to Evacuate with Patient',
  bloodPressure: 'BP',
  respirationRate: 'Resp Rate',
  temp: 'Temp (°F)',
  heightFt: 'Height (Ft)',
  heightIn: 'Height (In)',
  weight: 'Weight (Lbs)',
  pulseOxygen: 'Pulse Ox (%)',
  anyChangeToPatientWeight: "Any change to patient's weight",
  anyChangeToPatientWeightExplain: 'Explain weight change',
  unableToAssessVitals: 'Unable to assess vital signs due to',
  bowelSoundsOther: 'Describe other bowel sounds',
  pedalEdemaDescription: 'Pedal Edema (Describe)',
  allergies: 'Allergies (Details)',
  vitalSignsComments: 'Comments/Teachings',
  diagnosisCommentsTeaching: 'Diagnosis Comment & Teaching',
  pharmacyPhone: 'Pharmacy Tel',
  pharmacyPhone2: 'Pharmacy Tel 2',
  adequateMedSupplyAtHome: 'Has adequate medication supply at home',
  adequateMedSupplyAtHomeExplain: 'Explain why medication supply is not adequate',
  medicationsStoredProperly: 'Medications are stored/labeled properly',
  medicationsStoredProperlyExplain: 'Explain why medications are not stored properly',
  takesMedicationsAsOrdered: 'Takes medications as ordered',
  takesMedicationsAsOrderedExplain: 'Explain and document patient education',
  medicationTeachingReviewed: 'Medication teaching and schedule reviewed with patient/HCP',
  medicationTeachingReviewedExplain: 'Explain medication teaching/schedule',
  medicationSideEffects: 'Exhibiting any side effects or drug interactions?',
  sideEffectsExplanation: 'Explain side effects',
  hasInjections: 'Does patient have injection(s)?',
  injectionsAdministeredBy: 'Other (specify who administers injections)',
  medPrefillReferralRequired: 'Does patient require referral for medication prefills?',
  rnIntervention: 'RN intervention during visit',
  rnPrePouredDays: 'Number of days medications pre-poured',
  snAdministeredMedications: 'SN administered the following medications',
  medicationCommentsTeaching: 'Medication Comment & Teaching',
  secondPersonAvailable: 'Is 2nd person available for 2-person assist?',
  mealPlanningAssistedBy: 'Meal Planning - Assisted By',
  mealPlanningAssistedOther: 'Meal Planning - Other (please specify)',
  mealPreppingAssistedBy: 'Meal Prepping/Cooking - Assisted By',
  mealPreppingAssistedOther: 'Meal Prepping - Other (please specify)',
  manageMedicationAssistedBy: 'Manage Medication - Assisted By',
  manageMedicationAssistedOther: 'Manage Medication - Other (please specify)',
  laundryAssistedBy: 'Laundry - Assisted By',
  laundryAssistedOther: 'Laundry - Other (please specify)',
  shoppingAssistedBy: 'Shopping - Assisted By',
  shoppingAssistedOther: 'Shopping - Other (please specify)',
  laundryErrandsSafelyLeftAlone: 'Can patient be safely left alone while aide is out of the apt?',
  laundryErrandsSafetyPlan: 'Safety plan (select all that apply)',
  laundryErrandsSafetyPlanOther: 'Safety plan - Other (please specify)',
  manageMoneyAssistedBy: 'Manage Money - Assisted By',
  manageMoneyAssistedOther: 'Manage Money - Other (please specify)',
  patientLivesIn: 'Lives in',
  aptSleepSpaceForLiveIn: 'Apt is suitable for sleeping space for live-in',
  wcAccessible: 'WC accessible',
  buildingHasSuper: 'Building has super or case workers',
  buildingHasSecurityDeskDoorman: 'Building has front desk/security',
  adequateVentilationHeating: 'Adequate ventilation and heating',
  adequatePlumbingSanitation: 'Adequate plumbing/sanitation',
  roachRodentBedBugInfestation: 'Signs of infestation (Roach/rodent/bed bug)',
  homeDementiaSafe: 'Is the home environment dementia-safe (locks, stove, hazards secured)?',
  hasElectric: 'Has electric',
  hasRunningWater: 'Has running water',
  hasWorkingPhone: 'Working telephone',
  clutteredPathway: 'Cluttered pathways',
  lightedPathway: 'Lighted pathways',
  scatterRugsHazards: 'Scatter rugs hazards',
  structuralHazards: 'Structural Hazards',
  adequateCookingFacility: 'Adequate cooking facility',
  oxygenSafelyStored: 'Oxygen is safely stored',
  fireExtinguisher: 'Fire extinguisher',
  fireEscapeSecondExit: 'Fire escape/2nd exit',
  workingSmokeDetectors: 'Working smoke detectors',
  environmentalIssuesAddressed: 'Are issues being addressed by patient, family, HCP, NCM, SW, landlord, super, etc)?',
  environmentalIssuesAddressedDescribe: 'Describe how issues are being addressed',
  environmentOther: 'Other Environmental Safety Concerns',
  otherSupportSystem: 'Specify Other Support System',
  livingSituation: 'Living situation',
  substanceUseOther: 'Specify Other Substance Use',
  signsOfAbuseNeglect: 'Signs of Abuse or Neglect',
  signsOfAbuseNeglectOther: 'Specify Other Signs',
  dmeSuppliesInHome: 'Select DMEs & Supplies currently in use',
  dmeSuppliesOther: 'Specify Other Supplies',
  equipmentCondition: 'Condition of Equipment in home',
  repairReplacementNeededFor: 'Repair/replacement needed for',
  dmeSupplyKnowledge: 'Patient/Family/HHA knows how to use DME/supply',
  dmeSuppliesNeeded: 'DME/Supply needed',
  neededDmeSupplies: 'Specify needed DME/Supplies',
  prescribedDietOther: 'Specify Other Prescribed Diet',
  consistencyOther: 'Specify Other Consistency',
  changeInAppetite: 'Change in appetite',
  changeInAppetiteDescription: 'Describe change in appetite',
  fluidRestrictionAmount: 'Restriction amount (cc/24hr)',
  fluidEncourageAmount: 'Encourage amount (cc/24hr)',
  enteralFeedingPerformedBy: 'Performed by',
  enteralFeedingPerformedByOther: 'Specify Other Performer',
  moodBehaviorOther: 'Specify Other Mood/Behavior',
  frequencyOfBehaviorOther: 'Specify Other Frequency of Behavior',
  communicationOther: 'Specify Other Communication',
  cvaExtremities: 'Please specify which extremities',
  tremorsOther: 'Specify Other Tremor Location',
  paralysisOther: 'Specify Other Paralysis Type',
  cardioPulmonarySymptomsOther: 'Specify Other Symptoms',
  orthopneaPillows: 'Orthopnea, # of pillows',
  chestPainFrequency: 'How often does the pain occur?',
  chestPainFrequencyOther: 'Specify Other Frequency',
  chestPainDuration: 'How long does each episode last?',
  chestPainDurationOther: 'Specify Other Duration',
  chestPainLocation: 'Where is the pain located?',
  chestPainLocationOther: 'Specify Other Location',
  chestPainQuality: 'Describe the pain',
  chestPainQualityOther: 'Specify Other Quality',
  chestPainSeverity: 'Pain Severity',
  chestPainPrecipitatingFactors: 'What seems to bring on the pain?',
  chestPainPrecipitatingFactorsOther: 'Specify Other Factors',
  chestPainRelievedBy: 'What makes the pain better?',
  chestPainRelievedByOther: 'Specify Other Relief',
  chestPainAssociatedSymptoms: 'Does patient experience any of the following with the chest pain?',
  chestPainAssociatedSymptomsOther: 'Specify Other Associated Symptoms',
  oxygenEquipment: 'Equipment',
  oxygenFlowRate: 'Flow Rate (L/min)',
  oxygenFrequency: 'Frequency',
  oxygenPrnPercentage: 'PRN for SOB / O2 less than (%)',
  oxygenDeliveryVia: 'Delivery Via',
  oxygenDeliveryViaOther: 'Specify Other Delivery Method',
  eent: 'EENT Symptoms',
  eentOther: 'Other EENT Symptoms',
  denturesFit: 'Fit',
  musculoskeletalOther: 'Other Musculoskeletal Issues',
  castOther: 'Other Cast',
  ambulatoryOther: 'Specify Other Ambulatory Device',
  transferStatusOther: 'Specify Other Transfer Status',
  ambulationAssistOther: 'Specify Other Ambulation Assist',
  bedMobility: 'Bed Mobility (turning & repositioning in bed)',
  bedMobilityOther: 'Specify Other Bed Mobility',
  canPatientSelfPropelWheelchair: 'Can patient self-propel wheelchair?',
  secondPersonAssistWhenNeeded: 'Is someone available to provide 2nd person assist when needed?',
  gaitOther: 'Other Gait Issue',
  activitiesPermittedInHome: 'Activities Permitted in Home',
  activitiesPermittedInHomeOther: 'Other Activities',
  hoyerAgitationCombativeness: 'Does patient have periods of agitation, combativeness or sudden movements',
  hoyerBedMobilityAssistMoreThanOne: 'Does patient require assist of more than 1 person for bed mobility (turning from side to side while lying in bed)',
  hoyerSevereContractures: 'Does patient have severe contracture(s)',
  hoyerPoorTrunkControl: 'Does patient have poor trunk control, spasticity or flaccidity',
  hoyerRelatedAccidentHistory: 'Does patient have any history of Hoyer related accident',
  hoyerSafeManeuveringSpaceInHome: "Is there space to safely maneuver Hoyer lift in patient's home",
  anyFallPreventionRecommendations: 'Any fall prevention recommendations?',
  requiresPtReferralDueTo: 'Requires PT referral due to',
  fallPreventionDmeSupplyNeeded: 'Specify needed DME/Supplies',
  fallPreventionDmeSupplyNeedsRepair: 'DME/Supply needs repair',
  needsIncreaseInServiceHoursDueTo: 'Needs increase in service hours due to',
  fallPreventionRecOther: 'Other Fall Prevention Recommendation',
  guSymptoms: 'GU',
  guSymptomsOther: 'Other GU Symptoms',
  dialysisAccessType: 'Type of Access',
  dialysisLocation: 'Location',
  catheterChangedBy: 'Catheter changed every 4-6 weeks by',
  catheterChangedByOther: 'Other (specify)',
  catheterChangedByChhaMdInfo: 'CHHA/MD Info',
  urineOutput: 'Urine output color, clarity, and odor',
  urineOutputOther: 'Other (specify)',
  urinaryDiversion: 'Urinary Diversion / Ostomy',
  urinaryDiversionOther: 'Other Urinary Diversion',
  urinaryStomaSite: 'Stoma Site',
  urinaryStomaCondition: 'Stoma condition',
  urinaryStomaConditionOther: 'Other Stoma Condition',
  urinaryOstomyCareBy: 'Person Managing Ostomy Care',
  urinaryOstomyCareByOther: 'Other Caregiver',
  reproductiveSymptoms: 'Reproductive',
  reproductiveSymptomsOther: 'Other Reproductive Symptoms',
  giSymptoms: 'GI',
  giSymptomsOther: 'Other GI Symptoms',
  giCurrentTreatment: 'Any PRN treatment?',
  giCurrentTreatmentDescription: 'Treatment Description',
  bowelFrequencyOther: 'Other Frequency',
  lastBmDate: 'Date of last BM',
  giOstomyOther: 'Other GI Ostomy',
  giStomaSite: 'Stoma site',
  giStomaCondition: 'Peristomal Skin',
  giStomaConditionOther: 'Other peristomal skin',
  giOutput: 'Output',
  giOutputOther: 'Other Output',
  giOstomyCareBy: 'Person Managing Ostomy Care',
  giOstomyCareByOther: 'Other Caregiver',
  peripheralVascularSensation: 'Sensation',
  peripheralVascularSensationOther: 'Other Sensation',
  peripheralVascularColor: 'Color',
  peripheralVascularColorOther: 'Other Color',
  peripheralVascularTemperature: 'Temperature',
  peripheralVascularTemperatureOther: 'Other Temperature',
  endocrineSymptoms: 'Endocrine',
  endocrineSymptomsOther: 'Other Endocrine Symptoms',
  diabetesStatus: 'Diabetes',
  insulinAdministration: 'Insulin',
  bloodSugarValue: 'Blood Sugar Results (mg/dl)',
  bloodSugarCondition: 'Testing Condition',
  bloodSugarPostPrandialHours: 'Hours',
  integumentColor: 'Color',
  integumentTurgor: 'Turgor',
  integumentTemp: 'Temp',
  integumentCondition: 'Condition',
  skinProblems: 'Skin Problem',
  intervention: 'Intervention Required',
  wound1Location: 'Wound Location',
  wound2Location: 'Wound Location',
  wound1LesionType: 'Wound/Lesion Type',
  wound2LesionType: 'Wound/Lesion Type',
  wound1LesionTypeOther: 'Other Lesion Type',
  wound2LesionTypeOther: 'Other Lesion Type',
  wound1PressureUlcerStage: 'Pressure Ulcer Stage',
  wound2PressureUlcerStage: 'Pressure Ulcer Stage',
  wound1Assessed: 'Wound Assessed',
  wound2Assessed: 'Wound Assessed',
  wound1AssessedNoDueTo: 'No, due to',
  wound2AssessedNoDueTo: 'No, due to',
  wound1AssessedNoDueToOther: 'Other (specify)',
  wound2AssessedNoDueToOther: 'Other (specify)',
  wound1CareOrder: 'Wound Care order',
  wound2CareOrder: 'Wound Care order',
  wound1CareOrderOther: 'Other (specify)',
  wound2CareOrderOther: 'Other (specify)',
  wound1CarePerformedBy: 'Wound Care performed by',
  wound2CarePerformedBy: 'Wound Care performed by',
  wound1CarePerformedByOther: 'Other Caregiver',
  wound2CarePerformedByOther: 'Other Caregiver',
  wound1UnderMdCare: 'Is the patient\'s wound under MD care? (Select all that apply)',
  wound2UnderMdCare: 'Is the patient\'s wound under MD care? (Select all that apply)',
  painHistory: 'Is patient experiencing pain or have a history of pain?',
  pain1Location: 'Location',
  pain2Location: 'Location',
  pain1Intensity: 'Intensity (1-10)',
  pain2Intensity: 'Intensity (1-10)',
  pain1Type: 'Type',
  pain2Type: 'Type',
  pain1TypeOther: 'Other Pain Type',
  pain2TypeOther: 'Other Pain Type',
  pain1Frequency: 'Frequency',
  pain2Frequency: 'Frequency',
  pain1MakesBetter: 'What makes pain better?',
  pain2MakesBetter: 'What makes pain better?',
  pain1MakesBetterOther: 'Other Pain Relief',
  pain2MakesBetterOther: 'Other Pain Relief',
  pain1MakesWorse: 'What makes pain worse?',
  pain2MakesWorse: 'What makes pain worse?',
  pain1MakesWorseOther: 'Other Pain Triggers',
  pain2MakesWorseOther: 'Other Pain Triggers',
  palliativeCare: 'Palliative Care',
  palliativeCareProviderInfo: 'Obtain Palliative/Hospice Care Provider Info',
  supplementalHomeServices: 'Is patient receiving any supplemental home services?',
  supplementalHomeServicesChha: 'CHHA Provider Info',
  supplementalHomeServicesHospice: 'Hospice/Palliative Care Provider Info',
  supplementalHomeServicesOther: 'Other Supplemental Services',
  prognosis: 'Prognosis',
  safetyPrecautions: 'Safety Precautions',
  safetyPrecautionsOther: 'Other Safety Precaution',
  functionalLimitation: 'Functional Limitation',
  servicesProvided: 'Services Provided',
  daysPerWeek: 'Days per Week',
  hoursPerDay: 'Hours per Day',
  daysPerWeek2: 'Days per Week 2',
  hoursPerDay2: 'Hours per Day 2',
  daysPerWeek3: 'Days per Week 3',
  hoursPerDay3: 'Hours per Day 3',
  planOfCareReviewedWithOther: 'Plan of Care Reviewed With Other',
  nextVisit: 'Next Visit',
  goals: 'Goals',
  rnOrders: 'RN Orders',
  dischargePlan: 'Discharge Plan',
  nursingProgressNote: 'Nursing Progress Note',
  handOffNote: 'Hand-off Note',
  supervisionCompleted: 'Supervision Completed',
  maskMandateCompliance: 'Mask Mandate Compliance (Nov–June only)',
  nameOfProfessional: 'Name of Professional',
  patientInitials: 'Patient Initials',
  identificationBadge: 'Identification Badge',
  uniformScrubAdequateAttire: 'Uniform/Scrub Adequate Attire',
  changeInPocNeeded: 'Change in POC Needed',
  supervisionBathing: 'Bathing',
  supervisionGrooming: 'Grooming',
  groomingOther: 'Grooming Other',
  supervisionToileting: 'Toileting',
  toiletingOther: 'Toileting Other',
  supervisionTransfers: 'Transfers',
  transfersOther: 'Transfers Other',
  supervisionAmbulation: 'Ambulation',
  ambulationOther: 'Ambulation Other',
  otherTasks: 'Other Tasks',
  otherTasksOther: 'Other Tasks Other',
  vitalSignsReadRecord: 'Vital Signs Read & Record',
  instructionProvided: 'Instruction Provided',
  useOfDmeSpecify: 'Use of DME Specify',
  instructionProvidedOther: 'Instruction Provided Other',
  paraprofessionalResponse: 'Paraprofessional Response',
  hhaPcaCompliesWithPoc: 'HHA/PCA Complies with POC',
  pcaHhaCommunicatesEffectively: 'PCA/HHA Communicates Effectively',
  patientFamilyCooperative: 'Patient/Family Cooperative',
  patientSatisfiedWithServices: 'Patient Satisfied with Services',
  areServicesContinuing: 'Are Services Continuing',
  patientTreatedWithRespect: 'Patient Treated With Respect',
  aideRequiresAgencyFollowUp: 'Does aide require any agency follow-up?',
  comments: 'Comments',
};

const notTakingMedicationsHiddenFields = new Set([
  'pharmacyName',
  'pharmacyPhone',
  'pharmacyName2',
  'pharmacyPhone2',
  'adequateMedSupplyAtHome',
  'adequateMedSupplyAtHomeExplain',
  'medicationsStoredProperly',
  'medicationsStoredProperlyExplain',
  'takesMedicationsAsOrdered',
  'takesMedicationsAsOrderedExplain',
  'medicationSideEffects',
  'sideEffectsExplanation',
  'hasInjections',
  'injectionsAdministeredBy',
  'medPrefillReferralRequired',
  'medicationTeachingReviewed',
  'medicationTeachingReviewedExplain',
  'rnIntervention',
  'rnPrePouredDays',
  'snAdministeredMedications',
  'medicationCommentsTeaching',
]);

function isCheckedValue(value: unknown): boolean {
  return value === true || fieldValueEquals(value, 'true') || fieldValueEquals(value, 'Yes');
}

function clearDependentFields(next: Record<string, unknown>, fields: Set<string>): void {
  fields.forEach((fieldName) => {
    next[fieldName] = Array.isArray(next[fieldName]) ? [] : '';
  });
}

const conditionallyClearedFields = new Set([
  'activitiesPermittedInHomeOther',
  'additionalEmergencyContact',
  'adequateMedSupplyAtHomeExplain',
  'adequateMedSupplyAtHome',
  'allergies',
  'ambulationOther',
  'ambulatoryOther',
  'ambulatory',
  'amputation',
  'areServicesContinuing',
  'backupAideAvailable',
  'backupAideDetails',
  'bedMobility',
  'bloodSugarCondition',
  'bloodSugarPostPrandialHours',
  'bloodSugarValue',
  'bowelFrequencyOther',
  'bowelSoundsOther',
  'cvaExtremities',
  'cva',
  'cardioPulmonarySymptomsOther',
  'castOther',
  'cast',
  'catheterChangedByChhaMdInfo',
  'catheterChangedByOther',
  'catheterChangedBy',
  'changeInAppetiteDescription',
  'changeInPocNeeded',
  'chestPainAssociatedSymptomsOther',
  'chestPainDurationOther',
  'chestPainDuration',
  'chestPainFrequencyOther',
  'chestPainFrequency',
  'chestPainLocationOther',
  'chestPainLocation',
  'chestPainPrecipitatingFactorsOther',
  'chestPainPrecipitatingFactors',
  'chestPainQualityOther',
  'chestPainQuality',
  'chestPainRelievedByOther',
  'chestPainRelievedBy',
  'comments',
  'clusterPackages',
  'consistencyOther',
  'contracture',
  'copyObtained',
  'dmeSuppliesOther',
  'dialysisAccessType',
  'dialysisDays',
  'dialysisLocation',
  'dialysis',
  'eentOther',
  'emergencyContact2Name',
  'emergencyContact2Phone',
  'emergencyContact2Phone2',
  'emergencyContact2Relationship',
  'emergencyContact2Email',
  'emergencyContactName',
  'emergencyContactPhone',
  'emergencyContactPhone2',
  'emergencyContactRelationship',
  'emergencyContactEmail',
  'endocrineSymptomsOther',
  'enteralFeedingPerformedByOther',
  'enteralFeedingPerformedBy',
  'fluidEncourageAmount',
  'fluidRestrictionAmount',
  'giCurrentTreatmentDescription',
  'giOstomyCareByOther',
  'giOstomyCareBy',
  'giOstomyOther',
  'giOstomy',
  'giOutputOther',
  'giOutput',
  'giStomaConditionOther',
  'giStomaCondition',
  'giStomaSite',
  'giSymptomsOther',
  'guSymptomsOther',
  'gaitOther',
  'gait',
  'groomingOther',
  'hhaPcaCompliesWithPoc',
  'hasInjections',
  'hoyerAgitationCombativeness',
  'hoyerBedMobilityAssistMoreThanOne',
  'hoyerPoorTrunkControl',
  'hoyerRelatedAccidentHistory',
  'hoyerSafeManeuveringSpaceInHome',
  'hoyerSevereContractures',
  'identificationBadge',
  'injectionsAdministeredBy',
  'instructionProvidedOther',
  'instructionProvided',
  'insulinAdministration',
  'laundryErrandsSafelyLeftAlone',
  'laundryErrandsSafetyPlanOther',
  'laundryErrandsSafetyPlan',
  'lifeSustainingDmeCompanyInfo',
  'limitedRom',
  'maskMandateCompliance',
  'medPrefillReferralRequired',
  'medicationSideEffects',
  'medicationsStoredProperlyExplain',
  'medicationsStoredProperly',
  'moodBehaviorOther',
  'nameOfProfessional',
  'neededDmeSupplies',
  'nonAmbulatory',
  'orthopneaPillows',
  'otherSupportSystem',
  'otherTasksOther',
  'otherTasks',
  'oxygenDeliveryViaOther',
  'oxygenDeliveryVia',
  'oxygenEquipment',
  'oxygenFlowRate',
  'oxygenFrequency',
  'oxygenPrnPercentage',
  'pcaHhaCommunicatesEffectively',
  'pcpInfo',
  'pcpSelectedAddress',
  'pcp',
  'pain1Frequency',
  'pain1Intensity',
  'pain1Location',
  'pain1MakesBetterOther',
  'pain1MakesBetter',
  'pain1MakesWorseOther',
  'pain1MakesWorse',
  'pain1TypeOther',
  'pain1Type',
  'pain2Frequency',
  'pain2Intensity',
  'pain2Location',
  'pain2MakesBetterOther',
  'pain2MakesBetter',
  'pain2MakesWorseOther',
  'pain2MakesWorse',
  'pain2TypeOther',
  'pain2Type',
  'palliativeCareProviderInfo',
  'paralysisOther',
  'paralysis',
  'paraprofessionalResponse',
  'patientFamilyCooperative',
  'patientInitials',
  'patientSatisfiedWithServices',
  'patientTreatedWithRespect',
  'pedalEdemaDescription',
  'peripheralVascularColorOther',
  'peripheralVascularSensationOther',
  'peripheralVascularTemperatureOther',
  'pertinentHouseRulesOther',
  'petsInHomeList',
  'pharmacyName2',
  'pharmacyName',
  'pharmacyPhone2',
  'pharmacyPhone',
  'planOfCareReviewedWithOther',
  'prescribedDietOther',
  'rnIntervention',
  'rnPrePouredDays',
  'relativeFriendOtherContact',
  'repairReplacementNeededFor',
  'reproductiveSymptomsOther',
  'snAdministeredMedications',
  'safetyPrecautionsOther',
  'safetyPrecautions',
  'secondPersonAvailable',
  'shiftType1',
  'shiftType2',
  'shiftType3',
  'sideEffectsExplanation',
  'signsOfAbuseNeglectOther',
  'substanceUseOther',
  'supervisionAmbulation',
  'supervisionBathing',
  'supervisionGrooming',
  'supervisionToileting',
  'supervisionTransfers',
  'supplementalHomeServicesChha',
  'supplementalHomeServicesHospice',
  'supplementalHomeServicesOther',
  'takesMedicationsAsOrderedExplain',
  'takesMedicationsAsOrdered',
  'toiletingOther',
  'transferStatus',
  'secondPersonAssistWhenNeeded',
  'transfersOther',
  'tremorsOther',
  'tremors',
  'uniformScrubAdequateAttire',
  'urinaryCatheter',
  'urinaryDiversionOther',
  'urinaryDiversion',
  'urinaryOstomyCareByOther',
  'urinaryOstomyCareBy',
  'urinaryStomaConditionOther',
  'urinaryStomaCondition',
  'urinaryStomaSite',
  'urineOutputOther',
  'urineOutput',
  'useOfDmeSpecify',
  'vitalSignsReadRecord',
  'wound1AssessedNoDueToOther',
  'wound1AssessedNoDueTo',
  'wound1Assessed',
  'wound1CareOrderOther',
  'wound1CareOrder',
  'wound1CarePerformedByOther',
  'wound1CarePerformedBy',
  'wound1LesionTypeOther',
  'wound1LesionType',
  'wound1Location',
  'wound1PressureUlcerStage',
  'wound1UnderMdCare',
  'wound2AssessedNoDueToOther',
  'wound2AssessedNoDueTo',
  'wound2Assessed',
  'wound2CareOrderOther',
  'wound2CareOrder',
  'wound2CarePerformedByOther',
  'wound2CarePerformedBy',
  'wound2LesionTypeOther',
  'wound2LesionType',
  'wound2Location',
  'wound2PressureUlcerStage',
  'wound2UnderMdCare',
]);

function hasStoredValue(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && value !== '';
}

function clearHiddenConditionalFields(next: Record<string, unknown>): void {
  conditionallyClearedFields.forEach((fieldName) => {
    if (hasStoredValue(next[fieldName]) && !isFieldVisible(fieldName, next)) {
      next[fieldName] = Array.isArray(next[fieldName]) ? [] : '';
    }
  });
}

function clearSupervisionIncompleteFields(next: Record<string, unknown>): void {
  if (fieldValueEquals(next.supervisionCompleted, 'Yes')) return;
  supervisionIncompleteClearedFields.forEach((fieldName) => {
    if (hasStoredValue(next[fieldName])) {
      next[fieldName] = Array.isArray(next[fieldName]) ? [] : '';
    }
  });
}

function isFieldVisible(fieldName: string, data: Record<string, unknown>): boolean {
  if (fieldValueEquals(data.visitType, 'Revisit') && NARRATIVE_STANDARD_FIELDS.has(fieldName)) {
    return false;
  }

  if (fieldValueEquals(data.visitType, 'Revisit') && revisitHiddenStep4EnvironmentFields.has(fieldName)) {
    return false;
  }

  if (fieldValueEquals(data.visitType, 'Aide Supervision/Evaluation') && aideSupervisionHiddenStep1Fields.has(fieldName)) {
    return false;
  }

  if (fieldValueEquals(data.visitType, 'Patient Evaluation') && patientEvaluationHiddenStep1Fields.has(fieldName)) {
    return false;
  }

  if (
    fieldValueEquals(data.visitType, 'Patient Evaluation')
    && ['advanceDirectives', 'copyObtained'].includes(fieldName)
    && !hasAffirmativeAdvanceDirectiveSelection(data.advanceDirectives)
  ) {
    return false;
  }

  if (supervisionCompletedOnlyFields.has(fieldName) && !fieldValueEquals(data.supervisionCompleted, 'Yes')) {
    return false;
  }

  if (isCheckedValue(data.hasNoEmergencyContact) && noEmergencyContactFields.has(fieldName)) {
    return false;
  }

  if (isCheckedValue(data.notTakingMedications) && notTakingMedicationsHiddenFields.has(fieldName)) {
    return false;
  }

  const conditions: Record<string, () => boolean> = {
    petsInHomeList: () => fieldValueEquals(data.petsInHome, 'Yes'),
    pertinentHouseRulesOther: () => fieldValueIncludes(data.pertinentHouseRules, 'Other'),
    copyObtained: () => Boolean(data.advanceDirectives) && !fieldValueIncludes(data.advanceDirectives, 'No, patient/HCP was educated'),
    backupAideAvailable: () => fieldValueEquals(data.missingAidePoseSafetyRisk, 'Yes'),
    backupAideDetails: () => fieldValueEquals(data.missingAidePoseSafetyRisk, 'Yes') && fieldValueEquals(data.backupAideAvailable, 'Yes'),
    [PETS_IN_HOME_READONLY_FIELD]: () => fieldValueIncludes(data.medicalSuppliesPetsInfo, 'Pets'),
    [HOYER_CONTRACTURE_READONLY_FIELD]: () => fieldValueEquals(data.transferStatus, 'Hoyer') && fieldValueEquals(data.hoyerSevereContractures, 'Yes'),
    lifeSustainingDmeCompanyInfo: () => hasStoredValue(data.electricDependency) && !isExactNoneSelection(data.electricDependency),
    relativeFriendOtherContact: () => fieldValueIncludes(data.evacuationLocation, 'Relative/Friend/Other'),
    anyChangeToPatientWeightExplain: () => fieldValueEquals(data.anyChangeToPatientWeight, 'Yes'),
    bowelSoundsOther: () => fieldValueEquals(data.bowelSounds, 'Other'),
    allergies: () => fieldValueEquals(data.hasAllergies, 'Yes'),
    pedalEdemaDescription: () => fieldValueEquals(data.pedalEdema, 'Yes'),
    sideEffectsExplanation: () => fieldValueEquals(data.medicationSideEffects, 'Yes'),
    adequateMedSupplyAtHomeExplain: () => fieldValueIncludes(data.adequateMedSupplyAtHome, 'No'),
    medicationsStoredProperlyExplain: () => fieldValueIncludes(data.medicationsStoredProperly, 'No'),
    takesMedicationsAsOrderedExplain: () => fieldValueIncludes(data.takesMedicationsAsOrdered, 'No'),
    injectionsAdministeredBy: () => fieldValueIncludes(data.hasInjections, 'Yes'),
    rnPrePouredDays: () => fieldValueIncludes(data.rnIntervention, 'RN pre-poured'),
    medicationTeachingReviewedExplain: () => fieldValueIncludes(data.medicationTeachingReviewed, 'No'),
    palliativeCareProviderInfo: () => fieldValueEquals(data.palliativeCare, 'Already receiving palliative/hospice care'),
    supplementalHomeServicesChha: () => fieldValueIncludes(data.supplementalHomeServices, 'CHHA (RN, PT, OT, ST)'),
    supplementalHomeServicesHospice: () => fieldValueIncludes(data.supplementalHomeServices, 'Hospice/Palliative Care'),
    supplementalHomeServicesOther: () => fieldValueIncludes(data.supplementalHomeServices, 'Other'),
    safetyPrecautions: () => true,
    safetyPrecautionsOther: () => fieldValueIncludes(data.safetyPrecautions, 'Other'),
    specialNeedsOther: () => fieldValueIncludes(data.specialNeeds, 'Other'),
    clusterPackages: () => hasPpClusterContract(data.contractTypes),
    acceptedServiceForPatient: () => fieldValueIncludes(data.patientEvaluationOutcome, 'Accepted, safe and appropriate service for patient'),
    notTakenUnderCareSafetyConcern: () => fieldValueIncludes(data.patientEvaluationOutcome, 'Not taken under care'),
    mealPlanningAssistedBy: () => isRequiresAssistance(data.mealPlanning),
    mealPlanningAssistedOther: () => fieldValueIncludes(data.mealPlanningAssistedBy, 'Other'),
    mealPreppingAssistedBy: () => isRequiresAssistance(data.mealPreppingCooking),
    mealPreppingAssistedOther: () => fieldValueIncludes(data.mealPreppingAssistedBy, 'Other'),
    manageMedicationAssistedBy: () => isRequiresAssistance(data.manageMedication),
    manageMedicationAssistedOther: () => fieldValueIncludes(data.manageMedicationAssistedBy, 'Other'),
    laundryAssistedBy: () => isRequiresAssistance(data.laundry),
    laundryAssistedOther: () => fieldValueIncludes(data.laundryAssistedBy, 'Other'),
    shoppingAssistedBy: () => isRequiresAssistance(data.shopping),
    shoppingAssistedOther: () => fieldValueIncludes(data.shoppingAssistedBy, 'Other'),
    laundryErrandsSafelyLeftAlone: () => isRequiresAssistance(data.laundry) || isRequiresAssistance(data.shopping),
    laundryErrandsSafetyPlan: () => (isRequiresAssistance(data.laundry) || isRequiresAssistance(data.shopping)) && isNoSelection(data.laundryErrandsSafelyLeftAlone),
    laundryErrandsSafetyPlanOther: () => isNoSelection(data.laundryErrandsSafelyLeftAlone) && fieldValueIncludes(data.laundryErrandsSafetyPlan, 'Other'),
    manageMoneyAssistedBy: () => isRequiresAssistance(data.manageMoney),
    manageMoneyAssistedOther: () => fieldValueIncludes(data.manageMoneyAssistedBy, 'Other'),
    otherSupportSystem: () => fieldValueIncludes(data.supportSystem, 'Other'),
    substanceUseOther: () => fieldValueIncludes(data.substanceUse, 'Other'),
    signsOfAbuseNeglectOther: () => fieldValueIncludes(data.signsOfAbuseNeglect, 'Other'),
    dmeSuppliesOther: () => fieldValueIncludes(data.dmeSuppliesInHome, 'Other'),
    repairReplacementNeededFor: () => fieldValueIncludes(data.equipmentCondition, 'Repair/replacement needed'),
    neededDmeSupplies: () => fieldValueEquals(data.dmeSuppliesNeeded, 'Yes'),
    environmentalIssuesAddressed: () => hasEnvironmentalIssue(data),
    environmentalIssuesAddressedDescribe: () => hasEnvironmentalIssue(data) && fieldValueEquals(data.environmentalIssuesAddressed, 'Yes'),
    prescribedDietOther: () => fieldValueIncludes(data.prescribedDiet, 'Other'),
    consistencyOther: () => fieldValueEquals(data.consistency, 'Other'),
    changeInAppetiteDescription: () => fieldValueEquals(data.changeInAppetite, 'Yes'),
    fluidRestrictionAmount: () => fieldValueEquals(data.fluids, 'Restriction'),
    fluidEncourageAmount: () => fieldValueEquals(data.fluids, 'Encourage'),
    enteralFeedings: () => fieldValueIncludes(data.prescribedDiet, 'Enteral Feeding'),
    enteralFeedingPerformedBy: () => fieldValueIncludes(data.prescribedDiet, 'Enteral Feeding') && Boolean(data.enteralFeedings) && !fieldValueEquals(data.enteralFeedings, 'No'),
    enteralFeedingPerformedByOther: () => fieldValueIncludes(data.prescribedDiet, 'Enteral Feeding') && Boolean(data.enteralFeedings) && !fieldValueEquals(data.enteralFeedings, 'No') && fieldValueEquals(data.enteralFeedingPerformedBy, 'Other'),
    moodBehaviorOther: () => fieldValueIncludes(data.moodBehavior, 'Other'),
    frequencyOfBehavior: () => ['Wanders', 'Agitated', 'Combative'].some((value) => fieldValueIncludes(data.moodBehavior, value)),
    frequencyOfBehaviorOther: () => fieldValueIncludes(data.frequencyOfBehavior, 'Other'),
    communicationOther: () => fieldValueIncludes(data.communication, 'Other'),
    cva: () => fieldValueIncludes(data.neuro, 'CVA'),
    cvaExtremities: () => fieldValueEquals(data.cva, 'Residual weakness'),
    tremors: () => fieldValueIncludes(data.neuro, 'Tremors'),
    tremorsOther: () => fieldValueIncludes(data.tremors, 'Other'),
    paralysis: () => fieldValueIncludes(data.neuro, 'Paralysis'),
    paralysisOther: () => fieldValueEquals(data.paralysis, 'Other'),
    cardioPulmonarySymptomsOther: () => fieldValueIncludes(data.cardioPulmonarySymptoms, 'Other'),
    orthopneaPillows: () => fieldValueIncludes(data.cardioPulmonarySymptoms, 'Orthopnea'),
    coughType: () => fieldValueIncludes(data.cardioPulmonarySymptoms, 'Cough'),
    chestPainLocation: () => fieldValueIncludes(data.cardioPulmonarySymptoms, 'Chest Pain'),
    chestPainFrequency: () => fieldValueIncludes(data.cardioPulmonarySymptoms, 'Chest Pain'),
    chestPainDuration: () => fieldValueIncludes(data.cardioPulmonarySymptoms, 'Chest Pain'),
    chestPainQuality: () => fieldValueIncludes(data.cardioPulmonarySymptoms, 'Chest Pain'),
    chestPainSeverity: () => fieldValueIncludes(data.cardioPulmonarySymptoms, 'Chest Pain'),
    chestPainPrecipitatingFactors: () => fieldValueIncludes(data.cardioPulmonarySymptoms, 'Chest Pain'),
    chestPainRelievedBy: () => fieldValueIncludes(data.cardioPulmonarySymptoms, 'Chest Pain'),
    chestPainAssociatedSymptoms: () => fieldValueIncludes(data.cardioPulmonarySymptoms, 'Chest Pain'),
    chestPainLocationOther: () => fieldValueEquals(data.chestPainLocation, 'Other'),
    chestPainFrequencyOther: () => fieldValueEquals(data.chestPainFrequency, 'Other'),
    chestPainDurationOther: () => fieldValueEquals(data.chestPainDuration, 'Other'),
    chestPainQualityOther: () => fieldValueEquals(data.chestPainQuality, 'Other'),
    chestPainPrecipitatingFactorsOther: () => fieldValueEquals(data.chestPainPrecipitatingFactors, 'Other'),
    chestPainAssociatedSymptomsOther: () => fieldValueIncludes(data.chestPainAssociatedSymptoms, 'Other'),
    chestPainRelievedByOther: () => fieldValueEquals(data.chestPainRelievedBy, 'Other'),
    oxygenEquipment: () => fieldValueIncludes(data.cardioPulmonarySymptoms, 'Oxygen'),
    oxygenFlowRate: () => fieldValueIncludes(data.cardioPulmonarySymptoms, 'Oxygen'),
    oxygenFrequency: () => fieldValueIncludes(data.cardioPulmonarySymptoms, 'Oxygen'),
    oxygenDeliveryVia: () => fieldValueIncludes(data.cardioPulmonarySymptoms, 'Oxygen'),
    oxygenPrnPercentage: () => fieldValueIncludes(data.cardioPulmonarySymptoms, 'Oxygen') && fieldValueIncludes(data.oxygenFrequency, 'PRN for SOB'),
    oxygenDeliveryViaOther: () => fieldValueIncludes(data.cardioPulmonarySymptoms, 'Oxygen') && fieldValueEquals(data.oxygenDeliveryVia, 'Other'),
    intervention: () => requiresIntervention(data) || ['Cyanotic', 'Jaundiced'].some((value) => fieldValueEquals(data.integumentColor, value)),
    groomingOther: () => fieldValueIncludes(data.supervisionGrooming, 'Other'),
    toiletingOther: () => fieldValueIncludes(data.supervisionToileting, 'Other'),
    transfersOther: () => fieldValueIncludes(data.supervisionTransfers, 'Other'),
    ambulationOther: () => fieldValueIncludes(data.supervisionAmbulation, 'Other'),
    secondPersonAvailable: () => [
      data.bathing,
      data.grooming,
      data.dressingUpper,
      data.dressingLower,
      data.toileting,
      data.eatingFeeding,
    ].some((value) => fieldValueEquals(value, 'Assist of More Than 1')),
    otherTasksOther: () => fieldValueIncludes(data.otherTasks, 'Other'),
    useOfDmeSpecify: () => fieldValueIncludes(data.instructionProvided, 'Use of DME'),
    instructionProvidedOther: () => fieldValueIncludes(data.instructionProvided, 'Other'),
    shiftType1: () => Number(data.hoursPerDay) === 24,
    shiftType2: () => Number(data.hoursPerDay2) === 24,
    shiftType3: () => Number(data.hoursPerDay3) === 24,
    ppShiftType1: () => Number(data.ppHoursPerDay) === 24,
    ppShiftType2: () => Number(data.ppHoursPerDay2) === 24,
    eentOther: () => fieldValueIncludes(data.eent, 'Other'),
    dentures: () => fieldValueIncludes(data.eent, 'Dentures'),
    denturesFit: () => fieldValueIncludes(data.eent, 'Dentures'),
    musculoskeletalOther: () => fieldValueIncludes(data.musculoskeletal, 'Other'),
    amputation: () => fieldValueIncludes(data.musculoskeletal, 'Amputation'),
    cast: () => fieldValueIncludes(data.musculoskeletal, 'Cast'),
    castOther: () => fieldValueIncludes(data.musculoskeletal, 'Cast') && fieldValueIncludes(data.cast, 'Other'),
    limitedRom: () => fieldValueIncludes(data.rom, 'Limited ROM'),
    contracture: () => fieldValueIncludes(data.rom, 'Contracture'),
    ambulatory: () => fieldValueEquals(data.ambulationStatus, 'Ambulatory'),
    ambulatoryOther: () => fieldValueEquals(data.ambulationStatus, 'Ambulatory') && fieldValueIncludes(data.ambulatory, 'Other'),
    transferStatus: () => fieldValueEquals(data.ambulationStatus, 'Ambulatory') || fieldValueEquals(data.ambulationStatus, 'Non-Ambulatory'),
    transferStatusOther: () => fieldValueEquals(data.transferStatus, 'Other'),
    ambulationAssist: () => fieldValueEquals(data.ambulationStatus, 'Ambulatory'),
    ambulationAssistOther: () => fieldValueEquals(data.ambulationAssist, 'Other'),
    gait: () => fieldValueEquals(data.ambulationStatus, 'Ambulatory'),
    gaitOther: () => fieldValueEquals(data.ambulationStatus, 'Ambulatory') && fieldValueIncludes(data.gait, 'Other'),
    nonAmbulatory: () => fieldValueEquals(data.ambulationStatus, 'Non-Ambulatory'),
    bedMobility: () => fieldValueEquals(data.ambulationStatus, 'Non-Ambulatory'),
    bedMobilityOther: () => fieldValueEquals(data.bedMobility, 'Other'),
    canPatientSelfPropelWheelchair: () => fieldValueEquals(data.nonAmbulatory, 'Chair bound'),
    secondPersonAssistWhenNeeded: () => requiresSecondPersonAssist(data.transferStatus),
    activitiesPermittedInHomeOther: () => fieldValueIncludes(data.activitiesPermittedInHome, 'Other'),
    hoyerAgitationCombativeness: () => fieldValueEquals(data.transferStatus, 'Hoyer'),
    hoyerBedMobilityAssistMoreThanOne: () => fieldValueEquals(data.transferStatus, 'Hoyer'),
    hoyerSevereContractures: () => fieldValueEquals(data.transferStatus, 'Hoyer'),
    hoyerPoorTrunkControl: () => fieldValueEquals(data.transferStatus, 'Hoyer'),
    hoyerRelatedAccidentHistory: () => fieldValueEquals(data.transferStatus, 'Hoyer'),
    hoyerSafeManeuveringSpaceInHome: () => fieldValueEquals(data.transferStatus, 'Hoyer'),
    requiresPtReferralDueTo: () => fieldValueIncludes(data.anyFallPreventionRecommendations, 'Requires PT referral due to'),
    fallPreventionDmeSupplyNeeded: () => fieldValueIncludes(data.anyFallPreventionRecommendations, 'DME/Supply needed'),
    fallPreventionDmeSupplyNeedsRepair: () => fieldValueIncludes(data.anyFallPreventionRecommendations, 'DME/Supply needs repair'),
    needsIncreaseInServiceHoursDueTo: () => fieldValueIncludes(data.anyFallPreventionRecommendations, 'Needs increase in service hours due to'),
    fallPreventionRecOther: () => fieldValueIncludes(data.anyFallPreventionRecommendations, 'Other'),
    wound1Location: () => fieldValueIncludes(data.skinProblems, 'Wound'),
    wound1LesionType: () => fieldValueIncludes(data.skinProblems, 'Wound'),
    wound1LesionTypeOther: () => fieldValueIncludes(data.skinProblems, 'Wound') && fieldValueIncludes(data.wound1LesionType, 'Other'),
    wound1PressureUlcerStage: () => fieldValueIncludes(data.skinProblems, 'Wound') && fieldValueIncludes(data.wound1LesionType, 'Pressure ulcer'),
    wound1Assessed: () => fieldValueIncludes(data.skinProblems, 'Wound'),
    wound1AssessedNoDueTo: () => fieldValueEquals(data.wound1Assessed, 'No, due to'),
    wound1AssessedNoDueToOther: () => fieldValueEquals(data.wound1Assessed, 'No, due to') && fieldValueEquals(data.wound1AssessedNoDueTo, 'Other'),
    wound1CareOrder: () => fieldValueIncludes(data.skinProblems, 'Wound'),
    wound1CareOrderOther: () => fieldValueIncludes(data.skinProblems, 'Wound') && fieldValueEquals(data.wound1CareOrder, 'Other'),
    wound1CarePerformedBy: () => fieldValueIncludes(data.skinProblems, 'Wound'),
    wound1CarePerformedByOther: () => fieldValueIncludes(data.wound1CarePerformedBy, 'Other'),
    wound1UnderMdCare: () => fieldValueIncludes(data.skinProblems, 'Wound'),
    wound2Location: () => fieldValueIncludes(data.skinProblems, 'Wound'),
    wound2LesionType: () => fieldValueIncludes(data.skinProblems, 'Wound'),
    wound2LesionTypeOther: () => fieldValueIncludes(data.skinProblems, 'Wound') && fieldValueIncludes(data.wound2LesionType, 'Other'),
    wound2PressureUlcerStage: () => fieldValueIncludes(data.skinProblems, 'Wound') && fieldValueIncludes(data.wound2LesionType, 'Pressure ulcer'),
    wound2Assessed: () => fieldValueIncludes(data.skinProblems, 'Wound'),
    wound2AssessedNoDueTo: () => fieldValueEquals(data.wound2Assessed, 'No, due to'),
    wound2AssessedNoDueToOther: () => fieldValueEquals(data.wound2Assessed, 'No, due to') && fieldValueEquals(data.wound2AssessedNoDueTo, 'Other'),
    wound2CareOrder: () => fieldValueIncludes(data.skinProblems, 'Wound'),
    wound2CareOrderOther: () => fieldValueIncludes(data.skinProblems, 'Wound') && fieldValueEquals(data.wound2CareOrder, 'Other'),
    wound2CarePerformedBy: () => fieldValueIncludes(data.skinProblems, 'Wound'),
    wound2CarePerformedByOther: () => fieldValueIncludes(data.wound2CarePerformedBy, 'Other'),
    wound2UnderMdCare: () => fieldValueIncludes(data.skinProblems, 'Wound'),
    additionalWounds: () => fieldValueIncludes(data.skinProblems, 'Wound'),
    pain1Location: () => fieldValueEquals(data.painHistory, 'Yes'),
    pain1Intensity: () => fieldValueEquals(data.painHistory, 'Yes'),
    pain1Type: () => fieldValueEquals(data.painHistory, 'Yes'),
    pain1TypeOther: () => fieldValueEquals(data.painHistory, 'Yes') && fieldValueIncludes(data.pain1Type, 'Other'),
    pain1Frequency: () => fieldValueEquals(data.painHistory, 'Yes'),
    pain1MakesBetter: () => fieldValueEquals(data.painHistory, 'Yes'),
    pain1MakesBetterOther: () => fieldValueEquals(data.painHistory, 'Yes') && fieldValueIncludes(data.pain1MakesBetter, 'Other'),
    pain1MakesWorse: () => fieldValueEquals(data.painHistory, 'Yes'),
    pain1MakesWorseOther: () => fieldValueEquals(data.painHistory, 'Yes') && fieldValueIncludes(data.pain1MakesWorse, 'Other'),
    pain2Location: () => fieldValueEquals(data.painHistory, 'Yes'),
    pain2Intensity: () => fieldValueEquals(data.painHistory, 'Yes'),
    pain2Type: () => fieldValueEquals(data.painHistory, 'Yes'),
    pain2TypeOther: () => fieldValueEquals(data.painHistory, 'Yes') && fieldValueIncludes(data.pain2Type, 'Other'),
    pain2Frequency: () => fieldValueEquals(data.painHistory, 'Yes'),
    pain2MakesBetter: () => fieldValueEquals(data.painHistory, 'Yes'),
    pain2MakesBetterOther: () => fieldValueEquals(data.painHistory, 'Yes') && fieldValueIncludes(data.pain2MakesBetter, 'Other'),
    pain2MakesWorse: () => fieldValueEquals(data.painHistory, 'Yes'),
    pain2MakesWorseOther: () => fieldValueEquals(data.painHistory, 'Yes') && fieldValueIncludes(data.pain2MakesWorse, 'Other'),
    peripheralVascularSensationOther: () => fieldValueIncludes(data.peripheralVascularSensation, 'Other'),
    peripheralVascularColorOther: () => fieldValueIncludes(data.peripheralVascularColor, 'Other'),
    peripheralVascularTemperatureOther: () => fieldValueIncludes(data.peripheralVascularTemperature, 'Other'),
    guSymptomsOther: () => fieldValueIncludes(data.guSymptoms, 'Other'),
    dialysis: () => fieldValueIncludes(data.guSymptoms, 'Dialysis'),
    dialysisDays: () => fieldValueIncludes(data.guSymptoms, 'Dialysis'),
    dialysisAccessType: () => fieldValueIncludes(data.guSymptoms, 'Dialysis') && Boolean(data.dialysis) && !fieldValueEquals(data.dialysis, 'Peritoneal'),
    dialysisLocation: () => fieldValueIncludes(data.guSymptoms, 'Dialysis') && Boolean(data.dialysis) && !fieldValueEquals(data.dialysis, 'Peritoneal'),
    urinaryCatheter: () => fieldValueIncludes(data.guSymptoms, 'Urinary Catheter'),
    catheterChangedBy: () => ['Indwelling', 'Suprapubic'].some((type) => fieldValueEquals(data.urinaryCatheter, type)),
    catheterChangedByOther: () => fieldValueEquals(data.catheterChangedBy, 'Other'),
    catheterChangedByChhaMdInfo: () => ['CHHA', 'MD'].some((type) => fieldValueEquals(data.catheterChangedBy, type)),
    urineOutput: () => (
      ['Indwelling', 'Suprapubic', 'Condom Cath', 'External Device (Pure Wick, etc.)'].some((type) => fieldValueEquals(data.urinaryCatheter, type))
      || fieldValueIncludes(data.guSymptoms, 'Urinary Ostomy')
    ),
    urineOutputOther: () => fieldValueEquals(data.urineOutput, 'Other'),
    urinaryDiversion: () => fieldValueIncludes(data.guSymptoms, 'Urinary Ostomy'),
    urinaryDiversionOther: () => fieldValueEquals(data.urinaryDiversion, 'Other'),
    urinaryStomaSite: () => fieldValueIncludes(data.guSymptoms, 'Urinary Ostomy'),
    urinaryStomaCondition: () => fieldValueIncludes(data.guSymptoms, 'Urinary Ostomy'),
    urinaryStomaConditionOther: () => fieldValueEquals(data.urinaryStomaCondition, 'Other'),
    urinaryOstomyCareBy: () => fieldValueIncludes(data.guSymptoms, 'Urinary Ostomy'),
    urinaryOstomyCareByOther: () => fieldValueEquals(data.urinaryOstomyCareBy, 'Other'),
    reproductiveSymptomsOther: () => fieldValueIncludes(data.reproductiveSymptoms, 'Other'),
    giSymptomsOther: () => fieldValueIncludes(data.giSymptoms, 'Other'),
    giCurrentTreatmentDescription: () => fieldValueEquals(data.giCurrentTreatment, 'Yes'),
    bowelFrequencyOther: () => fieldValueEquals(data.bowelFrequency, 'Other'),
    giOstomy: () => fieldValueIncludes(data.giSymptoms, 'Ostomy'),
    giOstomyOther: () => fieldValueEquals(data.giOstomy, 'Other'),
    giStomaSite: () => fieldValueIncludes(data.giSymptoms, 'Ostomy'),
    giStomaCondition: () => fieldValueIncludes(data.giSymptoms, 'Ostomy'),
    giStomaConditionOther: () => fieldValueEquals(data.giStomaCondition, 'Other'),
    giOutput: () => fieldValueIncludes(data.giSymptoms, 'Ostomy'),
    giOutputOther: () => fieldValueEquals(data.giOutput, 'Other'),
    giOstomyCareBy: () => fieldValueIncludes(data.giSymptoms, 'Ostomy'),
    giOstomyCareByOther: () => fieldValueEquals(data.giOstomyCareBy, 'Other'),
    endocrineSymptomsOther: () => fieldValueIncludes(data.endocrineSymptoms, 'Other'),
    diabetesStatus: () => fieldValueIncludes(data.endocrineSymptoms, 'Diabetes'),
    insulinAdministration: () => fieldValueEquals(data.diabetesStatus, 'Insulin'),
    bloodSugarTaken: () => fieldValueIncludes(data.endocrineSymptoms, 'Diabetes'),
    bloodSugarValue: () => fieldValueEquals(data.bloodSugarTaken, 'Yes'),
    bloodSugarCondition: () => fieldValueEquals(data.bloodSugarTaken, 'Yes'),
    bloodSugarPostPrandialHours: () => fieldValueEquals(data.bloodSugarTaken, 'Yes') && fieldValueEquals(data.bloodSugarCondition, 'Post prandial'),
    pocStartDate: () => !fieldValueEquals(data.visitType, 'Aide Supervision/Evaluation'),
    pocEndDate: () => !fieldValueEquals(data.visitType, 'Aide Supervision/Evaluation'),
    planOfCareReviewedWith: () => !fieldValueEquals(data.visitType, 'Aide Supervision/Evaluation'),
    planOfCareReviewedWithOther: () => !fieldValueEquals(data.visitType, 'Aide Supervision/Evaluation') && fieldValueIncludes(data.planOfCareReviewedWith, 'Other'),
    planOfCareNote: () => !fieldValueEquals(data.visitType, 'Aide Supervision/Evaluation'),
    pcp: () => !data.pcpNotInSystem,
    pcpSelectedAddress: () => !data.pcpNotInSystem && Boolean(data.pcp),
    pcpInfo: () => Boolean(data.pcpNotInSystem),
  };

  return conditions[fieldName]?.() ?? true;
}

function isFieldLockedForRevisit(fieldName: string, data: Record<string, unknown>): boolean {
  if (!fieldValueEquals(data.visitType, 'Revisit')) return false;
  const gateField = REVISIT_FIELD_TO_GATE[fieldName];
  return Boolean(gateField && !fieldValueEquals(data[gateField], 'Yes'));
}

function requiresIntervention(data: Record<string, unknown>): boolean {
  const bp = String(data.bloodPressure || '');
  const bpMatch = bp.match(/(\d+)\s*\/\s*(\d+)/);
  if (bpMatch) {
    const systolic = Number(bpMatch[1]);
    const diastolic = Number(bpMatch[2]);
    if (systolic >= 180 || diastolic >= 110 || systolic < 90 || diastolic < 60) return true;
  }

  if (hasStoredValue(data.pulseRate)) {
    const pulse = Number(data.pulseRate);
    if (Number.isFinite(pulse) && (pulse < 50 || pulse > 100)) return true;
  }

  if (hasStoredValue(data.respirationRate)) {
    const respiration = Number(data.respirationRate);
    if (Number.isFinite(respiration) && (respiration < 12 || respiration > 22)) return true;
  }

  if (hasStoredValue(data.temp)) {
    const temp = Number(data.temp);
    if (Number.isFinite(temp) && (temp < 95 || temp > 100)) return true;
  }

  if (hasStoredValue(data.pulseOxygen)) {
    const oxygen = Number(data.pulseOxygen);
    if (Number.isFinite(oxygen) && oxygen < 90) return true;
  }

  const bloodSugar = Number(data.bloodSugarValue);
  if (fieldValueEquals(data.bloodSugarTaken, 'Yes') && Number.isFinite(bloodSugar) && (bloodSugar < 70 || bloodSugar > 250)) {
    return true;
  }

  return false;
}

function isVitalOutOfRange(fieldName: string, data: Record<string, unknown>): boolean {
  if (fieldName === 'bloodPressure') {
    const bpMatch = String(data.bloodPressure || '').match(/(\d+)\s*\/\s*(\d+)/);
    if (!bpMatch) return false;
    const systolic = Number(bpMatch[1]);
    const diastolic = Number(bpMatch[2]);
    return systolic >= 180 || diastolic >= 110 || systolic < 90 || diastolic < 60;
  }
  if (fieldName === 'pulseRate') {
    if (!hasStoredValue(data.pulseRate)) return false;
    const pulse = Number(data.pulseRate);
    return Number.isFinite(pulse) && (pulse < 50 || pulse > 100);
  }
  if (fieldName === 'respirationRate') {
    if (!hasStoredValue(data.respirationRate)) return false;
    const respiration = Number(data.respirationRate);
    return Number.isFinite(respiration) && (respiration < 12 || respiration > 22);
  }
  if (fieldName === 'temp') {
    if (!hasStoredValue(data.temp)) return false;
    const temp = Number(data.temp);
    return Number.isFinite(temp) && (temp < 95 || temp > 100);
  }
  if (fieldName === 'pulseOxygen') {
    if (!hasStoredValue(data.pulseOxygen)) return false;
    const oxygen = Number(data.pulseOxygen);
    return Number.isFinite(oxygen) && oxygen < 90;
  }
  return false;
}

function isBloodSugarFieldOutOfRange(fieldName: string, data: Record<string, unknown>): boolean {
  if (fieldName !== 'bloodSugarValue' || !fieldValueEquals(data.bloodSugarTaken, 'Yes')) return false;
  const bloodSugar = Number(data.bloodSugarValue);
  return Number.isFinite(bloodSugar) && (bloodSugar > 250 || bloodSugar < 80);
}

function isIntegumentColorOutOfRange(fieldName: string, data: Record<string, unknown>): boolean {
  return fieldName === 'integumentColor' && ['Cyanotic', 'Jaundiced'].some((value) => fieldValueEquals(data.integumentColor, value));
}

function normalizeTemperatureOnBlur(value: unknown): string {
  const raw = String(value || '');
  if (!raw) return '';
  const cleaned = raw.replace(/[^0-9.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot >= 0) {
    const rawInt = cleaned.slice(0, firstDot).replace(/\D/g, '');
    const intDigits = rawInt.startsWith('10') ? rawInt.slice(0, 3) : rawInt.slice(0, 2);
    const decDigits = cleaned.slice(firstDot + 1).replace(/\D/g, '').slice(0, 1);
    return decDigits.length > 0 ? `${intDigits}.${decDigits}` : `${intDigits}.0`;
  }
  const digits = cleaned.replace(/\./g, '');
  if (digits.length === 3) return digits.startsWith('10') ? `${digits}.0` : `${digits.slice(0, 2)}.${digits.slice(2, 3)}`;
  if (digits.length === 2) return `${digits}.0`;
  if (digits.length >= 4) return `${digits.slice(0, 3)}.${digits.slice(3, 4)}`;
  return digits;
}

function isEmptyValue(value: unknown, field?: FieldDef): boolean {
  if (field?.dataType === 'BOOLEAN') return value === null || value === undefined || value === '';
  if (field?.dataType === 'TIME') return timeInputValue(value) === '';
  if (Array.isArray(value)) return value.length === 0;
  return value === null || value === undefined || String(value).trim() === '';
}

function activeStepNumber(stepKey: string): number {
  const parsed = Number(stepKey.replace(/^step/, ''));
  return Number.isFinite(parsed) ? parsed : 1;
}

function stepKeyFromNumber(step: number): string {
  return `step${step}`;
}

function requiredFieldsForStep(step: number, data: Record<string, unknown>): string[] {
  const required = new Set<string>();
  const add = (...fields: string[]) => fields.forEach((field) => {
    if (isFieldVisible(field, data)) required.add(field);
  });

  switch (step) {
    case 1:
      add(
        'patientName',
        'dateOfBirth',
        'gender',
        'visitDate',
        'visitStartTime',
        'visitType',
        'street',
        'city',
        'state',
        'zipCode',
        'smokerInHome',
        'petsInHome',
        'pertinentHouseRules',
        'patientSelfDirecting',
        'patientPhoneForEvv',
        'advanceDirectives',
        'missingAidePoseSafetyRisk',
        'mobilityStatus',
        'electricDependency',
        'medicalSuppliesPetsInfo',
        'evacuationLocation',
        'copyObtained',
        'petsInHomeList',
        'pertinentHouseRulesOther',
        'backupAideAvailable',
        'backupAideDetails',
        'lifeSustainingDmeCompanyInfo',
        'relativeFriendOtherContact'
      );
      if (!data.hasNoEmergencyContact) {
        add('emergencyContactName', 'emergencyContactPhone', 'emergencyContactRelationship');
      }
      break;
    case 2:
      add('bowelSounds', 'lungSounds', 'pedalPulsePresent', 'pedalEdema', 'hasAllergies');
      if (data.unableToAssessVitals || data[UNABLE_TO_ASSESS_VITALS_CHECKED_FIELD]) {
        add('unableToAssessVitals');
      } else {
        add(
          'heightFt',
          'heightIn',
          'weight',
          'anyChangeToPatientWeight',
          'bloodPressure',
          'pulseRate',
          'respirationRate',
          'temp',
          'anyChangeToPatientWeightExplain'
        );
      }
      if (requiresIntervention(data)) add('intervention');
      add(
        'bowelSoundsOther',
        'allergies',
        'pedalEdemaDescription'
      );
      if (!data.notTakingMedications) {
        add(
          'pharmacyName',
          'pharmacyPhone',
          'adequateMedSupplyAtHome',
          'medicationsStoredProperly',
          'takesMedicationsAsOrdered',
          'medicationSideEffects',
          'hasInjections',
          'medPrefillReferralRequired',
          'rnIntervention',
          'medicationTeachingReviewed',
          'sideEffectsExplanation',
          'adequateMedSupplyAtHomeExplain',
          'medicationsStoredProperlyExplain',
          'takesMedicationsAsOrderedExplain',
          'injectionsAdministeredBy',
          'rnPrePouredDays',
          'medicationTeachingReviewedExplain'
        );
      }
      break;
    case 3:
      add(
        'bathing',
        'grooming',
        'dressingUpper',
        'dressingLower',
        'toileting',
        'eatingFeeding',
        'mealPlanning',
        'mealPreppingCooking',
        'manageMedication',
        'remindMedication',
        'laundry',
        'shopping',
        'housekeeping',
        'useThePhone',
        'manageMoney',
        'safetyAwareness'
      );
      if (isRequiresAssistance(data.mealPlanning)) add('mealPlanningAssistedBy', 'mealPlanningAssistedOther');
      if (isRequiresAssistance(data.mealPreppingCooking)) add('mealPreppingAssistedBy', 'mealPreppingAssistedOther');
      if (isRequiresAssistance(data.manageMedication)) add('manageMedicationAssistedBy', 'manageMedicationAssistedOther');
      if (isRequiresAssistance(data.laundry)) add('laundryAssistedBy', 'laundryAssistedOther');
      if (isRequiresAssistance(data.shopping)) add('shoppingAssistedBy', 'shoppingAssistedOther');
      if (isRequiresAssistance(data.laundry) || isRequiresAssistance(data.shopping)) {
        add('laundryErrandsSafelyLeftAlone', 'laundryErrandsSafetyPlan');
      }
      if (isNoSelection(data.laundryErrandsSafelyLeftAlone)) add('laundryErrandsSafetyPlanOther');
      if (isRequiresAssistance(data.manageMoney)) add('manageMoneyAssistedBy', 'manageMoneyAssistedOther');
      break;
    case 4:
      add(
        'patientLivesIn',
        'aptSleepSpaceForLiveIn',
        'wcAccessible',
        'buildingHasSuper',
        'buildingHasSecurityDeskDoorman',
        'adequateVentilationHeating',
        'adequatePlumbingSanitation',
        'roachRodentBedBugInfestation',
        'hasElectric',
        'hasRunningWater',
        'hasWorkingPhone',
        'clutteredPathway',
        'lightedPathway',
        'scatterRugsHazards',
        'structuralHazards',
        'adequateCookingFacility',
        'oxygenSafelyStored',
        'fireExtinguisher',
        'fireEscapeSecondExit',
        'workingSmokeDetectors',
        'homeDementiaSafe',
        'supportSystem',
        'livingSituation',
        'substanceUse',
        'signsOfAbuseNeglect',
        'requiresReferralFor',
        'dmeSuppliesInHome',
        'equipmentCondition',
        'dmeSupplyKnowledge',
        'dmeSuppliesNeeded',
        'otherSupportSystem',
        'substanceUseOther',
        'signsOfAbuseNeglectOther',
        'dmeSuppliesOther',
        'repairReplacementNeededFor',
        'neededDmeSupplies',
        'environmentalIssuesAddressed',
        'environmentalIssuesAddressedDescribe'
      );
      break;
    case 5:
      add(
        'prescribedDiet',
        'consistency',
        'appetite',
        'changeInAppetite',
        'fluids',
        'fluidConsistency',
        'hydrationAdequate',
        'orientationCognition',
        'levelOfConsciousness',
        'moodBehavior',
        'neuro',
        'communication',
        'cardioPulmonarySymptoms',
        'prescribedDietOther',
        'consistencyOther',
        'changeInAppetiteDescription',
        'fluidRestrictionAmount',
        'fluidEncourageAmount',
        'enteralFeedings',
        'enteralFeedingPerformedBy',
        'enteralFeedingPerformedByOther',
        'moodBehaviorOther',
        'frequencyOfBehavior',
        'frequencyOfBehaviorOther',
        'communicationOther',
        'cva',
        'cvaExtremities',
        'tremors',
        'tremorsOther',
        'paralysis',
        'paralysisOther',
        'cardioPulmonarySymptomsOther',
        'orthopneaPillows',
        'coughType',
        'chestPainFrequency',
        'chestPainDuration',
        'chestPainLocation',
        'chestPainQuality',
        'chestPainPrecipitatingFactors',
        'chestPainRelievedBy',
        'chestPainAssociatedSymptoms',
        'chestPainFrequencyOther',
        'chestPainDurationOther',
        'chestPainLocationOther',
        'chestPainQualityOther',
        'chestPainPrecipitatingFactorsOther',
        'chestPainRelievedByOther',
        'chestPainAssociatedSymptomsOther',
        'oxygenEquipment',
        'oxygenFlowRate',
        'oxygenFrequency',
        'oxygenDeliveryVia',
        'oxygenPrnPercentage',
        'oxygenDeliveryViaOther'
      );
      break;
    case 6:
      add('eent', 'musculoskeletal', 'rom', 'ambulationStatus', 'activitiesPermittedInHome', 'anyFallPreventionRecommendations');
      add(
        'eentOther',
        'dentures',
        'denturesFit',
        'musculoskeletalOther',
        'amputation',
        'cast',
        'castOther',
        'limitedRom',
        'contracture',
        'ambulatory',
        'ambulatoryOther',
        'transferStatus',
        'transferStatusOther',
        'ambulationAssist',
        'ambulationAssistOther',
        'gait',
        'gaitOther',
        'nonAmbulatory',
        'bedMobility',
        'bedMobilityOther',
        'canPatientSelfPropelWheelchair',
        'secondPersonAssistWhenNeeded',
        'activitiesPermittedInHomeOther',
        'hoyerAgitationCombativeness',
        'hoyerBedMobilityAssistMoreThanOne',
        'hoyerSevereContractures',
        'hoyerPoorTrunkControl',
        'hoyerRelatedAccidentHistory',
        'hoyerSafeManeuveringSpaceInHome',
        'requiresPtReferralDueTo',
        'fallPreventionDmeSupplyNeeded',
        'fallPreventionDmeSupplyNeedsRepair',
        'needsIncreaseInServiceHoursDueTo',
        'fallPreventionRecOther'
      );
      break;
    case 7:
      add(
        'guSymptoms',
        'reproductiveSymptoms',
        'giSymptoms',
        'giCurrentTreatment',
        'bowelFrequency',
        'lastBmDate',
        'peripheralVascularSensation',
        'peripheralVascularColor',
        'peripheralVascularTemperature',
        'endocrineSymptoms',
        'peripheralVascularSensationOther',
        'peripheralVascularColorOther',
        'peripheralVascularTemperatureOther',
        'guSymptomsOther',
        'dialysis',
        'dialysisDays',
        'dialysisAccessType',
        'dialysisLocation',
        'urinaryCatheter',
        'catheterChangedBy',
        'catheterChangedByOther',
        'catheterChangedByChhaMdInfo',
        'urineOutput',
        'urineOutputOther',
        'urinaryDiversion',
        'urinaryDiversionOther',
        'urinaryStomaSite',
        'urinaryStomaCondition',
        'urinaryStomaConditionOther',
        'urinaryOstomyCareBy',
        'urinaryOstomyCareByOther',
        'reproductiveSymptomsOther',
        'giSymptomsOther',
        'giCurrentTreatmentDescription',
        'bowelFrequencyOther',
        'giOstomy',
        'giOstomyOther',
        'giStomaSite',
        'giStomaCondition',
        'giStomaConditionOther',
        'giOutput',
        'giOutputOther',
        'giOstomyCareBy',
        'giOstomyCareByOther',
        'endocrineSymptomsOther',
        'diabetesStatus',
        'insulinAdministration',
        'bloodSugarTaken',
        'bloodSugarValue',
        'bloodSugarCondition',
        'bloodSugarPostPrandialHours'
      );
      if (fieldValueIncludes(data.endocrineSymptoms, 'Diabetes') && fieldValueEquals(data.bloodSugarTaken, 'Yes')) {
        const bloodSugar = Number(data.bloodSugarValue);
        if (Number.isFinite(bloodSugar) && (bloodSugar > 250 || bloodSugar < 70)) add('intervention');
      }
      break;
    case 8:
      add('integumentColor', 'integumentTurgor', 'integumentTemp', 'integumentCondition', 'skinProblems', 'painHistory');
      if (fieldValueEquals(data.integumentColor, 'Cyanotic') || fieldValueEquals(data.integumentColor, 'Jaundiced')) {
        add('intervention');
      }
      if (fieldValueIncludes(data.skinProblems, 'Wound')) {
        add(
          'wound1Location',
          'wound1LesionType',
          'wound1Assessed',
          'wound1CareOrder',
          'wound1CarePerformedBy',
          'wound1UnderMdCare',
          'wound1LesionTypeOther',
          'wound1PressureUlcerStage',
          'wound1AssessedNoDueTo',
          'wound1AssessedNoDueToOther',
          'wound1CareOrderOther',
          'wound1CarePerformedByOther'
        );
        if (isWound2Touched(data)) {
          add(
            'wound2Location',
            'wound2LesionType',
            'wound2Assessed',
            'wound2CareOrder',
            'wound2CarePerformedBy',
            'wound2UnderMdCare',
            'wound2LesionTypeOther',
            'wound2PressureUlcerStage',
            'wound2AssessedNoDueTo',
            'wound2AssessedNoDueToOther',
            'wound2CareOrderOther',
            'wound2CarePerformedByOther'
          );
        }
      }
      if (fieldValueEquals(data.painHistory, 'Yes')) {
        add(
          'pain1Location',
          'pain1Intensity',
          'pain1Type',
          'pain1Frequency',
          'pain1MakesBetter',
          'pain1MakesWorse',
          'pain1TypeOther',
          'pain1MakesBetterOther',
          'pain1MakesWorseOther'
        );
      }
      break;
    case 9:
      if (!fieldValueEquals(data.visitType, 'Revisit')) {
        add(
          'palliativeCare',
          'supplementalHomeServices',
          'prognosis',
          'safetyPrecautions',
          'functionalLimitation',
          'servicesProvided',
          'rnOrders',
          'goals',
          'dischargePlan',
          'nursingProgressNote',
          'safetyPrecautionsOther',
          'palliativeCareProviderInfo',
          'supplementalHomeServicesChha',
          'supplementalHomeServicesHospice',
          'supplementalHomeServicesOther'
        );
      }
      break;
    case 10:
      add('pocStartDate', 'pocEndDate', 'planOfCareReviewedWith');
      add('planOfCareReviewedWithOther');
      break;
    case 11:
      add('supervisionCompleted');
      if (fieldValueEquals(data.supervisionCompleted, 'Yes')) {
        add(
          'nameOfProfessional',
          'patientInitials',
          'identificationBadge',
          'uniformScrubAdequateAttire',
          'changeInPocNeeded',
          'supervisionBathing',
          'supervisionGrooming',
          'supervisionToileting',
          'supervisionTransfers',
          'supervisionAmbulation',
          'otherTasks',
          'vitalSignsReadRecord',
          'instructionProvided',
          'paraprofessionalResponse',
          'hhaPcaCompliesWithPoc',
          'pcaHhaCommunicatesEffectively',
          'patientFamilyCooperative',
          'patientSatisfiedWithServices',
          'areServicesContinuing',
          'patientTreatedWithRespect',
          'aideRequiresAgencyFollowUp',
          'maskMandateCompliance'
        );
        const hhaRatingFields = [
          'hhaPcaCompliesWithPoc',
          'pcaHhaCommunicatesEffectively',
          'patientFamilyCooperative',
          'patientSatisfiedWithServices',
          'areServicesContinuing',
          'patientTreatedWithRespect',
        ];
        if (hhaRatingFields.some((field) => fieldValueEquals(data[field], 'No') || fieldValueEquals(data[field], 'No (Explain Below)'))) {
          add('comments');
        }
      }
      break;
    case 12:
      add('Signature');
      break;
    default:
      break;
  }

  if (step === 1 && fieldValueEquals(data.visitType, 'Aide Supervision/Evaluation')) {
    [
      'advanceDirectives',
      'copyObtained',
      'priorityCode',
      'missingAidePoseSafetyRisk',
      'backupAideAvailable',
      'backupAideDetails',
      'mobilityStatus',
      'electricDependency',
      'lifeSustainingDmeCompanyInfo',
      'medicalSuppliesPetsInfo',
      'evacuationLocation',
      'relativeFriendOtherContact',
    ].forEach((field) => required.delete(field));
  }

  if (fieldValueEquals(data.visitType, 'Patient Evaluation')) {
    if (step === 2) {
      required.clear();
      add('neuro', 'musculoskeletal', 'fallRiskFactors', 'specialNeeds', 'specialNeedsOther', 'skilledNeeds', 'skilledNeedsPerformedBy');
    } else if (step === 3) {
      add('adlCompletionPace');
    } else if (step === 11) {
      required.clear();
      add('patientEvaluationOutcome', 'acceptedServiceForPatient', 'notTakenUnderCareSafetyConcern');
    } else if (step === 12) {
      required.clear();
      add('Signature');
    }
  }

  if (fieldValueEquals(data.visitType, 'Revisit')) {
    if (step === 3 && isRevisitReason(data, 'post-hospital', 'post hospital')) {
      add(
        'hospitalAdmissionDate',
        'hospitalDischargeDate',
        'hospitalOrRehabName',
        'briefHospitalCourse',
        'commentsOrAideInstructions',
        'dcSummaryObtainedReviewed'
      );
    } else if (step === 6 && isRevisitReason(data, 'post fall')) {
      add('fallPreventionEducation');
    } else if (step === 9) {
      required.clear();
    } else if (step === 10) {
      required.clear();
      add('isPocUpdateNeeded');
      if (fieldValueEquals(data.isPocUpdateNeeded, 'Yes')) {
        add('pocEndDate', 'planOfCareReviewedWith', 'planOfCareReviewedWithOther');
        if (!isRevisitReason(data, 'modification')) add('pocStartDate');
      }
    } else if (step === 11 || step === 12) {
      required.clear();
    }
  }

  if (step === 10 && shouldRequireClusterPackagesOnPlanOfCare(data)) {
    add('clusterPackages');
  }

  return Array.from(required);
}

function isStepReviewed(data: Record<string, unknown>, step: number): boolean {
  const reviewed = data.stepsReviewed;
  return typeof reviewed === 'object' && reviewed !== null && !Array.isArray(reviewed)
    ? Boolean((reviewed as Record<string, unknown>)[step])
    : false;
}

function displayDate(value: unknown): string | null {
  if (!value) return null;
  const parsed = new Date(String(value));
  if (Number.isNaN(parsed.getTime())) return String(value);
  return `${parsed.getUTCMonth() + 1}/${parsed.getUTCDate()}/${parsed.getUTCFullYear()}`;
}

// The date picker turns a short typed year into 0025 or 0202; Nexus rejects
// some of these and none are real visit dates.
function isValidDateInput(value: unknown): boolean {
  const match = dateInputValue(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;
  const [year, month, day] = match.slice(1).map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return year >= 1900 && year <= 2100 && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day;
}

function dateInputValue(value: unknown): string {
  if (!value) return '';
  const raw = String(value).trim();
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    return `${usMatch[3]}-${usMatch[1].padStart(2, '0')}-${usMatch[2].padStart(2, '0')}`;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getUTCFullYear();
  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parsed.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function timeInputValue(value: unknown): string {
  if (!value) return '';
  const raw = String(value).trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return '';
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || hours < 0 || hours > 23 || !Number.isInteger(minutes) || minutes < 0 || minutes > 59) return '';
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function ageFromDateOfBirth(value: unknown): string {
  if (!value) return '';
  const raw = dateInputValue(value);
  if (!raw) return '';
  let birthDate: Date;
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) {
    birthDate = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  } else {
    birthDate = new Date(raw);
  }
  if (Number.isNaN(birthDate.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const hasHadBirthdayThisYear = today.getMonth() > birthDate.getMonth()
    || (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());
  if (!hasHadBirthdayThisYear) age -= 1;
  return String(age);
}

function visitReasonDisplay(record: Record<string, unknown>): string | null {
  const reason = record.assessmentVisitReason || record.assessmentVisitReason;
  const other = record.assessmentVisitReasonOther || record.assessmentVisitReasonOther;
  if (reason === 'Other' && other) return `${reason}: ${other}`;
  return reason ? String(reason) : null;
}

function assignmentPrimaryLines(record: Record<string, unknown>): string[] {
  const assessmentNurse = typeof record.assessmentNurse === 'object' && record.assessmentNurse !== null
    ? record.assessmentNurse as Record<string, unknown>
    : null;
  const nurse = record.assessmentNurseName || assessmentNurse?.name;
  const visitReason = visitReasonDisplay(record);
  const visitDate = displayDate(record.assessmentVisitDate || record.assessmentVisitDate);
  return [
    nurse ? `Nurse: ${String(nurse)}` : null,
    record.assessmentType ? `Assessment Type: ${String(record.assessmentType)}` : null,
    record.assessmentVisitType ? `Visit Type: ${String(record.assessmentVisitType)}` : null,
    visitReason ? `Visit Reason: ${visitReason}` : null,
    record.assessmentEvaluationLocation || record.assessmentEvaluationLocation
      ? `Evaluation: ${String(record.assessmentEvaluationLocation || record.assessmentEvaluationLocation)}`
      : null,
    visitDate ? `Visit Date: ${visitDate}` : null,
  ].filter((line): line is string => Boolean(line));
}

function assignmentSecondaryLines(record: Record<string, unknown>): string[] {
  const serviceStart = displayDate(record.serviceStartDate || record.serviceStartDate);
  const certificationStart = displayDate(record.certificationPeriodStart || record.certificationPeriodStart);
  const certificationEnd = displayDate(record.certificationPeriodEnd || record.certificationPeriodEnd);
  const contractTypes = record.assessmentContractTypes || record.assessmentContractTypes;
  return [
    serviceStart ? `Service Start: ${serviceStart}` : null,
    record.assessmentContract || record.assessmentContract ? `Contract: ${String(record.assessmentContract || record.assessmentContract)}` : null,
    contractTypes ? `Contract Types: ${Array.isArray(contractTypes) ? contractTypes.join('; ') : String(contractTypes)}` : null,
    certificationStart && certificationEnd ? `Certification: ${certificationStart} - ${certificationEnd}` : null,
  ].filter((line): line is string => Boolean(line));
}

function patientContactLine(record: Record<string, unknown>): string {
  return [
    record.dateOfBirth || record.dateOfBirth ? `DOB: ${displayDate(record.dateOfBirth || record.dateOfBirth)}` : null,
    record.phone || record.Phone ? `Phone: ${String(record.phone || record.Phone)}` : null,
    record.mobile || record.personMobilePhone || record.PersonMobilePhone ? `Mobile: ${String(record.mobile || record.personMobilePhone || record.PersonMobilePhone)}` : null,
  ].filter(Boolean).join(' • ');
}

function coordinatorLine(record: Record<string, unknown>): string | null {
  const coordinator = typeof record.lhcsaCoordinator === 'object' && record.lhcsaCoordinator !== null
    ? record.lhcsaCoordinator as Record<string, unknown>
    : typeof record.LHCSA_Coordinator__r === 'object' && record.LHCSA_Coordinator__r !== null
      ? record.LHCSA_Coordinator__r as Record<string, unknown>
      : null;
  const name = record.lhcsaCoordinatorName || coordinator?.name || coordinator?.Name;
  return name ? `Coordinator: ${String(name)}` : null;
}

function nurseUserLabel(nurse: NurseUserRecord): string {
  return String(
    nurse.name
    || [nurse.firstName, nurse.lastName].filter(Boolean).join(' ')
    || nurse.username
    || nurse.id
  );
}

function readNurseAdminBackup(): NurseAdminSessionBackup | null {
  try {
    const raw = localStorage.getItem(NURSE_ADMIN_SESSION_KEY);
    return raw ? JSON.parse(raw) as NurseAdminSessionBackup : null;
  } catch {
    return null;
  }
}

function nestedRecord(record: Record<string, unknown>, key: string): Record<string, unknown> | null {
  const value = record[key];
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function caregiverId(caregiver: CaregiverRecord | null | undefined): string {
  if (!caregiver) return '';
  const contact = nestedRecord(caregiver, 'Contact') || nestedRecord(caregiver, 'contact');
  return String(caregiver.Id || caregiver.id || contact?.Id || contact?.id || '');
}

function caregiverName(caregiver: CaregiverRecord | null | undefined): string {
  if (!caregiver) return '';
  const contact = nestedRecord(caregiver, 'Contact') || nestedRecord(caregiver, 'contact');
  const firstLast = [caregiver.firstName || contact?.firstName, caregiver.lastName || contact?.lastName].filter(Boolean).join(' ');
  return String(contact?.Name || contact?.name || caregiver.Name || caregiver.name || firstLast || caregiverId(caregiver));
}

function caregiverBirthdate(caregiver: CaregiverRecord | null | undefined): string | null {
  if (!caregiver) return null;
  const contact = nestedRecord(caregiver, 'Contact') || nestedRecord(caregiver, 'contact');
  return displayDate(contact?.Birthdate || contact?.birthdate || contact?.dateOfBirth || caregiver.Birthdate || caregiver.birthdate || caregiver.dateOfBirth);
}

function caregiverDetailParts(caregiver: CaregiverRecord | null | undefined): string[] {
  if (!caregiver) return [];
  const contact = nestedRecord(caregiver, 'Contact') || nestedRecord(caregiver, 'contact') || {};
  const dob = caregiverBirthdate(caregiver);
  const phone = caregiver.phone || caregiver.Phone || contact.phone || contact.Phone;
  const mobile = caregiver.mobilePhone || caregiver.MobilePhone || contact.mobilePhone || contact.MobilePhone;
  return [
    dob ? `DOB: ${dob}` : null,
    phone ? `Phone: ${String(phone)}` : null,
    mobile ? `Mobile: ${String(mobile)}` : null,
  ].filter((part): part is string => Boolean(part));
}

function caregiverFluStatus(caregiver: CaregiverRecord | null | undefined): string {
  if (!caregiver) return 'Not provided';
  const contact = nestedRecord(caregiver, 'Contact') || nestedRecord(caregiver, 'contact');
  const value = contact?.fluExpiration || contact?.fluExpiration || caregiver.fluExpiration || caregiver.fluExpiration;
  return displayDate(value) || 'Not provided';
}

function initialForNamePart(value: unknown): string {
  const match = String(value || '').trim().match(/[A-Za-z0-9]/);
  return match ? match[0].toUpperCase() : '';
}

function initialsFromPatientName(value: unknown): string {
  const name = String(value || '').trim();
  if (!name) return '';
  if (name.includes(',')) {
    const [lastName = '', firstName = ''] = name.split(',');
    return `${initialForNamePart(firstName)}${initialForNamePart(lastName)}`.trim();
  }
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return initialForNamePart(parts[0]);
  return `${initialForNamePart(parts[0])}${initialForNamePart(parts[parts.length - 1])}`.trim();
}

function patientInitialsFromSources(data: Record<string, unknown>, patient?: Record<string, unknown> | null): string {
  const firstName = patient?.firstName || data.firstName;
  const lastName = patient?.lastName || data.lastName;
  const fromParts = `${initialForNamePart(firstName)}${initialForNamePart(lastName)}`.trim();
  if (fromParts) return fromParts;
  return initialsFromPatientName(data.patientName || patient?.name);
}

function caregiverOptionLabel(caregiver: CaregiverRecord): string {
  const name = caregiverName(caregiver);
  const dob = caregiverBirthdate(caregiver);
  return dob ? `${name} (DOB: ${dob})` : name;
}

function isDisplayableImageContentType(contentType: string): boolean {
  const normalized = contentType.split(';')[0].trim().toLowerCase();
  return normalized.startsWith('image/')
    && normalized !== 'image/heic'
    && normalized !== 'image/heif';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function getDoctorId(doctor: Record<string, unknown> | null | undefined): string {
  return String(doctor?.id || '').trim();
}

function doctorData(doctor: Record<string, unknown> | null | undefined): Record<string, unknown> {
  return isRecord(doctor?.data) ? doctor.data as Record<string, unknown> : {};
}

function doctorValue(doctor: Record<string, unknown> | null | undefined, ...keys: string[]): unknown {
  if (!doctor) return undefined;
  const data = doctorData(doctor);
  for (const key of keys) {
    if (doctor[key] !== undefined && doctor[key] !== null && doctor[key] !== '') return doctor[key];
    if (data[key] !== undefined && data[key] !== null && data[key] !== '') return data[key];
  }
  return undefined;
}

function validDoctorOptions(doctors: unknown): Record<string, unknown>[] {
  return Array.isArray(doctors) ? doctors.filter((doctor): doctor is Record<string, unknown> => (
    isRecord(doctor) && Boolean(getDoctorId(doctor))
  )) : [];
}

function pcpLabel(doctor: Record<string, unknown> | null): string {
  if (!doctor) return '';
  return String(doctorValue(doctor, 'name') || [
    doctorValue(doctor, 'firstName'),
    doctorValue(doctor, 'lastName'),
  ].filter(Boolean).join(' ') || getDoctorId(doctor));
}

function pcpDetailParts(doctor: Record<string, unknown> | null): string[] {
  if (!doctor) return [];
  const npi = doctorValue(doctor, 'npiNumber');
  const phone = doctorValue(doctor, 'phone', 'otherPhone', 'mobilePhone');
  return [
    npi ? `NPI: ${String(npi)}` : null,
    phone ? String(phone) : null,
  ].filter((part): part is string => Boolean(part));
}

function doctorAddressEntries(doctor: Record<string, unknown> | null): Array<{
  key: string;
  label: string;
  address: string;
  phone: string;
  fax: string;
}> {
  if (!doctor) return [];
  return pcpOffices(getDoctorId(doctor), doctor).map(office => ({
    key: office.slot === 'PRIMARY' ? 'Mailing' : office.slot === 'OFFICE_2' ? 'Address 2' : 'Address 3',
    label: office.slot === 'PRIMARY' ? 'Primary office' : office.slot === 'OFFICE_2' ? 'Office 2' : 'Office 3',
    address: office.address,
    phone: office.phone,
    fax: office.fax,
  }));
}

function doctorAddressOptions(doctor: Record<string, unknown> | null): string[] {
  return doctorAddressEntries(doctor).map((entry) => entry.address);
}

function displayFieldValue(value: unknown): string {
  if (Array.isArray(value)) return value.length ? value.map(displayFieldValue).join(', ') : 'Not provided';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value === null || value === undefined || String(value).trim() === '') return 'Not provided';
  const stringValue = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(stringValue)) return displayDate(stringValue) || stringValue;
  return stringValue;
}

function isWinAnsiCharacter(char: string): boolean {
  const code = char.charCodeAt(0);
  return (code >= 32 && code <= 126) || (code >= 160 && code <= 255) || code === 9 || code === 10 || code === 13;
}

function validateWinAnsiString(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    if (!isWinAnsiCharacter(value[index])) return false;
  }
  return true;
}

function hasAffirmativeAdvanceDirectiveSelection(value: unknown): boolean {
  return selectedValues(value).some((token) => {
    const normalized = token.trim().toLowerCase();
    return normalized.startsWith('yes') || normalized.includes('proxy has');
  });
}

function sanitizeWinAnsiString(value: string): string {
  const replacements: Record<string, string> = {
    '\u2018': "'",
    '\u2019': "'",
    '\u201C': '"',
    '\u201D': '"',
    '\u201A': ',',
    '\u201E': '"',
    '\u2013': '-',
    '\u2014': '-',
    '\u2010': '-',
    '\u2011': '-',
    '\u2212': '-',
    '\u2022': '*',
    '\u2023': '>',
    '\u25E6': 'o',
    '\u2026': '...',
    '\u00A0': ' ',
    '\u20AC': 'E',
    '\u2122': 'TM',
    '\u00A9': '(c)',
    '\u00AE': '(R)',
  };
  return value.split('').map((char) => (isWinAnsiCharacter(char) ? char : replacements[char] || char)).join('');
}

function sanitizeAssessmentPayloadStrings(value: unknown): unknown {
  if (typeof value === 'string') return sanitizeWinAnsiString(value);
  if (Array.isArray(value)) return value.map((item) => sanitizeAssessmentPayloadStrings(item));
  if (value instanceof Date) return value;
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, key === 'pcpOfficeSelection' ? item : sanitizeAssessmentPayloadStrings(item)])
    );
  }
  return value;
}

function taskNumber(taskName: string): number {
  const match = taskName.match(/^(\d+)/);
  return match ? Number(match[1]) : 999999;
}

function sortPlanOfCareRows(rows: PlanOfCareTaskRow[]): PlanOfCareTaskRow[] {
  return [...rows].sort((a, b) => taskNumber(a.task) - taskNumber(b.task));
}

function isTimesheetTask(taskName: string): boolean {
  return taskNumber(taskName) === 600;
}

function normalizePlanOfCareTask(row: Partial<PlanOfCareTaskRow> | null | undefined): PlanOfCareTaskRow {
  const normalized: PlanOfCareTaskRow = {
    task: String(row?.task || ''),
    min: String(row?.min || ''),
    max: String(row?.max || ''),
    instructions: String(row?.instructions || ''),
    required: Boolean(row?.required),
    assist: Boolean(row?.assist),
    supervision: String(row?.supervision || ''),
  };

  if (isTimesheetTask(normalized.task)) {
    normalized.required = false;
    normalized.assist = false;
  } else if (!normalized.required && !normalized.assist && SYSTEM_REQUIRED_POC_TASKS.has(normalized.task)) {
    normalized.required = true;
  } else if (normalized.required && normalized.assist) {
    normalized.assist = false;
  }

  return normalized;
}

function parsePlanOfCareTasks(value: unknown): PlanOfCareTaskRow[] {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (!Array.isArray(parsed)) return [];
    return sortPlanOfCareRows(parsed.map((row) => normalizePlanOfCareTask(row)));
  } catch {
    return [];
  }
}

function serializePlanOfCareTasks(rows: PlanOfCareTaskRow[]): string {
  return JSON.stringify(sortPlanOfCareRows(rows.map((row) => normalizePlanOfCareTask(row))));
}

function planOfCareTaskValidationIssues(tasks: PlanOfCareTaskRow[]): PlanOfCareTaskValidationIssue[] {
  return tasks
    .map((task, rowIndex) => {
      const fields: PlanOfCareTaskField[] = [];
      const missingLabels: string[] = [];
      const addMissing = (field: PlanOfCareTaskField, label: string) => {
        fields.push(field);
        missingLabels.push(label);
      };

      if (!task.task) addMissing('task', 'task name');
      if (isTimesheetTask(task.task)) {
        if (!task.instructions.trim()) addMissing('instructions', 'timesheet reason');
      } else {
        if (!task.min) addMissing('min', 'min');
        if (!task.max) addMissing('max', 'max');
        if (!task.required && !task.assist) {
          addMissing('required', 'Aide to do');
          addMissing('assist', 'Aide to Assist');
        }
      }

      return fields.length > 0
        ? { rowIndex, task: task.task || 'Unnamed task', fields, missingLabels }
        : null;
    })
    .filter((item): item is PlanOfCareTaskValidationIssue => Boolean(item));
}

function planOfCareTaskValidationMessage(tasks: PlanOfCareTaskRow[]): string | null {
  const invalidTasks = planOfCareTaskValidationIssues(tasks);
  if (invalidTasks.length === 0) return null;

  const visibleInvalidTasks = invalidTasks
    .slice(0, 3)
    .map((issue) => `${issue.task} (${issue.missingLabels.join(', ')})`)
    .join('; ');
  const remainingCount = invalidTasks.length - 3;
  const suffix = remainingCount > 0 ? `; and ${remainingCount} more` : '';
  return `Complete missing POC task fields: ${visibleInvalidTasks}${suffix}.`;
}

function conditionalPlanOfCareTasks(data: Record<string, unknown>, currentRows: PlanOfCareTaskRow[], maxDaysAllowed: number): PlanOfCareTaskRow[] {
  const rows: PlanOfCareTaskRow[] = [];
  const max = String(maxDaysAllowed || 1);
  const add = (task: string) => rows.push(normalizePlanOfCareTask({
    task,
    min: '1',
    max,
    instructions: PLAN_OF_CARE_TASKS[task]?.defaultDescription || '',
    required: false,
    assist: false,
  }));

  if (fieldValueEquals(data.hasAllergies, 'Yes')) add('547 Follow allergy precautions');
  if (fieldValueIncludes(data.hasInjections, 'Yes')) add('545 Follow Needles/sharps precautions');
  if (data.eatingFeeding && !fieldValueEquals(data.eatingFeeding, 'Independent')) {
    add('546 Follow choking/aspiration precautions');
  }

  const bathingTasks = ['100 Tub', '101 Shower', '102 Bed / Sponge', '103 Total care / perineal care', '104 Mouth Care'];
  if (currentRows.some((row) => bathingTasks.some((task) => row.task.includes(task)))) {
    add('544 Report wound/bruises');
  }

  return rows;
}

function normalizedWholeNumber(value: unknown, min = 0, max?: number): string {
  if (value === undefined || value === null || String(value).trim() === '') return '';
  const parsed = Math.trunc(Number(value));
  if (!Number.isFinite(parsed)) return '';
  const cappedLow = Math.max(parsed, min);
  return String(Number.isFinite(max) ? Math.min(cappedLow, Number(max)) : cappedLow);
}

class ApiRequestError extends Error {
  readonly status: number;
  readonly retryable: boolean | undefined;
  readonly fieldErrors: Record<string, string>;
  readonly step: string | undefined;

  constructor(message: string, status: number, retryable: boolean | undefined, fieldErrors: Record<string, string> = {}, step?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.retryable = retryable;
    this.fieldErrors = fieldErrors;
    this.step = step;
  }
}

async function apiJson<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await authFetch(url, init);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const payload = data && typeof data === 'object'
      ? data as { message?: unknown; retryable?: unknown; fieldErrors?: unknown; step?: unknown }
      : {};
    throw new ApiRequestError(
      typeof payload.message === 'string' ? payload.message : 'Request failed',
      response.status,
      typeof payload.retryable === 'boolean' ? payload.retryable : undefined,
      payload.fieldErrors && typeof payload.fieldErrors === 'object'
        ? Object.fromEntries(Object.entries(payload.fieldErrors).filter((entry): entry is [string, string] => typeof entry[1] === 'string'))
        : {},
      typeof payload.step === 'string' ? payload.step : undefined
    );
  }
  return data as T;
}

function formatRecordingTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  }
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
}

export default function NursingAssessment() {
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser());
  const [metadata, setMetadata] = useState<Metadata | null>(null);
  const [patients, setPatients] = useState<Record<string, unknown>[]>([]);
  const [nurseUsers, setNurseUsers] = useState<NurseUserRecord[]>([]);
  const [nurseUsersLoading, setNurseUsersLoading] = useState(false);
  const [setupTab, setSetupTab] = useState(0);
  const [impersonatingNurseId, setImpersonatingNurseId] = useState<string | null>(null);
  const [nurseAdminBackup, setNurseAdminBackup] = useState<NurseAdminSessionBackup | null>(() => readNurseAdminBackup());
  const [selectedPatient, setSelectedPatient] = useState<Record<string, unknown> | null>(null);
  const [assessment, setAssessment] = useState<Record<string, unknown> | null>(null);
  const [completedAssessment, setCompletedAssessment] = useState<Record<string, unknown> | null>(null);
  const [completedDialogOpen, setCompletedDialogOpen] = useState(false);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [activeStep, setActiveStep] = useState('step1');
  const [unableToAssessVitalsChecked, setUnableToAssessVitalsChecked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Fields whose error came from a rejected save; a later successful save clears them.
  const serverFieldErrorKeysRef = useRef<Set<string>>(new Set());
  const [saveErrorStep, setSaveErrorStep] = useState<string | null>(null);
  useEffect(() => { setSaveErrorStep(null); }, [assessment?.id]);
  const [documents, setDocuments] = useState<NursingDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [consentEmailLoading, setConsentEmailLoading] = useState(false);
  const [manualDocumentUploads, setManualDocumentUploads] = useState<Record<string, boolean>>({});
  const [manualDocumentTab, setManualDocumentTab] = useState(0);
  const [viewingDocument, setViewingDocument] = useState<NursingDocument | null>(null);
  const [viewingDocumentUrl, setViewingDocumentUrl] = useState<string | null>(null);
  const [viewingDocumentLoading, setViewingDocumentLoading] = useState(false);
  const [expandedDocumentIds, setExpandedDocumentIds] = useState<number[]>([]);
  const [documentPreviewUrls, setDocumentPreviewUrls] = useState<Record<number, string>>({});
  const [documentPreviewLoading, setDocumentPreviewLoading] = useState<Record<number, boolean>>({});
  const [activeCaregivers, setActiveCaregivers] = useState<CaregiverRecord[]>([]);
  const [caregiversLoading, setCaregiversLoading] = useState(false);
  const [caregiversError, setCaregiversError] = useState<string | null>(null);
  const [caregiverSearchInput, setCaregiverSearchInput] = useState('');
  const [debouncedCaregiverSearch, setDebouncedCaregiverSearch] = useState('');
  const [noCaregiverPresent, setNoCaregiverPresent] = useState(false);
  const [caregiverIdPicture, setCaregiverIdPicture] = useState<CaregiverIdPicture | null | undefined>(undefined);
  const [caregiverIdPictureLoading, setCaregiverIdPictureLoading] = useState(false);
  const [caregiverIdPictureError, setCaregiverIdPictureError] = useState<string | null>(null);
  const [caregiverIdPictureImageError, setCaregiverIdPictureImageError] = useState(false);
  const [pcpOptions, setPcpOptions] = useState<Record<string, unknown>[]>([]);
  const [pcpLoading, setPcpLoading] = useState(false);
  const [pcpSearchInput, setPcpSearchInput] = useState('');
  const [pcpAddressDialog, setPcpAddressDialog] = useState<{ open: boolean; addressType: 'Mailing' | 'Address 2' | 'Address 3' | null }>({ open: false, addressType: null });
  const [pcpAddressForm, setPcpAddressForm] = useState({ address: '', street: '', aptSuite: '', city: '', state: '', zipCode: '', phone: '', fax: '' });
  const [pcpAddressSaving, setPcpAddressSaving] = useState(false);
  const [pcpAddressError, setPcpAddressError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [recordingConsentOpen, setRecordingConsentOpen] = useState(false);
  const [recordingDeclineVisible, setRecordingDeclineVisible] = useState(false);
  const [hasRespondedToRecordingConsent, setHasRespondedToRecordingConsent] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [reauthMessage, setReauthMessage] = useState('');
  const [transcriptionText, setTranscriptionText] = useState('');
  const [summaryText, setSummaryText] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showConsentPrerequisite, setShowConsentPrerequisite] = useState(false);
  const [consentPrerequisiteStep, setConsentPrerequisiteStep] = useState(1);
  const [consentServiceSearch, setConsentServiceSearch] = useState('');
  const [planOfCareTaskPickerOpen, setPlanOfCareTaskPickerOpen] = useState(false);
  const [planOfCareTaskSearch, setPlanOfCareTaskSearch] = useState('');
  const [otherDiagnosisInput, setOtherDiagnosisInput] = useState('');
  const [recordingTextDialogOpen, setRecordingTextDialogOpen] = useState(false);
  const [recordingTextTab, setRecordingTextTab] = useState(0);
  const [recordingTextLoading, setRecordingTextLoading] = useState(false);
  const [recordingPaused, setRecordingPaused] = useState(false);
  const [recordingWasStopped, setRecordingWasStopped] = useState(false);
  const [recordingFinalizing, setRecordingFinalizing] = useState(false);
  const [recordingElapsedSeconds, setRecordingElapsedSeconds] = useState(0);
  const [recordingAnalyserNode, setRecordingAnalyserNode] = useState<AnalyserNode | null>(null);
  const [recordingWaveLevels, setRecordingWaveLevels] = useState<number[]>(() => Array(5).fill(20));
  const [handOffNoteDialogOpen, setHandOffNoteDialogOpen] = useState(false);
  const [handOffNoteDraft, setHandOffNoteDraft] = useState('');
  const [handOffNoteDialogError, setHandOffNoteDialogError] = useState('');
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [ticketDescription, setTicketDescription] = useState('');
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [ticketError, setTicketError] = useState('');
  const [ticketResult, setTicketResult] = useState<DelegatedTicketResult | null>(null);
  const [ticketPortalRequestId, setTicketPortalRequestId] = useState<string | null>(null);
  const [ticketRequestAssessmentId, setTicketRequestAssessmentId] = useState<string | null>(null);
  const [topNavToolsTarget, setTopNavToolsTarget] = useState<HTMLElement | null>(null);
  const [topNavInfoTarget, setTopNavInfoTarget] = useState<HTMLElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const recordingAudioContextRef = useRef<AudioContext | null>(null);
  const recordingAudioSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const chunkIndexRef = useRef(0);
  const pendingRecordingUploadsRef = useRef<Promise<void>[]>([]);
  const restoredPatientRef = useRef(false);
  const pendingAuthRetriesRef = useRef<Array<{ key: string; retry: () => unknown | Promise<unknown> }>>([]);
  const pendingAuthRetrySequenceRef = useRef(0);
  const ticketSubmittingRef = useRef(false);
  const ticketIdentityEpochRef = useRef(0);
  const pendingTicketAttemptsRef = useRef<Record<string, PendingTicketAttempt>>({});
  const completedTicketResultsRef = useRef<Record<string, DelegatedTicketResult>>({});
  const documentPreviewUrlsRef = useRef<Record<number, string>>({});
  const selectedCaregiverIdRef = useRef('');
  const consentSignaturePadRef = useRef<SignatureCanvas | null>(null);
  const nurseSignaturePadRef = useRef<SignatureCanvas | null>(null);
  const navActiveStepRef = useRef<HTMLDivElement | null>(null);
  const navScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const isCompactNav = useMediaQuery('(max-width:992px)');
  const theme = useTheme();
  const fullScreenRecordingDialog = useMediaQuery(theme.breakpoints.down('md'));
  const [canScrollNavLeft, setCanScrollNavLeft] = useState(false);
  const [canScrollNavRight, setCanScrollNavRight] = useState(false);

  const steps = useMemo(() => Object.entries(metadata?.steps ?? {}), [metadata]);
  const visitType = String(formData.visitType || formData.visitType || selectedPatient?.assessmentVisitType || 'Initial');
  const visibleStepNumbers = useMemo(() => (
    VISIT_TYPE_VISIBLE_STEPS[visitType] || VISIT_TYPE_VISIBLE_STEPS.Initial
  ), [visitType]);
  const visibleSteps = useMemo(() => (
    steps.filter(([key]) => visibleStepNumbers.includes(activeStepNumber(key)))
  ), [steps, visibleStepNumbers]);

  const openReauthDialog = useCallback((retry: () => unknown | Promise<unknown>, key?: string) => {
    const retryKey = key || `retry-${pendingAuthRetrySequenceRef.current++}`;
    if (!pendingAuthRetriesRef.current.some((pending) => pending.key === retryKey)) {
      pendingAuthRetriesRef.current.push({ key: retryKey, retry });
    }
    if (nurseAdminBackup && !localStorage.getItem(NURSE_ADMIN_SESSION_KEY)) {
      setNurseAdminBackup(null);
    }
    setReauthMessage('Your session expired. Sign in again to continue without losing your nursing assessment work.');
    setReauthOpen(true);
    setError(null);
  }, [nurseAdminBackup]);

  const handleAuthExpired = useCallback((err: unknown, retry: () => unknown | Promise<unknown>, key?: string) => {
    if (!(err instanceof AuthExpiredError)) return false;
    openReauthDialog(retry, key);
    return true;
  }, [openReauthDialog]);

  const waitForReauthRetry = useCallback((
    err: unknown,
    retry: () => unknown | Promise<unknown>,
    key?: string
  ): Promise<boolean> => (
    new Promise((resolve) => {
      const wrappedRetry = async () => {
        try {
          await retry();
          resolve(true);
        } catch (retryErr) {
          if (!handleAuthExpired(retryErr, wrappedRetry, key)) {
            resolve(false);
            throw retryErr;
          }
        }
      };

      if (!handleAuthExpired(err, wrappedRetry, key)) {
        resolve(false);
      }
    })
  ), [handleAuthExpired]);

  const completeReauth = useCallback(() => {
    const retries = [...pendingAuthRetriesRef.current];
    const refreshedUser = getStoredUser();
    const shouldClearAdminBackup = Boolean(
      nurseAdminBackup
      && refreshedUser
      && user
      && refreshedUser.id !== user.id
    );
    pendingAuthRetriesRef.current = [];
    setReauthOpen(false);
    if (shouldClearAdminBackup) {
      localStorage.removeItem(NURSE_ADMIN_SESSION_KEY);
      setNurseAdminBackup(null);
    }
    setUser(refreshedUser);
    if (retries.length === 0) return;
    void (async () => {
      for (const { key, retry } of retries) {
        try {
          await retry();
        } catch (err) {
          if (!handleAuthExpired(err, retry, key)) {
            setError(err instanceof Error ? err.message : 'Request failed after signing in again');
          }
        }
      }
    })();
  }, [handleAuthExpired, nurseAdminBackup, user]);

  useEffect(() => {
    setTopNavToolsTarget(document.getElementById('nursing-top-nav-tools'));
    setTopNavInfoTarget(document.getElementById('nursing-pinned-info'));
  }, []);

  useEffect(() => {
    const { style } = document.body;
    const originalPosition = style.backgroundPosition;
    const originalAttachment = style.backgroundAttachment;

    style.backgroundPosition = 'bottom';
    style.backgroundAttachment = 'fixed';

    return () => {
      style.backgroundPosition = originalPosition;
      style.backgroundAttachment = originalAttachment;
    };
  }, []);

  useEffect(() => {
    if (!recording || recordingPaused) return undefined;
    const interval = window.setInterval(() => {
      setRecordingElapsedSeconds((current) => current + 1);
    }, 1000);
    return () => window.clearInterval(interval);
  }, [recording, recordingPaused]);

  useEffect(() => {
    if (!recordingAnalyserNode || !recording || recordingPaused) {
      setRecordingWaveLevels(Array(5).fill(20));
      return undefined;
    }

    const dataArray = new Uint8Array(recordingAnalyserNode.frequencyBinCount);
    let animationFrame = 0;
    const updateLevels = () => {
      recordingAnalyserNode.getByteFrequencyData(dataArray);
      const barCount = 5;
      const bandSize = Math.max(1, Math.floor(dataArray.length / barCount));
      const nextLevels = Array.from({ length: barCount }, (_, index) => {
        let max = 0;
        const start = index * bandSize;
        const end = Math.min(start + bandSize, dataArray.length);
        for (let dataIndex = start; dataIndex < end; dataIndex += 1) {
          if (dataArray[dataIndex] > max) max = dataArray[dataIndex];
        }
        const normalized = max / 255;
        const boosted = Math.pow(normalized, 0.6);
        return Math.max(10, Math.min(100, boosted * 95 + 5));
      });
      setRecordingWaveLevels(nextLevels);
      animationFrame = window.requestAnimationFrame(updateLevels);
    };

    updateLevels();
    return () => window.cancelAnimationFrame(animationFrame);
  }, [recordingAnalyserNode, recording, recordingPaused]);

  useEffect(() => () => {
    recordingAudioContextRef.current?.close().catch(() => undefined);
  }, []);
  const updateNavArrowVisibility = useCallback(() => {
    const container = navScrollContainerRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollNavLeft(scrollLeft > 0);
    setCanScrollNavRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);
  const scrollNav = useCallback((scrollOffset: number) => {
    navScrollContainerRef.current?.scrollBy({ left: scrollOffset, behavior: 'smooth' });
  }, []);
  useEffect(() => {
    navActiveStepRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeStep, visibleSteps]);
  useEffect(() => {
    const container = navScrollContainerRef.current;
    if (!container) return undefined;

    updateNavArrowVisibility();
    container.addEventListener('scroll', updateNavArrowVisibility);
    container.addEventListener('scrollend', updateNavArrowVisibility);
    window.addEventListener('resize', updateNavArrowVisibility);

    return () => {
      container.removeEventListener('scroll', updateNavArrowVisibility);
      container.removeEventListener('scrollend', updateNavArrowVisibility);
      window.removeEventListener('resize', updateNavArrowVisibility);
    };
  }, [isCompactNav, updateNavArrowVisibility, visibleSteps]);
  const statusForStep = useCallback((stepKey: string) => {
    const stepNumber = activeStepNumber(stepKey);
    const statusOptions = metadata?.fields['status']?.picklistValues || [];
    const stepTitle = metadata?.steps[stepKey]?.title || '';
    const titleStatus = statusOptions.find((option) => fieldValueEquals(option, stepTitle));
    if (titleStatus) return titleStatus;
    return statusOptions[stepNumber - 1] || metadata?.steps[stepKey]?.title || 'In Progress';
  }, [metadata]);
  const stepKeyFromStatus = useCallback((status: unknown) => {
    const normalizedStatus = String(status || '').trim();
    if (!normalizedStatus || fieldValueEquals(normalizedStatus, 'Completed')) return 'step1';
    const statusOptions = metadata?.fields['status']?.picklistValues || [];
    const titleMatch = Object.entries(metadata?.steps || {}).find(([, step]) => fieldValueEquals(step.title, normalizedStatus));
    if (titleMatch) return titleMatch[0];
    const statusIndex = statusOptions.findIndex((option) => fieldValueEquals(option, normalizedStatus));
    if (statusIndex >= 0) return stepKeyFromNumber(statusIndex + 1);
    return 'step1';
  }, [metadata]);
  const caregiverOptions = useMemo(() => {
    const seen = new Set<string>();
    return activeCaregivers
      .filter((caregiver) => {
        const id = caregiverId(caregiver);
        if (!id) return true;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .sort((a, b) => caregiverOptionLabel(a).localeCompare(caregiverOptionLabel(b), undefined, { sensitivity: 'base' }));
  }, [activeCaregivers]);
  const visibleCaregiverOptions = caregiverOptions;
  const selectedCaregiver = useMemo(() => {
    const selectedId = String(formData.caregiver || '');
    if (!selectedId) return null;
    return caregiverOptions.find((caregiver) => caregiverId(caregiver) === selectedId) || {
      id: selectedId,
      name: String(formData.caregiverName || formData.nameOfProfessional || selectedId),
    };
  }, [caregiverOptions, formData.caregiver, formData.caregiverName, formData.nameOfProfessional]);
  const selectedCaregiverId = caregiverId(selectedCaregiver);

  const loadCaregiverIdPicture = async (caregiverIdValue: string, signal?: AbortSignal) => {
    if (selectedCaregiverIdRef.current !== caregiverIdValue) return;
    setCaregiverIdPictureLoading(true);
    setCaregiverIdPictureError(null);
    setCaregiverIdPictureImageError(false);
    try {
      const result = await apiJson<{ picture: CaregiverIdPicture | null }>(
        `/api/nursing-assessment/caregiver/${encodeURIComponent(caregiverIdValue)}/id-picture`,
        { signal }
      );
      if (selectedCaregiverIdRef.current !== caregiverIdValue) return;
      setCaregiverIdPicture(result.picture);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (selectedCaregiverIdRef.current !== caregiverIdValue) return;
      if (handleAuthExpired(
        err,
        () => loadCaregiverIdPicture(caregiverIdValue),
        `caregiver-id-picture:${caregiverIdValue}`
      )) return;
      setCaregiverIdPicture(undefined);
      setCaregiverIdPictureError(err instanceof Error ? err.message : 'Failed to load caregiver ID picture');
    } finally {
      if (!signal?.aborted && selectedCaregiverIdRef.current === caregiverIdValue) {
        setCaregiverIdPictureLoading(false);
      }
    }
  };

  useLayoutEffect(() => {
    // Commit the new identity before paint so an older request cannot display a stale caregiver photo.
    selectedCaregiverIdRef.current = selectedCaregiverId;
    setCaregiverIdPicture(undefined);
    setCaregiverIdPictureError(null);
    setCaregiverIdPictureImageError(false);
    if (!selectedCaregiverId || noCaregiverPresent) {
      setCaregiverIdPictureLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    void loadCaregiverIdPicture(selectedCaregiverId, controller.signal);
    return () => controller.abort();
  }, [selectedCaregiverId, noCaregiverPresent]);
  const maxDaysAllowed = useMemo(() => {
    const total = [
      formData.daysPerWeek,
      formData.daysPerWeek2,
      formData.daysPerWeek3,
    ].reduce<number>((sum, value) => sum + (Number.parseInt(String(value || '0'), 10) || 0), 0);
    return Math.max(1, Math.min(total || 1, 7));
  }, [formData.daysPerWeek, formData.daysPerWeek2, formData.daysPerWeek3]);
  const planOfCareRows = useMemo(() => parsePlanOfCareTasks(formData.planOfCareTasks), [formData.planOfCareTasks]);
  const primaryDiagnosisRows = useMemo(() => parseDiagnosisRows(formData.primaryDx, true), [formData.primaryDx]);
  const otherDiagnosisRows = useMemo(() => parseDiagnosisRows(formData.otherPertinentDiagnosis), [formData.otherPertinentDiagnosis]);
  const medicationRows = useMemo(() => parseMedicationRows(formData.medications), [formData.medications]);
  const assessmentContractName = String(selectedPatient?.assessmentContract || formData.assessmentContract || formData.contract || '');
  const isHhaContract = useMemo(() => {
    const contract = assessmentContractName.trim().toLowerCase();
    return HHA_CONTRACTS.has(contract);
  }, [assessmentContractName]);
  const planOfCareTaskOptions = useMemo(() => (
    Object.keys(PLAN_OF_CARE_TASKS).filter((task) => isHhaContract || !task.includes('(HHA only)'))
  ), [isHhaContract]);
  const consentContractName = assessmentContractName;
  const consentRows = useMemo(() => (
    parseConsentRows(formData.consent, formData, consentContractName)
  ), [formData, consentContractName]);
  const hasWaivedConsent = useMemo(() => (
    consentRows.some((row) => row.expectedCoverage === 'Waived' || row.patientResponsibility === 'Waived')
  ), [consentRows]);
  const consentOptions = useMemo(() => (
    consentServiceOptions(formData, consentRows, consentContractName)
  ), [formData, consentRows, consentContractName]);
  const selectedPcp = useMemo(() => (
    pcpOptions.find((doctor) => getDoctorId(doctor) === String(formData.pcp)) || null
  ), [formData.pcp, pcpOptions]);
  const contactBaseline = formData.patientContactBaseline as Record<string, unknown> | undefined;
  const selectedPatientEmail = String(contactBaseline && Object.prototype.hasOwnProperty.call(contactBaseline, 'patientEmail')
    ? contactBaseline.patientEmail || '' : selectedPatient?.email || '').trim();
  const unableToAssessVitalsActive = unableToAssessVitalsChecked || hasStoredValue(formData.unableToAssessVitals);

  const dataWithUiValidationState = useCallback((data = formData) => ({
    ...data,
    [UNABLE_TO_ASSESS_VITALS_CHECKED_FIELD]: unableToAssessVitalsChecked || hasStoredValue(data.unableToAssessVitals),
    patientInitials: data.patientInitials || patientInitialsFromSources(data, selectedPatient),
  }), [formData, selectedPatient, unableToAssessVitalsChecked]);

  useEffect(() => {
    if (hasStoredValue(formData.unableToAssessVitals)) setUnableToAssessVitalsChecked(true);
  }, [formData.unableToAssessVitals]);

  useEffect(() => {
    const calculatedAge = ageFromDateOfBirth(formData.dateOfBirth);
    if (String(formData.age || '') === calculatedAge) return;
    setFormData((current) => ({
      ...current,
      age: calculatedAge,
    }));
  }, [formData.dateOfBirth, formData.age]);

  useEffect(() => {
    const calculatedInitials = patientInitialsFromSources(formData, selectedPatient);
    if (!calculatedInitials || String(formData.patientInitials || '') === calculatedInitials) return;
    setFormData((current) => ({
      ...current,
      patientInitials: calculatedInitials,
    }));
  }, [
    formData.firstName,
    formData.lastName,
    formData.patientInitials,
    formData.patientName,
    selectedPatient,
  ]);

  const adjustConsentSignaturePadSize = useCallback(() => {
    const canvas = consentSignaturePadRef.current?.getCanvas();
    const container = canvas?.parentNode as HTMLElement | null;
    if (!canvas || !container) return;
    const { width } = container.getBoundingClientRect();
    canvas.width = width;
    canvas.height = 200;
    canvas.style.width = `${width}px`;
    canvas.style.height = '200px';
    if (formData.ConsentSignature) {
      consentSignaturePadRef.current?.fromDataURL(String(formData.ConsentSignature), { width, height: 200 });
    }
  }, [formData.ConsentSignature]);

  useLayoutEffect(() => {
    if (showConsentPrerequisite || fieldValueEquals(formData.consentType, 'Digital')) adjustConsentSignaturePadSize();
  }, [adjustConsentSignaturePadSize, showConsentPrerequisite, formData.consentType]);

  useEffect(() => {
    if (!showConsentPrerequisite && !fieldValueEquals(formData.consentType, 'Digital')) return undefined;
    const timeout = window.setTimeout(adjustConsentSignaturePadSize, 100);
    window.addEventListener('resize', adjustConsentSignaturePadSize);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('resize', adjustConsentSignaturePadSize);
    };
  }, [adjustConsentSignaturePadSize, showConsentPrerequisite, formData.consentType]);

  const adjustNurseSignaturePadSize = useCallback(() => {
    const canvas = nurseSignaturePadRef.current?.getCanvas();
    const container = canvas?.parentNode as HTMLElement | null;
    if (!canvas || !container) return;
    const { width } = container.getBoundingClientRect();
    canvas.width = width;
    canvas.height = 200;
    canvas.style.width = `${width}px`;
    canvas.style.height = '200px';
    if (formData.Signature) {
      nurseSignaturePadRef.current?.fromDataURL(String(formData.Signature), { width, height: 200 });
    }
  }, [formData.Signature]);

  useLayoutEffect(() => {
    if (activeStep === 'step12' || (showConsentPrerequisite && consentPrerequisiteStep === 2)) {
      adjustNurseSignaturePadSize();
    }
  }, [activeStep, adjustNurseSignaturePadSize, consentPrerequisiteStep, showConsentPrerequisite]);

  useEffect(() => {
    if (activeStep !== 'step12' && !(showConsentPrerequisite && consentPrerequisiteStep === 2)) return undefined;
    const timeout = window.setTimeout(adjustNurseSignaturePadSize, 100);
    window.addEventListener('resize', adjustNurseSignaturePadSize);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('resize', adjustNurseSignaturePadSize);
    };
  }, [activeStep, adjustNurseSignaturePadSize, consentPrerequisiteStep, showConsentPrerequisite]);

  const loadMetadataAndAssignments = useCallback(async (cancelled?: () => boolean) => {
    setLoading(true);
    setError(null);
    setNurseUsersLoading(hasRole(user, 'nurse-admin') || hasRole(user, 'admin'));
    try {
      const requests: [
        Promise<Metadata>,
        Promise<{ patients: Record<string, unknown>[] }>,
        Promise<{ nurses?: NurseUserRecord[] }> | null
      ] = [
        apiJson<Metadata>('/api/nursing-assessment/metadata'),
        apiJson<{ patients: Record<string, unknown>[] }>('/api/nursing-assessment/assignments'),
        hasRole(user, 'nurse-admin') || hasRole(user, 'admin')
          ? apiJson<{ nurses?: NurseUserRecord[] }>('/api/nursing-assessment/nurse-users')
          : null,
      ];
      const [metadataResult, assignmentResult, nurseUsersResult] = await Promise.all(requests);
      if (cancelled?.()) return;
      setMetadata(metadataResult);
      setPatients([...(assignmentResult.patients || [])].sort((a, b) => (
        patientLabel(a).localeCompare(patientLabel(b), undefined, { sensitivity: 'base' })
      )));
      if (nurseUsersResult) {
        const currentNexusId = user?.nexusUser?.nexusId || user?.username;
        setNurseUsers((nurseUsersResult.nurses || []).filter((nurse) => String(nurse.id) !== String(currentNexusId)));
      } else {
        setNurseUsers([]);
      }
    } catch (err) {
      if (!cancelled?.() && !handleAuthExpired(err, () => loadMetadataAndAssignments(), 'load-metadata-assignments')) {
        setError(err instanceof Error ? err.message : 'Failed to load nursing assessment');
      }
    } finally {
      if (!cancelled?.()) setLoading(false);
      if (!cancelled?.()) setNurseUsersLoading(false);
    }
  }, [handleAuthExpired, user]);

  useEffect(() => {
    let cancelled = false;
    void loadMetadataAndAssignments(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [loadMetadataAndAssignments]);

  useEffect(() => {
    if (!fieldValueEquals(formData.consentType, 'Digital') || selectedValues(formData.contractTypes).length === 0) return;
    const existing = new Set(consentRows.map((row) => row.frequency));
    const missingDefaults = ['Initial visit', 'Recertification'].filter((frequency) => !existing.has(frequency));
    if (missingDefaults.length === 0) return;

    const contractTypes = selectedValues(formData.contractTypes);
    const newRows = missingDefaults.map((frequency, index) => {
      const config = CONSENT_SERVICE_CONFIG.find((item) => item.frequency === frequency);
      return normalizeConsentRow({
        id: `consent-row-${Date.now()}-${index}`,
        service: config?.services || 'RN',
        frequency,
        fee: config ? consentServiceFee(config, contractTypes, consentContractName, formData) : '',
        weekendFee: 'Daily Rate',
        expectedCoverage: '',
        patientResponsibility: '',
        requiresFrequencyInput: false,
        requiresFeeInput: false,
        isPrivatePay: false,
      }, index, formData, consentContractName);
    });
    const nextRows = [...newRows.filter((row) => row.frequency === 'Initial visit'), ...consentRows, ...newRows.filter((row) => row.frequency === 'Recertification')];
    setField('consent', serializeConsentRows(nextRows));
  }, [formData.consentType, formData.contractTypes, consentRows, consentContractName]);

  useEffect(() => {
    if (hasWaivedConsent || !formData.financeClearanceReceived) return;
    setField('financeClearanceReceived', '');
  }, [hasWaivedConsent, formData.financeClearanceReceived]);

  const patientLabel = (patient: Record<string, unknown>) => (
    String(patient.name || [patient.firstName, patient.lastName].filter(Boolean).join(' ') || patient.id || '')
  );

  const searchActiveCaregivers = async (patientId: unknown, search: string, signal?: AbortSignal) => {
    if (!patientId || search.trim().length < 2) {
      setActiveCaregivers([]);
      return;
    }
    setCaregiversLoading(true);
    setCaregiversError(null);
    try {
      const result = await apiJson<{ caregivers: CaregiverRecord[] }>(
        `/api/nursing-assessment/active-caregivers/${encodeURIComponent(String(patientId))}?search=${encodeURIComponent(search.trim())}`,
        { signal }
      );
      setActiveCaregivers(result.caregivers || []);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (handleAuthExpired(err, () => searchActiveCaregivers(patientId, search), `active-caregivers:${patientId}:${search.trim()}`)) return;
      setActiveCaregivers([]);
      setCaregiversError(err instanceof Error ? err.message : 'Failed to load active caregivers');
    } finally {
      if (!signal?.aborted) setCaregiversLoading(false);
    }
  };

  useEffect(() => {
    const search = caregiverSearchInput.trim();
    if (noCaregiverPresent || search.length < 2) {
      setDebouncedCaregiverSearch('');
      setActiveCaregivers([]);
      setCaregiversLoading(false);
      setCaregiversError(null);
      return;
    }

    const timeout = window.setTimeout(() => {
      setDebouncedCaregiverSearch(search);
    }, 400);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [caregiverSearchInput, noCaregiverPresent]);

  useEffect(() => {
    if (!selectedPatient?.id || !debouncedCaregiverSearch) return;

    setActiveCaregivers([]);
    setCaregiversError(null);
    const controller = new AbortController();
    void searchActiveCaregivers(selectedPatient.id, debouncedCaregiverSearch, controller.signal);

    return () => {
      controller.abort();
    };
  }, [selectedPatient?.id, debouncedCaregiverSearch]);

  const searchPcps = async (search = '') => {
    setPcpLoading(true);
    try {
      const result = await apiJson<{ doctors: Record<string, unknown>[] }>(
        `/api/nursing-assessment/doctor${search ? `?search=${encodeURIComponent(search)}` : ''}`
      );
      setPcpOptions(validDoctorOptions(result.doctors));
    } catch (err) {
      if (handleAuthExpired(err, () => searchPcps(search), `search-pcps:${search}`)) return;
      setError(err instanceof Error ? err.message : 'Failed to load doctors');
      setPcpOptions([]);
    } finally {
      setPcpLoading(false);
    }
  };

  const isConsentComplete = (data: Record<string, unknown>) => {
    if (!fieldValueEquals(data.visitType, 'Initial')) return true;
    if (!data.consentType) return false;
    if (fieldValueEquals(data.consentType, 'Paper')) return true;
    if (!fieldValueEquals(data.consentType, 'Digital')) return false;
    const hasCompletedConsentFlow = data.consentPrerequisiteCompleted === true
      || fieldValueEquals(data.consentPrerequisiteCompleted, 'Yes')
      || (Array.isArray(data.consentEmailHistory) && data.consentEmailHistory.length > 0);
    if (!hasCompletedConsentFlow) return false;
    if (selectedValues(data.contractTypes).length === 0 || !data.consentSignedBy) return false;
    if (hasPpClusterContract(data.contractTypes) && selectedValues(data.clusterPackages).length === 0) return false;
    if (fieldValueEquals(data.consentSignedBy, 'Patient')) {
      if (!data.patientSelfDirecting || !data.patientHasDementia) return false;
      if (fieldValueEquals(data.patientSelfDirecting, 'No') || fieldValueEquals(data.patientHasDementia, 'Yes')) return false;
    }
    if (fieldValueEquals(data.consentSignedBy, 'Proxy')) {
      if (!data.proxyName || !data.proxyRelationship || !data.proxyPhone) return false;
    }
    const rows = parseConsentRows(data.consent, data, consentContractName);
    if (!rows.some((row) => row.frequency === 'Initial visit') || !rows.some((row) => row.frequency === 'Recertification')) return false;
    if (rows.some((row) => !row.service || !row.frequency || !row.expectedCoverage || !row.patientResponsibility)) return false;
    return !(rows.some((row) => row.expectedCoverage === 'Waived' || row.patientResponsibility === 'Waived') && !fieldValueEquals(data.financeClearanceReceived, 'Yes'));
  };

  const validateConsentPrerequisite = (data = formData) => {
    const errors: Record<string, string> = {};
    if (!data.consentType) errors.consentType = 'Consent type is required';
    if (fieldValueEquals(data.consentType, 'Digital')) {
      const rows = parseConsentRows(data.consent, data, consentContractName);
      if (selectedValues(data.contractTypes).length === 0) errors.contractTypes = 'Contract Types is required';
      if (hasPpClusterContract(data.contractTypes) && selectedValues(data.clusterPackages).length === 0) {
        errors.clusterPackages = 'Select at least one Cluster Package';
      }
      if (!data.consentSignedBy) errors.consentSignedBy = 'This field is required';
      if (fieldValueEquals(data.consentSignedBy, 'Patient')) {
        if (fieldValueEquals(data.patientSelfDirecting, 'No') || fieldValueEquals(data.patientHasDementia, 'Yes')) {
          errors.consentSignedBy = 'Patient cannot sign. Please select Proxy.';
        }
        if (!data.patientEmail) errors.patientEmail = 'Patient email is required';
        else if (!isValidEmail(data.patientEmail)) errors.patientEmail = 'Please enter a valid email address';
      }
      if (fieldValueEquals(data.consentSignedBy, 'Proxy')) {
        if (!data.proxyName) errors.proxyName = 'Proxy name is required';
        if (!data.proxyRelationship) errors.proxyRelationship = 'Proxy relationship is required';
        if (!data.proxyPhone) errors.proxyPhone = 'Proxy phone is required';
        if (!data.proxyEmail) errors.proxyEmail = 'Proxy email is required';
        else if (!isValidEmail(data.proxyEmail)) errors.proxyEmail = 'Please enter a valid email address';
      }
      if (!rows.some((row) => row.frequency === 'Initial visit') || !rows.some((row) => row.frequency === 'Recertification')) {
        errors.consent = 'Initial visit and Recertification services are required';
      } else if (rows.some((row) => !row.service || !row.frequency || !row.fee || !row.expectedCoverage || !row.patientResponsibility)) {
        errors.consent = 'All consent rows must have service, frequency, fee, expected coverage, and patient responsibility';
      }
      if (selectedValues(data.contractTypes).includes('Contract')) {
        if (Number(data.hoursPerDay) === 24 && !data.shiftType1) errors.shiftType1 = 'Shift type is required for 24-hour shifts';
        if (Number(data.hoursPerDay2) === 24 && !data.shiftType2) errors.shiftType2 = 'Shift type is required for 24-hour shifts';
        if (Number(data.hoursPerDay3) === 24 && !data.shiftType3) errors.shiftType3 = 'Shift type is required for 24-hour shifts';
      }
      if (selectedValues(data.contractTypes).some((type) => type === 'PP' || type === 'PP (1 to 1)')) {
        if (Number(data.ppHoursPerDay) === 24 && !data.ppShiftType1) errors.ppShiftType1 = 'Shift type is required for 24-hour shifts';
        if (Number(data.ppHoursPerDay2) === 24 && !data.ppShiftType2) errors.ppShiftType2 = 'Shift type is required for 24-hour shifts';
      }
      if (rows.some((row) => row.expectedCoverage === 'Waived' || row.patientResponsibility === 'Waived') && !fieldValueEquals(data.financeClearanceReceived, 'Yes')) {
        errors.financeClearanceReceived = 'Finance clearance is required when fee is waived';
      }
    }
    return { isValid: Object.keys(errors).length === 0, errors };
  };

  const hydrateAssessmentFromPatient = (assessmentRecord: Record<string, unknown>, patient: Record<string, unknown>) => {
    const nextFormData = { ...(metadata?.defaultFormData || {}), ...assessmentRecord };
    if (!String(nextFormData.patientName || '').trim()) {
      const patientName = patientLabel(patient).trim();
      if (patientName) nextFormData.patientName = patientName;
    }
    if (!String(nextFormData.patientInitials || '').trim()) {
      const initials = patientInitialsFromSources(nextFormData, patient);
      if (initials) nextFormData.patientInitials = initials;
    }
    const assessmentNurse = nestedRecord(patient, 'assessmentNurse');
    const nurseName = patient.assessmentNurseName || assessmentNurse?.name || assessmentNurse?.Name;
    if (patient.assessmentType) nextFormData.assessmentType = patient.assessmentType;
    if (nurseName) nextFormData.assessmentNurse = nurseName;
    if (patient.assessmentVisitType) nextFormData.visitType = patient.assessmentVisitType;
    if (patient.assessmentVisitReason) nextFormData.visitReason = patient.assessmentVisitReason;
    if (patient.assessmentVisitReasonOther) nextFormData.visitReasonOther = patient.assessmentVisitReasonOther;
    if (patient.assessmentEvaluationLocation) nextFormData.evaluationLocation = patient.assessmentEvaluationLocation;
    if (patient.assessmentEvaluationAddress) nextFormData.evaluationAddress = patient.assessmentEvaluationAddress;
    if (patient.assessmentVisitDate) nextFormData.visitDate = patient.assessmentVisitDate;
    if (patient.assessmentContract) nextFormData.assessmentContract = patient.assessmentContract;
    if (patient.assessmentContract) nextFormData.contract = patient.assessmentContract;
    if (patient.assessmentContractTypes) nextFormData.contractTypes = patient.assessmentContractTypes;
    if (patient.assessmentAideHours) nextFormData.assessmentAideHours = patient.assessmentAideHours;
    if (patient.assessmentAllowVisitTypeSwitch !== undefined) nextFormData.assessmentAllowVisitTypeSwitch = patient.assessmentAllowVisitTypeSwitch;
    Object.entries(LOCKED_MULTISELECT_DEFAULTS).forEach(([fieldName, defaults]) => {
      nextFormData[fieldName] = valuesWithLockedDefaults(nextFormData[fieldName], defaults);
    });
    return nextFormData;
  };

  const applyLoadedAssessment = (assessmentRecord: Record<string, unknown>, patient: Record<string, unknown>, submitted: boolean) => {
    setSelectedPatient(patient);
    setAssessment(assessmentRecord);
    setIsSubmitted(submitted);
    setHasRespondedToRecordingConsent(false);
    setRecordingConsentOpen(false);
    setRecordingDeclineVisible(false);
    const nextFormData = hydrateAssessmentFromPatient(assessmentRecord, patient);
    setFormData(nextFormData);
    setUnableToAssessVitalsChecked(hasStoredValue(nextFormData.unableToAssessVitals));
    if (!submitted) setActiveStep(stepKeyFromStatus(nextFormData.status || nextFormData.status));
    const needsConsentPrerequisite = !submitted && fieldValueEquals(nextFormData.visitType, 'Initial') && !isConsentComplete(nextFormData);
    setShowConsentPrerequisite(needsConsentPrerequisite);
    setConsentPrerequisiteStep(1);
    setNoCaregiverPresent(!nextFormData.caregiver && fieldValueEquals(nextFormData.supervisionCompleted, 'No'));
    setActiveCaregivers([]);
    setCaregiverSearchInput(String(nextFormData.caregiverName || nextFormData.nameOfProfessional || ''));
    setDocuments([]);
    void loadDocuments(assessmentRecord.id);
    if (!needsConsentPrerequisite && !submitted) {
      setRecordingConsentOpen(true);
    }
  };

  const loadAssessment = async (patient = selectedPatient) => {
    if (!patient?.id || !metadata) return;
    localStorage.setItem(NURSING_SELECTED_PATIENT_KEY, String(patient.id));
    const patientVisitType = String(patient.assessmentVisitType || patient.assessmentVisitType || '');
    if (fieldValueEquals(patientVisitType, 'Triage Assessment') && !hasRole(user, 'nurse-admin') && !hasRole(user, 'admin')) {
      setSelectedPatient(patient);
      localStorage.removeItem(NURSING_SELECTED_PATIENT_KEY);
      setError('Triage Assessment is only available to nurse-admin users. Please contact your administrator.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await apiJson<{
        assessment?: Record<string, unknown>;
        hasCompletedAssessment?: boolean;
        completedAssessmentData?: Record<string, unknown>;
      }>('/api/nursing-assessment/get-or-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patientId: patient.id }),
      });
      if (result.hasCompletedAssessment && result.completedAssessmentData) {
        setSelectedPatient(patient);
        setCompletedAssessment(result.completedAssessmentData);
        setCompletedDialogOpen(true);
        return;
      }
      if (!result.assessment) throw new Error('Assessment was not returned');
      applyLoadedAssessment(
        result.assessment,
        patient,
        fieldValueEquals(result.assessment.status, 'Completed') || fieldValueEquals(result.assessment.status, 'Completed')
      );
    } catch (err) {
      if (!handleAuthExpired(err, () => loadAssessment(patient), `load-assessment:${patient.id || ''}`)) {
        setError(err instanceof Error ? err.message : 'Failed to load assessment');
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (restoredPatientRef.current || assessment || selectedPatient || patients.length === 0 || !metadata) return;
    const savedPatientId = localStorage.getItem(NURSING_SELECTED_PATIENT_KEY);
    if (!savedPatientId) return;
    const savedPatient = patients.find((patient) => String(patient.id) === savedPatientId);
    if (!savedPatient) {
      localStorage.removeItem(NURSING_SELECTED_PATIENT_KEY);
      restoredPatientRef.current = true;
      return;
    }
    restoredPatientRef.current = true;
    setSelectedPatient(savedPatient);
    void loadAssessment(savedPatient);
  }, [assessment, loadAssessment, metadata, patients, selectedPatient]);

  const clearTicketStateForIdentitySwitch = () => {
    ticketIdentityEpochRef.current += 1;
    pendingTicketAttemptsRef.current = {};
    completedTicketResultsRef.current = {};
    pendingAuthRetriesRef.current = pendingAuthRetriesRef.current.filter(
      ({ key }) => !key.startsWith('submit-assessment-ticket:')
    );
    ticketSubmittingRef.current = false;
    setTicketDialogOpen(false);
    setTicketDescription('');
    setTicketSubmitting(false);
    setTicketError('');
    setTicketResult(null);
    setTicketPortalRequestId(null);
    setTicketRequestAssessmentId(null);
  };

  const loginAsNurse = async (nurse: NurseUserRecord) => {
    setImpersonatingNurseId(String(nurse.id));
    setError(null);
    try {
      const response = await authFetch('/api/nursing-assessment/nurse-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nexusUserId: nurse.id }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to login as nurse');
      const currentAccessToken = getAccessToken();
      const currentRefreshToken = getRefreshToken();
      if ((hasRole(user, 'nurse-admin') || hasRole(user, 'admin')) && currentAccessToken && currentRefreshToken && user) {
        const backup = {
          accessToken: currentAccessToken,
          refreshToken: currentRefreshToken,
          user,
          nurseName: nurseUserLabel(nurse),
        };
        localStorage.setItem(NURSE_ADMIN_SESSION_KEY, JSON.stringify(backup));
        setNurseAdminBackup(backup);
      }
      storeAuthSession(data.accessToken, data.refreshToken, data.user);
      clearTicketStateForIdentitySwitch();
      localStorage.removeItem(NURSING_SELECTED_PATIENT_KEY);
      restoredPatientRef.current = false;
      setUser(data.user);
      setSelectedPatient(null);
      setAssessment(null);
      setFormData(metadata?.defaultFormData || {});
      setUnableToAssessVitalsChecked(false);
      setDocuments([]);
      setIsSubmitted(false);
      setShowConsentPrerequisite(false);
      setSetupTab(0);
    } catch (err) {
      if (handleAuthExpired(err, () => loginAsNurse(nurse), `login-nurse:${nurse.id}`)) return;
      setError(err instanceof Error ? err.message : 'Failed to login as nurse');
    } finally {
      setImpersonatingNurseId(null);
    }
  };

  const returnToAdminSession = () => {
    if (!nurseAdminBackup) return;
    storeAuthSession(nurseAdminBackup.accessToken, nurseAdminBackup.refreshToken, nurseAdminBackup.user);
    clearTicketStateForIdentitySwitch();
    localStorage.removeItem(NURSE_ADMIN_SESSION_KEY);
    localStorage.removeItem(NURSING_SELECTED_PATIENT_KEY);
    restoredPatientRef.current = false;
    setNurseAdminBackup(null);
    setUser(nurseAdminBackup.user);
    setSelectedPatient(null);
    setAssessment(null);
    setFormData(metadata?.defaultFormData || {});
    setUnableToAssessVitalsChecked(false);
    setDocuments([]);
    setIsSubmitted(false);
    setShowConsentPrerequisite(false);
    setSetupTab(0);
  };

  const reviewCompletedAssessment = () => {
    if (!completedAssessment || !selectedPatient) return;
    applyLoadedAssessment(completedAssessment, selectedPatient, true);
    setCompletedDialogOpen(false);
    setCompletedAssessment(null);
  };

  const cancelCompletedAssessmentDialog = () => {
    setCompletedDialogOpen(false);
    setCompletedAssessment(null);
    setSelectedPatient(null);
    localStorage.removeItem(NURSING_SELECTED_PATIENT_KEY);
    restoredPatientRef.current = false;
  };

  const createNewAfterCompleted = async () => {
    if (!selectedPatient?.id || !metadata) return;
    setSaving(true);
    setError(null);
    try {
      const result = await apiJson<{ assessment: Record<string, unknown> }>('/api/nursing-assessment/get-or-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patientId: selectedPatient.id,
          forceNew: true,
          visitType: selectedPatient.assessmentVisitType || completedAssessment?.visitType || '',
          contract: selectedPatient.assessmentContract || completedAssessment?.assessmentContract || completedAssessment?.contract || '',
          completedAssessmentData: completedAssessment,
        }),
      });
      applyLoadedAssessment(result.assessment, selectedPatient, false);
      setCompletedDialogOpen(false);
      setCompletedAssessment(null);
    } catch (err) {
      if (!handleAuthExpired(err, () => createNewAfterCompleted(), 'create-new-after-completed')) {
        setError(err instanceof Error ? err.message : 'Failed to create new assessment');
      }
    } finally {
      setSaving(false);
    }
  };

  const validateStep = (stepKey: string, data = formData, includeReviewConfirmation = false, skipRequiredFields = false) => {
    const errors: Record<string, string> = {};
    const step = activeStepNumber(stepKey);
    const validationData = dataWithUiValidationState(data);
    const requiredFields = requiredFieldsForStep(step, validationData)
      .filter((fieldName) => !isFieldLockedForRevisit(fieldName, data));
    if (!skipRequiredFields) {
      for (const fieldName of requiredFields) {
        const field = metadata?.fields[fieldName];
        if (fieldName === 'pcpSelectedAddress' && parsePcpOffice(data.pcpOfficeSelection)) continue;
        if (isEmptyValue(data[fieldName], field)) errors[fieldName] = 'This field is required';
      }
    }

    if (step === 8 && isRevisitReason(data, 'new injury', 'wound') && !fieldValueIncludes(data.skinProblems, 'Wound')) {
      errors.skinProblems = 'Wound must be selected for this revisit reason.';
    }

    const shouldValidatePocTasks = !fieldValueEquals(data.visitType, 'Aide Supervision/Evaluation')
      && !(fieldValueEquals(data.visitType, 'Revisit') && !fieldValueEquals(data.isPocUpdateNeeded, 'Yes'));
    if (!skipRequiredFields && step === 10 && shouldValidatePocTasks) {
      if (data.pocStartDate) {
        const certStart = normalizeIsoDate(selectedPatient?.certificationPeriodStart || data.certificationPeriodStart);
        const certEnd = normalizeIsoDate(selectedPatient?.certificationPeriodEnd || data.certificationPeriodEnd);
        const pocStart = normalizeIsoDate(data.pocStartDate);
        if (pocStart) {
          if (certStart && pocStart < certStart) {
            errors.pocStartDate = 'POC Start Date cannot be before Certification Period Start';
          }
          if (certEnd && pocStart >= certEnd) {
            errors.pocStartDate = 'POC Start Date must be before Certification Period End';
          }
        }
      }

      const scheduleRows = [
        ['daysPerWeek', 'hoursPerDay'],
        ['daysPerWeek2', 'hoursPerDay2'],
        ['daysPerWeek3', 'hoursPerDay3'],
      ] as const;
      const hasSchedulePair = scheduleRows.some(([daysField, hoursField]) => (
        positiveNumber(data[daysField]) > 0 && positiveNumber(data[hoursField]) > 0
      ));
      if (!hasSchedulePair && !data.auth_fields_from_auth) {
        errors.daysPerWeek = 'At least one set of Days per Week and Hours per Day is required';
        errors.hoursPerDay = 'At least one set of Days per Week and Hours per Day is required';
      }
      scheduleRows.slice(1).forEach(([daysField, hoursField], index) => {
        const labelNumber = index + 2;
        const days = positiveNumber(data[daysField]);
        const hours = positiveNumber(data[hoursField]);
        if (days > 0 && hours <= 0 && !data.auth_fields_from_auth) {
          errors[hoursField] = `Hours per Day ${labelNumber} is required when Days per Week ${labelNumber} is specified`;
        }
        if (hours > 0 && days <= 0) {
          errors[daysField] = `Days per Week ${labelNumber} is required when Hours per Day ${labelNumber} is specified`;
        }
      });

      if (!isRnOnlyClusterAssessment(data)) {
        const tasks = parsePlanOfCareTasks(data.planOfCareTasks);
        if (tasks.length < 5) {
          errors.planOfCareTasks = `Plan of Care requires at least 5 tasks. Currently ${tasks.length} task${tasks.length === 1 ? '' : 's'} selected.`;
        } else {
          const taskValidationMessage = planOfCareTaskValidationMessage(tasks);
          if (taskValidationMessage) errors.planOfCareTasks = taskValidationMessage;
        }
      }
    }

    // The MD Order prints both diagnoses, so they must be documented. Revisits with the diagnosis
    // card locked can't edit them, and revisits don't produce MD Orders.
    if (step === 2 && !skipRequiredFields) {
      const primaryRows = parseDiagnosisRows(data.primaryDx, true);
      if (
        !isFieldLockedForRevisit('primaryDx', data)
        && (primaryRows.length === 0 || !primaryRows[0].icdCode || !primaryRows[0].description || !primaryRows[0].date)
      ) {
        errors.primaryDx = 'Primary diagnosis requires ICD code, description, and date.';
      }
    }

    if (step === 2 && !isFieldLockedForRevisit('otherPertinentDiagnosis', data)) {
      const message = otherDiagnosisValidationMessage(parseDiagnosisRows(data.otherPertinentDiagnosis), !skipRequiredFields);
      if (message) errors.otherPertinentDiagnosis = message;
    }

    if (step === 2) {
      if (!isCheckedValue(data.notTakingMedications)) {
        const meds = parseMedicationRows(data.medications);
        const medsGateUnlocked = !fieldValueEquals(data.visitType, 'Revisit') || fieldValueEquals(data.anyNewOrDiscontinuedMedication, 'Yes');
        if (medsGateUnlocked) {
          const invalidMedication = meds.find((row) => {
            if (row.status === 'Discontinued') return !row.dcdDate;
            if (row.status !== 'New') return false;
            const hasAnyData = [row.orderDate, row.medication, row.dose, row.route, row.frequency, row.purposeDx, row.status]
              .some((value) => !isEmptyValue(value));
            return hasAnyData && (
              !row.orderDate || !row.medication || !row.dose || !row.route || !row.frequency || !row.purposeDx || !row.status
            );
          });
          if (invalidMedication) errors.medications = 'One or more medications are missing required fields.';
        }
      } else {
        const meds = parseMedicationRows(data.medications);
        const medsGateUnlocked = !fieldValueEquals(data.visitType, 'Revisit') || fieldValueEquals(data.anyNewOrDiscontinuedMedication, 'Yes');
        if (medsGateUnlocked && meds.some((row) => row.status !== 'Discontinued')) {
          errors.medications = 'All medications must be discontinued if "Patient is not taking medications" is checked.';
        }
      }
    }

    if (step === 7 && fieldValueIncludes(data.guSymptoms, 'Dialysis') && selectedValues(data.dialysisDays).length > 3) {
      errors.dialysisDays = 'Select no more than 3 days';
    }

    if (!skipRequiredFields && fieldValueEquals(data.visitType, 'Revisit')) {
      const gateFields = Object.entries(REVISIT_CARD_GATES)
        .filter(([, config]) => config.step === step)
        .map(([gateField]) => gateField);
      for (const gateField of gateFields) {
        if (isEmptyValue(data[gateField])) errors[gateField] = 'This field is required';
      }
    }

    if (includeReviewConfirmation && fieldValueEquals(data.visitType, 'Triage Assessment') && step <= 12 && !isStepReviewed(data, step)) {
      errors[`step_${step}_reviewed`] = 'Please confirm you have reviewed this page';
    }

    for (const fieldName of ['emergencyContactEmail', 'emergencyContact2Email']) {
      if (data[fieldName] && !isValidEmail(data[fieldName])) errors[fieldName] = 'Please enter a valid email address';
    }

    for (const fieldName of ['phone', 'mobile', 'emergencyContactPhone', 'emergencyContactPhone2', 'emergencyContact2Phone', 'emergencyContact2Phone2', 'pharmacyPhone', 'proxyPhone']) {
      const value = data[fieldName];
      if (value && String(value).replace(/\D/g, '').length !== 10) {
        errors[fieldName] = 'Phone number must be 10 digits.';
      }
    }

    // Only this step's dates, so a bad date elsewhere cannot block reaching it.
    const stepFieldNames = new Set([
      ...Object.values(metadata?.steps[stepKey]?.cards ?? {}).flatMap((card) => card.fields),
      ...requiredFields,
    ]);
    for (const fieldName of stepFieldNames) {
      const value = data[fieldName];
      if (metadata?.fields[fieldName]?.dataType !== 'DATE' || isEmptyValue(value)) continue;
      if (!isFieldVisible(fieldName, data) || isFieldLockedForRevisit(fieldName, data)) continue;
      if (!isValidDateInput(value)) errors[fieldName] = 'Enter a valid date with a 4-digit year.';
    }

    for (const [fieldName, message] of [
      ['visitDate', 'Visit date cannot be in the future.'],
      ['lastBmDate', 'Date of last BM cannot be in the future.'],
    ] as const) {
      if (!data[fieldName]) continue;
      const parsed = new Date(String(data[fieldName]));
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (!Number.isNaN(parsed.getTime()) && parsed > today) errors[fieldName] = message;
    }

    const characterLimitExceptions = new Set([
      'medications',
      'planOfCareTasks',
      'Signature',
      'ConsentSignature',
      'otherPertinentDiagnosis',
      'primaryDx',
      'consent',
      'allergies',
      'nursingProgressNote',
      'handOffNote',
      'notTakenUnderCareSafetyConcern',
      'assessmentNote',
    ]);
    const customCharacterLimits: Record<string, number> = { comments: 2000 };
    for (const [fieldName, rawValue] of Object.entries(data)) {
      // The office bundle is canonical Nexus data, not a PDF text answer.
      if (fieldName === 'pcpOfficeSelection') continue;
      if (rawValue === null || rawValue === undefined || rawValue === '' || errors[fieldName]) continue;
      if (typeof rawValue !== 'string') continue;
      const fieldLabel = metadata ? getFieldLabel(fieldName, metadata.fields) : fieldName.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
      if (!validateWinAnsiString(sanitizeWinAnsiString(rawValue))) {
        errors[fieldName] = `${fieldLabel} contains unsupported characters. Only standard characters are allowed.`;
        continue;
      }
      const field = metadata?.fields[fieldName];
      const isMultiSelect = field?.dataType === 'MULTIPICKLIST';
      if (!isMultiSelect && !characterLimitExceptions.has(fieldName)) {
        const characterLimit = customCharacterLimits[fieldName] || 255;
        if (rawValue.length > characterLimit) {
          errors[fieldName] = `${fieldLabel} cannot exceed ${characterLimit} characters.`;
        }
      }
    }

    return { isValid: Object.keys(errors).length === 0, errors };
  };

  const validateSpecificOnly = (stepKey: string, data = formData) => validateStep(stepKey, data, false, true);

  // The step that renders a field, so a rejected save can send the nurse to it.
  const stepKeyForField = (fieldName: string) => {
    const validationData = dataWithUiValidationState(formData);
    return visibleSteps.find(([key, step]) => (
      Object.values(step.cards).some((card) => card.fields.includes(fieldName))
      || requiredFieldsForStep(activeStepNumber(key), validationData).includes(fieldName)
    ))?.[0];
  };

  const logValidationErrors = (
    context: string,
    stepKey: string,
    validation: { isValid: boolean; errors: Record<string, string> },
  ) => {
    if (validation.isValid || Object.keys(validation.errors).length === 0) return;
    const rows = Object.entries(validation.errors).map(([fieldName, message]) => ({
      step: activeStepNumber(stepKey),
      fieldName,
      label: metadata?.fields[fieldName] ? getFieldLabel(fieldName, metadata.fields) : fieldName,
      message,
    }));
    console.groupCollapsed(`[NursingAssessment validation] ${context}: ${statusForStep(stepKey)} (${stepKey})`);
    console.table(rows);
    console.groupEnd();
  };

  const stepStatusesFromData = (data = formData): Record<string, string> => (
    typeof data.stepStatuses === 'object' && data.stepStatuses !== null && !Array.isArray(data.stepStatuses)
      ? data.stepStatuses as Record<string, string>
      : {}
  );

  const updatedStepStatusesForStep = (stepKey: string, data = formData) => {
    const stepNumber = activeStepNumber(stepKey);
    const currentStatuses = stepStatusesFromData(data);
    const nextStatuses = { ...currentStatuses };
    const validation = validateStep(stepKey, data, false);

    if (validation.isValid) {
      nextStatuses[stepNumber] = 'completed';
    } else {
      nextStatuses[stepNumber] = 'error';
    }

    return nextStatuses;
  };

  const stepStatusesForAllVisibleSteps = (data = formData) => {
    const nextStatuses = { ...stepStatusesFromData(data) };
    for (const [stepKey] of visibleSteps) {
      const stepNumber = activeStepNumber(stepKey);
      const validation = validateStep(stepKey, data, true);
      if (validation.isValid) {
        nextStatuses[stepNumber] = 'completed';
      } else {
        nextStatuses[stepNumber] = 'error';
      }
    }
    return nextStatuses;
  };

  const validateAllSteps = () => {
    const combinedErrors: Record<string, string> = {};
    let firstInvalidStep = activeStep;
    for (const [stepKey] of visibleSteps) {
      const result = validateStep(stepKey, formData, true);
      if (!result.isValid && Object.keys(combinedErrors).length === 0) firstInvalidStep = stepKey;
      Object.assign(combinedErrors, result.errors);
    }
    return { isValid: Object.keys(combinedErrors).length === 0, errors: combinedErrors, firstInvalidStep };
  };

  const derivedHandOffPayload = (data = formData) => {
    const buckets = deriveHandOffBuckets(data);
    return {
      handOffNeedsReferralFor: buckets.needsReferralFor.join(';'),
      handOffSafetyConcerns: buckets.safetyConcerns.join(';'),
      handOffFollowUpNeeded: buckets.needsFollowUp.join(';'),
      specialRequestUsedForScheduling: buckets.specialRequestUsedForScheduling.join(';'),
      specialRequestOther: buckets.specialRequestOther.join(';'),
      specialRequestNotUsedForScheduling: buckets.specialRequestNotUsedForScheduling.join(';'),
    };
  };

  const handleStepChange = async (_event: unknown, nextStep: string) => {
    if (!assessment) {
      setActiveStep(nextStep);
      return;
    }

    const currentIndex = visibleSteps.findIndex(([key]) => key === activeStep);
    const nextIndex = visibleSteps.findIndex(([key]) => key === nextStep);
    if (nextIndex === currentIndex) return;
    // A failed save must not trap the nurse away from the field that needs
    // fixing. Navigation keeps all unsaved answers in formData.
    if (nextStep === saveErrorStep) {
      setActiveStep(nextStep);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setError(null);
    try {
      const saved = await saveAssessment(statusForStep(nextStep));
      if (saved) {
        setActiveStep(nextStep);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      if (!handleAuthExpired(err, () => handleStepChange(_event, nextStep), `step-change:${nextStep}`)) {
        setError(err instanceof Error ? err.message : 'Failed to save assessment');
      }
    }
  };

  const goToPreviousStep = async () => {
    const currentIndex = visibleSteps.findIndex(([key]) => key === activeStep);
    if (currentIndex <= 0) return;
    const previousStep = visibleSteps[currentIndex - 1][0];
    if (previousStep === saveErrorStep) {
      await handleStepChange(null, previousStep);
      return;
    }
    const fullValidation = validateStep(activeStep);
    const specificValidation = validateSpecificOnly(activeStep);
    if (!specificValidation.isValid) logValidationErrors('previous step navigation', activeStep, fullValidation);
    setFieldErrors((current) => ({ ...current, ...fullValidation.errors }));
    if (!specificValidation.isValid) {
      setError('Please fix validation errors before continuing.');
      return;
    }
    setError(null);
    try {
      const saved = await saveAssessment(statusForStep(previousStep));
      if (saved) {
        setActiveStep(previousStep);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      if (!handleAuthExpired(err, () => goToPreviousStep(), `previous-step:${previousStep}`)) {
        setError(err instanceof Error ? err.message : 'Failed to save assessment');
      }
    }
  };

  const goToNextStep = async () => {
    const currentIndex = visibleSteps.findIndex(([key]) => key === activeStep);
    if (currentIndex < 0) return;
    const isLastStep = currentIndex === visibleSteps.length - 1;
    if (isLastStep) {
      try {
        await saveAssessment('Completed');
      } catch (err) {
        if (!handleAuthExpired(err, () => goToNextStep(), 'complete-assessment')) {
          setError(err instanceof Error ? err.message : 'Failed to save assessment');
        }
      }
      return;
    }

    const fullValidation = validateStep(activeStep, formData, true);
    const blockingValidation = validateStep(activeStep, formData, true, true);
    if (!blockingValidation.isValid) logValidationErrors('next step navigation', activeStep, fullValidation);
    setFieldErrors((current) => ({ ...current, ...fullValidation.errors }));
    if (!blockingValidation.isValid) {
      setError('Please fix validation errors before continuing.');
      return;
    }

    setError(null);
    const nextStep = visibleSteps[currentIndex + 1][0];
    try {
      const saved = await saveAssessment(statusForStep(nextStep));
      if (saved) {
        setActiveStep(nextStep);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      if (!handleAuthExpired(err, () => goToNextStep(), `next-step:${nextStep}`)) {
        setError(err instanceof Error ? err.message : 'Failed to save assessment');
      }
    }
  };

  const saveAssessment = async (nextStatus?: string) => {
    if (!assessment?.id) return false;
    if (nextStatus === 'Completed') {
      const validation = validateAllSteps();
      setFieldErrors(validation.errors);
      if (!validation.isValid) {
        logValidationErrors('final submit', validation.firstInvalidStep, validation);
        setFormData((current) => ({
          ...current,
          stepStatuses: stepStatusesForAllVisibleSteps(current),
        }));
        setActiveStep(validation.firstInvalidStep);
        setError('Please complete all required fields before submitting.');
        return false;
      }
    } else {
      const fullValidation = validateStep(activeStep);
      const specificValidation = validateSpecificOnly(activeStep);
      logValidationErrors(nextStatus ? 'step navigation save' : 'manual save', activeStep, fullValidation);
      if (!specificValidation.isValid) {
        setFieldErrors((current) => ({ ...current, ...fullValidation.errors }));
        setError('Please fix validation errors before saving.');
        return false;
      }
      if (!nextStatus) {
        setFieldErrors((current) => ({ ...current, ...fullValidation.errors }));
      }
    }

    setSaving(true);
    setError(null);
    try {
      const stepStatuses = nextStatus === 'Completed'
        ? stepStatusesForAllVisibleSteps(formData)
        : updatedStepStatusesForStep(activeStep, formData);
      const payload = nextStatus === 'Completed'
        ? { ...formData, ...derivedHandOffPayload(), status: nextStatus, submissionDate: new Date().toISOString().slice(0, 10), stepStatuses }
        : nextStatus
          ? { ...formData, ...derivedHandOffPayload(), status: nextStatus, stepStatuses }
          : { ...formData, ...derivedHandOffPayload(), stepStatuses };
      const sanitizedPayload = sanitizeAssessmentPayloadStrings(payload) as Record<string, unknown>;
      const result = await apiJson<{ assessment: Record<string, unknown> }>('/api/nursing-assessment/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assessmentId: assessment.id, formData: sanitizedPayload }),
      });
      setAssessment(result.assessment);
      setSaveErrorStep(null);
      const staleServerErrorKeys = serverFieldErrorKeysRef.current;
      serverFieldErrorKeysRef.current = new Set();
      setFieldErrors(current => {
        const next = { ...current };
        staleServerErrorKeys.forEach((fieldName) => delete next[fieldName]);
        const officeError = validateStep('step1').errors.pcpSelectedAddress;
        if (officeError) next.pcpSelectedAddress = officeError;
        else delete next.pcpSelectedAddress;
        return next;
      });
      setFormData((current) => ({ ...current, ...sanitizedPayload, ...result.assessment }));
      if (nextStatus === 'Completed') {
        setIsSubmitted(true);
      }
      return true;
    } catch (err) {
      if (err instanceof AuthExpiredError) throw err;
      const errorStep = err instanceof ApiRequestError
        ? err.step ?? Object.keys(err.fieldErrors).map(stepKeyForField).find(Boolean)
        : undefined;
      if (errorStep && visibleSteps.some(([key]) => key === errorStep)) {
        setSaveErrorStep(errorStep);
      }
      if (err instanceof ApiRequestError) {
        Object.keys(err.fieldErrors).forEach((fieldName) => serverFieldErrorKeysRef.current.add(fieldName));
        setFieldErrors(current => ({ ...current, ...err.fieldErrors }));
      }
      setError(err instanceof Error ? err.message : 'Failed to save assessment');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleManualSave = async () => {
    try {
      await saveAssessment();
    } catch (err) {
      if (!handleAuthExpired(err, () => handleManualSave(), 'manual-save')) {
        setError(err instanceof Error ? err.message : 'Failed to save assessment');
      }
    }
  };

  const saveConsentPrerequisiteDraft = async (extraData: Record<string, unknown> = {}) => {
    if (!assessment?.id) return false;

    setSaving(true);
    setError(null);
    try {
      const sanitizedConsentPayload = sanitizeAssessmentPayloadStrings({
        ...formData,
        ...extraData,
      }) as Record<string, unknown>;
      const result = await apiJson<{ assessment: Record<string, unknown> }>('/api/nursing-assessment/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assessmentId: assessment.id, formData: sanitizedConsentPayload }),
      });
      setAssessment(result.assessment);
      setFormData((current) => ({ ...current, ...sanitizedConsentPayload, ...result.assessment }));
      return true;
    } catch (err) {
      if (handleAuthExpired(err, () => saveConsentPrerequisiteDraft(extraData), 'save-consent-prerequisite')) return false;
      setError(err instanceof Error ? err.message : 'Failed to save consent');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const goToConsentSignatureStep = async () => {
    const validation = validateConsentPrerequisite();
    setFieldErrors(validation.errors);
    if (!validation.isValid) {
      setError('Please complete consent before continuing.');
      return;
    }
    if (fieldValueEquals(formData.consentType, 'Paper')) {
      void continueFromConsentPrerequisite();
      return;
    }
    const saved = await saveConsentPrerequisiteDraft();
    if (saved) {
      setError(null);
      setConsentPrerequisiteStep(2);
    }
  };

  const goToConsentDetailsStep = async () => {
    const saved = await saveConsentPrerequisiteDraft();
    if (saved) setConsentPrerequisiteStep(1);
  };

  const consentRecipientEmail = (data = formData) => {
    if (fieldValueEquals(data.consentSignedBy, 'Proxy')) return String(data.proxyEmail || '').trim();
    if (fieldValueEquals(data.consentSignedBy, 'Patient')) return String(data.patientEmail || '').trim();
    return '';
  };

  const continueFromConsentPrerequisite = async () => {
    if (!assessment?.id) return;
    const validation = validateConsentPrerequisite();
    setFieldErrors(validation.errors);
    if (!validation.isValid) {
      setError('Please complete consent before continuing.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const sanitizedConsentPayload = sanitizeAssessmentPayloadStrings(formData) as Record<string, unknown>;
      const result = await apiJson<{ assessment: Record<string, unknown> }>('/api/nursing-assessment/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assessmentId: assessment.id, formData: sanitizedConsentPayload }),
      });
      setAssessment(result.assessment);
      setFormData((current) => ({ ...current, ...sanitizedConsentPayload, ...result.assessment }));
      if (fieldValueEquals(formData.consentType, 'Digital')) {
        await apiJson<{ recipientEmail?: string }>('/api/nursing-assessment/email-consent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assessmentId: assessment.id, email: consentRecipientEmail() }),
        });
      }
      if (fieldValueEquals(formData.consentType, 'Paper')) {
        await apiJson<{ assessment: Record<string, unknown> }>('/api/nursing-assessment/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            assessmentId: assessment.id,
            formData: sanitizeAssessmentPayloadStrings({ ...formData, consentPrerequisiteCompleted: true }),
          }),
        });
      }
      setShowConsentPrerequisite(false);
      setRecordingConsentOpen(true);
      setFormData((current) => ({ ...current, consentPrerequisiteCompleted: true }));
      await loadDocuments(assessment.id);
    } catch (err) {
      if (!handleAuthExpired(err, () => continueFromConsentPrerequisite(), 'continue-consent-prerequisite')) {
        setError(err instanceof Error ? err.message : 'Failed to save consent');
      }
    } finally {
      setSaving(false);
    }
  };

  const exitConsentPrerequisite = () => {
    localStorage.removeItem(NURSING_SELECTED_PATIENT_KEY);
    restoredPatientRef.current = false;
    setShowConsentPrerequisite(false);
    setConsentPrerequisiteStep(1);
    setAssessment(null);
    setSelectedPatient(null);
    setFormData({});
    setUnableToAssessVitalsChecked(false);
    setDocuments([]);
    setRecordingConsentOpen(false);
    setFieldErrors({});
    setError(null);
  };

  const resetAssessmentToSetup = () => {
    localStorage.removeItem(NURSING_SELECTED_PATIENT_KEY);
    restoredPatientRef.current = false;
    setConsentPrerequisiteStep(1);
    setAssessment(null);
    setSelectedPatient(null);
    setFormData({});
    setUnableToAssessVitalsChecked(false);
    setDocuments([]);
    setIsSubmitted(false);
    setFieldErrors({});
    setError(null);
    setRecordingConsentOpen(false);
  };

  const handleExitWithoutSaving = () => {
    setShowExitDialog(false);
    stopRecording();
    setHasRespondedToRecordingConsent(false);
    resetAssessmentToSetup();
  };

  const handleSaveAndExit = async () => {
    setShowExitDialog(false);
    stopRecording();
    setHasRespondedToRecordingConsent(false);
    try {
      const saved = await saveAssessment();
      if (saved) resetAssessmentToSetup();
    } catch (err) {
      if (!handleAuthExpired(err, () => handleSaveAndExit(), 'save-and-exit')) {
        setError(err instanceof Error ? err.message : 'Failed to save assessment');
      }
    }
  };

  const setField = (fieldName: string, value: unknown) => {
    setFormData((current) => {
      let processedValue: unknown = value;
      const next = { ...current };

      if (typeof processedValue === 'string') {
        let textValue = sanitizeWinAnsiString(processedValue);
        if (fieldName === 'bloodPressure') {
          const cleanValue = textValue.replace(/[^\d/]/g, '');
          if (!cleanValue.includes('/') && cleanValue.length >= 4) {
            const threeDigitSystolic = Number(cleanValue.slice(0, 3));
            const splitAt = threeDigitSystolic >= 100 && threeDigitSystolic <= 250 ? 3 : 2;
            textValue = `${cleanValue.slice(0, splitAt)}/${cleanValue.slice(splitAt)}`;
          } else {
            textValue = cleanValue;
          }
          if (textValue.length > 7) textValue = textValue.substring(0, 7);
        } else if ([
          'hoursPerDay',
          'hoursPerDay2',
          'hoursPerDay3',
          'ppHoursPerDay',
          'ppHoursPerDay2',
        ].includes(fieldName)) {
          textValue = textValue.replace(/^-/, '');
          const parsedHours = Number.parseFloat(textValue);
          if (!Number.isNaN(parsedHours) && parsedHours > 24) textValue = '24';
        } else if ([
          'pulseRate',
          'respirationRate',
        ].includes(fieldName)) {
          textValue = textValue.replace(/\D/g, '').slice(0, 3);
        } else if (fieldName === 'heightFt') {
          const digits = textValue.replace(/\D/g, '').slice(0, 1);
          textValue = digits === '' ? '' : String(Math.min(7, Math.max(0, Number.parseInt(digits, 10) || 0)));
        } else if (fieldName === 'heightIn') {
          const digits = textValue.replace(/\D/g, '').slice(0, 2);
          textValue = digits === '' ? '' : String(Math.min(11, Math.max(0, Number.parseInt(digits, 10) || 0)));
        } else if (fieldName === 'pulseOxygen') {
          const digits = textValue.replace(/\D/g, '').slice(0, 3);
          textValue = digits === '' ? '' : String(Math.min(100, Math.max(0, Number.parseInt(digits, 10) || 0)));
        } else if (fieldName === 'weight') {
          textValue = textValue.replace(/\D/g, '').slice(0, 3);
        } else if (fieldName === 'temp') {
          const raw = textValue;
          const previous = String(current.temp || '');
          const isDeleting = raw.length < previous.length;
          let cleaned = raw.replace(/[^0-9.]/g, '');
          const parts = cleaned.split('.');
          if (parts.length > 2) cleaned = `${parts[0]}.${parts.slice(1).join('').replace(/\./g, '')}`;
          if (isDeleting) {
            textValue = cleaned;
          } else {
            const dotIndex = cleaned.indexOf('.');
            if (dotIndex >= 0) {
              const rawInt = cleaned.slice(0, dotIndex).replace(/\D/g, '');
              const intDigits = rawInt.startsWith('10') ? rawInt.slice(0, 3) : rawInt.slice(0, 2);
              const decDigits = cleaned.slice(dotIndex + 1).replace(/\D/g, '').slice(0, 1);
              textValue = decDigits.length > 0 ? `${intDigits}.${decDigits}` : `${intDigits}.`;
            } else {
              const digitsOnly = cleaned.replace(/\./g, '');
              if (digitsOnly.startsWith('10')) {
                const upTo4 = digitsOnly.slice(0, 4);
                textValue = upTo4.length <= 3 ? upTo4 : `${upTo4.slice(0, 3)}.${upTo4.slice(3, 4)}`;
              } else {
                const upTo3 = digitsOnly.slice(0, 3);
                textValue = upTo3.length <= 2 ? (upTo3.length === 2 ? `${upTo3}.` : upTo3) : `${upTo3.slice(0, 2)}.${upTo3.slice(2, 3)}`;
              }
            }
          }
        } else if ([
          'heightFt',
          'heightIn',
        ].includes(fieldName)) {
          textValue = textValue.replace(/^-/, '');
        } else if (['pain1Intensity', 'pain2Intensity', 'chestPainSeverity'].includes(fieldName)) {
          const digits = textValue.replace(/\D/g, '');
          if (digits === '') {
            textValue = '';
          } else {
            const parsed = Number.parseInt(digits, 10);
            textValue = String(Math.min(10, Math.max(1, parsed)));
          }
        }
        processedValue = textValue;
      }

      next[fieldName] = processedValue;

      if (fieldName === 'dateOfBirth') {
        next.age = ageFromDateOfBirth(processedValue);
      }

      if (fieldName === 'hasNoEmergencyContact' && isCheckedValue(processedValue)) {
        clearDependentFields(next, noEmergencyContactFields);
      }

      if (fieldName === 'notTakingMedications' && isCheckedValue(processedValue)) {
        clearDependentFields(next, notTakingMedicationsHiddenFields);
      }

      if (fieldName === 'cardioPulmonarySymptoms') {
        next.chestPain = fieldValueIncludes(processedValue, 'Chest Pain') ? 'Yes' : 'No';
        next.oxygenRequired = fieldValueIncludes(processedValue, 'Oxygen') ? 'Yes' : 'No';
      }

      if (fieldName === 'oxygenFrequency' && fieldValueIncludes(processedValue, 'PRN for SOB') && !next.oxygenPrnPercentage) {
        next.oxygenPrnPercentage = '90';
      }

      if (fieldName === 'oxygenPrnPercentage' && typeof processedValue === 'string') {
        let cleaned = processedValue.replace(/[^0-9.]/g, '');
        const firstDot = cleaned.indexOf('.');
        if (firstDot !== -1) {
          const integerPart = cleaned.slice(0, firstDot);
          const decimalPart = cleaned.slice(firstDot + 1).replace(/\./g, '').slice(0, 1);
          cleaned = `${integerPart}.${decimalPart}`;
        }
        const numeric = Number(cleaned);
        next.oxygenPrnPercentage = cleaned !== '' && Number.isFinite(numeric)
          ? String(Math.round(Math.min(100, Math.max(1, numeric)) * 10) / 10)
          : '';
      }

      if (fieldName === 'communication') {
        const choices = selectedValues(processedValue);
        if (choices.includes('Able to communicate')) {
          next.communication = ['Able to communicate'];
        } else if (choices.includes('Unable to communicate')) {
          next.communication = ['Unable to communicate'];
        } else {
          next.communication = choices.filter((choice) => choice !== 'Able to communicate' && choice !== 'Unable to communicate');
        }
      }

      if (fieldName === 'manageMedicationAssistedBy') {
        next.medPrefillReferralRequired = fieldValueIncludes(processedValue, 'Require referral for medication prefills') ? 'Yes' : 'No';
      }

      const field = metadata?.fields[fieldName];
      if (field?.dataType === 'MULTIPICKLIST') {
        const choices = selectedValues(processedValue);
        const previous = selectedValues(current[fieldName]);
        let exclusiveTokens = ['WNL', 'Asymptomatic', 'None', 'No Device'];
        if (fieldName === 'orientationCognition') exclusiveTokens = [...exclusiveTokens, 'Disoriented'];
        const hasExclusive = choices.some((choice) => exclusiveTokens.includes(choice));
        const hadExclusive = previous.some((choice) => exclusiveTokens.includes(choice));
        const otherChoices = choices.filter((choice) => !exclusiveTokens.includes(choice));
        const previousOtherChoices = previous.filter((choice) => !exclusiveTokens.includes(choice));
        if (hasExclusive && !hadExclusive && otherChoices.length > 0) {
          const exclusiveChoices = choices.filter((choice) => exclusiveTokens.includes(choice));
          next[fieldName] = [exclusiveChoices[exclusiveChoices.length - 1]];
        } else if (hasExclusive && otherChoices.length > previousOtherChoices.length) {
          next[fieldName] = otherChoices;
        }
        if (fieldName === 'prescribedDiet') {
          next[fieldName] = selectedValues(next[fieldName]).slice(0, 2);
        }
        if (LOCKED_MULTISELECT_DEFAULTS[fieldName]) {
          next[fieldName] = valuesWithLockedDefaults(next[fieldName], LOCKED_MULTISELECT_DEFAULTS[fieldName]);
        }
      }

      if (fieldName === 'servicesProvided') {
        const choices = selectedValues(next[fieldName]);
        if (choices.includes('HHA') && choices.includes('PCA')) {
          const previous = selectedValues(current.servicesProvided);
          const keep = previous.includes('HHA') && !previous.includes('PCA') ? 'PCA' : 'HHA';
          next.servicesProvided = [...choices.filter((choice) => choice !== 'HHA' && choice !== 'PCA'), keep];
        }
      }

      if (fieldName === 'supplementalHomeServices') {
        const choices = selectedValues(next[fieldName]);
        if (!choices.includes('Hospice/Palliative Care')) {
          next.supplementalHomeServicesHospice = '';
        }
        if (!choices.includes('CHHA (RN, PT, OT, ST)')) {
          next.supplementalHomeServicesChha = '';
        }
        if (!choices.includes('Other')) {
          next.supplementalHomeServicesOther = '';
        }
        if (choices.length === 0 || choices.includes('None')) {
          next.safetyPrecautions = Array.isArray(next.safetyPrecautions) ? [] : '';
          next.safetyPrecautionsOther = '';
        }
      }

      if (fieldName === 'safetyPrecautions' && !fieldValueIncludes(next[fieldName], 'Other')) {
        next.safetyPrecautionsOther = '';
      }

      if (fieldName === 'palliativeCare' && !fieldValueEquals(processedValue, 'Already receiving palliative/hospice care')) {
        next.palliativeCareProviderInfo = '';
      }

      if (fieldName === 'gender') {
        const reproductive = selectedValues(next.reproductiveSymptoms);
        if (fieldValueEquals(processedValue, 'Female')) {
          next.reproductiveSymptoms = reproductive.filter((choice) => choice !== 'Prostate Disorder' && choice !== 'Scrotal Edema');
        } else if (fieldValueEquals(processedValue, 'Male')) {
          next.reproductiveSymptoms = reproductive.filter((choice) => choice !== 'Mastectomy: L' && choice !== 'Mastectomy: R');
        }
      }

      if (fieldName === 'advanceDirectives') {
        const choices = selectedValues(next[fieldName]);
        const previous = selectedValues(current[fieldName]);
        const noEducated = 'No, patient/HCP was educated';
        const hasNoEducated = choices.includes(noEducated);
        const hadNoEducated = previous.includes(noEducated);
        const otherChoices = choices.filter((choice) => choice !== noEducated);
        const previousOtherChoices = previous.filter((choice) => choice !== noEducated);
        if (hasNoEducated && !hadNoEducated && otherChoices.length > 0) {
          next[fieldName] = [noEducated];
        } else if (hasNoEducated && otherChoices.length > previousOtherChoices.length) {
          next[fieldName] = otherChoices;
        }
      }

      clearHiddenConditionalFields(next);
      if (fieldName === 'supervisionCompleted') {
        clearSupervisionIncompleteFields(next);
      }

      return next;
    });
    setFieldErrors((current) => {
      const fieldsToClear = new Set([fieldName]);
      if (fieldName === 'hasNoEmergencyContact' && isCheckedValue(value)) {
        noEmergencyContactFields.forEach((dependentField) => fieldsToClear.add(dependentField));
      }
      if (fieldName === 'notTakingMedications' && isCheckedValue(value)) {
        notTakingMedicationsHiddenFields.forEach((dependentField) => fieldsToClear.add(dependentField));
      }
      if (fieldName === 'supplementalHomeServices') {
        [
          'supplementalHomeServicesHospice',
          'supplementalHomeServicesChha',
          'supplementalHomeServicesOther',
          'safetyPrecautions',
          'safetyPrecautionsOther',
        ].forEach((dependentField) => fieldsToClear.add(dependentField));
      }
      if (fieldName === 'palliativeCare') {
        fieldsToClear.add('palliativeCareProviderInfo');
      }
      if (fieldName === 'safetyPrecautions') {
        fieldsToClear.add('safetyPrecautionsOther');
      }
      if (![...fieldsToClear].some((field) => current[field])) return current;
      const next = { ...current };
      fieldsToClear.forEach((field) => delete next[field]);
      return next;
    });
  };

  const handOffBuckets = useMemo(() => deriveHandOffBuckets(formData), [formData]);

  useEffect(() => {
    if (!assessment) return;
    const nextHandOffData = {
      handOffNeedsReferralFor: handOffBuckets.needsReferralFor.join(';'),
      handOffSafetyConcerns: handOffBuckets.safetyConcerns.join(';'),
      handOffFollowUpNeeded: handOffBuckets.needsFollowUp.join(';'),
      specialRequestUsedForScheduling: handOffBuckets.specialRequestUsedForScheduling.join(';'),
      specialRequestOther: handOffBuckets.specialRequestOther.join(';'),
      specialRequestNotUsedForScheduling: handOffBuckets.specialRequestNotUsedForScheduling.join(';'),
    };
    setFormData((current) => {
      if (Object.entries(nextHandOffData).every(([field, value]) => current[field] === value)) return current;
      return { ...current, ...nextHandOffData };
    });
  }, [assessment, handOffBuckets]);

  const revisitGateForCard = (card: CardDef): string | null => {
    if (!fieldValueEquals(formData.visitType, 'Revisit')) return null;
    const activeStepNo = activeStepNumber(activeStep);
    return Object.entries(REVISIT_CARD_GATES).find(([, config]) => (
      config.step === activeStepNo && card.fields.some((fieldName) => config.fields.includes(fieldName))
    ))?.[0] || null;
  };

  const renderRevisitGate = (gateField: string | null) => {
    if (!gateField || !fieldValueEquals(formData.visitType, 'Revisit')) return null;
    const gate = REVISIT_CARD_GATES[gateField];
    if (!gate) return null;
    return (
      <Box
        sx={{
          px: 1.25,
          py: 0.35,
          borderRadius: 1,
          bgcolor: 'rgba(134,0,225,0.08)',
          border: '1px solid rgba(134,0,225,0.22)',
          minWidth: { xs: '100%', md: 360 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.25,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#5b18a8', lineHeight: 1.2, flex: 1 }}>
          {gate.label}
        </Typography>
        <RadioGroup
          row
          name={gateField}
          value={String(formData[gateField] || '')}
          onChange={(event) => setField(gateField, event.target.value)}
          sx={{
            '& .MuiFormControlLabel-root': { my: 0, mr: 0.75 },
            '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
          }}
        >
          <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
          <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
        </RadioGroup>
        {fieldErrors[gateField] && (
          <Typography variant="caption" color="error">{fieldErrors[gateField]}</Typography>
        )}
      </Box>
    );
  };

  useEffect(() => {
    if (!assessment) return;
    const hasCaregiver = Boolean(formData.caregiver) && !noCaregiverPresent;
    const nextValue = hasCaregiver ? 'Yes' : 'No';
    if (!formData.supervisionCompleted || (noCaregiverPresent && formData.supervisionCompleted !== 'No')) {
      setField('supervisionCompleted', nextValue);
    }
  }, [assessment, formData.caregiver, formData.supervisionCompleted, noCaregiverPresent]);

  useEffect(() => {
    if (!assessment) return;
    if (fieldValueEquals(formData.visitType, 'Aide Supervision/Evaluation')) return;
    if (fieldValueEquals(formData.visitType, 'Revisit') && isRevisitReason(formData, 'modification') && fieldValueEquals(formData.isPocUpdateNeeded, 'Yes')) return;
    const certStart = normalizeIsoDate(formData.certificationPeriodStart);
    const certEnd = normalizeIsoDate(formData.certificationPeriodEnd);
    if (!certStart && !certEnd) return;
    setFormData((current) => {
      const next: Record<string, unknown> = { ...current };
      let changed = false;
      const currentEnd = normalizeIsoDate(current.pocEndDate);
      if (certEnd && !currentEnd) {
        next.pocEndDate = certEnd;
        changed = true;
      }
      const currentStart = normalizeIsoDate(current.pocStartDate);
      if (certStart && (!currentStart || currentStart < certStart)) {
        next.pocStartDate = certStart;
        changed = true;
      }
      return changed ? next : current;
    });
  }, [
    assessment,
    formData.certificationPeriodStart,
    formData.certificationPeriodEnd,
    formData.isPocUpdateNeeded,
    formData.visitType,
    formData.visitReason,
    selectedPatient,
    selectedPatient?.certificationPeriodStart,
    selectedPatient?.certificationPeriodEnd,
  ]);

  useEffect(() => {
    if (!assessment) return;
    if (!isRevisitReason(formData, 'new injury', 'wound')) return;
    setFormData((current) => {
      const values = selectedValues(current.skinProblems);
      if (values.some((value) => fieldValueEquals(value, 'Wound'))) return current;
      return {
        ...current,
        skinProblems: Array.isArray(current.skinProblems) ? [...values, 'Wound'] : [...values, 'Wound'],
      };
    });
  }, [
    assessment,
    formData.visitReason,
    formData.skinProblems,
    formData.visitType,
  ]);

  useEffect(() => {
    if (!assessment) return;
    if (!isRevisitReason(formData, 'modification')) return;
    if (!fieldValueEquals(formData.isPocUpdateNeeded, 'Yes')) return;
    const certEnd = normalizeIsoDate(formData.certificationPeriodEnd);
    setFormData((current) => {
      const next: Record<string, unknown> = { ...current };
      let changed = false;
      for (const fieldName of ['pocStartDate', 'shiftType1', 'shiftType2', 'shiftType3']) {
        if (next[fieldName]) {
          next[fieldName] = '';
          changed = true;
        }
      }
      if (certEnd && next.pocEndDate !== certEnd) {
        next.pocEndDate = certEnd;
        changed = true;
      }
      return changed ? next : current;
    });
  }, [
    assessment,
    formData.isPocUpdateNeeded,
    formData.visitReason,
    formData.visitType,
    formData.certificationPeriodEnd,
    selectedPatient,
    selectedPatient?.certificationPeriodEnd,
  ]);

  useEffect(() => {
    if (!assessment) return;
    let priorityCode = '';
    if (fieldValueEquals(formData.missingAidePoseSafetyRisk, 'No')) {
      priorityCode = '3';
    } else if (fieldValueEquals(formData.missingAidePoseSafetyRisk, 'Yes')) {
      if (fieldValueEquals(formData.backupAideAvailable, 'No')) priorityCode = '1';
      if (fieldValueEquals(formData.backupAideAvailable, 'Yes')) priorityCode = '2';
    }
    if (formData.priorityCode !== priorityCode) {
      setFormData((current) => ({ ...current, priorityCode: priorityCode }));
    }
  }, [
    assessment,
    formData.backupAideAvailable,
    formData.missingAidePoseSafetyRisk,
    formData.priorityCode,
  ]);

  useEffect(() => () => {
    if (viewingDocumentUrl) URL.revokeObjectURL(viewingDocumentUrl);
  }, [viewingDocumentUrl]);

  useEffect(() => {
    documentPreviewUrlsRef.current = documentPreviewUrls;
  }, [documentPreviewUrls]);

  useEffect(() => () => {
    Object.values(documentPreviewUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const handleCaregiverChange = (nextCaregiver: CaregiverRecord | null) => {
    const nextCaregiverId = caregiverId(nextCaregiver);
    const nextCaregiverName = caregiverName(nextCaregiver);
    setCaregiverSearchInput(nextCaregiverName);
    setNoCaregiverPresent(false);
    setFormData((current) => {
      const next: Record<string, unknown> = {
        ...current,
        caregiver: nextCaregiverId,
        caregiverName: nextCaregiverName,
        nameOfProfessional: nextCaregiverName || current.nameOfProfessional || '',
        supervisionCompleted: nextCaregiverId ? 'Yes' : 'No',
      };
      clearSupervisionIncompleteFields(next);
      return next;
    });
    setFieldErrors((current) => {
      const next = { ...current };
      delete next.caregiver;
      delete next.nameOfProfessional;
      delete next.supervisionCompleted;
      return next;
    });
  };

  const handleNoCaregiverChange = (checked: boolean) => {
    setNoCaregiverPresent(checked);
    if (!checked) {
      setFormData((current) => {
        const next: Record<string, unknown> = {
          ...current,
          supervisionCompleted: current.caregiver ? 'Yes' : 'No',
        };
        clearSupervisionIncompleteFields(next);
        return next;
      });
      return;
    }
    setFormData((current) => {
      const next: Record<string, unknown> = {
        ...current,
        caregiver: '',
        supervisionCompleted: 'No',
      };
      clearSupervisionIncompleteFields(next);
      return next;
    });
    setFieldErrors((current) => {
      const next = { ...current };
      delete next.caregiver;
      delete next.supervisionCompleted;
      return next;
    });
  };

  const handleStepReviewChange = (checked: boolean) => {
    const step = activeStepNumber(activeStep);
    const currentReviewed = typeof formData.stepsReviewed === 'object' && formData.stepsReviewed !== null && !Array.isArray(formData.stepsReviewed)
      ? formData.stepsReviewed as Record<string, unknown>
      : {};
    setFormData((current) => ({
      ...current,
      stepsReviewed: {
        ...currentReviewed,
        [step]: checked,
      },
    }));
    if (checked) {
      setFieldErrors((current) => {
        const next = { ...current };
        delete next[`step_${step}_reviewed`];
        return next;
      });
    }
  };

  useEffect(() => {
    if (!assessment || !formData.planOfCareTasks) return;
    const nextRows = planOfCareRows
      .filter((row) => isHhaContract || !row.task.includes('(HHA only)'))
      .map((row) => normalizePlanOfCareTask({
        ...row,
        max: row.max ? normalizedWholeNumber(row.max, 1, maxDaysAllowed) : row.max,
        min: row.min ? normalizedWholeNumber(row.min, 1, maxDaysAllowed) : row.min,
      }));
    const nextValue = serializePlanOfCareTasks(nextRows);
    if (nextValue !== formData.planOfCareTasks) {
      setField('planOfCareTasks', nextValue);
    }
  }, [assessment, formData.planOfCareTasks, isHhaContract, maxDaysAllowed, planOfCareRows]);

  useEffect(() => {
    if (!assessment || visibleStepNumbers.includes(activeStepNumber(activeStep))) return;
    const currentStepNumber = activeStepNumber(activeStep);
    const lastVisible = visibleStepNumbers[visibleStepNumbers.length - 1] || 1;
    const nextStepNumber = currentStepNumber > lastVisible ? lastVisible : visibleStepNumbers[0] || 1;
    setActiveStep(stepKeyFromNumber(nextStepNumber));
  }, [activeStep, assessment, visibleStepNumbers]);

  useEffect(() => {
    if (!assessment || activeStepNumber(activeStep) !== 10) return;
    const existingTasks = new Set(planOfCareRows.map((row) => row.task));
    const additions = conditionalPlanOfCareTasks(formData, planOfCareRows, maxDaysAllowed)
      .filter((row) => !existingTasks.has(row.task));
    if (additions.length > 0) {
      setField('planOfCareTasks', serializePlanOfCareTasks([...planOfCareRows, ...additions]));
    }
  }, [
    activeStep,
    assessment,
    formData.eatingFeeding,
    formData.hasAllergies,
    formData.hasInjections,
    formData.planOfCareTasks,
    maxDaysAllowed,
    planOfCareRows,
  ]);

  useEffect(() => {
    const pcpId = String(formData.pcp || '');
    if (!pcpId || pcpOptions.some((doctor) => getDoctorId(doctor) === pcpId)) return;
    let cancelled = false;
    async function loadPcp() {
      try {
        const doctor = await apiJson<Record<string, unknown>>(`/api/nursing-assessment/doctor/${encodeURIComponent(pcpId)}`);
        if (!cancelled) {
          const normalizedDoctor = validDoctorOptions([doctor])[0];
          setPcpOptions((current) => (
            normalizedDoctor ? [normalizedDoctor, ...current.filter((item) => getDoctorId(item) !== pcpId)] : current
          ));
        }
      } catch {
        if (!cancelled) setField('pcp', '');
      }
    }
    void loadPcp();
    return () => {
      cancelled = true;
    };
  }, [formData.pcp, pcpOptions]);

  useEffect(() => {
    if (String(formData.status || '').toLowerCase() === 'completed' || formData.pcpNotInSystem || !formData.pcp || formData.pcpOfficeSelection || !selectedPcp) return;
    const offices = pcpOffices(getDoctorId(selectedPcp), selectedPcp);
    if (offices.length === 1) setFormData(current => ({ ...current, pcpOfficeSelection: JSON.stringify(offices[0]), pcpSelectedAddress: offices[0].address }));
  }, [formData.status, formData.pcp, formData.pcpNotInSystem, formData.pcpOfficeSelection, selectedPcp]);

  useEffect(() => {
    if (formData.pcpNotInSystem && (formData.pcp || formData.pcpSelectedAddress || formData.pcpOfficeSelection)) {
      setFormData((current) => ({
        ...current,
        pcp: '',
        pcpSelectedAddress: '',
        pcpOfficeSelection: '',
      }));
    }
    if (!formData.pcpNotInSystem && formData.pcpInfo) {
      setField('pcpInfo', '');
    }
  }, [formData.pcpInfo, formData.pcpNotInSystem, formData.pcpSelectedAddress, formData.pcpOfficeSelection, formData.pcp]);

  const loadDocuments = async (assessmentId = assessment?.id) => {
    if (!assessmentId) return;
    setDocumentsLoading(true);
    try {
      const result = await apiJson<{ documents: NursingDocument[] }>(
        `/api/nursing-assessment/documents?assessmentId=${encodeURIComponent(String(assessmentId))}`
      );
      setDocuments(result.documents || []);
    } catch (err) {
      if (!handleAuthExpired(err, () => loadDocuments(assessmentId), `load-documents:${assessmentId || assessment?.id || ''}`)) {
        setError(err instanceof Error ? err.message : 'Failed to load documents');
      }
    } finally {
      setDocumentsLoading(false);
    }
  };

  const reopenCompletedAssessment = async () => {
    if (!assessment?.id) return;
    setSaving(true);
    setError(null);
    try {
      const result = await apiJson<{ assessment?: Record<string, unknown>; message?: string }>('/api/nursing-assessment/reopen', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assessmentId: assessment.id }),
      });
      const reopened = result.assessment || {};
      setAssessment((current) => current ? { ...current, ...reopened, status: 'Paraprofessional Supervision' } : current);
      setFormData((current) => ({
        ...current,
        ...reopened,
        status: 'Paraprofessional Supervision',
        submissionDate: '',
      }));
      setActiveStep('step11');
      setIsSubmitted(false);
    } catch (err) {
      if (!handleAuthExpired(err, () => reopenCompletedAssessment(), `reopen-assessment:${assessment?.id || ''}`)) {
        setError(err instanceof Error ? err.message : 'Failed to reopen assessment');
      }
    } finally {
      setSaving(false);
    }
  };

  const emailConsent = async () => {
    if (!assessment?.id) return;
    setConsentEmailLoading(true);
    setError(null);
    try {
      await apiJson<{ recipientEmail?: string; missingTemplates?: string[] }>('/api/nursing-assessment/email-consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assessmentId: assessment.id, email: consentRecipientEmail() }),
      });
      await loadDocuments();
    } catch (err) {
      if (!handleAuthExpired(err, () => emailConsent(), `email-consent:${assessment?.id || ''}`)) {
        setError(err instanceof Error ? err.message : 'Failed to email consent document');
      }
    } finally {
      setConsentEmailLoading(false);
    }
  };

  const openPcpAddressEditor = (addressType: 'Mailing' | 'Address 2' | 'Address 3') => {
    if (!selectedPcp) return;
    const isSecond = addressType === 'Address 2';
    const isPrimary = addressType === 'Mailing';
    setPcpAddressForm({
      address: String(doctorValue(selectedPcp, isSecond ? 'doctorAddress2' : 'doctorAddress3') || ''),
      street: String(doctorValue(selectedPcp, 'street') || ''),
      aptSuite: String(doctorValue(selectedPcp, 'aptSuite') || ''),
      city: String(doctorValue(selectedPcp, 'city') || ''),
      state: String(doctorValue(selectedPcp, 'state') || ''),
      zipCode: String(doctorValue(selectedPcp, 'zipCode') || ''),
      phone: String(doctorValue(selectedPcp, isPrimary ? 'phone' : isSecond ? 'doctorPhone2' : 'doctorPhone3') || ''),
      fax: String(doctorValue(selectedPcp, isPrimary ? 'fax' : isSecond ? 'doctorFax2' : 'doctorFax3') || ''),
    });
    setPcpAddressError(null);
    setPcpAddressDialog({ open: true, addressType });
  };

  const closePcpAddressEditor = () => {
    if (pcpAddressSaving) return;
    setPcpAddressDialog({ open: false, addressType: null });
    setPcpAddressError(null);
  };

  const savePcpAddress = async () => {
    if (!selectedPcp || !pcpAddressDialog.addressType) return;
    const doctorId = String(selectedPcp.id || selectedPcp.Id || '');
    if (!doctorId) return;
    setPcpAddressSaving(true);
    setPcpAddressError(null);
    try {
      const isSecond = pcpAddressDialog.addressType === 'Address 2';
      const { street, aptSuite, city, state, zipCode, phone, fax } = pcpAddressForm;
      const payload = pcpAddressDialog.addressType === 'Mailing'
        ? { street, aptSuite, city, state, zipCode, phone, fax }
        : isSecond
        ? { doctorAddress2: pcpAddressForm.address, doctorPhone2: pcpAddressForm.phone, doctorFax2: pcpAddressForm.fax }
        : { doctorAddress3: pcpAddressForm.address, doctorPhone3: pcpAddressForm.phone, doctorFax3: pcpAddressForm.fax };
      const result = await apiJson<{ doctor: Record<string, unknown> }>(`/api/nursing-assessment/doctor/${encodeURIComponent(doctorId)}/addresses`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const updatedDoctor = result.doctor || { ...selectedPcp, ...payload };
      setPcpOptions((current) => current.map((doctor) => (
        getDoctorId(doctor) === doctorId ? { ...doctor, ...updatedDoctor } : doctor
      )));
      // Updating the shared provider does not silently change the patient selection.
      // The refreshed office card must be explicitly selected to confirm its new details.
      setPcpAddressDialog({ open: false, addressType: null });
    } catch (err) {
      if (handleAuthExpired(err, () => savePcpAddress(), `save-pcp-address:${doctorId}:${pcpAddressDialog.addressType || ''}`)) return;
      setPcpAddressError(err instanceof Error ? err.message : 'Failed to update PCP address');
    } finally {
      setPcpAddressSaving(false);
    }
  };

  const loadDocumentBlob = async (documentId: number): Promise<Blob> => {
    const response = await authFetch(`/api/documents/${documentId}`);
    if (!response.ok) throw new Error('Failed to open document');
    return response.blob();
  };

  const openDocument = async (document: NursingDocument) => {
    if (viewingDocumentUrl) URL.revokeObjectURL(viewingDocumentUrl);
    setViewingDocument(document);
    setViewingDocumentUrl(null);
    setViewingDocumentLoading(true);
    try {
      const blob = await loadDocumentBlob(document.id);
      const url = URL.createObjectURL(blob);
      setViewingDocumentUrl(url);
    } catch (err) {
      if (handleAuthExpired(err, () => openDocument(document), `open-document:${document.id}`)) return;
      setError(err instanceof Error ? err.message : 'Failed to open document');
      setViewingDocument(null);
    } finally {
      setViewingDocumentLoading(false);
    }
  };

  const closeDocumentPreview = () => {
    if (viewingDocumentUrl) URL.revokeObjectURL(viewingDocumentUrl);
    setViewingDocument(null);
    setViewingDocumentUrl(null);
    setViewingDocumentLoading(false);
  };

  const downloadDocument = async (document: NursingDocument) => {
    try {
      const blob = await loadDocumentBlob(document.id);
      downloadBlob(blob, `${document.documentName || `document-${document.id}`}.pdf`);
    } catch (err) {
      if (handleAuthExpired(err, () => downloadDocument(document), `download-document:${document.id}`)) return;
      setError(err instanceof Error ? err.message : 'Failed to download document');
    }
  };

  const toggleCompletedDocument = async (document: NursingDocument) => {
    const documentId = document.id;
    if (expandedDocumentIds.includes(documentId)) {
      setExpandedDocumentIds((current) => current.filter((id) => id !== documentId));
      return;
    }

    setExpandedDocumentIds((current) => (current.includes(documentId) ? current : [...current, documentId]));
    if (documentPreviewUrls[documentId] || documentPreviewLoading[documentId]) return;

    setDocumentPreviewLoading((current) => ({ ...current, [documentId]: true }));
    try {
      const blob = await loadDocumentBlob(documentId);
      const url = URL.createObjectURL(blob);
      setDocumentPreviewUrls((current) => ({ ...current, [documentId]: url }));
    } catch (err) {
      setExpandedDocumentIds((current) => current.filter((id) => id !== documentId));
      if (handleAuthExpired(err, () => toggleCompletedDocument(document), `toggle-completed-document:${document.id}`)) return;
      setError(err instanceof Error ? err.message : 'Failed to open document');
    } finally {
      setDocumentPreviewLoading((current) => ({ ...current, [documentId]: false }));
    }
  };

  const uploadManualNursingDocument = async (documentConfig: ManualNursingDocumentConfig, file: File | null | undefined) => {
    if (!assessment?.id || !file) return;
    if (documentConfig.parent === 'caregiver' && !formData.caregiver) {
      setError('Select a caregiver before uploading the aide evaluation.');
      return;
    }

    setManualDocumentUploads((current) => ({ ...current, [documentConfig.key]: true }));
    setError(null);
    try {
      const uploadForm = new FormData();
      uploadForm.append('assessmentId', String(assessment.id));
      uploadForm.append('documentKey', documentConfig.key);
      uploadForm.append('file', file);
      if (documentConfig.parent === 'caregiver') {
        uploadForm.append('caregiverId', String(formData.caregiver || ''));
        uploadForm.append('caregiverName', caregiverName(selectedCaregiver) || String(formData.caregiverName || ''));
      }

      const response = await authFetch('/api/nursing-assessment/upload-document', {
        method: 'POST',
        body: uploadForm,
      });
      const data = await response.json().catch(() => ({})) as ManualNursingDocumentUploadResponse & { message?: string };
      if (!response.ok) throw new Error(data.message || 'Failed to upload document');

      setFormData((current) => {
        const currentFiles = Array.isArray(current.manualDocumentFiles) ? current.manualDocumentFiles : [];
        return {
          ...current,
          manualDocumentFiles: data.document ? [...currentFiles, data.document] : currentFiles,
        };
      });
    } catch (err) {
      if (handleAuthExpired(err, () => uploadManualNursingDocument(documentConfig, file), `upload-manual-document:${assessment.id}:${documentConfig.key}:${file.name}`)) return;
      setError(err instanceof Error ? err.message : `Failed to upload ${documentConfig.label}`);
    } finally {
      setManualDocumentUploads((current) => ({ ...current, [documentConfig.key]: false }));
    }
  };

  const handleManualDocumentDrop = (documentConfig: ManualNursingDocumentConfig, event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files?.[0];
    if (file) void uploadManualNursingDocument(documentConfig, file);
  };

  const renderManualNursingDocuments = (includeCaregiverDocuments: boolean) => {
    const uploadedFiles = Array.isArray(formData.manualDocumentFiles)
      ? formData.manualDocumentFiles as Record<string, unknown>[]
      : [];
    const caregiverLabel = caregiverName(selectedCaregiver) || String(formData.caregiverName || '');
    const tabs = [
      {
        label: `Client Documents${patientLabel(selectedPatient || {}) ? ` (${patientLabel(selectedPatient || {})})` : ''}`,
        documents: MANUAL_NURSING_DOCUMENTS.filter((documentConfig) => documentConfig.parent === 'patient'),
      },
      ...(
        includeCaregiverDocuments && caregiverLabel && formData.caregiver
          ? [{
              label: `Caregiver Documents (${caregiverLabel})`,
              documents: MANUAL_NURSING_DOCUMENTS.filter((documentConfig) => documentConfig.parent === 'caregiver'),
            }]
          : []
      ),
    ];
    const activeTab = Math.min(manualDocumentTab, tabs.length - 1);

    return (
      <Box
        sx={{
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          mb: 3,
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setManualDocumentTab(value)}
            aria-label="assessment documents tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            TabIndicatorProps={{ style: { backgroundColor: '#330662' } }}
            sx={{
              '& .MuiTab-root': {
                fontWeight: 'bold',
                color: 'rgba(0, 0, 0, 0.6)',
                textTransform: 'none',
                whiteSpace: 'normal',
                lineHeight: 1.2,
                '&.Mui-selected': { color: '#330662' },
                '&:hover': { color: '#8900e1', opacity: 1 },
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab key={tab.label} label={tab.label} id={`assessment-doc-tab-${index}`} aria-controls={`assessment-doc-tabpanel-${index}`} />
            ))}
          </Tabs>
        </Box>
        {tabs.map((tab, index) => (
          <Box
            key={tab.label}
            role="tabpanel"
            hidden={activeTab !== index}
            id={`assessment-doc-tabpanel-${index}`}
            aria-labelledby={`assessment-doc-tab-${index}`}
            sx={{ p: 3 }}
          >
            {activeTab === index && (
              <Box
              sx={{
                display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                  justifyContent: {
                    xs: 'center',
                    md: tab.documents.length % 3 !== 0 ? 'center' : 'flex-start',
                    lg: tab.documents.length % 4 !== 0 ? 'center' : 'flex-start',
                  },
                  gap: { xs: 1, sm: 2 },
              }}
            >
                {tab.documents.map((documentConfig) => {
                const uploadedFile = uploadedFiles.find((file) => file.documentKey === documentConfig.key);
                const uploaded = Boolean(uploadedFile);
                const uploading = Boolean(manualDocumentUploads[documentConfig.key]);
                const subjectName = documentConfig.parent === 'caregiver'
                    ? caregiverLabel || 'N/A'
                  : patientLabel(selectedPatient || {});

                return (
                    <Card
                      key={documentConfig.key}
                      sx={{
                        width: { xs: '100%', sm: 215 },
                        mx: 'auto',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderColor: '#330662',
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': { boxShadow: '0 4px 8px rgba(51, 6, 98, 0.2)' },
                      }}
                    >
                      <CardContent
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flex: '0 0 auto',
                          minHeight: { xs: 70, sm: 90 },
                          height: { xs: 70, sm: 80 },
                          p: { xs: 1.5, sm: 2 },
                          bgcolor: documentConfig.parent === 'patient' ? '#330662' : '#702b9d',
                          color: 'white',
                          borderTopLeftRadius: 2,
                          borderTopRightRadius: 2,
                          textAlign: 'center',
                        }}
                      >
                        <Box sx={{ width: '100%' }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              width: '100%',
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              lineHeight: 1.3,
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              fontWeight: 600,
                              mb: 0.5,
                              textAlign: 'center',
                            }}
                          >
                            {documentConfig.label}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' }, opacity: 0.9, fontStyle: 'italic' }}>
                            {documentConfig.parent === 'patient' ? `Client: ${subjectName || 'N/A'}` : `Caregiver: ${subjectName || 'N/A'}`}
                          </Typography>
                        </Box>
                      </CardContent>
                      <CardContent
                        component="label"
                        onDrop={(event) => handleManualDocumentDrop(documentConfig, event)}
                        onDragOver={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        sx={{
                          flex: '1 1 auto',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          p: { xs: 1, sm: 1.5 },
                          cursor: uploading ? 'default' : 'pointer',
                          border: '2px dashed #702b9d',
                          borderTop: 'none',
                          borderBottomLeftRadius: 2,
                          borderBottomRightRadius: 2,
                          minHeight: { xs: 90, sm: 110 },
                          height: { xs: 90, sm: 110 },
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: 'rgba(137, 0, 225, 0.05)',
                            borderColor: '#8900e1',
                          },
                        }}
                      >
                        <input
                          hidden
                          type="file"
                          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                          disabled={uploading}
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = '';
                            void uploadManualNursingDocument(documentConfig, file);
                          }}
                        />
                        {uploading ? (
                          <>
                            <CircularProgress size={24} sx={{ mb: 0.25, color: '#8900e1' }} />
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.6rem', sm: '0.875rem' }, textAlign: 'center' }}>
                              Uploading...
                            </Typography>
                          </>
                        ) : uploaded ? (
                          <>
                            <CheckCircleIcon sx={{ fontSize: { xs: 20, sm: 30 }, color: '#8900e1', mb: 0.25 }} />
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.6rem', sm: '0.875rem' }, color: '#8900e1', textAlign: 'center' }}>
                              Upload successful
                            </Typography>
                          </>
                        ) : (
                          <>
                            <CloudUploadIcon sx={{ fontSize: { xs: 20, sm: 30 }, color: '#702b9d', mb: 0.25 }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', sm: '0.875rem' }, textAlign: 'center' }}>
                              Drag file or browse
                            </Typography>
                          </>
                        )}
                      </CardContent>
                    </Card>
                );
              })}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const uploadAudioChunk = async (blob: Blob, chunkIndex: number) => {
    if (!assessment?.nexusId || !selectedPatient?.id || blob.size === 0) return;
    console.info('[NursingRecording] Uploading audio chunk', {
      patientId: selectedPatient.id,
      assessmentId: assessment.nexusId,
      chunkIndex,
      size: blob.size,
      type: blob.type,
    });
    const form = new FormData();
    form.append('audio', blob, `recording-${chunkIndex}.webm`);
    form.append('patientId', String(selectedPatient.id));
    form.append('assessmentId', String(assessment.nexusId));
    form.append('chunkIndex', String(chunkIndex));

    const response = await authFetch('/api/nursing-assessment/upload-recording-chunk', {
      method: 'POST',
      body: form,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'Failed to upload recording');
    console.info('[NursingRecording] Audio chunk upload completed', {
      patientId: selectedPatient.id,
      assessmentId: assessment.nexusId,
      chunkIndex,
      blobKey: data.blobUrl,
      hasTranscription: Boolean(data.transcription),
      hasSummary: Boolean(data.summary),
    });
    if (data.transcription) {
      setTranscriptionText((current) => current ? `${current}\n\n${data.transcription}` : data.transcription);
    }
    if (data.summary) {
      setSummaryText((current) => current ? `${current}\n\n${data.summary}` : data.summary);
    }
  };

  const nextRecordingChunkIndex = async (): Promise<number> => {
    if (!assessment?.nexusId || !selectedPatient?.id) return 0;
    const result = await apiJson<{ chunks?: Array<{ chunkIndex?: unknown; blobKey?: unknown }> }>(
      `/api/nursing-assessment/recording-chunks/${encodeURIComponent(String(selectedPatient.id))}/${encodeURIComponent(String(assessment.nexusId))}`
    );
    const indexes = (result.chunks || [])
      .map((chunk) => {
        const directIndex = Number(chunk.chunkIndex);
        if (Number.isInteger(directIndex) && directIndex >= 0) return directIndex;
        const match = String(chunk.blobKey || '').match(/chunk-(\d+)\.[^.]+$/);
        return match ? Number(match[1]) : -1;
      })
      .filter((index) => Number.isInteger(index) && index >= 0);
    const nextIndex = indexes.length ? Math.max(...indexes) + 1 : 0;
    console.info('[NursingRecording] Existing chunks loaded', {
      patientId: selectedPatient.id,
      assessmentId: assessment.nexusId,
      chunkCount: result.chunks?.length || 0,
      indexes,
      nextIndex,
    });
    return nextIndex;
  };

  const cleanupRecordingVisualization = () => {
    recordingAudioSourceRef.current = null;
    setRecordingAnalyserNode(null);
    setRecordingWaveLevels(Array(5).fill(20));
    const audioContext = recordingAudioContextRef.current;
    recordingAudioContextRef.current = null;
    audioContext?.close().catch(() => undefined);
  };

  const setupRecordingVisualization = (stream: MediaStream) => {
    cleanupRecordingVisualization();
    const AudioContextClass = window.AudioContext || (window as typeof window & {
      webkitAudioContext?: typeof AudioContext;
    }).webkitAudioContext;
    if (!AudioContextClass) return;

    try {
      const audioContext = new AudioContextClass();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.3;
      source.connect(analyser);
      recordingAudioContextRef.current = audioContext;
      recordingAudioSourceRef.current = source;
      setRecordingAnalyserNode(analyser);
    } catch (err) {
      cleanupRecordingVisualization();
      console.warn('[Nursing] Failed to initialize recording visualization:', err);
    }
  };

  const startRecording = async (): Promise<boolean> => {
    setRecordingError(null);
    try {
      if (recordingFinalizing || pendingRecordingUploadsRef.current.length > 0 || (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive')) {
        console.warn('[NursingRecording] Recording start blocked while previous recording is finalizing', {
          recordingFinalizing,
          pendingUploads: pendingRecordingUploadsRef.current.length,
          recorderState: mediaRecorderRef.current?.state,
        });
        setRecordingError('Recording is still finishing. Please try again in a moment.');
        return false;
      }
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
        setRecordingError('Audio recording is not supported in this browser.');
        return false;
      }
      if (!assessment?.nexusId || !selectedPatient?.id) {
        setRecordingError('Load an assessment before recording.');
        return false;
      }
      console.info('[NursingRecording] Starting recording', {
        patientId: selectedPatient.id,
        assessmentId: assessment.nexusId,
      });
      const nextChunkIndex = await nextRecordingChunkIndex();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      recordingStreamRef.current = stream;
      setupRecordingVisualization(stream);
      const recorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : undefined });
      mediaRecorderRef.current = recorder;
      chunkIndexRef.current = nextChunkIndex;
      recorder.ondataavailable = (event) => {
        if (!event.data || event.data.size === 0) {
          console.info('[NursingRecording] Ignored empty dataavailable event', {
            patientId: selectedPatient.id,
            assessmentId: assessment.nexusId,
            recorderState: recorder.state,
          });
          return;
        }
        console.info('[NursingRecording] MediaRecorder data available', {
          patientId: selectedPatient.id,
          assessmentId: assessment.nexusId,
          size: event.data.size,
          type: event.data.type,
          nextChunkIndex: chunkIndexRef.current,
        });
        const uploadChunkIndex = chunkIndexRef.current;
        chunkIndexRef.current += 1;
        const upload = (async () => {
          try {
            await uploadAudioChunk(event.data, uploadChunkIndex);
          } catch (err) {
            console.error('[NursingRecording] Audio chunk upload failed', err);
            const retried = await waitForReauthRetry(
              err,
              () => uploadAudioChunk(event.data, uploadChunkIndex),
              `upload-audio-chunk:${assessment.nexusId}:${uploadChunkIndex}`
            );
            if (!retried) {
              setRecordingError(err instanceof Error ? err.message : 'Failed to upload recording');
            }
          }
        })();
        pendingRecordingUploadsRef.current.push(upload);
        void upload.finally(() => {
          pendingRecordingUploadsRef.current = pendingRecordingUploadsRef.current.filter((pending) => pending !== upload);
        });
      };
      recorder.onstop = async () => {
        console.info('[NursingRecording] MediaRecorder stopped; waiting for uploads', {
          patientId: selectedPatient.id,
          assessmentId: assessment.nexusId,
          pendingUploads: pendingRecordingUploadsRef.current.length,
        });
        const uploadResults = await Promise.allSettled([...pendingRecordingUploadsRef.current]);
        console.info('[NursingRecording] Stop upload wait finished', {
          patientId: selectedPatient.id,
          assessmentId: assessment.nexusId,
          fulfilled: uploadResults.filter((result) => result.status === 'fulfilled').length,
          rejected: uploadResults.filter((result) => result.status === 'rejected').length,
        });
        recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
        recordingStreamRef.current = null;
        mediaRecorderRef.current = null;
        cleanupRecordingVisualization();
        setRecording(false);
        setRecordingPaused(false);
        setRecordingWasStopped(true);
        try {
          await loadRecordingText();
        } finally {
          setRecordingFinalizing(false);
          console.info('[NursingRecording] Recording stop finalized', {
            patientId: selectedPatient.id,
            assessmentId: assessment.nexusId,
          });
        }
      };
      recorder.start(5 * 60 * 1000);
      setRecordingElapsedSeconds(0);
      setRecordingPaused(false);
      setRecordingWasStopped(false);
      setRecordingFinalizing(false);
      setRecording(true);
      console.info('[NursingRecording] Recording started', {
        patientId: selectedPatient.id,
        assessmentId: assessment.nexusId,
        mimeType: recorder.mimeType,
        startChunkIndex: nextChunkIndex,
      });
      return true;
    } catch (err) {
      console.error('[NursingRecording] Failed to start recording', err);
      if (handleAuthExpired(err, () => startRecording(), `start-recording:${assessment?.nexusId || ''}`)) return false;
      setRecordingError(err instanceof Error ? err.message : 'Failed to start recording');
      setRecording(false);
      setRecordingPaused(false);
      setRecordingFinalizing(false);
      cleanupRecordingVisualization();
      return false;
    }
  };

  const requestRecordingStart = async () => {
    if (hasRespondedToRecordingConsent) {
      await startRecording();
      return;
    }
    setRecordingDeclineVisible(false);
    setRecordingConsentOpen(true);
  };

  const acceptRecordingConsent = async () => {
    setHasRespondedToRecordingConsent(true);
    setRecordingConsentOpen(false);
    await startRecording();
  };

  const declineRecordingConsent = () => {
    setHasRespondedToRecordingConsent(true);
    setRecordingConsentOpen(false);
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      console.info('[NursingRecording] Stopping recording', {
        patientId: selectedPatient?.id,
        assessmentId: assessment?.nexusId,
        recorderState: recorder.state,
        pendingUploads: pendingRecordingUploadsRef.current.length,
        nextChunkIndex: chunkIndexRef.current,
      });
      setRecording(false);
      setRecordingPaused(false);
      setRecordingWasStopped(true);
      setRecordingFinalizing(true);
      setRecordingTextLoading(true);
      recorder.stop();
      return;
    }
    console.info('[NursingRecording] Stop requested without active recorder', {
      patientId: selectedPatient?.id,
      assessmentId: assessment?.nexusId,
    });
    recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
    recordingStreamRef.current = null;
    cleanupRecordingVisualization();
    setRecording(false);
    setRecordingPaused(false);
    setRecordingWasStopped(true);
    setRecordingFinalizing(true);
    void loadRecordingText().finally(() => setRecordingFinalizing(false));
  };

  const toggleRecordingPause = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;
    if (recorder.state === 'recording') {
      console.info('[NursingRecording] Pausing recording', {
        patientId: selectedPatient?.id,
        assessmentId: assessment?.nexusId,
      });
      recorder.pause();
      setRecordingPaused(true);
      return;
    }
    if (recorder.state === 'paused') {
      console.info('[NursingRecording] Resuming recording', {
        patientId: selectedPatient?.id,
        assessmentId: assessment?.nexusId,
      });
      recorder.resume();
      setRecordingPaused(false);
    }
  };

  const loadRecordingText = async () => {
    if (!assessment?.nexusId || !selectedPatient?.id) {
      setRecordingError('Load an assessment before viewing AI scribe text.');
      return;
    }
    setRecordingTextLoading(true);
    try {
      console.info('[NursingRecording] Loading recording text', {
        patientId: selectedPatient.id,
        assessmentId: assessment.nexusId,
      });
      const [transcriptions, summaries] = await Promise.all([
        apiJson<{ fullText: string }>(`/api/nursing-assessment/transcriptions/${selectedPatient.id}/${assessment.nexusId}`),
        apiJson<{ fullText: string }>(`/api/nursing-assessment/summaries/${selectedPatient.id}/${assessment.nexusId}`),
      ]);
      setTranscriptionText(transcriptions.fullText || '');
      setSummaryText(summaries.fullText || '');
      console.info('[NursingRecording] Recording text loaded', {
        patientId: selectedPatient.id,
        assessmentId: assessment.nexusId,
        transcriptionLength: transcriptions.fullText?.length || 0,
        summaryLength: summaries.fullText?.length || 0,
      });
    } catch (err) {
      console.error('[NursingRecording] Failed to load recording text', err);
      if (handleAuthExpired(err, () => loadRecordingText(), `load-recording-text:${assessment?.nexusId || ''}`)) return;
      setRecordingError(err instanceof Error ? err.message : 'Failed to load recording text');
    } finally {
      setRecordingTextLoading(false);
    }
  };

  const openRecordingTextDialog = () => {
    setRecordingTextDialogOpen(true);
    if (recordingFinalizing) {
      setRecordingTextLoading(true);
      return;
    }
    void loadRecordingText();
  };

  const copyRecordingText = async () => {
    const text = recordingTextTab === 0 ? summaryText : transcriptionText;
    if (!text.trim()) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      setRecordingError('Unable to copy to clipboard');
    }
  };

  const openHandOffNoteDialog = () => {
    setHandOffNoteDraft(String(formData.handOffNote || ''));
    setHandOffNoteDialogError(fieldErrors.handOffNote || '');
    setHandOffNoteDialogOpen(true);
  };

  const assessmentNexusId = String(assessment?.nexusId || '').trim();
  const canUseAssessmentTickets = hasRole(user, 'nurse') || hasRole(user, 'nurse-admin');
  const canCreateAssessmentTicket = canUseAssessmentTickets
    && /^NUR_[A-Za-z0-9_-]+$/.test(assessmentNexusId);
  const assessmentTicketTooltip = !canUseAssessmentTickets
    ? 'Sign in as a nurse before creating a ticket'
    : canCreateAssessmentTicket
      ? 'Create Nexus Ticket'
      : 'Save the assessment before creating a ticket';

  const openTicketDialog = () => {
    if (!canCreateAssessmentTicket) return;
    const completedResult = completedTicketResultsRef.current[assessmentNexusId];
    if (completedResult) {
      setTicketDescription('');
      setTicketError('');
      setTicketResult(completedResult);
      setTicketPortalRequestId(null);
      setTicketRequestAssessmentId(assessmentNexusId);
      setTicketDialogOpen(true);
      return;
    }
    const pendingAttempt = pendingTicketAttemptsRef.current[assessmentNexusId];
    setTicketDescription(pendingAttempt?.description || '');
    setTicketError(pendingAttempt?.error || '');
    setTicketResult(null);
    setTicketPortalRequestId(pendingAttempt?.portalRequestId || null);
    setTicketRequestAssessmentId(pendingAttempt ? assessmentNexusId : null);
    setTicketDialogOpen(true);
  };

  const closeTicketDialog = () => {
    if (ticketSubmittingRef.current) return;
    setTicketDialogOpen(false);
    if (ticketResult || !ticketPortalRequestId) {
      if (ticketResult) {
        delete completedTicketResultsRef.current[
          ticketRequestAssessmentId || assessmentNexusId
        ];
      }
      setTicketDescription('');
      setTicketError('');
      setTicketResult(null);
      setTicketPortalRequestId(null);
      setTicketRequestAssessmentId(null);
    }
  };

  const submitAssessmentTicket = async (retryAttempt?: {
    description: string;
    nursingAssessmentId: string;
    portalRequestId: string;
  }) => {
    if (ticketSubmittingRef.current) return;

    const requestAssessmentId = retryAttempt?.nursingAssessmentId || assessmentNexusId;
    const description = (retryAttempt?.description ?? ticketDescription).trim();
    if (!description) {
      setTicketError('Describe the problem before submitting.');
      return;
    }
    if (description.length > NURSE_TICKET_DESCRIPTION_LIMIT) {
      setTicketError(`Description must be ${NURSE_TICKET_DESCRIPTION_LIMIT.toLocaleString()} characters or fewer.`);
      return;
    }
    if (!canCreateAssessmentTicket || requestAssessmentId !== assessmentNexusId) {
      setTicketError('Save the assessment in Nexus before creating a ticket.');
      return;
    }

    if (ticketRequestAssessmentId && ticketRequestAssessmentId !== assessmentNexusId) {
      setTicketError('This assessment changed. Close and reopen the ticket dialog before submitting.');
      return;
    }

    const portalRequestId = retryAttempt?.portalRequestId
      || ticketPortalRequestId
      || crypto.randomUUID();
    if (!ticketPortalRequestId || retryAttempt) {
      setTicketPortalRequestId(portalRequestId);
      setTicketRequestAssessmentId(requestAssessmentId);
    }
    setTicketDescription(description);
    pendingTicketAttemptsRef.current[requestAssessmentId] = {
      description,
      portalRequestId,
      error: '',
    };
    const identityEpoch = ticketIdentityEpochRef.current;
    ticketSubmittingRef.current = true;
    setTicketSubmitting(true);
    setTicketError('');
    setTicketResult(null);

    try {
      const result = await apiJson<DelegatedTicketResult>('/api/nursing-assessment/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description,
          nursingAssessmentId: requestAssessmentId,
          portalRequestId,
        }),
      });
      if (identityEpoch !== ticketIdentityEpochRef.current) return;
      delete pendingTicketAttemptsRef.current[requestAssessmentId];
      completedTicketResultsRef.current[requestAssessmentId] = result;
      setTicketResult(result);
    } catch (err) {
      if (identityEpoch !== ticketIdentityEpochRef.current) return;
      let message: string;
      if (err instanceof AuthExpiredError) {
        message = 'Your session expired. Sign in again, then retry this same ticket request.';
        handleAuthExpired(
          err,
          () => submitAssessmentTicket({
            description,
            nursingAssessmentId: requestAssessmentId,
            portalRequestId,
          }),
          `submit-assessment-ticket:${requestAssessmentId}:${portalRequestId}`
        );
      } else if (err instanceof TypeError) {
        message = 'The result could not be confirmed. Retry this same request to safely check for the ticket.';
      } else {
        message = err instanceof Error
          ? err.message
          : 'The result could not be confirmed. Retry this same request to safely check for the ticket.';
      }

      const preserveAttempt = err instanceof AuthExpiredError
        || err instanceof TypeError
        || (err instanceof ApiRequestError && (
          err.retryable === true
          || (err.status >= 500 && err.retryable !== false)
        ));
      if (preserveAttempt) {
        pendingTicketAttemptsRef.current[requestAssessmentId] = {
          description,
          portalRequestId,
          error: message,
        };
      } else {
        delete pendingTicketAttemptsRef.current[requestAssessmentId];
        setTicketPortalRequestId(null);
        setTicketRequestAssessmentId(null);
      }
      setTicketError(message);
    } finally {
      if (identityEpoch === ticketIdentityEpochRef.current) {
        ticketSubmittingRef.current = false;
        setTicketSubmitting(false);
      }
    }
  };

  const saveHandOffNoteDialog = async () => {
    if (!assessment?.id) return;
    const sanitizedValue = sanitizeWinAnsiString(handOffNoteDraft);
    if (!validateWinAnsiString(sanitizedValue)) {
      setHandOffNoteDialogError('Contains invalid characters. Please remove special characters.');
      return;
    }
    if (sanitizedValue.length > 131072) {
      setHandOffNoteDialogError('Hand Off Note cannot exceed 131,072 characters.');
      return;
    }

    const nextFormData = {
      ...formData,
      handOffNote: sanitizedValue,
    };
    const sanitizedPayload = sanitizeAssessmentPayloadStrings({
      ...nextFormData,
      ...derivedHandOffPayload(nextFormData),
    }) as Record<string, unknown>;

    setSaving(true);
    setError(null);
    setHandOffNoteDialogError('');
    try {
      const result = await apiJson<{ assessment: Record<string, unknown> }>('/api/nursing-assessment/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assessmentId: assessment.id, formData: sanitizedPayload }),
      });
      setAssessment(result.assessment);
      setFormData((current) => ({ ...current, ...sanitizedPayload, ...result.assessment }));
      setHandOffNoteDialogOpen(false);
    } catch (err) {
      if (!handleAuthExpired(err, () => saveHandOffNoteDialog(), `save-handoff-note:${assessment?.id || ''}`)) {
        setHandOffNoteDialogError(err instanceof Error ? err.message : 'Failed to save Hand Off Note');
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    setTranscriptionText('');
    setSummaryText('');
    setRecordingError(null);
    setRecordingWasStopped(false);
    if (assessment?.nexusId && selectedPatient?.id) {
      void loadRecordingText();
    }
  }, [assessment?.nexusId, selectedPatient?.id]);

  const savePlanOfCareRows = (rows: PlanOfCareTaskRow[]) => {
    setField('planOfCareTasks', serializePlanOfCareTasks(rows));
  };

  const updatePlanOfCareRow = (taskName: string, updates: Partial<PlanOfCareTaskRow>) => {
    savePlanOfCareRows(planOfCareRows.map((row) => (
      row.task === taskName ? normalizePlanOfCareTask({ ...row, ...updates }) : row
    )));
  };

  const updateScheduleDays = (
    fieldName: string,
    value: unknown,
    dayFields = ['daysPerWeek', 'daysPerWeek2', 'daysPerWeek3']
  ) => {
    const currentDays = dayFields.reduce<Record<string, number>>((acc, dayField) => {
      acc[dayField] = Number.parseInt(String(formData[dayField] || '0'), 10) || 0;
      return acc;
    }, {});
    const otherDays = Object.entries(currentDays)
      .filter(([key]) => key !== fieldName)
      .reduce((sum, [, days]) => sum + days, 0);
    const maxForField = Math.max(0, 7 - otherDays);
    setField(fieldName, normalizedWholeNumber(value, 0, maxForField));
  };

  const renderShiftTypeField = (fieldName: string, label: string) => (
    <FormControl fullWidth error={Boolean(fieldErrors[fieldName])}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={String(formData[fieldName] || '')}
        label={label}
        onChange={(event) => setField(fieldName, event.target.value)}
      >
        <MenuItem value=""><em>Select an option</em></MenuItem>
        <MenuItem value="Split Shift">Split Shift</MenuItem>
        <MenuItem value="Live-In">Live-In</MenuItem>
      </Select>
      {fieldErrors[fieldName] && <FormHelperText>{fieldErrors[fieldName]}</FormHelperText>}
    </FormControl>
  );

  const renderPlanOfCareSchedule = () => {
    if (fieldValueEquals(formData.visitType, 'Aide Supervision/Evaluation')) return null;
    const hasSchedule = Boolean(formData.daysPerWeek || formData.daysPerWeek2);
    return (
      <Box
        sx={{
          mt: 3,
          mb: 3,
          p: 2,
          bgcolor: hasSchedule ? 'rgba(134, 0, 225, 0.04)' : 'rgba(255, 193, 7, 0.1)',
          borderRadius: 1,
          border: hasSchedule ? '1px solid rgba(134, 0, 225, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography variant="h6" sx={{ color: hasSchedule ? '#8600e1' : 'warning.main', mb: 1 }}>
              PCA/HHA Schedule Hours
            </Typography>
          </Box>
          {isFieldVisible('clusterPackages', formData) && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              {renderField('clusterPackages')}
            </Box>
          )}
          <TextField label="Days per Week" type="number" value={String(formData.daysPerWeek || '')} error={Boolean(fieldErrors.daysPerWeek)} helperText={fieldErrors.daysPerWeek} inputProps={{ min: 0, max: 7, step: 1 }} onChange={(event) => updateScheduleDays('daysPerWeek', event.target.value)} />
          <TextField label="Hours per Day" type="number" value={String(formData.hoursPerDay || '')} error={Boolean(fieldErrors.hoursPerDay)} helperText={fieldErrors.hoursPerDay} inputProps={{ min: 0, max: 24, step: 0.5 }} onChange={(event) => setField('hoursPerDay', event.target.value)} />
          <TextField label="Days per Week 2" type="number" value={String(formData.daysPerWeek2 || '')} error={Boolean(fieldErrors.daysPerWeek2)} helperText={fieldErrors.daysPerWeek2} inputProps={{ min: 0, max: 7, step: 1 }} onChange={(event) => updateScheduleDays('daysPerWeek2', event.target.value)} />
          <TextField label="Hours per Day 2" type="number" value={String(formData.hoursPerDay2 || '')} error={Boolean(fieldErrors.hoursPerDay2)} helperText={fieldErrors.hoursPerDay2} inputProps={{ min: 0, max: 24, step: 0.5 }} onChange={(event) => setField('hoursPerDay2', event.target.value)} />
          <TextField label="Days per Week 3" type="number" value={String(formData.daysPerWeek3 || '')} error={Boolean(fieldErrors.daysPerWeek3)} helperText={fieldErrors.daysPerWeek3} inputProps={{ min: 0, max: 7, step: 1 }} onChange={(event) => updateScheduleDays('daysPerWeek3', event.target.value)} />
          <TextField label="Hours per Day 3" type="number" value={String(formData.hoursPerDay3 || '')} error={Boolean(fieldErrors.hoursPerDay3)} helperText={fieldErrors.hoursPerDay3} inputProps={{ min: 0, max: 24, step: 0.5 }} onChange={(event) => setField('hoursPerDay3', event.target.value)} />
        </Box>
        {(Number(formData.hoursPerDay) === 24 || Number(formData.hoursPerDay2) === 24 || Number(formData.hoursPerDay3) === 24) && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
              mt: 2,
            }}
          >
            {Number(formData.hoursPerDay) === 24 && renderShiftTypeField('shiftType1', 'Shift Type')}
            {Number(formData.hoursPerDay2) === 24 && renderShiftTypeField('shiftType2', 'Shift Type 2')}
            {Number(formData.hoursPerDay3) === 24 && renderShiftTypeField('shiftType3', 'Shift Type 3')}
          </Box>
        )}
      </Box>
    );
  };

  const saveConsentRows = (rows: ConsentRow[]) => {
    setField('consent', serializeConsentRows(rows));
  };

  const updateConsentRow = (rowId: string, updates: Partial<ConsentRow>) => {
    saveConsentRows(consentRows.map((row, index) => (
      row.id === rowId ? normalizeConsentRow({ ...row, ...updates }, index, formData, consentContractName) : row
    )));
  };

  const addConsentService = (option: (typeof consentOptions)[number] | null) => {
    if (!option || option.disabled || consentRows.length >= MAX_CONSENT_ROWS) return;
    const nextRow = normalizeConsentRow({
      id: `consent-row-${Date.now()}`,
      service: option.config.services,
      frequency: option.frequency,
      fee: option.fee,
      weekendFee: option.weekendFee,
      expectedCoverage: '',
      patientResponsibility: '',
      requiresFrequencyInput: option.config.requiresFrequencyInput && option.config.services !== 'PCA/HHA',
      requiresFeeInput: !option.fee,
      isPrivatePay: option.config.isPrivatePay,
    }, consentRows.length, formData, consentContractName);
    saveConsentRows([...consentRows, nextRow]);
    setConsentServiceSearch('');
  };

  const processConsentRowUpdate = (updatedRow: ConsentRow, originalRow?: ConsentRow) => {
    const rowWithLinkedCoverage = { ...updatedRow };
    if (originalRow && updatedRow.expectedCoverage !== originalRow.expectedCoverage) {
      const complement = complementaryConsentCoverage(updatedRow.expectedCoverage);
      if (complement) rowWithLinkedCoverage.patientResponsibility = complement;
    }
    if (originalRow && updatedRow.patientResponsibility !== originalRow.patientResponsibility) {
      const complement = complementaryConsentCoverage(updatedRow.patientResponsibility);
      if (complement) rowWithLinkedCoverage.expectedCoverage = complement;
    }
    const normalizedRow = normalizeConsentRow(rowWithLinkedCoverage, consentRows.findIndex((row) => row.id === updatedRow.id), formData, consentContractName);
    saveConsentRows(consentRows.map((row, index) => (
      row.id === normalizedRow.id ? normalizeConsentRow(normalizedRow, index, formData, consentContractName) : row
    )));
    return normalizedRow;
  };

  const isDefaultPlanOfCareTask = (taskName: string) => {
    if (DEFAULT_POC_TASKS.has(taskName)) return true;
    if (taskName === '547 Follow allergy precautions' && fieldValueEquals(formData.hasAllergies, 'Yes')) return true;
    if (taskName === '545 Follow Needles/sharps precautions' && fieldValueIncludes(formData.hasInjections, 'Yes')) return true;
    if (taskName === '546 Follow choking/aspiration precautions' && formData.eatingFeeding && !fieldValueEquals(formData.eatingFeeding, 'Independent')) return true;
    if (taskName === '544 Report wound/bruises') {
      const bathingTasks = ['100 Tub', '101 Shower', '102 Bed / Sponge', '103 Total care / perineal care', '104 Mouth Care'];
      return planOfCareRows.some((row) => bathingTasks.some((task) => row.task.includes(task)));
    }
    return false;
  };

  const renderPlanOfCareTasks = (disabled = false) => {
    const isAideSupervisionVisit = fieldValueEquals(formData.visitType, 'Aide Supervision/Evaluation');
    const showFullPocDetails = !isAideSupervisionVisit;
    const planOfCareReadOnly = disabled || isAideSupervisionVisit;
    const fieldError = fieldErrors.planOfCareTasks;
    const selectedTasks = new Set(planOfCareRows.map((row) => row.task));
    const editableRows = planOfCareRows.filter((row) => !isTimesheetTask(row.task));
    const allRequired = editableRows.length > 0 && editableRows.every((row) => row.required);
    const allAssist = editableRows.length > 0 && editableRows.every((row) => row.assist);
    const timesheetTaskRow = planOfCareRows.find((row) => isTimesheetTask(row.task)) || null;
    const validationIssues = fieldError ? planOfCareTaskValidationIssues(planOfCareRows) : [];
    const invalidPocFieldsByRow = new Map(validationIssues.map((issue) => [issue.rowIndex, new Set(issue.fields)]));

    return (
      <Stack spacing={2}>
        {showFullPocDetails && (
          <Autocomplete
            options={planOfCareTaskOptions}
            value={null}
            inputValue={planOfCareTaskSearch}
            open={planOfCareTaskPickerOpen}
            disabled={disabled || !(formData.daysPerWeek || formData.daysPerWeek2)}
            disableCloseOnSelect
            getOptionDisabled={(option) => selectedTasks.has(option)}
            onOpen={() => setPlanOfCareTaskPickerOpen(true)}
            onClose={(_, reason) => {
              if (reason !== 'selectOption') setPlanOfCareTaskPickerOpen(false);
            }}
            onInputChange={(_, value, reason) => {
              if (reason !== 'reset') setPlanOfCareTaskSearch(value);
            }}
            onChange={(_, taskName) => {
              if (!taskName || selectedTasks.has(taskName)) return;
              savePlanOfCareRows([
                ...planOfCareRows,
                normalizePlanOfCareTask({
                  task: taskName,
                  min: '1',
                  max: String(maxDaysAllowed),
                  instructions: PLAN_OF_CARE_TASKS[taskName]?.defaultDescription || '',
                  required: true,
                  assist: false,
                }),
              ]);
              setPlanOfCareTaskSearch('');
              setPlanOfCareTaskPickerOpen(true);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add a Plan of Care Task"
                placeholder="Start typing to add a task"
                helperText={!(formData.daysPerWeek || formData.daysPerWeek2) ? 'Enter days per week before adding tasks.' : undefined}
              />
            )}
          />
        )}

        {showFullPocDetails && !isHhaContract && (
          <Alert severity="info" variant="outlined">
            This client's contract does not allow HHA-only tasks.
          </Alert>
        )}

        {showFullPocDetails && fieldError && <Alert severity="error">{fieldError}</Alert>}

        {planOfCareRows.length === 0 ? (
          <Box sx={{ border: '2px dashed #ccc', borderRadius: 1, p: 4, textAlign: 'center', bgcolor: '#fafafa' }}>
            <Typography variant="h6" color="text.secondary">No plan of care tasks added yet</Typography>
            <Typography variant="body2" color="text.secondary">
              {showFullPocDetails ? 'Use the field above to add tasks to the plan of care' : 'No plan of care tasks available for this assessment'}
            </Typography>
          </Box>
        ) : (
          <>
            <DataGridPremium
            rows={planOfCareRows.map((row, index) => ({ ...row, id: index }))}
            columns={[
              {
                field: 'task',
                headerName: 'Task',
                flex: 1.35,
                minWidth: 160,
                sortable: false,
                editable: false,
                renderCell: (params: GridRenderCellParams) => (
                  <Box sx={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', width: '100%' }}>
                    {String(params.value || '')}
                  </Box>
                ),
              },
              {
                field: 'min',
                headerName: 'Min',
                flex: 0.5,
                minWidth: 70,
                sortable: false,
                editable: !planOfCareReadOnly,
                renderCell: (params: GridRenderCellParams) => (
                  <Box sx={{ color: params.value ? 'inherit' : '#999', fontStyle: params.value ? 'normal' : 'italic', width: '100%' }}>
                    {String(params.value || 'Click to edit')}
                  </Box>
                ),
              },
              {
                field: 'max',
                headerName: 'Max',
                flex: 0.5,
                minWidth: 70,
                sortable: false,
                editable: !planOfCareReadOnly,
                renderCell: (params: GridRenderCellParams) => (
                  <Box sx={{ color: params.value ? 'inherit' : '#999', fontStyle: params.value ? 'normal' : 'italic', width: '100%' }}>
                    {String(params.value || 'Click to edit')}
                  </Box>
                ),
              },
              {
                field: 'instructions',
                headerName: 'Additional Instructions',
                flex: 1.45,
                minWidth: 200,
                sortable: false,
                editable: !planOfCareReadOnly,
                renderCell: (params: GridRenderCellParams) => (
                  <Box sx={{ color: params.value ? 'inherit' : '#999', fontStyle: params.value ? 'normal' : 'italic', whiteSpace: 'pre-wrap', wordWrap: 'break-word', width: '100%' }}>
                    {String(params.value || 'Click to edit')}
                  </Box>
                ),
              },
              {
                field: 'required',
                headerName: 'Aide to do',
                width: 110,
                sortable: false,
                filterable: false,
                headerAlign: 'center',
                renderHeader: () => (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Typography component="span" sx={{ color: 'white', fontWeight: 700, fontSize: '0.8125rem' }}>Aide to do</Typography>
                    {showFullPocDetails && (
                      <Checkbox
                        size="small"
                        checked={allRequired}
                        disabled={disabled}
                        sx={{ color: 'common.white', p: 0, '&.Mui-checked': { color: 'common.white' } }}
                        onChange={(event) => savePlanOfCareRows(planOfCareRows.map((row) => (
                          isTimesheetTask(row.task)
                            ? row
                            : normalizePlanOfCareTask({ ...row, required: event.target.checked, assist: event.target.checked ? false : row.assist })
                        )))}
                      />
                    )}
                  </Box>
                ),
                renderCell: (params: GridRenderCellParams) => {
                  const row = params.row as PlanOfCareTaskRow;
                  const timesheetTask = isTimesheetTask(row.task);
                  return (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <Checkbox
                        checked={Boolean(row.required)}
                        disabled={planOfCareReadOnly || timesheetTask}
                        onChange={(event) => updatePlanOfCareRow(row.task, {
                          required: event.target.checked,
                          assist: event.target.checked ? false : row.assist,
                        })}
                      />
                    </Box>
                  );
                },
              },
              {
                field: 'assist',
                headerName: 'Aide to Assist',
                width: 130,
                sortable: false,
                filterable: false,
                headerAlign: 'center',
                renderHeader: () => (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Typography component="span" sx={{ color: 'white', fontWeight: 700, fontSize: '0.8125rem' }}>Aide to Assist</Typography>
                    {showFullPocDetails && (
                      <Checkbox
                        size="small"
                        checked={allAssist}
                        disabled={disabled}
                        sx={{ color: 'common.white', p: 0, '&.Mui-checked': { color: 'common.white' } }}
                        onChange={(event) => savePlanOfCareRows(planOfCareRows.map((row) => (
                          isTimesheetTask(row.task)
                            ? row
                            : normalizePlanOfCareTask({ ...row, assist: event.target.checked, required: event.target.checked ? false : row.required })
                        )))}
                      />
                    )}
                  </Box>
                ),
                renderCell: (params: GridRenderCellParams) => {
                  const row = params.row as PlanOfCareTaskRow;
                  const timesheetTask = isTimesheetTask(row.task);
                  return (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <Checkbox
                        checked={Boolean(row.assist)}
                        disabled={planOfCareReadOnly || timesheetTask}
                        onChange={(event) => updatePlanOfCareRow(row.task, {
                          assist: event.target.checked,
                          required: event.target.checked ? false : row.required,
                        })}
                      />
                    </Box>
                  );
                },
              },
              {
                field: 'actions',
                headerName: 'Actions',
                width: 100,
                sortable: false,
                filterable: false,
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams) => {
                  const row = params.row as PlanOfCareTaskRow;
                  const defaultTask = isDefaultPlanOfCareTask(row.task);
                  if (defaultTask || planOfCareReadOnly) {
                    return (
                      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', color: '#999', fontSize: '0.8rem' }}>
                        {defaultTask || isAideSupervisionVisit ? 'Default' : 'Locked'}
                      </Box>
                    );
                  }
                  return (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => savePlanOfCareRows(planOfCareRows.filter((taskRow) => taskRow.task !== row.task))}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                },
              },
            ] as GridColDef[]}
            disableColumnMenu
            disableColumnSelector
            disableRowSelectionOnClick
            autoHeight
            isCellEditable={(params) => !planOfCareReadOnly && !isTimesheetTask(String(params.row.task || ''))}
            getRowClassName={(params) => invalidPocFieldsByRow.has(Number(params.id)) ? 'poc-task-row-error' : ''}
            getCellClassName={(params) => {
              const invalidFields = invalidPocFieldsByRow.get(Number(params.id));
              return invalidFields?.has(params.field as PlanOfCareTaskField) ? 'poc-task-cell-error' : '';
            }}
            columnHeaderHeight={60}
            processRowUpdate={(updatedRow) => {
              const row = updatedRow as PlanOfCareTaskRow & { id?: number };
              const normalized = normalizePlanOfCareTask({
                ...row,
                min: normalizedWholeNumber(row.min, 1, maxDaysAllowed),
                max: normalizedWholeNumber(row.max, 1, maxDaysAllowed),
              });
              savePlanOfCareRows(planOfCareRows.map((currentRow) => (
                currentRow.task === row.task ? normalized : currentRow
              )));
              return { ...normalized, id: row.id };
            }}
            onProcessRowUpdateError={() => undefined}
            sx={{
              backgroundColor: '#fafafa',
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              '& .MuiDataGrid-cell': {
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                cursor: planOfCareReadOnly ? 'default' : 'text',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                lineHeight: 1.2,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                '&:last-child': { borderRight: 'none' },
              },
              '& .MuiDataGrid-cell[data-field="actions"], & .MuiDataGrid-cell[data-field="required"], & .MuiDataGrid-cell[data-field="assist"]': {
                cursor: 'default',
              },
              '& .poc-task-row-error': {
                backgroundColor: 'rgba(211, 47, 47, 0.04)',
                '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' },
              },
              '& .poc-task-cell-error': {
                backgroundColor: 'rgba(211, 47, 47, 0.12)',
                boxShadow: 'inset 0 0 0 1px rgba(211, 47, 47, 0.65)',
                color: '#7f1d1d',
                '& .MuiCheckbox-root': {
                  color: '#d32f2f',
                },
              },
              '& .MuiDataGrid-topContainer, & .MuiDataGrid-columnHeaders': {
                backgroundColor: '#702B9D',
              },
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: '#702B9D',
                color: 'white',
                fontWeight: 'bold',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: 'white',
                fontWeight: 'bold',
              },
            }}
            />
            {showFullPocDetails && timesheetTaskRow && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2, mt: 1 }}>
                <Autocomplete
                  freeSolo
                  options={TIMESHEET_REASON_OPTIONS}
                  value={timesheetTaskRow.instructions || ''}
                  disabled={disabled}
                  onChange={(_, value) => updatePlanOfCareRow(timesheetTaskRow.task, { instructions: value || '' })}
                  onInputChange={(_, value, reason) => {
                    if (reason !== 'reset') updatePlanOfCareRow(timesheetTaskRow.task, { instructions: value || '' });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Due to reason"
                      helperText="Task 600 keeps the aide boxes blank and uses this reason in the POC PDF."
                      fullWidth
                    />
                  )}
                />
              </Box>
            )}
          </>
        )}
      </Stack>
    );
  };

  const renderConsentSignerFields = () => {
    const patientSigner = fieldValueEquals(formData.consentSignedBy, 'Patient');
    const proxySigner = fieldValueEquals(formData.consentSignedBy, 'Proxy');
    const savedContactEmails = emergencyContactEmailChoices(formData);
    const consentSignerOptions = ['Patient', 'Proxy'];
    const consentSignerValue = picklistOptionForValue(formData.consentSignedBy, consentSignerOptions);
    const patientCannotSign = patientSigner && (
      fieldValueEquals(formData.patientSelfDirecting, 'No') || fieldValueEquals(formData.patientHasDementia, 'Yes')
    );

    return (
      <Stack spacing={2}>
        <FormControl fullWidth error={Boolean(fieldErrors.consentSignedBy)}>
          <InputLabel>Consent discussed with and signed by</InputLabel>
          <Select
            value={consentSignerOptions.some((option) => fieldValueEquals(option, consentSignerValue)) ? consentSignerValue : ''}
            label="Consent discussed with and signed by"
            onChange={(event) => setField('consentSignedBy', event.target.value)}
          >
            <MenuItem value=""><em>Select an option</em></MenuItem>
            <MenuItem value="Patient">Patient</MenuItem>
            <MenuItem value="Proxy">Proxy</MenuItem>
          </Select>
          {fieldErrors.consentSignedBy && <FormHelperText>{fieldErrors.consentSignedBy}</FormHelperText>}
        </FormControl>

        {patientSigner && (
          <Stack spacing={2}>
            {selectedPatientEmail && (
              <Button variant="outlined" onClick={() => setField('patientEmail', selectedPatientEmail)}>
                Use patient email: {selectedPatientEmail}
              </Button>
            )}
            <TextField
              label="Patient Email"
              type="email"
              value={String(formData.patientEmail || '')}
              error={Boolean(fieldErrors.patientEmail)}
              helperText={fieldErrors.patientEmail}
              onChange={(event) => setField('patientEmail', event.target.value)}
              fullWidth
            />
            {renderField('patientSelfDirecting')}
            {renderField('patientHasDementia')}
          </Stack>
        )}

        {proxySigner && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="subtitle1" fontWeight={700}>Proxy Information</Typography>
            </Box>
            {savedContactEmails.length > 0 && (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>Confirm an emergency contact to use as the consent proxy:</Typography>
                <Stack spacing={1}>
                  {savedContactEmails.map((choice, index) => (
                    <Button key={index} variant="outlined" onClick={() => {
                      setField('proxyName', choice.name || '');
                      setField('proxyPhone', choice.phone || '');
                      setField('proxyRelationship', choice.relationship || '');
                      setField('proxyEmail', choice.email);
                    }}>{choice.label}</Button>
                  ))}
                </Stack>
              </Box>
            )}
            <TextField label="Proxy Name" value={String(formData.proxyName || '')} error={Boolean(fieldErrors.proxyName)} helperText={fieldErrors.proxyName} onChange={(event) => setField('proxyName', event.target.value)} fullWidth />
            {renderField('proxyRelationship')}
            <InputMask
              mask="999 999 9999"
              value={String(formData.proxyPhone || '')}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setField('proxyPhone', event.target.value)}
              maskPlaceholder={null}
            >
              <TextField
                label="Proxy Phone"
                error={Boolean(fieldErrors.proxyPhone)}
                helperText={fieldErrors.proxyPhone}
                fullWidth
                inputMode="tel"
                autoComplete="tel"
              />
            </InputMask>
            <TextField label="Proxy Email" type="email" value={String(formData.proxyEmail || '')} error={Boolean(fieldErrors.proxyEmail)} helperText={fieldErrors.proxyEmail} onChange={(event) => setField('proxyEmail', event.target.value)} fullWidth />
          </Box>
        )}

        {patientCannotSign && (
          <Alert severity="error">
            <Typography variant="body1" fontWeight={700}>Patient is not allowed to sign the consent.</Typography>
            <Typography variant="body2">
              {fieldValueEquals(formData.patientSelfDirecting, 'No')
                ? 'Patient is not self-directing and cannot make decisions about their care. '
                : ''}
              {fieldValueEquals(formData.patientHasDementia, 'Yes')
                ? 'Patient has a diagnosis of dementia and cannot sign. '
                : ''}
              A proxy must sign on their behalf. Please change "Consent discussed with and signed by" to "Proxy".
            </Typography>
          </Alert>
        )}
      </Stack>
    );
  };

  const renderConsentSignatureStep = () => {
    const proxySigner = fieldValueEquals(formData.consentSignedBy, 'Proxy');
    return (
      <Stack spacing={3}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              By signing below, you acknowledge and consent to the following:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 0 }}>
              {[
                'You agree to receive services from Link Home Care',
                'Your services have been explained to you by a Registered Nurse',
                'You authorize the release of your medical information to Link Home Care as needed for care coordination and billing',
                'You confirm receipt of verbal and written information regarding patient rights, responsibilities, how to contact the agency, complaint procedures, advance directives, emergency preparedness, privacy policies, and how to contact the Department of Health',
                'You authorize Link Home Care to bill your insurance directly for covered services and understand your financial responsibilities',
                'You understand your charges for services and acknowledge that charges may change if authorized by you or your insurance',
                'You agree to maintain regular medical follow up with your doctor to avoid interruption of services',
                'You consent to being referred to a physician who can perform at least one telehealth visit to authorize services if Link Home Care is unable to reach your primary doctor',
                'You allow aides to use a phone associated with you for clocking in and out when necessary',
                'You agree to promptly inform the agency of any events affecting your health, including falls, wounds, or changes in condition',
                'You agree to provide a safe environment for care and to report any complaints or concerns to the agency',
                'For live-ins only - You agree to follow the live in policy as provided',
              ].map((item) => (
                <Typography key={item} component="li" variant="body2" sx={{ mb: 1 }}>
                  {item}
                </Typography>
              ))}
            </Box>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <AssignmentIcon sx={{ mb: 1, mr: 1, color: '#8600e1' }} />
              <Typography variant="h6" gutterBottom>
                {proxySigner ? 'Proxy Signature' : 'Patient Signature'}
              </Typography>
            </Box>
            <Box
              sx={{
                border: fieldErrors.ConsentSignature ? '2px solid #d32f2f' : '1px solid #000',
                width: '100%',
                height: 200,
                mb: 1,
              }}
            >
              <SignatureCanvas
                ref={consentSignaturePadRef}
                onEnd={() => {
                  const signaturePad = consentSignaturePadRef.current;
                  if (!signaturePad || signaturePad.isEmpty()) return;
                  setField('ConsentSignature', signaturePad.getCanvas().toDataURL('image/png'));
                }}
                canvasProps={{
                  height: 200,
                  className: 'signatureCanvas',
                }}
              />
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                consentSignaturePadRef.current?.clear();
                setField('ConsentSignature', '');
              }}
            >
              Clear Signature
            </Button>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <AssignmentIcon sx={{ mb: 1, mr: 1, color: '#8600e1' }} />
              <Typography variant="h6" gutterBottom>Nurse Signature</Typography>
            </Box>
            {renderNurseSignature()}
          </CardContent>
        </Card>
      </Stack>
    );
  };

  const renderConsentServices = (includeSignerFields = true, includeSignature = true) => {
    const fieldError = fieldErrors.consent;
    const digitalConsent = fieldValueEquals(formData.consentType, 'Digital');
    const proxySigner = fieldValueEquals(formData.consentSignedBy, 'Proxy');

    if (fieldValueEquals(formData.consentType, 'Paper')) {
      return <Alert severity="info">Paper consent selected. No additional consent table is required.</Alert>;
    }

    if (!digitalConsent) {
      return <Alert severity="info">Select Digital consent to enter consent services.</Alert>;
    }

    return (
      <Stack spacing={2}>
        {includeSignerFields && renderConsentSignerFields()}

        <Autocomplete
          options={consentOptions}
          value={null}
          inputValue={consentServiceSearch}
          onInputChange={(_, value, reason) => {
            setConsentServiceSearch(reason === 'reset' ? '' : value);
          }}
          disabled={consentRows.length >= MAX_CONSENT_ROWS}
          getOptionLabel={(option) => option.label}
          getOptionDisabled={(option) => option.disabled}
          onChange={(_, option) => addConsentService(option)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Add Consent Service"
              helperText={consentRows.length >= MAX_CONSENT_ROWS ? 'Maximum of 6 consent services reached.' : 'Select a service to add it to the consent table.'}
            />
          )}
        />

        {fieldError && <Alert severity="error">{fieldError}</Alert>}

        {consentRows.length === 0 ? (
          <Box sx={{ border: '2px dashed #ccc', borderRadius: 1, p: 4, textAlign: 'center', bgcolor: '#fafafa' }}>
            <Typography variant="h6" color="text.secondary">No consent services added yet</Typography>
            <Typography variant="body2" color="text.secondary">Contract Types are required before adding services.</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              p: 2,
              bgcolor: '#fff',
            }}
          >
            <DataGridPremium
              rows={consentRows}
              columns={[
                {
                  field: 'service',
                  headerName: 'Service',
                  flex: 1,
                  minWidth: 100,
                  editable: false,
                  renderCell: (params: GridRenderCellParams<ConsentRow>) => (
                    <Typography variant="body2">{params.value || '-'}</Typography>
                  ),
                },
                {
                  field: 'frequency',
                  headerName: 'Frequency',
                  flex: 1.2,
                  minWidth: 140,
                  editable: true,
                  renderCell: (params: GridRenderCellParams<ConsentRow>) => (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.3, py: 0.5 }}>
                      {params.value || '-'}
                    </Typography>
                  ),
                },
                {
                  field: 'fee',
                  headerName: 'Fee',
                  flex: 1.5,
                  minWidth: 180,
                  editable: true,
                  renderCell: (params: GridRenderCellParams<ConsentRow>) => (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.3, py: 0.5 }}>
                      {params.value || '-'}
                    </Typography>
                  ),
                },
                {
                  field: 'weekendFee',
                  headerName: 'Weekend Fee',
                  flex: 1,
                  minWidth: 120,
                  editable: false,
                  renderCell: (params: GridRenderCellParams<ConsentRow>) => (
                    <Typography variant="body2">{params.value || '-'}</Typography>
                  ),
                },
                {
                  field: 'expectedCoverage',
                  headerName: 'Expected Coverage',
                  flex: 1,
                  minWidth: 120,
                  editable: true,
                  type: 'singleSelect',
                  valueOptions: CONSENT_COVERAGE_OPTIONS,
                  renderCell: (params: GridRenderCellParams<ConsentRow>) => (
                    <Typography variant="body2">{params.value || '-'}</Typography>
                  ),
                },
                {
                  field: 'patientResponsibility',
                  headerName: 'Patient Responsibility',
                  flex: 1,
                  minWidth: 130,
                  editable: true,
                  type: 'singleSelect',
                  valueOptions: CONSENT_COVERAGE_OPTIONS,
                  renderCell: (params: GridRenderCellParams<ConsentRow>) => (
                    <Typography variant="body2">{params.value || '-'}</Typography>
                  ),
                },
                {
                  field: 'actions',
                  headerName: '',
                  width: 50,
                  sortable: false,
                  disableColumnMenu: true,
                  filterable: false,
                  renderCell: (params: GridRenderCellParams<ConsentRow>) => {
                    const protectedFrequency = isProtectedConsentFrequency(params.row.frequency);
                    const button = (
                      <IconButton
                        size="small"
                        aria-label="Remove row"
                        disabled={protectedFrequency}
                        onClick={(event) => {
                          event.stopPropagation();
                          saveConsentRows(consentRows.filter((consentRow) => consentRow.id !== params.row.id));
                        }}
                        sx={{
                          color: protectedFrequency ? 'rgba(0,0,0,0.26)' : '#8600e1',
                          '&:hover': protectedFrequency ? {} : { bgcolor: 'rgba(134,0,225,0.08)' },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    );
                    return (
                      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        {protectedFrequency ? (
                          <Tooltip title="Initial visit and Recertification are required">
                            <span>{button}</span>
                          </Tooltip>
                        ) : button}
                      </Box>
                    );
                  },
                },
              ] satisfies GridColDef<ConsentRow>[]}
              autoHeight
              hideFooter
              columnHeaderHeight={72}
              editMode="cell"
              disableRowSelectionOnClick
              disableColumnMenu
              getRowId={(row) => row.id}
              processRowUpdate={(updatedRow, originalRow) => processConsentRowUpdate(updatedRow as ConsentRow, originalRow as ConsentRow)}
              onProcessRowUpdateError={() => setFieldErrors((current) => ({ ...current, consent: 'Unable to update consent row.' }))}
              isCellEditable={(params) => {
                if (params.field === 'frequency') return params.row.requiresFrequencyInput === true;
                if (params.field === 'fee') return params.row.requiresFeeInput === true;
                if (params.field === 'weekendFee') return false;
                if (params.field === 'service' || params.field === 'actions') return false;
                return true;
              }}
              sx={{
                bgcolor: '#fafafa',
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                '& .MuiDataGrid-topContainer, & .MuiDataGrid-columnHeaders': {
                  bgcolor: '#702B9D',
                },
                '& .MuiDataGrid-columnHeader': {
                  bgcolor: '#702B9D',
                  color: '#fff',
                  fontWeight: 700,
                  whiteSpace: 'normal',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: '#fff',
                  fontWeight: 700,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.25,
                  textAlign: 'center',
                },
                '& .MuiDataGrid-columnHeaders': {
                  color: '#fff',
                  fontWeight: 700,
                  alignItems: 'stretch',
                },
                '& .MuiDataGrid-columnSeparator': {
                  color: 'rgba(255,255,255,0.45)',
                },
                '& .MuiDataGrid-cell': {
                  borderRight: '1px solid rgba(224,224,224,1)',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  lineHeight: 1.3,
                  py: 1,
                  px: 1.5,
                },
                '& .MuiDataGrid-row:nth-of-type(even)': {
                  bgcolor: '#f5f0ff',
                },
                '& .MuiDataGrid-row:hover': {
                  bgcolor: '#ede1ff',
                },
              }}
            />
          </Box>
        )}

        {includeSignature && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            {proxySigner ? 'Proxy Signature' : 'Patient Signature'}
          </Typography>
          <Box
            sx={{
              border: fieldErrors.ConsentSignature ? '2px solid #d32f2f' : '1px solid #000',
              width: '100%',
              height: 200,
              mb: 1,
            }}
          >
            <SignatureCanvas
              ref={consentSignaturePadRef}
              onEnd={() => {
                const signaturePad = consentSignaturePadRef.current;
                if (!signaturePad || signaturePad.isEmpty()) return;
                setField('ConsentSignature', signaturePad.getCanvas().toDataURL('image/png'));
              }}
              canvasProps={{
                height: 200,
                className: 'signatureCanvas',
              }}
            />
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                consentSignaturePadRef.current?.clear();
                setField('ConsentSignature', '');
              }}
            >
              Clear Signature
            </Button>
            {fieldErrors.ConsentSignature && <Typography color="error" variant="body2">{fieldErrors.ConsentSignature}</Typography>}
          </Stack>
        </Box>
        )}

        {hasWaivedConsent && (
          <FormControl fullWidth error={Boolean(fieldErrors.financeClearanceReceived)}>
            <InputLabel>Was finance clearance received to waive fee?</InputLabel>
            <Select
              value={String(formData.financeClearanceReceived || '')}
              label="Was finance clearance received to waive fee?"
              onChange={(event) => setField('financeClearanceReceived', event.target.value)}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
            {fieldErrors.financeClearanceReceived && <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>{fieldErrors.financeClearanceReceived}</Typography>}
          </FormControl>
        )}
      </Stack>
    );
  };

  const renderConsentScheduleField = (
    fieldName: string,
    label: string,
    dayFields: string[],
    relatedShiftField?: string
  ) => {
    const isDayField = dayFields.includes(fieldName);
    return (
      <TextField
        label={label}
        type="number"
        value={String(formData[fieldName] || '')}
        error={Boolean(fieldErrors[fieldName])}
        helperText={fieldErrors[fieldName]}
        inputProps={{
          min: 0,
          max: isDayField ? 7 : 24,
          step: isDayField ? 1 : 0.5,
          onWheel: (event) => (event.target as HTMLInputElement).blur(),
        }}
        onChange={(event) => {
          if (isDayField) {
            updateScheduleDays(fieldName, event.target.value, dayFields);
            return;
          }
          setField(fieldName, event.target.value);
          if (relatedShiftField && Number(event.target.value) !== 24 && formData[relatedShiftField]) {
            setField(relatedShiftField, '');
          }
        }}
        fullWidth
      />
    );
  };

  const renderConsentScheduleRow = (
    daysField: string,
    hoursField: string,
    shiftField: string,
    daysLabel: string,
    hoursLabel: string,
    shiftLabel: string,
    dayFields: string[]
  ) => {
    const showShift = Number(formData[hoursField]) === 24;
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: showShift ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        {renderConsentScheduleField(daysField, daysLabel, dayFields)}
        {renderConsentScheduleField(hoursField, hoursLabel, dayFields, shiftField)}
        {showShift && renderShiftTypeField(shiftField, shiftLabel)}
      </Box>
    );
  };

  const renderConsentScheduleSection = (title: string, privatePay = false) => {
    const dayFields = privatePay
      ? ['ppDaysPerWeek', 'ppDaysPerWeek2']
      : ['daysPerWeek', 'daysPerWeek2', 'daysPerWeek3'];
    const frequency = generatePcaHhaFrequency(formData, privatePay);
    const hasHoursError = privatePay
      ? fieldErrors.consent === 'Private Pay PCA/HHA service requires PP schedule hours.'
      : fieldErrors.consent === 'Contract PCA/HHA service requires schedule hours.';

    return (
      <Box
        sx={{
          mt: 1,
          mb: 1,
          p: 2,
          bgcolor: hasHoursError ? 'rgba(211, 47, 47, 0.04)' : 'rgba(134, 0, 225, 0.04)',
          borderRadius: 1,
          border: hasHoursError ? '1px solid #d32f2f' : '1px solid rgba(134, 0, 225, 0.2)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: hasHoursError ? 1 : 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: hasHoursError ? '#d32f2f' : '#8600e1' }}>
            {title}
          </Typography>
          {privatePay && (
            <FormControlLabel
              control={(
                <Checkbox
                  checked={Boolean(formData.ppMutualCase)}
                  onChange={(event) => setField('ppMutualCase', event.target.checked)}
                  sx={{ color: '#8600e1', '&.Mui-checked': { color: '#8600e1' } }}
                />
              )}
              label="Mutual Case"
              sx={{ mr: 0 }}
            />
          )}
        </Box>
        {hasHoursError && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            Please fill in at least one set of Days per Week and Hours per Day.
          </Typography>
        )}
        <Stack spacing={1}>
          {privatePay ? (
            <>
              {renderConsentScheduleRow('ppDaysPerWeek', 'ppHoursPerDay', 'ppShiftType1', 'Days per Week', 'Hours per Day', 'Shift Type', dayFields)}
              {renderConsentScheduleRow('ppDaysPerWeek2', 'ppHoursPerDay2', 'ppShiftType2', 'Days per Week 2', 'Hours per Day 2', 'Shift Type 2', dayFields)}
            </>
          ) : (
            <>
              {renderConsentScheduleRow('daysPerWeek', 'hoursPerDay', 'shiftType1', 'Days per Week', 'Hours per Day', 'Shift Type', dayFields)}
              {renderConsentScheduleRow('daysPerWeek2', 'hoursPerDay2', 'shiftType2', 'Days per Week 2', 'Hours per Day 2', 'Shift Type 2', dayFields)}
              {renderConsentScheduleRow('daysPerWeek3', 'hoursPerDay3', 'shiftType3', 'Days per Week 3', 'Hours per Day 3', 'Shift Type 3', dayFields)}
            </>
          )}
        </Stack>
        {frequency && (
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            {privatePay ? 'Private Pay PCA/HHA Frequency' : 'PCA/HHA Frequency'}: <strong>{frequency}</strong>
          </Typography>
        )}
      </Box>
    );
  };

  const renderNurseSignature = () => (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Signature
      </Typography>
      <Box
        sx={{
          border: fieldErrors.Signature ? '2px solid #d32f2f' : '1px solid #000',
          width: '100%',
          height: 200,
          mb: 1,
        }}
      >
        <SignatureCanvas
          ref={nurseSignaturePadRef}
          onEnd={() => {
            const signaturePad = nurseSignaturePadRef.current;
            if (!signaturePad || signaturePad.isEmpty()) return;
            setField('Signature', signaturePad.getCanvas().toDataURL('image/png'));
          }}
          canvasProps={{
            height: 200,
            className: 'signatureCanvas',
          }}
        />
      </Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            nurseSignaturePadRef.current?.clear();
            setField('Signature', '');
          }}
        >
          Clear Signature
        </Button>
        {fieldErrors.Signature && <Typography color="error" variant="body2">{fieldErrors.Signature}</Typography>}
      </Stack>
    </Box>
  );

  const renderDerivedHandOffSection = (title: string, items: string[]) => (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No items</Typography>
          ) : items.map((item) => (
            <Chip key={item} label={item} size="small" />
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const renderHandOffStep = () => (
    <Card variant="outlined" sx={{ mb: 2, mt: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box display="flex" alignItems="center" mb={2}>
            <AssignmentIcon sx={{ mb: 1, mr: 1, color: '#8600e1' }} />
            <Typography variant="h6" gutterBottom>Expanded Hand-off</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {renderDerivedHandOffSection('Needs Referral For', handOffBuckets.needsReferralFor)}
            {renderDerivedHandOffSection('Safety Concerns', handOffBuckets.safetyConcerns)}
            {renderDerivedHandOffSection('Needs Follow Up', handOffBuckets.needsFollowUp)}
            {renderDerivedHandOffSection('Patient Preferences: Used for Scheduling', handOffBuckets.specialRequestUsedForScheduling)}
            {renderDerivedHandOffSection('Other', handOffBuckets.specialRequestOther)}
            {renderDerivedHandOffSection('Patient Preferences: Not Used for Scheduling', handOffBuckets.specialRequestNotUsedForScheduling)}
          </Box>
          {renderField('handOffNote')}
          {renderNurseSignature()}
        </Stack>
      </CardContent>
    </Card>
  );

  const renderDiagnosisRows = (fieldName: 'primaryDx' | 'otherPertinentDiagnosis', disabled = false) => {
    const isPrimary = fieldName === 'primaryDx';
    const rows = isPrimary ? primaryDiagnosisRows : otherDiagnosisRows;
    const saveRows = (nextRows: DiagnosisRow[]) => setField(fieldName, serializeDiagnosisRows(nextRows));
    const visitDate = normalizeIsoDate(formData.visitDate) || normalizeIsoDate(new Date().toISOString());
    const addDiagnosis = (diagnosis: DiagnosisOption & { date?: string }) => {
      if (rows.some((row) => fieldValueEquals(row.icdCode, diagnosis.code))) return;
      const nextId = rows.length ? Math.max(...rows.map((row) => row.id)) + 1 : 1;
      saveRows([
        ...rows,
        {
          id: nextId,
          icdCode: diagnosis.code || '',
          description: diagnosis.description || '',
          date: normalizeIsoDate(diagnosis.date) || visitDate,
        },
      ]);
    };
    const updateRowsFromGrid = (newRow: DiagnosisRow, oldRow: DiagnosisRow) => {
      const normalizedRow = {
        ...newRow,
        date: normalizeIsoDate(newRow.date) || normalizeIsoDate(oldRow.date) || visitDate,
      };
      const nextRows = rows.map((row) => (row.id === normalizedRow.id ? normalizedRow : row));
      saveRows(nextRows);
      return normalizedRow;
    };
    const selectPrimaryDiagnosis = (option: typeof PRIMARY_DIAGNOSIS_OPTIONS[number]) => {
      const existingDate = normalizeIsoDate(rows[0]?.date) || visitDate;
      saveRows([{ id: 1, icdCode: option.code, description: option.description, date: existingDate }]);
    };
    const diagnosisGridSx = {
      backgroundColor: '#fafafa',
      border: '1px solid #e0e0e0',
      borderRadius: 2,
      '& .MuiDataGrid-cell': {
        borderRight: '1px solid rgba(224, 224, 224, 1)',
        cursor: disabled ? 'default' : 'text',
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word',
        lineHeight: '1.2',
        padding: '8px',
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
          backgroundColor: disabled ? 'inherit' : '#f5f5f5',
        },
        '&:last-child': {
          borderRight: 'none',
        },
      },
      '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader': {
        backgroundColor: '#702B9D',
        color: 'white',
        fontWeight: 'bold',
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        color: 'white',
        fontWeight: 'bold',
      },
      '& .MuiDataGrid-row:hover': {
        backgroundColor: '#f5f0ff',
      },
      '& .MuiDataGrid-virtualScroller': {
        backgroundColor: 'white',
      },
    };
    const diagnosisColumns: GridColDef<DiagnosisRow>[] = [
      {
        field: 'icdCode',
        headerName: 'ICD 10 Code',
        flex: 1,
        minWidth: 120,
        editable: !disabled && !isPrimary,
        sortable: false,
        renderCell: (params: GridRenderCellParams<DiagnosisRow>) => (
          <Box sx={{ width: '100%', color: params.value ? 'inherit' : '#999', fontStyle: params.value ? 'normal' : 'italic', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {params.value || (isPrimary ? '' : 'Click to edit')}
          </Box>
        ),
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        minWidth: 200,
        editable: !disabled && !isPrimary,
        sortable: false,
        renderCell: (params: GridRenderCellParams<DiagnosisRow>) => (
          <Box sx={{ width: '100%', color: params.value ? 'inherit' : '#999', fontStyle: params.value ? 'normal' : 'italic', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {params.value || (isPrimary ? '' : 'Click to edit')}
          </Box>
        ),
      },
      {
        field: 'date',
        headerName: 'Date',
        flex: 1,
        minWidth: 120,
        editable: !disabled,
        sortable: false,
        renderCell: (params: GridRenderCellParams<DiagnosisRow>) => (
          <Box sx={{ width: '100%', color: params.value ? 'inherit' : '#999', fontStyle: params.value ? 'normal' : 'italic', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {params.value ? displayDate(params.value) : 'Select date'}
          </Box>
        ),
      },
      ...(!isPrimary ? [{
        field: 'actions',
        headerName: 'Actions',
        flex: 0.5,
        minWidth: 70,
        sortable: false,
        filterable: false,
        headerAlign: 'center' as const,
        align: 'center' as const,
        renderCell: (params: GridRenderCellParams<DiagnosisRow>) => (
          disabled ? null : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
              <IconButton
                onClick={() => saveRows(rows.filter((row) => row.id !== params.row.id))}
                size="small"
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          )
        ),
      } satisfies GridColDef<DiagnosisRow>] : []),
    ];

    if (isPrimary) {
      return (
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            Primary Dx
          </Typography>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Choose one primary diagnosis
            </Typography>
            <Box role="radiogroup">
              {PRIMARY_DIAGNOSIS_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.code}
                  control={(
                    <Radio
                      checked={(rows[0]?.icdCode || '') === option.code}
                      disabled={disabled}
                      onChange={(event) => {
                        if (event.target.checked) selectPrimaryDiagnosis(option);
                      }}
                    />
                  )}
                  label={option.label}
                />
              ))}
            </Box>
          </Box>
          {fieldErrors.primaryDx && <Alert severity="error">{fieldErrors.primaryDx}</Alert>}
          <DataGridPremium
            rows={rows}
            getRowId={(row) => row.id}
            columns={diagnosisColumns}
            processRowUpdate={updateRowsFromGrid}
            onProcessRowUpdateError={() => undefined}
            autoHeight
            disableColumnResize
            getRowHeight={() => 'auto'}
            hideFooter
            disableColumnMenu
            disableColumnSelector
            disableRowSelectionOnClick
            sx={diagnosisGridSx}
          />
        </Stack>
      );
    }

    const existingCodes = new Set(rows.map((row) => row.icdCode.trim().toLowerCase()).filter(Boolean));
    const availableOptions = ICD10_DIAGNOSIS_OPTIONS.filter((option) => !existingCodes.has(option.code.toLowerCase()));

    const DiagnosisAutocompletePaper = (props: ComponentProps<typeof Paper>) => {
      return (
        <Paper {...props}>
          {props.children}
          <MenuItem
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.stopPropagation();
              if (!otherDiagnosisInput.trim() || disabled) return;
              addDiagnosis({ code: otherDiagnosisInput.trim(), description: '' });
              setOtherDiagnosisInput('');
            }}
            disabled={!otherDiagnosisInput.trim() || disabled}
            disableRipple
            sx={(theme) => ({
              position: 'sticky',
              bottom: 0,
              zIndex: 1,
              backgroundColor: theme.palette.background.paper,
              justifyContent: 'center',
              fontWeight: 'bold',
              color: '#8600e1',
              py: 1.5,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                borderTop: '1px solid',
                borderColor: theme.palette.divider,
              },
              '&:hover': {
                backgroundColor: theme.palette.grey[100],
                color: '#8600e1',
                opacity: 1,
              },
            })}
          >
            Add
          </MenuItem>
        </Paper>
      );
    };

    return (
      <Stack spacing={2}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 3 }}>
          Other Pertinent Diagnosis
        </Typography>
        {fieldErrors.otherPertinentDiagnosis && <Alert severity="error">{fieldErrors.otherPertinentDiagnosis}</Alert>}
        <Autocomplete
          options={availableOptions}
          getOptionLabel={(option) => typeof option === 'string' ? option : `${option.code} - ${option.description}`}
          filterOptions={(options, state) => {
            const searchTerm = state.inputValue.toLowerCase();
            return options.filter((option) => option.code.toLowerCase().includes(searchTerm) || option.description.toLowerCase().includes(searchTerm));
          }}
          disabled={disabled}
          freeSolo
          clearOnBlur={false}
          value={null}
          inputValue={otherDiagnosisInput}
          onInputChange={(_event, nextInput) => setOtherDiagnosisInput(nextInput)}
          onChange={(_event, newValue) => {
            if (!newValue || disabled) return;
            if (typeof newValue === 'string') {
              addDiagnosis({ code: newValue.trim(), description: '' });
            } else {
              addDiagnosis({ code: newValue.code, description: newValue.description });
            }
            setOtherDiagnosisInput('');
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="ICD-10 Diagnosis"
              placeholder="Select from list or type custom ICD-10 code"
              helperText="Type to search by code or description, or enter custom text"
              fullWidth
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Typography variant="body2" fontWeight={700}>{option.code}</Typography>
                <Typography variant="body2" color="text.secondary">{option.description}</Typography>
              </Box>
            </li>
          )}
          PaperComponent={DiagnosisAutocompletePaper}
          ListboxProps={{ sx: { maxHeight: 300 } }}
        />
        <Box sx={{ mt: 1.5 }}>
          <DataGridPremium
            rows={rows}
            getRowId={(row) => row.id}
            columns={diagnosisColumns}
            processRowUpdate={updateRowsFromGrid}
            onProcessRowUpdateError={() => undefined}
            autoHeight
            disableColumnResize
            getRowHeight={() => 'auto'}
            hideFooter
            disableColumnMenu
            disableColumnSelector
            disableRowSelectionOnClick
            sx={diagnosisGridSx}
          />
        </Box>
      </Stack>
    );
  };

  // purposeDxOnly keeps the medication list locked while still letting the RN document Purpose/Dx.
  const renderMedications = (disabled = false, purposeDxOnly = false) => {
    const medicationListLocked = disabled || purposeDxOnly;
    const saveRows = (rows: MedicationRow[]) => setField('medications', serializeMedicationRows(rows));
    const notTakingMedications = isCheckedValue(formData.notTakingMedications);
    const visitDate = normalizeIsoDate(formData.visitDate) || normalizeIsoDate(new Date().toISOString());
    const addRow = () => {
      if (medicationListLocked || notTakingMedications) return;
      const nextId = medicationRows.length ? Math.max(...medicationRows.map((row) => row.id)) + 1 : 1;
      saveRows([...medicationRows, {
        id: nextId,
        clinicalRowId: crypto.randomUUID(),
        orderDate: visitDate,
        medication: '',
        dose: '',
        route: '',
        frequency: '',
        purposeDx: '',
        status: 'New',
        dcdDate: '',
        isFromPreviousAssessment: false,
      }]);
    };
    const processMedicationRowUpdate = (newRow: MedicationRow, oldRow: MedicationRow) => {
      const updatedRow: MedicationRow = {
        ...oldRow,
        ...newRow,
        orderDate: normalizeIsoDate(newRow.orderDate) || oldRow.orderDate || visitDate,
        dcdDate: normalizeIsoDate(newRow.dcdDate),
      };
      if (updatedRow.isFromPreviousAssessment && updatedRow.status === 'New') updatedRow.status = 'Existing';
      if (updatedRow.status !== oldRow.status) {
        if (updatedRow.status === 'Discontinued' && !updatedRow.dcdDate) updatedRow.dcdDate = visitDate;
        if (oldRow.status === 'Discontinued' && updatedRow.status !== 'Discontinued') updatedRow.dcdDate = '';
      }
      saveRows(medicationRows.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
      return updatedRow;
    };
    const medicationCellSx = (value: unknown, fallback = 'Click to edit') => ({
      width: '100%',
      color: hasStoredValue(value) ? 'inherit' : '#999',
      fontStyle: hasStoredValue(value) ? 'normal' : 'italic',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    });
    const medicationGridSx = {
      backgroundColor: '#fafafa',
      border: '1px solid #e0e0e0',
      borderRadius: 2,
      '& .MuiDataGrid-cell': {
        borderRight: '1px solid rgba(224, 224, 224, 1)',
        cursor: 'text',
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word',
        lineHeight: '1.2',
        padding: '8px',
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
          backgroundColor: '#f5f5f5',
        },
        '&:last-child': {
          borderRight: 'none',
        },
      },
      '& .MuiDataGrid-cell[data-field="actions"]': {
        cursor: 'default',
      },
      '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader': {
        backgroundColor: '#702B9D',
        color: 'white',
        fontWeight: 'bold',
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        color: 'white',
        fontWeight: 'bold',
      },
      '& .MuiDataGrid-row:hover': {
        backgroundColor: '#f5f0ff',
      },
      '& .MuiDataGrid-virtualScroller': {
        backgroundColor: 'white',
      },
    };

    return (
      <Stack spacing={2}>
        <Typography color="error" sx={{ mb: 0.5 }}>
          RN must reconcile medications (call pharmacy, proxy, MD or agency if needed)
        </Typography>
        <FormControlLabel
          control={(
            <Checkbox
              checked={notTakingMedications}
              disabled={medicationListLocked}
              onChange={(event) => setField('notTakingMedications', event.target.checked)}
            />
          )}
          label="Patient is not taking any prescribed or PRN medications"
        />
        {fieldErrors.medications && <Alert severity="error">{fieldErrors.medications}</Alert>}
        {medicationRows.length === 0 ? (
          <Box
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              bgcolor: '#fafafa',
              cursor: medicationListLocked || notTakingMedications ? 'default' : 'pointer',
              '&:hover': !(medicationListLocked || notTakingMedications) ? {
                borderColor: '#8600e1',
                backgroundColor: '#f5f0ff',
              } : {},
            }}
            onClick={() => {
              if (!(medicationListLocked || notTakingMedications)) addRow();
            }}
          >
            <MedicationIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>No medications added yet</Typography>
            <Typography variant="body2" color="text.secondary">
              {notTakingMedications
                ? 'No medication information available'
                : "Click here or use the 'Add Medication' button to start adding medications"}
            </Typography>
          </Box>
        ) : (
          <DataGridPremium
            rows={medicationRows}
            getRowId={(row) => row.id}
            columns={[
              {
                field: 'orderDate',
                headerName: 'ORDER DATE',
                flex: 1,
                minWidth: 115,
                editable: false,
                sortable: false,
                renderCell: (params: GridRenderCellParams<MedicationRow>) => (
                  <Box sx={medicationCellSx(params.value, 'No date')}>
                    {params.value ? displayDate(params.value) : 'No date'}
                  </Box>
                ),
              },
              {
                field: 'medication',
                headerName: 'MEDICATION',
                flex: 1.5,
                minWidth: 150,
                editable: true,
                sortable: false,
                renderCell: (params: GridRenderCellParams<MedicationRow>) => <Box sx={medicationCellSx(params.value)}>{params.value || 'Click to edit'}</Box>,
              },
              {
                field: 'dose',
                headerName: 'DOSE',
                flex: 1,
                minWidth: 100,
                editable: true,
                sortable: false,
                renderCell: (params: GridRenderCellParams<MedicationRow>) => <Box sx={medicationCellSx(params.value)}>{params.value || 'Click to edit'}</Box>,
              },
              {
                field: 'route',
                headerName: 'ROUTE',
                flex: 1,
                minWidth: 120,
                editable: true,
                sortable: false,
                type: 'singleSelect',
                valueOptions: MEDICATION_ROUTE_OPTIONS,
                renderCell: (params: GridRenderCellParams<MedicationRow>) => <Box sx={medicationCellSx(params.value)}>{params.value || 'Click to edit'}</Box>,
              },
              {
                field: 'frequency',
                headerName: 'FREQUENCY',
                flex: 1.2,
                minWidth: 110,
                editable: true,
                sortable: false,
                type: 'singleSelect',
                valueOptions: MEDICATION_FREQUENCY_OPTIONS,
                renderCell: (params: GridRenderCellParams<MedicationRow>) => <Box sx={medicationCellSx(params.value)}>{params.value || 'Click to edit'}</Box>,
              },
              {
                field: 'purposeDx',
                headerName: 'PURPOSE/DX',
                flex: 1.3,
                minWidth: 130,
                editable: true,
                sortable: false,
                renderCell: (params: GridRenderCellParams<MedicationRow>) => <Box sx={medicationCellSx(params.value)}>{params.value || 'Click to edit'}</Box>,
              },
              {
                field: 'status',
                headerName: 'STATUS',
                flex: 1,
                minWidth: 100,
                editable: true,
                sortable: false,
                type: 'singleSelect',
                valueOptions: ['Existing', 'Discontinued', 'New'],
                renderCell: (params: GridRenderCellParams<MedicationRow>) => <Box sx={medicationCellSx(params.value, 'Select status')}>{params.value || 'Select status'}</Box>,
              },
              {
                field: 'dcdDate',
                headerName: "DC'D DATE",
                flex: 1,
                minWidth: 100,
                editable: true,
                sortable: false,
                renderCell: (params: GridRenderCellParams<MedicationRow>) => {
                  if ((params.row.status || 'Existing') !== 'Discontinued') return null;
                  return <Box sx={medicationCellSx(params.value, 'Select date')}>{params.value ? displayDate(params.value) : 'Select date'}</Box>;
                },
              },
              {
                field: 'actions',
                headerName: '',
                flex: 0.7,
                minWidth: 30,
                sortable: false,
                filterable: false,
                headerAlign: 'center',
                align: 'center',
                renderCell: (params: GridRenderCellParams<MedicationRow>) => {
                  const canDelete = (params.row.status || 'Existing') === 'New';
                  return (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                      <IconButton
                        color="error"
                        size="small"
                        disabled={medicationListLocked || !canDelete}
                        onClick={() => saveRows(medicationRows.filter((medRow) => medRow.id !== params.row.id))}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                },
              },
            ] satisfies GridColDef<MedicationRow>[]}
            autoHeight
            disableColumnResize
            isCellEditable={(params) => {
              if (disabled) return false;
              if (params.field === 'purposeDx') return true;
              if (purposeDxOnly) return false;
              const status = params.row.status || 'Existing';
              if (params.field === 'status') return true;
              if (params.field === 'actions') return false;
              if (params.field === 'dcdDate') return status === 'Discontinued';
              if (params.field === 'orderDate') return false;
              return status === 'New';
            }}
            processRowUpdate={processMedicationRowUpdate}
            onProcessRowUpdateError={() => undefined}
            disableRowSelectionOnClick
            hideFooter
            disableColumnMenu
            disableColumnSelector
            getRowHeight={() => 'auto'}
            sx={medicationGridSx}
          />
        )}
        {!notTakingMedications && (
          <Box display="flex" justifyContent={{ xs: 'center', sm: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={medicationListLocked}
              onClick={addRow}
              sx={{
                backgroundColor: '#8600e1',
                '&:hover': { backgroundColor: '#6a00b3' },
                width: { xs: '100%', sm: 'auto' },
                maxWidth: { xs: 300, sm: 'none' },
              }}
            >
              Add Medication
            </Button>
          </Box>
        )}
      </Stack>
    );
  };

  const updateFallRiskFactor = (riskFactor: string, checked: boolean) => {
    const currentFactors = selectedValues(formData.fallRiskFactors);
    let nextFactors: string[];
    if (checked) {
      if (riskFactor.includes('Prior History of Falls Within 6 Months')) {
        nextFactors = currentFactors.filter((factor) => !factor.includes('Prior History of Falls Within 6 Months'));
        nextFactors.push(riskFactor);
      } else {
        nextFactors = currentFactors.includes(riskFactor) ? currentFactors : [...currentFactors, riskFactor];
      }
    } else {
      nextFactors = currentFactors.filter((factor) => factor !== riskFactor);
    }
    setFormData((current) => ({
      ...current,
      fallRiskFactors: nextFactors,
      fallRiskScore: String(calculateFallRiskScore(nextFactors)),
    }));
    setFieldErrors((current) => {
      const next = { ...current };
      delete next.fallRiskFactors;
      delete next.fallRiskScore;
      return next;
    });
  };

  const renderFallRiskAssessment = (disabled = false) => {
    const fallRiskField = metadata?.fields['fallRiskFactors'];
    const riskFactors = fallRiskField?.picklistValues || [];
    const selectedFactors = selectedValues(formData.fallRiskFactors);
    const recommendations = selectedValues(formData.anyFallPreventionRecommendations);
    const score = String(formData.fallRiskScore || calculateFallRiskScore(selectedFactors));

    return (
      <Stack spacing={2}>
        <Box>
          <FormLabel component="legend" sx={{ mb: 1.5, fontWeight: 700 }}>
            Risk Factors (Assess 1 point for each Assessment Tool)
          </FormLabel>
          {fieldErrors.fallRiskFactors && <Typography variant="caption" color="error">{fieldErrors.fallRiskFactors}</Typography>}
          <FormGroup>
            {riskFactors.map((riskFactor) => (
              <FormControlLabel
                key={riskFactor}
                control={(
                  <Checkbox
                    checked={selectedFactors.includes(riskFactor)}
                    disabled={disabled}
                    onChange={(event) => updateFallRiskFactor(riskFactor, event.target.checked)}
                    sx={{ color: '#8600e1', '&.Mui-checked': { color: '#8600e1' } }}
                  />
                )}
                label={(
                  <Typography variant="body2" sx={{ fontWeight: 500, display: 'inline' }}>
                    {fallRiskDisplayLabel(riskFactor)}
                    {FALL_RISK_DESCRIPTIONS[riskFactor] && (
                      <Tooltip title={FALL_RISK_DESCRIPTIONS[riskFactor]}>
                        <InfoIcon
                          sx={{
                            ml: 0.5,
                            fontSize: '1rem',
                            color: 'grey',
                            opacity: 0.7,
                            cursor: 'pointer',
                            verticalAlign: 'middle',
                            '&:hover': { opacity: 1 },
                          }}
                        />
                      </Tooltip>
                    )}
                  </Typography>
                )}
                sx={{ alignItems: 'center', mb: 0.5 }}
              />
            ))}
          </FormGroup>
        </Box>

        <Box
          sx={{
            mt: 1,
            p: 2,
            border: '2px solid #8600e1',
            borderRadius: 2,
            bgcolor: 'rgba(134, 0, 225, 0.05)',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
            <Typography variant="h6" fontWeight={700}>TOTAL SCORE: {score}</Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>(A Score Of &gt;= 4 Indicates Risk for Falls)</Typography>
          </Stack>
        </Box>

        <Box>{renderField('anyFallPreventionRecommendations', disabled)}</Box>
        {recommendations.includes('Requires PT referral due to') && <Box>{renderField('requiresPtReferralDueTo', disabled)}</Box>}
        {recommendations.includes('DME/Supply needed') && <Box>{renderField('fallPreventionDmeSupplyNeeded', disabled)}</Box>}
        {recommendations.includes('DME/Supply needs repair') && <Box>{renderField('fallPreventionDmeSupplyNeedsRepair', disabled)}</Box>}
        {recommendations.includes('Needs increase in service hours due to') && <Box>{renderField('needsIncreaseInServiceHoursDueTo', disabled)}</Box>}
        {recommendations.includes('Other') && <Box>{renderField('fallPreventionRecOther', disabled)}</Box>}
      </Stack>
    );
  };

  const pcpStepHasError = () => Boolean(
    saveErrorStep === 'step1'
    || (selectedPcp && validateStep('step1').errors.pcpSelectedAddress)
  );

  const renderNursingNavStatusBar = (completed = false) => {
    const navItems = visibleSteps.map(([key]) => ({
      key,
      label: statusForStep(key),
      stepNumber: activeStepNumber(key),
    }));
    navItems.push({ key: 'completed', label: 'Completed', stepNumber: navItems.length + 1 });
    const activeIndex = completed ? navItems.length - 1 : Math.max(0, navItems.findIndex((item) => item.key === activeStep));
    const matches = isCompactNav || navItems.length > 10;
    const currentActiveLabel = navItems[activeIndex]?.label || statusForStep(activeStep);
    const stepStatuses = typeof formData.stepStatuses === 'object' && formData.stepStatuses !== null && !Array.isArray(formData.stepStatuses)
      ? formData.stepStatuses as Record<string, unknown>
      : {};
    const derivedStepStatus = (stepKey: string, stepNumber: number) => {
      if (!completed && (stepKey === saveErrorStep || (stepKey === 'step1' && pcpStepHasError()))) return 'error';
      const explicitStatus = stepStatuses[stepNumber] || stepStatuses[String(stepNumber)];
      if (explicitStatus) return explicitStatus;
      if (completed) return 'completed';
      if (stepKey === 'completed') return '';
      if (stepKey === activeStep) return '';
      return '';
    };
    const activeGlowSx = {
      position: 'absolute',
      inset: 0,
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,255,255,0.5) 25%, rgba(134,0,225,0.7) 35%, rgba(134,0,225,0.5) 50%, rgba(134,0,225,0.2) 65%, rgba(134,0,225,0.08) 75%, rgba(134,0,225,0.04) 82%, rgba(134,0,225,0.02) 88%, rgba(134,0,225,0.01) 94%, rgba(134,0,225,0) 100%)',
      pointerEvents: 'none',
      zIndex: 0,
    } as const;
    const renderActiveStepIcon = (props: ComponentProps<typeof StepIcon>) => (
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: '6px',
          m: '-6px',
          overflow: 'visible',
        }}
      >
        <Box sx={activeGlowSx} />
        <StepIcon
          {...props}
          sx={{
            color: '#8600e1',
            fontSize: 29,
            position: 'relative',
            zIndex: 1,
          }}
        />
      </Box>
    );

    return (
      <Box className="navStatusBar" sx={{ mb: 3, overflow: 'hidden' }}>
        {matches && (
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 2, fontWeight: 'bold' }}>
            {currentActiveLabel}
          </Typography>
        )}
        <Box
          sx={{
            width: '100%',
            position: 'relative',
            py: 1.5,
            overflow: 'hidden',
          }}
        >
        <Box
          ref={navScrollContainerRef}
          sx={{
            width: '100%',
            overflowX: isCompactNav ? 'auto' : 'hidden',
            overflowY: 'hidden',
            pb: 1,
            pt: 1,
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          <Stepper
            alternativeLabel
            nonLinear={!completed}
            activeStep={activeIndex}
            sx={{
              minWidth: matches ? 'max-content' : '100%',
              overflow: 'visible',
              '& .MuiStep-root': {
                px: '14px',
                overflow: 'visible',
                '@media (max-width:992px)': {
                  px: '12px',
                },
                '@media (max-width:430px)': {
                  px: '10px',
                  mb: '10px',
                },
              },
              '& .MuiStepButton-root': {
                color: '#8900e1',
                overflow: 'visible',
                '&:hover': { color: completed || saving ? '#8900e1' : '#7600c4' },
                '&.Mui-disabled': {
                  color: '#ccc',
                  '& .MuiStepLabel-label': { color: '#ccc' },
                },
              },
              '& .MuiStepLabel-label.Mui-active': {
                color: '#8600e1',
                fontWeight: 700,
              },
              '& .MuiStepLabel-label': {
                display: matches ? 'none' : 'block',
              },
              '& .MuiStepLabel-iconContainer': {
                overflow: 'visible !important',
                position: 'relative',
              },
            }}
          >
            {navItems.map((item, index) => {
              const status = derivedStepStatus(item.key, item.stepNumber);
              const isCompletedStep = status === 'completed';
              const hasError = status === 'error';
              const StepIconComponent = (props: ComponentProps<typeof StepIcon>) => {
                if (props.active && hasError) {
                  return (
                    <Box
                      sx={{
                        position: 'relative',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: '6px',
                        m: '-6px',
                        overflow: 'visible',
                      }}
                    >
                      <Box sx={activeGlowSx} />
                      <CancelIcon
                        sx={{
                          color: (theme) => theme.palette.error.main,
                          fontSize: 28,
                          position: 'relative',
                          zIndex: 1,
                        }}
                      />
                    </Box>
                  );
                }
                if (props.active) return renderActiveStepIcon(props);
                if (hasError) return <CancelIcon sx={{ color: (theme) => theme.palette.error.main, fontSize: 28 }} />;
                if (isCompletedStep) return <CheckCircleIcon sx={{ color: '#8600e1', fontSize: 28 }} />;
                if (status === 'in_progress') {
                  return (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: (theme) => theme.palette.warning.main,
                      }}
                    >
                      <HourglassEmptyRoundedIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                  );
                }
                return <StepIcon {...props} />;
              };
              return (
                <Step
                  key={item.key}
                  ref={index === activeIndex ? navActiveStepRef : null}
                  completed={isCompletedStep}
                >
                  <StepButton
                    aria-label={`${item.label}${hasError ? ' — needs attention' : ''}`}
                    disabled={completed || saving || item.key === 'completed'}
                    onClick={() => {
                      if (!completed && item.key !== 'completed' && item.key !== activeStep) handleStepChange(null, item.key);
                    }}
                  >
                    <StepLabel StepIconComponent={StepIconComponent} error={hasError || undefined}>
                      {item.label}
                    </StepLabel>
                  </StepButton>
                </Step>
              );
            })}
          </Stepper>
        </Box>
        {isCompactNav && (
          <>
            {canScrollNavLeft && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: '-1px',
                  width: '24px',
                  height: '100%',
                  background: (theme) => `linear-gradient(to right, ${theme.palette.background.paper} 20%, transparent 100%)`,
                  pointerEvents: 'none',
                  zIndex: 2,
                }}
              />
            )}
            {canScrollNavRight && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: '-1px',
                  width: '24px',
                  height: '100%',
                  background: (theme) => `linear-gradient(to left, ${theme.palette.background.paper} 20%, transparent 100%)`,
                  pointerEvents: 'none',
                  zIndex: 2,
                }}
              />
            )}
            {canScrollNavLeft && (
              <IconButton
                onClick={() => scrollNav(-200)}
                size="small"
                sx={{
                  position: 'absolute',
                  left: -20,
                  top: 'calc(50% - 8px)',
                  transform: 'translateY(-50%)',
                  zIndex: 3,
                }}
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
            )}
            {canScrollNavRight && (
              <IconButton
                onClick={() => scrollNav(200)}
                size="small"
                sx={{
                  position: 'absolute',
                  right: -20,
                  top: 'calc(50% - 8px)',
                  transform: 'translateY(-50%)',
                  zIndex: 3,
                }}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            )}
          </>
        )}
        </Box>
      </Box>
    );
  };

  const renderPinnedAssessmentHeader = () => {
    if (!assessment || !topNavInfoTarget) return null;
    const patientName = String(formData.patientName || selectedPatient?.name || [selectedPatient?.firstName, selectedPatient?.lastName].filter(Boolean).join(' ') || '').trim();
    const visitType = String(formData.visitType || selectedPatient?.assessmentVisitType || '').trim();
    const visitDate = displayDate(formData.visitDate || selectedPatient?.assessmentVisitDate);
    const aideHours = String(formData.assessmentAideHours || selectedPatient?.assessmentAideHours || '').trim();
    const items = [
      { label: 'Patient', value: patientName },
      { label: 'Visit Type', value: visitType },
      { label: 'Visit Date', value: visitDate },
      { label: 'Aide Hours', value: aideHours },
    ];

    return createPortal(
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          pt: 0.25,
          pb: 1.25,
          bgcolor: '#fff',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'center',
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
            gap: { xs: 0.25, sm: 2 },
            fontSize: { xs: '0.75rem', sm: '0.85rem' },
            color: 'text.primary',
          }}
        >
          {items.map((item) => (
            <Box
              key={item.label}
              sx={{
                whiteSpace: { xs: 'normal', sm: 'nowrap' },
                textAlign: { xs: 'center', sm: 'left' },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {item.label}:
              </Box>{' '}
              <Box component="span">{item.value || '-'}</Box>
            </Box>
          ))}
        </Box>
      </Box>,
      topNavInfoTarget
    );
  };

  const renderTopNavTools = () => {
    if (!assessment || !topNavToolsTarget) return null;
    const hasRecordingText = Boolean(transcriptionText.trim() || summaryText.trim());
    const iconButtonSx = {
      color: '#8900e1',
      border: '1px solid #8900e1',
      width: 32,
      height: 32,
      '&:hover': { bgcolor: 'rgba(137, 0, 225, 0.1)' },
    };

    const controls = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
        {!recording && (
          <Tooltip title="Summary">
            <span>
              <IconButton size="small" onClick={openRecordingTextDialog} disabled={!hasRecordingText} sx={{
                bgcolor: '#8900e1',
                color: 'white',
                width: 32,
                height: 32,
                '&:hover': { bgcolor: '#6a00b3' },
                '&.Mui-disabled': {
                  bgcolor: '#8900e1',
                  color: 'rgba(255,255,255,0.6)',
                },
              }}>
                <AutoAwesomeIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {recording ? (
          <>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: recordingPaused ? 'warning.main' : 'error.main',
                animation: recordingPaused ? 'none' : 'recordingPulse 1.5s ease-in-out infinite',
                display: { xs: 'block', sm: 'none' },
                '@keyframes recordingPulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.4 },
                },
              }}
            />
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: '3px',
                height: 24,
              }}
            >
              {recordingWaveLevels.map((level, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 3,
                    height: `${level}%`,
                    minHeight: 4,
                    borderRadius: 1,
                    bgcolor: recordingPaused ? 'warning.main' : 'error.main',
                    transition: recordingPaused ? 'height 0.3s ease' : 'height 0.05s ease',
                  }}
                />
              ))}
            </Box>
            <Typography
              variant="body2"
              fontFamily="monospace"
              fontWeight="bold"
              color={recordingPaused ? 'warning.main' : 'error.main'}
              sx={{ minWidth: { xs: 36, sm: 50 } }}
            >
              {formatRecordingTime(recordingElapsedSeconds)}
            </Typography>
            <Tooltip title={recordingPaused ? 'Resume recording' : 'Pause recording'}>
              <IconButton
                size="small"
                onClick={toggleRecordingPause}
                sx={{
                  bgcolor: recordingPaused ? 'success.main' : 'warning.main',
                  color: 'white',
                  width: 32,
                  height: 32,
                  '&:hover': { bgcolor: recordingPaused ? 'success.dark' : 'warning.dark' },
                }}
              >
                {recordingPaused ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Stop recording">
              <IconButton
                size="small"
                onClick={stopRecording}
                sx={{
                  bgcolor: 'error.main',
                  color: 'white',
                  width: 32,
                  height: 32,
                  '&:hover': { bgcolor: 'error.dark' },
                }}
              >
                <StopIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Tooltip title={recordingWasStopped ? 'Resume recording' : 'Start recording'}>
            <IconButton size="small" onClick={requestRecordingStart} sx={iconButtonSx}>
              <MicIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Files">
          <IconButton size="small" onClick={() => void handleStepChange(null, 'step11')} sx={iconButtonSx}>
            <FolderIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Hand Off Note">
          <IconButton size="small" onClick={openHandOffNoteDialog} sx={iconButtonSx}>
            <NoteAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={assessmentTicketTooltip}>
          <span>
            <IconButton
              aria-label="Create Nexus Ticket"
              size="small"
              onClick={openTicketDialog}
              disabled={!canCreateAssessmentTicket}
              sx={iconButtonSx}
            >
              <ReportProblemOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );

    return createPortal(controls, topNavToolsTarget);
  };

  const renderRecordingTextDialog = () => {
    const currentText = recordingTextTab === 0 ? summaryText : transcriptionText;
    const showLoading = recordingTextLoading || recordingFinalizing;
    const currentError = recordingError;
    const recordingLoadingMessage = recordingTextTab === 0
      ? (recordingFinalizing || !transcriptionText.trim()
          ? 'Waiting for transcription to complete...'
          : 'Analyzing recording and generating summary...')
      : 'Converting speech to text...';
    return (
      <Dialog
        open={recordingTextDialogOpen}
        onClose={() => setRecordingTextDialogOpen(false)}
        fullScreen={fullScreenRecordingDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: fullScreenRecordingDialog ? 0 : 3,
            background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)',
            border: '1px solid rgba(99, 102, 241, 0.2)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AutoAwesomeIcon sx={{ color: '#8600e1', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
              Recording Analysis
            </Typography>
          </Box>
          <IconButton onClick={() => setRecordingTextDialogOpen(false)} sx={{ color: 'grey.400', '&:hover': { color: 'white' } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <Tabs
            value={recordingTextTab}
            onChange={(_, value) => setRecordingTextTab(value)}
            sx={{
              px: 2,
              '& .MuiTab-root': {
                color: 'grey.500',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                minHeight: 48,
                '&.Mui-selected': { color: '#8600e1' },
              },
              '& .MuiTabs-indicator': { backgroundColor: '#8600e1' },
            }}
          >
            <Tab icon={<SummarizeIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Summary" />
            <Tab icon={<TextSnippetIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Transcription" />
          </Tabs>
        </Box>
        <DialogContent sx={{ pt: 4, pb: 3, minHeight: 300 }}>
          {showLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 8, gap: 2 }}>
              <CircularProgress size={48} sx={{ color: '#6366f1' }} />
              <Typography sx={{ color: 'grey.400' }}>
                {recordingLoadingMessage}
              </Typography>
            </Stack>
          ) : currentError ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 8, gap: 2 }}>
              <Typography variant="h6" sx={{ color: 'warning.main' }}>
                {recordingTextTab === 0 ? 'Summary Unavailable' : 'Transcription Unavailable'}
              </Typography>
              <Typography sx={{ color: 'grey.400', textAlign: 'center' }}>{currentError}</Typography>
            </Stack>
          ) : currentText.trim() ? (
            <Box sx={{ mt: 1 }}>
              {recordingTextTab === 0 ? (
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {currentText.split('\n').filter((line) => line.trim()).map((line, index) => (
                    <Box
                      component="li"
                      key={`${line}-${index}`}
                      sx={{
                        color: 'grey.200',
                        lineHeight: 1.6,
                        fontSize: '0.95rem',
                        fontFamily: '"Inter", "Segoe UI", sans-serif',
                        py: 0.5,
                      }}
                    >
                      {line.trim().startsWith('•') ? line.trim() : `• ${line.trim()}`}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: 'grey.200',
                    lineHeight: 1.7,
                    fontSize: '0.95rem',
                    whiteSpace: 'pre-line',
                    fontFamily: '"Inter", "Segoe UI", sans-serif',
                  }}
                >
                  {currentText}
                </Typography>
              )}
            </Box>
          ) : (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 8, gap: 2 }}>
              <Typography variant="h6" sx={{ color: 'grey.400' }}>
                {recordingTextTab === 0 ? 'No Summary Available' : 'No Transcription Available'}
              </Typography>
              <Typography sx={{ color: 'grey.500', textAlign: 'center' }}>
                {recordingTextTab === 0
                  ? 'Record some audio to generate an AI summary of the conversation.'
                  : 'Record some audio to see the transcription here.'}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Box>
            {currentText.trim() && !showLoading && !currentError && (
              <Button
                onClick={copyRecordingText}
                startIcon={<ContentCopyIcon sx={{ color: '#a0a0a0' }} />}
                sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}
              >
                <span style={{ color: '#a0a0a0' }}>Copy to Clipboard</span>
              </Button>
            )}
          </Box>
          <Button
            onClick={() => setRecordingTextDialogOpen(false)}
            variant="contained"
            sx={{
              bgcolor: '#8600e1',
              '&:hover': { bgcolor: '#6a00b3' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderHandOffNoteDialog = () => (
    <Dialog
      open={handOffNoteDialogOpen}
      onClose={() => setHandOffNoteDialogOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography component="span" variant="h6" sx={{ color: '#8600e1', fontWeight: 600 }}>
          Hand Off Note
        </Typography>
        <IconButton onClick={() => setHandOffNoteDialogOpen(false)} size="small" sx={{ color: 'grey.500' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <TextField
          value={handOffNoteDraft}
          onChange={(event) => {
            setHandOffNoteDraft(event.target.value);
            if (handOffNoteDialogError) setHandOffNoteDialogError('');
          }}
          error={Boolean(handOffNoteDialogError || fieldErrors.handOffNote)}
          helperText={handOffNoteDialogError || fieldErrors.handOffNote || ''}
          fullWidth
          multiline
          rows={8}
          placeholder="Enter hand off notes..."
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: handOffNoteDialogError || fieldErrors.handOffNote ? undefined : '#8600e1',
              },
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button
          onClick={() => setHandOffNoteDialogOpen(false)}
          variant="outlined"
          sx={{
            color: '#8600e1',
            borderColor: '#8600e1',
            '&:hover': {
              borderColor: '#6a00b3',
              bgcolor: 'rgba(134, 0, 225, 0.04)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={saveHandOffNoteDialog}
          variant="contained"
          disabled={saving}
          sx={{
            bgcolor: '#8600e1',
            '&:hover': { bgcolor: '#6a00b3' },
          }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderTicketDialog = () => (
    <Dialog
      open={ticketDialogOpen}
      onClose={closeTicketDialog}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={ticketSubmitting}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <ReportProblemOutlinedIcon sx={{ color: '#8600e1' }} />
        <Typography component="span" variant="h6" sx={{ color: '#8600e1', fontWeight: 600 }}>
          Create Nexus Ticket
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Describe the problem. Nexus will classify and route the ticket, and attach this nursing assessment.
        </Typography>

        {ticketResult ? (
          <Stack spacing={1.5}>
            <Alert severity="success">
              Ticket {ticketResult.ticketId} created: {ticketResult.subject}
            </Alert>
            {!ticketResult.assessmentTagged && (
              <Alert severity="warning">
                {ticketResult.warnings.length > 0
                  ? ticketResult.warnings.join(' ')
                  : 'Ticket created, but Nexus could not attach the NursingAssessment.'}
              </Alert>
            )}
            {ticketResult.assessmentTagged && ticketResult.warnings.length > 0 && (
              <Alert severity="warning">{ticketResult.warnings.join(' ')}</Alert>
            )}
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {ticketError && <Alert severity="error">{ticketError}</Alert>}
            <TextField
              autoFocus
              label="Description"
              value={ticketDescription}
              onChange={(event) => {
                setTicketDescription(event.target.value);
                if (ticketError) setTicketError('');
              }}
              helperText={ticketPortalRequestId
                ? 'Description is locked while this request is retried.'
                : `${ticketDescription.length.toLocaleString()} / ${NURSE_TICKET_DESCRIPTION_LIMIT.toLocaleString()}`}
              fullWidth
              multiline
              minRows={6}
              disabled={ticketSubmitting || Boolean(ticketPortalRequestId)}
              inputProps={{ maxLength: NURSE_TICKET_DESCRIPTION_LIMIT }}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={closeTicketDialog} disabled={ticketSubmitting}>
          {ticketResult || ticketPortalRequestId ? 'Close' : 'Cancel'}
        </Button>
        {!ticketResult && (
          <Button
            onClick={() => void submitAssessmentTicket()}
            variant="contained"
            disabled={ticketSubmitting || !ticketDescription.trim()}
            sx={{ bgcolor: '#8600e1', '&:hover': { bgcolor: '#6a00b3' } }}
          >
            {ticketSubmitting
              ? <CircularProgress size={20} color="inherit" />
              : ticketPortalRequestId
                ? 'Retry Ticket Request'
                : 'Submit Ticket'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  const renderDocumentPreviewDialog = () => (
    <Dialog open={Boolean(viewingDocument)} onClose={closeDocumentPreview} maxWidth="lg" fullWidth>
      <DialogTitle>{viewingDocument?.documentName || 'Document'}</DialogTitle>
      <DialogContent dividers sx={{ minHeight: { xs: 360, md: 620 }, p: 0 }}>
        {viewingDocumentLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: { xs: 360, md: 620 } }}>
            <CircularProgress />
          </Stack>
        ) : viewingDocumentUrl ? (
          <Box
            component="iframe"
            src={viewingDocumentUrl}
            title={viewingDocument?.documentName || 'Document'}
            sx={{ width: '100%', height: { xs: 360, md: 620 }, border: 0, display: 'block' }}
          />
        ) : (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: { xs: 360, md: 620 } }}>
            <Typography color="text.secondary">Document preview is not available.</Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {viewingDocument && (
          <Button onClick={() => downloadDocument(viewingDocument)}>
            Download
          </Button>
        )}
        <Button onClick={closeDocumentPreview}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  const renderPcpAddressDialog = () => (
    <Dialog open={pcpAddressDialog.open} onClose={closePcpAddressEditor} maxWidth="sm" fullWidth>
      <DialogTitle>{pcpAddressDialog.addressType === 'Mailing' ? 'Edit Primary Office' : pcpAddressDialog.addressType ? `Edit ${pcpAddressDialog.addressType}` : 'Edit Doctor Address'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {pcpAddressError && <Alert severity="error">{pcpAddressError}</Alert>}
          {pcpAddressDialog.addressType === 'Mailing' ? (
            <>
              {([
                ['street', 'Street'], ['aptSuite', 'Apt / Suite'], ['city', 'City'], ['state', 'State'], ['zipCode', 'ZIP Code'],
              ] as const).map(([field, label]) => (
                <TextField key={field} label={label} value={pcpAddressForm[field]}
                  onChange={(event) => setPcpAddressForm((current) => ({ ...current, [field]: event.target.value }))}
                  fullWidth disabled={pcpAddressSaving} />
              ))}
            </>
          ) : <TextField
            label="Address"
            value={pcpAddressForm.address}
            onChange={(event) => setPcpAddressForm((current) => ({ ...current, address: event.target.value }))}
            fullWidth
            multiline
            minRows={2}
            disabled={pcpAddressSaving}
          />}
          <TextField
            label="Phone"
            value={pcpAddressForm.phone}
            onChange={(event) => setPcpAddressForm((current) => ({ ...current, phone: event.target.value }))}
            fullWidth
            disabled={pcpAddressSaving}
          />
          <TextField
            label="Fax"
            value={pcpAddressForm.fax}
            onChange={(event) => setPcpAddressForm((current) => ({ ...current, fax: event.target.value }))}
            fullWidth
            disabled={pcpAddressSaving}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={closePcpAddressEditor} disabled={pcpAddressSaving}>Cancel</Button>
        <Button onClick={savePcpAddress} variant="contained" disabled={pcpAddressSaving}>
          {pcpAddressSaving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const canEditCompletedAssessment = canEditCompletedNursingAssessment({
    isNurse: hasRole(user, 'nurse'),
    currentNurseId: user?.nexusUser?.nexusId || user?.username,
    assessmentNurse: assessment?.nurse ?? formData.nurse,
    approvalStatus: formData.approvalStatus,
  });

  const renderCompletedSummary = () => {
    const visibleDocuments = documents;
    return (
      <Container maxWidth="lg" sx={{ mt: 3, mb: 8 }}>
        <Paper elevation={3} sx={{ p: { xs: 1.5, md: 2 }, mb: 10, bgcolor: 'rgba(255,255,255,1)' }}>
          <Box sx={{ mb: 3 }}>
            {renderNursingNavStatusBar(true)}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 3, mb: 3 }}>
            <Typography variant="h4" component="h1" align="center" gutterBottom>
              Your nursing assessment is complete!
            </Typography>
            <Typography variant="subtitle1" align="center" color="text.secondary" gutterBottom>
              Assessment Summary
            </Typography>
          </Box>

          <Box
            sx={{
              m: { xs: 0.5, sm: 1 },
              ml: { xs: 1 },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Stack spacing={2}>
              {visibleSteps.map(([stepKey, step]) => (
                <Accordion key={stepKey} defaultExpanded={activeStepNumber(stepKey) === 1}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">{`${activeStepNumber(stepKey)}. ${step.title}`}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {Object.entries(step.cards).map(([cardKey, card]) => {
                    const hasConsentEditor = card.fields.includes('consent');
                    const hasFallRiskEditor = card.fields.includes('fallRiskFactors');
                    const visibleFields = card.fields.filter((fieldName) => (
                      isFieldVisible(fieldName, formData) && !(hasConsentEditor && CONSENT_EMBEDDED_FIELDS.has(fieldName))
                      && !(hasFallRiskEditor && FALL_RISK_EMBEDDED_FIELDS.has(fieldName))
                      && !COMPLETED_SUMMARY_HIDDEN_FIELDS.has(fieldName)
                    ));
                    if (visibleFields.length === 0) return null;
                    return (
                      <Card key={cardKey} variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                            {card.title}
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                              gap: 1.5,
                            }}
                          >
                            {visibleFields.map((fieldName) => {
                              if (fieldName === 'planOfCareTasks') {
                                const tasks = parsePlanOfCareTasks(formData[fieldName]);
                                return (
                                  <Box key={fieldName} sx={{ gridColumn: '1 / -1' }}>
                                    <Typography variant="body2" fontWeight={700}>Plan of Care Tasks</Typography>
                                    {tasks.length === 0 ? (
                                      <Typography variant="body2" color="text.secondary">Not provided</Typography>
                                    ) : (
                                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                                        {tasks.map((task) => (
                                          <Typography key={task.task} variant="body2">
                                            {`${task.task} (${task.min || '-'}-${task.max || '-'})${task.instructions ? `: ${task.instructions}` : ''}`}
                                          </Typography>
                                        ))}
                                      </Stack>
                                    )}
                                  </Box>
                                );
                              }
                              if (fieldName === 'consent') {
                                const rows = parseConsentRows(formData[fieldName], formData, consentContractName);
                                return (
                                  <Box key={fieldName} sx={{ gridColumn: '1 / -1' }}>
                                    <Typography variant="body2" fontWeight={700}>Consent Services</Typography>
                                    {rows.length === 0 ? (
                                      <Typography variant="body2" color="text.secondary">Not provided</Typography>
                                    ) : (
                                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                                        {rows.map((row) => (
                                          <Typography key={row.id} variant="body2">
                                            {`${row.service} - ${row.frequency}: ${row.fee || '-'} | Coverage: ${row.expectedCoverage || '-'} | Responsibility: ${row.patientResponsibility || '-'}`}
                                          </Typography>
                                        ))}
                                      </Stack>
                                    )}
                                  </Box>
                                );
                              }
                              if (fieldName === 'primaryDx' || fieldName === 'otherPertinentDiagnosis') {
                                const rows = parseDiagnosisRows(formData[fieldName], fieldName === 'primaryDx');
                                return (
                                  <Box key={fieldName} sx={{ gridColumn: '1 / -1' }}>
                                    <Typography variant="body2" fontWeight={700}>
                                      {fieldName === 'primaryDx' ? 'Primary Dx' : 'Other Pertinent Diagnosis'}
                                    </Typography>
                                    {rows.length === 0 ? (
                                      <Typography variant="body2" color="text.secondary">Not provided</Typography>
                                    ) : rows.map((row) => (
                                      <Typography key={row.id} variant="body2" color="text.secondary">
                                        {[row.icdCode, row.description, displayDate(row.date)].filter(Boolean).join(' - ') || 'Not provided'}
                                      </Typography>
                                    ))}
                                  </Box>
                                );
                              }
                              if (fieldName === 'medications') {
                                const rows = parseMedicationRows(formData[fieldName]);
                                return (
                                  <Box key={fieldName} sx={{ gridColumn: '1 / -1' }}>
                                    <Typography variant="body2" fontWeight={700}>Medications</Typography>
                                    {rows.length === 0 ? (
                                      <Typography variant="body2" color="text.secondary">Not provided</Typography>
                                    ) : rows.map((row) => (
                                      <Typography key={row.id} variant="body2" color="text.secondary">
                                        {[row.medication, row.dose, row.route, row.frequency, row.status].filter(Boolean).join(' - ')}
                                      </Typography>
                                    ))}
                                  </Box>
                                );
                              }
                              if (fieldName === 'fallRiskFactors') {
                                return (
                                  <Box key={fieldName} sx={{ gridColumn: '1 / -1' }}>
                                    <Typography variant="body2" fontWeight={700}>Fall Risk Assessment</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {`Risk Factors: ${displayFieldValue(formData.fallRiskFactors)}`}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {`Total Score: ${formData.fallRiskScore || '0'}`}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {`Fall Prevention Recommendations: ${displayFieldValue(formData.anyFallPreventionRecommendations)}`}
                                    </Typography>
                                  </Box>
                                );
                              }
                              return (
                                <Box key={fieldName}>
                                  <Typography variant="body2" fontWeight={700}>
                                    {metadata ? getFieldLabel(fieldName, metadata.fields) : fieldName}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {displayFieldValue(formData[fieldName])}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                  {activeStepNumber(stepKey) === 1 && (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                          Consent Summary
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                            gap: 1.5,
                          }}
                        >
                          {[
                            ['Consent Type', formData.consentType],
                            ['Consent discussed with and signed by', formData.consentSignedBy],
                            ['Patient Email', formData.patientEmail],
                            ['Proxy Name', formData.proxyName],
                            ['Proxy Relationship', formData.proxyRelationship],
                            ['Proxy Phone', formData.proxyPhone],
                            ['Contract Types', displayFieldValue(formData.contractTypes)],
                            ['Days per Week', formData.daysPerWeek],
                            ['Hours per Day', formData.hoursPerDay],
                            ['Days per Week 2', formData.daysPerWeek2],
                            ['Hours per Day 2', formData.hoursPerDay2],
                            ['Days per Week 3', formData.daysPerWeek3],
                            ['Hours per Day 3', formData.hoursPerDay3],
                            ['Finance Clearance Received', formData.financeClearanceReceived],
                          ].filter(([, value]) => !isEmptyValue(value)).map(([label, value]) => (
                            <Box key={String(label)}>
                              <Typography variant="body2" fontWeight={700}>{String(label)}</Typography>
                              <Typography variant="body2" color="text.secondary">{displayFieldValue(value)}</Typography>
                            </Box>
                          ))}
                        </Box>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" fontWeight={700}>Authorized Consent Services</Typography>
                          {consentRows.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No consent services recorded.</Typography>
                          ) : (
                            <Stack spacing={1} sx={{ mt: 1 }}>
                              {consentRows.map((row) => (
                                <Card key={row.id} variant="outlined" sx={{ bgcolor: '#f9f9f9' }}>
                                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Box
                                      sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                        gap: 1,
                                      }}
                                    >
                                      <Typography variant="body2"><strong>Service:</strong> {row.service || 'Not provided'}</Typography>
                                      <Typography variant="body2"><strong>Frequency:</strong> {row.frequency || 'Not provided'}</Typography>
                                      <Typography variant="body2"><strong>Fee:</strong> {row.fee || 'Not provided'}</Typography>
                                      <Typography variant="body2"><strong>Weekend/Holiday Fee:</strong> {row.weekendFee || 'Not provided'}</Typography>
                                      <Typography variant="body2"><strong>Expected Insurance Coverage:</strong> {row.expectedCoverage || 'Not provided'}</Typography>
                                      <Typography variant="body2"><strong>Patient&apos;s Financial Responsibility:</strong> {row.patientResponsibility || 'Not provided'}</Typography>
                                    </Box>
                                  </CardContent>
                                </Card>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                </Stack>
              </AccordionDetails>
                </Accordion>
              ))}

              <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center">
                <FolderIcon sx={{ mr: 1, color: '#8600e1' }} />
                <Typography variant="h6">Documents</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ position: 'relative', minHeight: 200 }}>
                {documentsLoading && (
                  <Backdrop
                    open
                    sx={{
                      backgroundColor: 'white',
                      color: '#330662',
                      zIndex: (theme) => theme.zIndex.drawer + 2,
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Typography sx={{ mb: 1 }}>Generating documents...</Typography>
                    <CircularProgress color="inherit" />
                  </Backdrop>
                )}

                {!documentsLoading && visibleDocuments.map((document) => {
                  const isExpanded = expandedDocumentIds.includes(document.id);
                  const documentTitle = (document.documentName || `Document ${document.id}`).replace(/\.pdf$/i, '');
                  return (
                    <Box key={document.id} mt={3}>
                      <Box
                        onClick={() => void toggleCompletedDocument(document)}
                        sx={{ width: '100%', position: 'relative', cursor: 'pointer' }}
                      >
                        <Paper
                          elevation={3}
                          sx={{
                            p: 2,
                            mb: 0,
                            bgcolor: '#330662',
                            color: 'white',
                            borderRadius: '8px 8px 0 0',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                              alignItems: 'center',
                              gap: 1,
                              textAlign: { xs: 'center', sm: 'left' },
                            }}
                          >
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              {documentTitle}
                            </Typography>
                            <Button
                              variant="contained"
                              color="inherit"
                              sx={{ color: '#330662', fontWeight: 'bold', bgcolor: 'white' }}
                              onClick={(event) => {
                                event.stopPropagation();
                                void downloadDocument(document);
                              }}
                            >
                              Download PDF
                            </Button>
                          </Box>
                        </Paper>
                        <Paper
                          elevation={2}
                          sx={{
                            mt: '-2px',
                            height: 20,
                            width: '100%',
                            borderRadius: '0 0 8px 8px',
                            bgcolor: 'rgba(51, 6, 98, 0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <ExpandMoreIcon
                            sx={{
                              color: 'white',
                              stroke: '#330662',
                              strokeWidth: 0.5,
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease-in-out',
                            }}
                          />
                        </Paper>
                      </Box>
                      {isExpanded && (
                        <Paper
                          elevation={1}
                          sx={{
                            p: 2,
                            display: 'flex',
                            justifyContent: 'center',
                            bgcolor: '#f5f5f5',
                            borderRadius: '8px',
                          }}
                        >
                          {documentPreviewLoading[document.id] ? (
                            <CircularProgress />
                          ) : documentPreviewUrls[document.id] ? (
                            <Box
                              component="iframe"
                              src={documentPreviewUrls[document.id]}
                              title={documentTitle}
                              sx={{ width: '100%', height: { xs: 420, md: 720 }, border: 0, display: 'block' }}
                            />
                          ) : (
                            <CircularProgress />
                          )}
                        </Paper>
                      )}
                    </Box>
                  );
                })}

                {!documentsLoading && visibleDocuments.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No documents found.
                    </Typography>
                  </Box>
                )}
              </Box>
            </AccordionDetails>
              </Accordion>
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4, mb: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<ReportProblemOutlinedIcon />}
                onClick={openTicketDialog}
                disabled={!canCreateAssessmentTicket}
                sx={{
                  borderColor: '#8600e1',
                  color: '#8600e1',
                  px: 3,
                  py: 1.5,
                  '&:hover': { borderColor: '#6a00b3', color: '#6a00b3' },
                }}
              >
                Create Nexus Ticket
              </Button>
              {canEditCompletedAssessment && (
                <Button
                  variant="contained"
                  disabled={saving}
                  onClick={reopenCompletedAssessment}
                  sx={{
                    bgcolor: '#8600e1',
                    '&:hover': { bgcolor: '#6a00b3' },
                    px: 4,
                    py: 1.5,
                  }}
                >
                  {saving ? <CircularProgress size={20} color="inherit" /> : 'Edit Assessment'}
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => {
                  localStorage.removeItem(NURSING_SELECTED_PATIENT_KEY);
                  restoredPatientRef.current = false;
                  setAssessment(null);
                  setSelectedPatient(null);
                  setFormData({});
                  setUnableToAssessVitalsChecked(false);
                  setDocuments([]);
                  setIsSubmitted(false);
                }}
                sx={{
                  bgcolor: '#8600e1',
                  '&:hover': { bgcolor: '#6a00b3' },
                  px: 4,
                  py: 1.5,
                }}
              >
                Return to Setup
              </Button>
            </Box>
          </Box>
          {renderPcpAddressDialog()}
        </Paper>
      </Container>
    );
  };

  const renderConsentPrerequisite = () => {
    const contractTypes = selectedValues(formData.contractTypes);
    const isDigitalConsent = fieldValueEquals(formData.consentType, 'Digital');
    const isPaperConsent = fieldValueEquals(formData.consentType, 'Paper');
    const hasContractSelected = contractTypes.includes('Contract');
    const hasPrivatePayHours = contractTypes.some((type) => type === 'PP' || type === 'PP (1 to 1)');
    const hasClusterContract = hasPpClusterContract(contractTypes);
    const clientName = selectedPatient ? patientLabel(selectedPatient) : '';

    return (
      <>
        <Container maxWidth="lg" sx={{ mt: 5, mb: 10 }}>
          <Paper
          elevation={3}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            bgcolor: 'rgba(255,255,255,1)',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <ShieldIcon sx={{ color: '#8600e1', fontSize: 32 }} />
            <Box>
              <Typography variant="h5" component="h1" fontWeight={700} sx={{ color: '#333' }}>
                Consent Information
              </Typography>
              {clientName && (
                <Typography variant="body2" color="text.secondary">
                  Client: {clientName}
                </Typography>
              )}
            </Box>
          </Box>

          {consentPrerequisiteStep === 1 ? (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Please complete the consent information before proceeding to the assessment.
              </Typography>

              {renderField('consentType')}

              {isPaperConsent && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Paper consent selected. No additional consent fields are required. You may proceed to the assessment.
                </Alert>
              )}

              {isDigitalConsent && (
                <>
                  {renderField('contractTypes', Boolean(selectedPatient?.assessmentContractTypes))}
                  {hasClusterContract && renderField('clusterPackages', false, true)}
                  {hasContractSelected && renderConsentScheduleSection('Contract PCA/HHA Hours')}
                  {hasPrivatePayHours && renderConsentScheduleSection('Private Pay PCA/HHA Hours', true)}
                  {renderConsentSignerFields()}

                  <Card variant="outlined" sx={{ mb: 2, mt: 3 }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <AssignmentIcon sx={{ mb: 1, mr: 1, color: '#8600e1' }} />
                        <Typography variant="h6" gutterBottom>Consent Services</Typography>
                      </Box>
                      <Alert
                        severity="info"
                        icon={false}
                        sx={{
                          mb: 2,
                          p: 2,
                          bgcolor: '#f0e6ff',
                          border: '1px solid #8900e1',
                          color: 'text.secondary',
                          '& .MuiAlert-message': { width: '100%' },
                        }}
                      >
                        <strong>How it works:</strong>{' '}
                        {contractTypes.length > 0
                          ? 'Select a service from the dropdown below to add it to the consent table. Some services may require you to enter the frequency or fee details after adding them.'
                          : 'First select Contract Types above, then service options will become available in the dropdown below.'}
                      </Alert>
                      {renderConsentServices(false, false)}
                    </CardContent>
                  </Card>
                </>
              )}
            </Stack>
          ) : (
            renderConsentSignatureStep()
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3 }}>
            <Button
              variant="text"
              onClick={consentPrerequisiteStep === 1 ? exitConsentPrerequisite : goToConsentDetailsStep}
              disabled={saving}
              sx={{
                color: '#8600e1',
                '&:hover': { color: '#6a00b3', backgroundColor: 'rgba(134, 0, 225, 0.04)' },
              }}
            >
              {consentPrerequisiteStep === 1 ? 'Exit' : 'Back'}
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={consentPrerequisiteStep === 1 && !isPaperConsent ? goToConsentSignatureStep : continueFromConsentPrerequisite}
              disabled={saving}
              sx={{
                px: 4,
              }}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : consentPrerequisiteStep === 1 && !isPaperConsent ? 'Next' : 'Continue to Assessment'}
            </Button>
          </Box>
          </Paper>
        </Container>
      </>
    );
  };

  const renderPatientEvaluationNeeds = () => (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            Patient Needs
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            {['specialNeeds', 'specialNeedsOther', 'skilledNeeds', 'skilledNeedsPerformedBy']
              .filter((fieldName) => isFieldVisible(fieldName, formData))
              .map((fieldName) => <Box key={fieldName}>{renderField(fieldName)}</Box>)}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  const renderPatientEvaluationAdlPace = () => (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            ADL Completion Pace
          </Typography>
          {renderField('adlCompletionPace')}
        </Stack>
      </CardContent>
    </Card>
  );

  const renderPatientEvaluationOutcome = () => (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            Patient Evaluation Outcome
          </Typography>
          {['patientEvaluationOutcome', 'acceptedServiceForPatient', 'notTakenUnderCareSafetyConcern', 'nursingProgressNote']
            .filter((fieldName) => isFieldVisible(fieldName, formData))
            .map((fieldName) => <Box key={fieldName}>{renderField(fieldName)}</Box>)}
        </Stack>
      </CardContent>
    </Card>
  );

  const renderRevisitPostHospitalization = () => (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            Post-Hospitalization Details
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            {[
              'hospitalAdmissionDate',
              'hospitalDischargeDate',
              'hospitalOrRehabName',
              'dcSummaryObtainedReviewed',
              'briefHospitalCourse',
              'commentsOrAideInstructions',
            ].map((fieldName) => (
              <Box
                key={fieldName}
                sx={['briefHospitalCourse', 'commentsOrAideInstructions'].includes(fieldName) ? { gridColumn: '1 / -1' } : undefined}
              >
                {renderField(fieldName)}
              </Box>
            ))}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  const renderRevisitPostFall = () => (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            Post-Fall Education
          </Typography>
          {renderField('fallPreventionEducation')}
        </Stack>
      </CardContent>
    </Card>
  );

  const renderField = (fieldName: string, disabledOverride = false, requiredOverride = false) => {
    if (fieldName === ADDRESS_AUTOCOMPLETE_FIELD) {
      return (
        <AddressAutocomplete
          label="Search Address"
          onAddressSelect={(address) => {
            setFormData((current) => ({
              ...current,
              street: address.street,
              aptSuite: address.aptSuite,
              city: address.city,
              state: address.state,
              zipCode: address.zipCode,
            }));
            setFieldErrors((current) => {
              const next = { ...current };
              delete next.street;
              delete next.aptSuite;
              delete next.city;
              delete next.state;
              delete next.zipCode;
              return next;
            });
          }}
        />
      );
    }

    if (fieldName === PETS_IN_HOME_READONLY_FIELD) {
      if (!fieldValueIncludes(formData.medicalSuppliesPetsInfo, 'Pets')) return null;
      return (
        <TextField
          label="Pets in Home (from Patient Information)"
          value={String(formData.petsInHomeList || 'No pet information provided')}
          fullWidth
          multiline
          InputProps={{ readOnly: true }}
          sx={readOnlyTextFieldSx}
        />
      );
    }

    if (fieldName === HOYER_CONTRACTURE_READONLY_FIELD) {
      if (!fieldValueEquals(formData.hoyerSevereContractures, 'Yes')) return null;
      return renderField('contracture', true);
    }

    const field = metadata?.fields[fieldName];
    if (!field) return null;
    const value = formData[fieldName] ?? '';
    const label = metadata ? getFieldLabel(fieldName, metadata.fields) : fieldName;
    const dataType = field.dataType;
    let options = field?.picklistValues || [];
    if (fieldName === 'reproductiveSymptoms') {
      if (fieldValueEquals(formData.gender, 'Female')) {
        options = options.filter((option) => !['Prostate Disorder', 'Scrotal Edema'].includes(option));
      } else if (fieldValueEquals(formData.gender, 'Male')) {
        options = options.filter((option) => !['Mastectomy: L', 'Mastectomy: R'].includes(option));
      }
    }
    if (IADL_RADIO_FIELDS.has(fieldName)) {
      options = [
        ...options.filter((option) => fieldValueEquals(option, 'Independent')),
        ...options.filter((option) => !fieldValueEquals(option, 'Independent')),
      ];
    }
    const fieldError = fieldErrors[fieldName];
    const fieldHasError = Boolean(fieldError)
      || isVitalOutOfRange(fieldName, formData)
      || isBloodSugarFieldOutOfRange(fieldName, formData)
      || isIntegumentColorOutOfRange(fieldName, formData)
      || isEnvironmentalIssueFieldOut(fieldName, formData);
    const disabled = disabledOverride || isFieldLockedForRevisit(fieldName, formData);
    const required = requiredOverride
      || requiredFieldsForStep(activeStepNumber(activeStep), dataWithUiValidationState(formData)).includes(fieldName);

    if (fieldName === 'unableToAssessVitals') {
      const selectedReason = picklistOptionForValue(value, options);
      return (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: unableToAssessVitalsActive ? 'repeat(2, minmax(0, 1fr))' : '1fr' },
            gap: 2,
            alignItems: 'center',
            width: '100%',
          }}
        >
          <FormControlLabel
            control={(
              <Checkbox
                checked={unableToAssessVitalsActive}
                disabled={disabled}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setUnableToAssessVitalsChecked(checked);
                  if (!checked) setField('unableToAssessVitals', '');
                  setFieldErrors((current) => {
                    const next = { ...current };
                    [
                      'bloodPressure',
                      'pulseRate',
                      'respirationRate',
                      'temp',
                      'heightFt',
                      'heightIn',
                      'weight',
                      'anyChangeToPatientWeight',
                      'anyChangeToPatientWeightExplain',
                      'unableToAssessVitals',
                    ].forEach((key) => delete next[key]);
                    return next;
                  });
                }}
              />
            )}
            label="Unable to assess vital signs"
          />
          {unableToAssessVitalsActive && (
            <FormControl fullWidth error={Boolean(fieldError)} required>
              <InputLabel>Unable to assess vital signs due to</InputLabel>
              <Select
                value={options.some((option) => fieldValueEquals(option, selectedReason)) ? selectedReason : ''}
                label="Unable to assess vital signs due to"
                disabled={disabled}
                onChange={(event) => setField('unableToAssessVitals', event.target.value)}
              >
                <MenuItem value=""><em>Select an option</em></MenuItem>
                {options.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
              {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
            </FormControl>
          )}
        </Box>
      );
    }

    if (fieldName === 'planOfCareTasks') {
      return renderPlanOfCareTasks(disabled);
    }

    if (fieldName === 'consent') {
      return renderConsentServices();
    }

    if (fieldName === 'Signature') {
      return renderNurseSignature();
    }

    if (fieldName === 'primaryDx' || fieldName === 'otherPertinentDiagnosis') {
      return renderDiagnosisRows(fieldName, disabled);
    }

    if (fieldName === 'medications') {
      return renderMedications(disabled);
    }

    if (fieldName === 'fallRiskFactors') {
      return renderFallRiskAssessment(disabled);
    }

    if (fieldName === 'fallRiskScore') {
      return (
        <TextField
          label={label}
          value={String(value || '0')}
          fullWidth
          required={required}
          InputProps={{ readOnly: true }}
        />
      );
    }

    if (fieldName === 'patientInitials') {
      return (
        <TextField
          label={label}
          value={patientInitialsFromSources(formData, selectedPatient) || String(value || '')}
          error={Boolean(fieldError)}
          helperText={fieldError}
          fullWidth
          required={required}
          InputProps={{ readOnly: true }}
          sx={readOnlyTextFieldSx}
        />
      );
    }

    if (fieldName === 'age') {
      return (
        <TextField
          label={label}
          value={ageFromDateOfBirth(formData.dateOfBirth)}
          fullWidth
          InputProps={{ readOnly: true }}
          placeholder="Age will be calculated automatically"
          sx={readOnlyTextFieldSx}
        />
      );
    }

    if (fieldName === 'nextVisit') {
      return (
        <TextField
          label={label}
          value={String(value)}
          error={Boolean(fieldError)}
          helperText={fieldError}
          fullWidth
          required={required}
          InputProps={{ readOnly: true }}
        />
      );
    }

    if (fieldName === 'priorityCode') {
      return (
        <TextField
          label="Priority Code (Auto-calculated)"
          value={String(value || '')}
          fullWidth
          required={required}
          helperText={fieldError || 'Calculated based on safety risk assessment'}
          error={Boolean(fieldError)}
          InputProps={{ readOnly: true }}
          sx={readOnlyTextFieldSx}
        />
      );
    }

    if (fieldName === 'pcp') {
      const selectedDoctor = pcpOptions.find((doctor) => getDoctorId(doctor) === String(value)) || null;
      if (selectedDoctor) {
        const details = pcpDetailParts(selectedDoctor);
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {label}{required && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                p: 2,
                bgcolor: 'rgba(134, 0, 225, 0.04)',
                borderRadius: 1,
                border: fieldError ? '1px solid #d32f2f' : '1px solid rgba(134, 0, 225, 0.2)',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#8600e1' }}>
                  {pcpLabel(selectedDoctor)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {details.join(' • ')}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                disabled={disabled}
                onClick={() => {
                  setFormData((current) => ({ ...current, pcp: '', pcpSelectedAddress: '', pcpOfficeSelection: '' }));
                }}
                sx={{
                  color: '#8600e1',
                  borderColor: '#8600e1',
                  '&:hover': {
                    borderColor: '#6a00b3',
                    bgcolor: 'rgba(134, 0, 225, 0.04)',
                  },
                }}
              >
                Change
              </Button>
            </Box>
            {fieldError && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 1.75 }}>{fieldError}</Typography>}
          </Box>
        );
      }
      return (
        <Autocomplete
          options={pcpOptions}
          value={selectedDoctor}
          inputValue={pcpSearchInput}
          loading={pcpLoading}
          disabled={disabled}
          getOptionLabel={pcpLabel}
          filterOptions={(options) => options}
          isOptionEqualToValue={(option, selected) => getDoctorId(option) === getDoctorId(selected)}
          noOptionsText={pcpSearchInput.trim().length < 3 ? 'Type at least 3 characters to search' : 'No results found'}
          onInputChange={(_, inputValue, reason) => {
            if (reason === 'reset') return;
            setPcpSearchInput(inputValue);
            if (reason !== 'input' && reason !== 'clear') return;
            const search = inputValue.trim();
            if (search.length >= 3) {
              void searchPcps(search);
            } else {
              setPcpOptions([]);
            }
          }}
          onChange={(_, doctor) => {
            const doctorId = doctor ? getDoctorId(doctor) : '';
            const offices = pcpOffices(doctorId, doctor);
            const office = offices.length === 1 ? offices[0] : null;
            setPcpSearchInput('');
            setFormData((current) => ({
              ...current,
              pcp: doctorId,
              pcpSelectedAddress: office?.address || '',
              pcpOfficeSelection: office ? JSON.stringify(office) : '',
            }));
            setFieldErrors((current) => {
              const next = { ...current };
              delete next.pcp;
              delete next.pcpSelectedAddress;
              return next;
            });
          }}
          renderOption={(props, option) => (
            <li {...props} key={getDoctorId(option)}>
              <Box>
                <Typography variant="body1">{pcpLabel(option)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {pcpDetailParts(option).join(' • ')}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              error={Boolean(fieldError)}
              helperText={fieldError || 'Type at least 3 characters to search by name, phone, or NPI'}
              required={required}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {pcpLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      );
    }

    if (fieldName === 'pcpSelectedAddress') {
      const addressEntries = doctorAddressEntries(selectedPcp);
      const offices = pcpOffices(selectedPcp ? getDoctorId(selectedPcp) : '', selectedPcp);
      const savedOffice = parsePcpOffice(formData.pcpOfficeSelection);
      const currentOffice = offices.find(office => samePcpOffice(office, savedOffice));
      const nextEmptyAddressType: 'Address 2' | 'Address 3' | null = selectedPcp && !String(selectedPcp.doctorAddress2 || '').trim()
        ? 'Address 2'
        : selectedPcp && !String(selectedPcp.doctorAddress3 || '').trim()
          ? 'Address 3'
          : null;
      return (
        <Stack spacing={1}>
          <Box
            id="nursing-pcp-office"
            sx={{
              mt: 2,
              p: 2,
              border: fieldError ? '1px solid #d32f2f' : '1px solid #e0e0e0',
              borderRadius: 1,
              backgroundColor: '#fafafa',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              {label}{required && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
            </Typography>
            {fieldError && <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>{fieldError}</Typography>}
            {Boolean(formData.pcp) && !selectedPcp && <Typography role="status" variant="body2">Loading PCP offices…</Typography>}
            <RadioGroup
              value={currentOffice?.slot || ''}
              onChange={(event) => {
                const office = offices.find(item => item.slot === event.target.value);
                if (office) setFormData(current => ({ ...current, pcpSelectedAddress: office.address, pcpOfficeSelection: JSON.stringify(office) }));
              }}
            >
              {addressEntries.map((entry) => (
                <Box key={entry.key} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                  <FormControlLabel
                    value={entry.key === 'Mailing' ? 'PRIMARY' : entry.key === 'Address 2' ? 'OFFICE_2' : 'OFFICE_3'}
                    control={<Radio size="small" disabled={disabled} />}
                    label={(
                      <Box>
                        <Typography variant="body2" color="text.secondary">{entry.address || 'No address provided'}</Typography>
                        {(entry.phone || entry.fax) && (
                          <Typography variant="caption" color="text.disabled">
                            {[entry.phone ? `Phone: ${entry.phone}` : null, entry.fax ? `Fax: ${entry.fax}` : null].filter(Boolean).join(' - ')}
                          </Typography>
                        )}
                      </Box>
                    )}
                    sx={{ flex: 1 }}
                  />
                  {!disabled && (
                    <IconButton size="small" aria-label={`Edit ${entry.label}`} onClick={() => openPcpAddressEditor(entry.key as 'Mailing' | 'Address 2' | 'Address 3')} sx={{ ml: 1 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </RadioGroup>
            {nextEmptyAddressType && !disabled && (
              <Button size="small" variant="outlined" onClick={() => openPcpAddressEditor(nextEmptyAddressType)} sx={{ mt: 1 }}>
                Add Address
              </Button>
            )}
          </Box>
        </Stack>
      );
    }

    if (dataType === 'MULTIPICKLIST') {
      const selected = picklistSelectedValues(value, options);
      const multiSelectOptions = options.map((option) => {
        let optionDisabled = false;
        if (fieldName === 'communication') {
          const hasAble = selected.includes('Able to communicate');
          const hasUnable = selected.includes('Unable to communicate');
          const hasOtherCommunication = selected.some((choice) => (
            choice !== 'Able to communicate' && choice !== 'Unable to communicate'
          ));
          optionDisabled = (
            (option === 'Able to communicate' && (hasUnable || hasOtherCommunication))
            || (option === 'Unable to communicate' && (hasAble || hasOtherCommunication))
            || (
              option !== 'Able to communicate'
              && option !== 'Unable to communicate'
              && (hasAble || hasUnable)
            )
          );
        }
        return { value: option, label: option, disabled: optionDisabled };
      });
      return (
        <MultiSelectPicklist
          name={fieldName}
          label={label}
          value={selected.join(';')}
          options={multiSelectOptions}
          onChange={(nextValue) => setField(fieldName, selectedValues(nextValue))}
          error={Boolean(fieldError)}
          helperText={fieldError}
          required={required}
          disabled={disabled}
          readOnly={READ_ONLY_MULTISELECT_FIELDS.has(fieldName)}
          maxSelections={MULTISELECT_MAX_SELECTIONS[fieldName] ?? null}
          showConfirmButton
        />
      );
    }

    if (dataType === 'PICKLIST' && options.length > 0 && LEGACY_RADIO_PICKLIST_FIELDS.has(fieldName)) {
      return (
        <Box>
          <FormControl component="fieldset" error={fieldHasError} fullWidth required={required}>
            <FormLabel
              component="legend"
              sx={{
                color: fieldHasError ? 'error.main' : 'black',
                fontWeight: 'bold',
                mb: -1.5,
                '&.Mui-focused': { color: 'black' },
                '&.Mui-error': { color: 'error.main' },
              }}
            >
              {label}
            </FormLabel>
            <RadioGroup
              row
              value={picklistOptionForValue(value, options)}
              onChange={(event) => setField(fieldName, event.target.value)}
              name={fieldName}
            >
              {options.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={(
                    <Radio
                      disabled={disabled}
                      sx={{
                        color: '#702b9d',
                        '&.Mui-checked': { color: '#702b9d' },
                        '&.Mui-disabled.Mui-checked': {
                          color: '#702b9d',
                          opacity: 0.8,
                        },
                      }}
                    />
                  )}
                  label={option}
                />
              ))}
            </RadioGroup>
            {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
          </FormControl>
        </Box>
      );
    }

    if (dataType === 'BOOLEAN') {
      return (
        <FormControlLabel
          control={(
            <Checkbox
              checked={Boolean(value)}
              disabled={disabled}
              onChange={(event) => setField(fieldName, event.target.checked)}
            />
          )}
          label={label}
          required={required}
        />
      );
    }

    if (dataType === 'PICKLIST' && options.length > 0) {
      const selectedOption = picklistOptionForValue(value, options);
      const picklistControl = (
        <FormControl fullWidth error={fieldHasError} required={required}>
          <InputLabel>{label}</InputLabel>
          <Select
            value={options.some((option) => fieldValueEquals(option, selectedOption)) ? selectedOption : ''}
            label={label}
            disabled={disabled}
            onChange={(event) => setField(fieldName, event.target.value)}
          >
            <MenuItem value=""><em>Select an option</em></MenuItem>
            {options.map((option) => (
              <MenuItem key={option} value={option} sx={{ whiteSpace: 'normal', wordWrap: 'break-word', minHeight: 'auto' }}>
                {option}
              </MenuItem>
            ))}
          </Select>
          {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
        </FormControl>
      );
      if (fieldName === 'insulinAdministration') {
        return (
          <Box>
            {picklistControl}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontStyle: 'italic',
                color: 'text.secondary',
                mt: 1,
                fontSize: '0.75rem',
              }}
            >
              **HHA/PCA cannot administer insulin
            </Typography>
          </Box>
        );
      }
      return picklistControl;
    }

    if (PHONE_MASK_FIELDS.has(fieldName)) {
      return (
        <InputMask
          mask="999 999 9999"
          value={String(value)}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setField(fieldName, event.target.value)}
          disabled={disabled}
          maskPlaceholder={null}
        >
          <TextField
            label={label}
            value={undefined}
            onChange={undefined}
            error={Boolean(fieldError)}
            helperText={fieldError}
            fullWidth
            required={required}
            disabled={undefined}
            placeholder="Enter phone number"
            inputMode="tel"
            autoComplete="tel"
          />
        </InputMask>
      );
    }

    const isDateField = dataType === 'DATE' || /date/i.test(fieldName);
    const isTimeField = /time/i.test(fieldName);
    const fieldValue = isDateField ? dateInputValue(value) : isTimeField ? timeInputValue(value) : String(value);

    return (
      <TextField
        label={label}
        value={fieldValue}
        error={fieldHasError}
        helperText={fieldError}
        required={required}
        type={
          fieldName === 'temp'
            ? 'text'
            : isDateField
            ? 'date'
            : isTimeField
              ? 'time'
              : dataType === 'INTEGER' || dataType === 'DECIMAL' || dataType === 'FLOAT'
                ? 'number'
                : dataType === 'EMAIL' ? 'email' : 'text'
        }
        multiline={isLongText(fieldName, field)}
        minRows={isLongText(fieldName, field) ? 3 : undefined}
        fullWidth
        disabled={disabled}
        InputLabelProps={isDateField || isTimeField ? { shrink: true } : undefined}
        sx={isDateField ? nursingDateFieldSx : undefined}
        placeholder={fieldName === 'chestPainSeverity' ? 'Enter 1-10' : undefined}
        inputProps={fieldName === 'chestPainSeverity' ? { min: 1, max: 10, step: 1 } : undefined}
        onBlur={fieldName === 'temp' ? () => setField('temp', normalizeTemperatureOnBlur(formData.temp)) : undefined}
        onChange={(event) => setField(fieldName, event.target.value)}
      />
    );
  };

  const renderVisitDetailField = (fieldName: string) => {
    if (fieldName === 'visitType') {
      const allowVisitTypeSwitch = selectedPatient?.assessmentAllowVisitTypeSwitch === true
        || selectedPatient?.assessmentAllowVisitTypeSwitch === 'true'
        || formData.assessmentAllowVisitTypeSwitch === true
        || formData.assessmentAllowVisitTypeSwitch === 'true';
      const canSwitchPatientEvaluationToInitial = allowVisitTypeSwitch
        && (fieldValueEquals(selectedPatient?.assessmentVisitType, 'Patient Evaluation') || fieldValueEquals(formData.visitType, 'Patient Evaluation'));
      if (canSwitchPatientEvaluationToInitial) {
        const options = ['Patient Evaluation', 'Initial'];
        const fieldError = fieldErrors.visitType;
        return (
          <FormControl fullWidth required error={Boolean(fieldError)}>
            <InputLabel>Visit Type</InputLabel>
            <Select
              value={picklistOptionForValue(formData.visitType, options)}
              label="Visit Type"
              onChange={(event) => setField('visitType', event.target.value)}
            >
              {options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
            </Select>
            {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
          </FormControl>
        );
      }
      return (
        <TextField
          label="Visit Type"
          value={String(formData.visitType || selectedPatient?.assessmentVisitType || '')}
          fullWidth
          required
          InputProps={{ readOnly: true }}
          sx={readOnlyTextFieldSx}
        />
      );
    }

    const readOnlyValue = (() => {
      if (fieldName === 'assessmentNurse') {
        const currentUser = user as unknown as Record<string, unknown>;
        return formData.assessmentNurse || currentUser.name || currentUser.username || '';
      }
      if (fieldName === 'visitReason') {
        return fieldValueEquals(formData.visitReason, 'Other') && formData.visitReasonOther
          ? `Other: ${String(formData.visitReasonOther)}`
          : formData.visitReason || '';
      }
      if (fieldName === 'serviceStartDate') return displayDate(formData.serviceStartDate) || '';
      if (fieldName === 'contract') return formData.contract || formData.assessmentContract || selectedPatient?.assessmentContract || '';
      if (fieldName === 'contractTypes') return displayFieldValue(formData.contractTypes);
      if (fieldName === 'certificationPeriod') {
        const start = displayDate(formData.certificationPeriodStart);
        const end = displayDate(formData.certificationPeriodEnd);
        return start && end ? `${start} - ${end}` : '';
      }
      if (fieldName === 'evaluationLocation') return formData.evaluationLocation || '';
      return undefined;
    })();

    if (readOnlyValue !== undefined) {
      const labels: Record<string, string> = {
        assessmentNurse: 'Nurse',
        visitReason: 'Visit Reason',
        evaluationLocation: 'Evaluation Location',
        serviceStartDate: 'Service Start Date',
        contract: 'Contract',
        contractTypes: 'Contract Type',
        certificationPeriod: 'Certification Period',
      };
      return (
        <TextField
          label={labels[fieldName] || fieldName}
          value={String(readOnlyValue)}
          fullWidth
          InputProps={{ readOnly: true }}
          sx={readOnlyTextFieldSx}
        />
      );
    }

    return renderField(fieldName);
  };

  const renderLegacyDetailCard = (cardKey: string, title: string, fields: string[]) => {
    const CardIcon = iconForCard(cardKey);
    const visibleFields = fields.filter((fieldName) => isFieldVisible(fieldName, formData));
    if (visibleFields.length === 0) return null;
    if (cardKey === 'medications') {
      return (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <CardIcon sx={{ mb: 1, mr: 1, color: '#8600e1' }} />
              <Typography variant="h6" gutterBottom>
                {title}
              </Typography>
            </Box>
            {renderMedications(isSubmitted)}
          </CardContent>
        </Card>
      );
    }
    return (
      <Card variant="outlined" sx={{ mb: 2, mt: cardKey === 'wound1' || cardKey === 'painLocation1' ? 2 : undefined }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <CardIcon sx={{ mb: 1, mr: 1, color: '#8600e1' }} />
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: cardGridTemplateColumns(cardKey),
              gap: 3,
            }}
          >
            {visibleFields.map((fieldName) => (
              <Box key={fieldName} sx={fieldGridSx(cardKey, fieldName, metadata?.fields[fieldName], formData)}>
                {renderField(fieldName)}
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderLegacyStep8DetailCards = () => {
    const cards: ReactElement[] = [];
    if (fieldValueIncludes(formData.skinProblems, 'Wound')) {
      cards.push(
        <Box key="wound1">
          {renderLegacyDetailCard('wound1', 'Wound #1', [
            'wound1Location',
            'wound1LesionType',
            'wound1LesionTypeOther',
            'wound1PressureUlcerStage',
            'wound1Assessed',
            'wound1AssessedNoDueTo',
            'wound1AssessedNoDueToOther',
            'wound1CareOrder',
            'wound1CareOrderOther',
            'wound1CarePerformedBy',
            'wound1CarePerformedByOther',
            'wound1UnderMdCare',
          ])}
        </Box>,
        <Box key="wound2">
          {renderLegacyDetailCard('wound2', 'Wound #2', [
            'wound2Location',
            'wound2LesionType',
            'wound2LesionTypeOther',
            'wound2PressureUlcerStage',
            'wound2Assessed',
            'wound2AssessedNoDueTo',
            'wound2AssessedNoDueToOther',
            'wound2CareOrder',
            'wound2CareOrderOther',
            'wound2CarePerformedBy',
            'wound2CarePerformedByOther',
            'wound2UnderMdCare',
          ])}
        </Box>,
        <Box key="additionalWounds">
          {renderLegacyDetailCard('additionalWounds', 'Additional Wounds', ['additionalWounds'])}
        </Box>,
      );
    }
    if (fieldValueEquals(formData.painHistory, 'Yes')) {
      cards.push(
        <Box key="painLocation1">
          {renderLegacyDetailCard('painLocation1', 'Pain Location 1', [
            'pain1Location',
            'pain1Intensity',
            'pain1Type',
            'pain1TypeOther',
            'pain1Frequency',
            'pain1MakesBetter',
            'pain1MakesBetterOther',
            'pain1MakesWorse',
            'pain1MakesWorseOther',
          ])}
        </Box>,
        <Box key="painLocation2">
          {renderLegacyDetailCard('painLocation2', 'Pain Location 2', [
            'pain2Location',
            'pain2Intensity',
            'pain2Type',
            'pain2TypeOther',
            'pain2Frequency',
            'pain2MakesBetter',
            'pain2MakesBetterOther',
            'pain2MakesWorse',
            'pain2MakesWorseOther',
          ])}
        </Box>,
      );
    }
    return cards;
  };

  const supervisionFieldSx = (fieldName: string, fullWidthFields: Set<string>) => {
    const pairedOtherByMain: Record<string, string> = {
      supervisionGrooming: 'groomingOther',
      supervisionToileting: 'toiletingOther',
      supervisionTransfers: 'transfersOther',
      supervisionAmbulation: 'ambulationOther',
      otherTasks: 'otherTasksOther',
    };
    const pairedMainByOther: Record<string, string> = Object.entries(pairedOtherByMain).reduce<Record<string, string>>((acc, [main, other]) => {
      acc[other] = main;
      return acc;
    }, {});
    const pairedOther = pairedOtherByMain[fieldName];
    if (pairedOther) {
      return { gridColumn: { xs: '1 / -1', md: isFieldVisible(pairedOther, formData) ? 'span 1' : '1 / -1' } };
    }
    if (pairedMainByOther[fieldName]) return { gridColumn: { xs: '1 / -1', md: 'span 1' } };
    return fullWidthFields.has(fieldName) ? { gridColumn: '1 / -1' } : undefined;
  };

  const renderSupervisionFields = (fields: string[], fullWidthFields = new Set<string>()) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        gap: 2,
      }}
    >
      {fields.filter((fieldName) => isFieldVisible(fieldName, formData)).map((fieldName) => (
        <Box key={fieldName} sx={supervisionFieldSx(fieldName, fullWidthFields)}>
          {renderField(fieldName)}
        </Box>
      ))}
    </Box>
  );

  const renderSupervisionCard = (
    title: string,
    IconComponent: typeof PersonIcon,
    fields: string[],
    fullWidthFields = new Set<string>(),
  ) => (
    <Card variant="outlined" sx={{ mb: 2, mt: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box display="flex" alignItems="center" mb={2}>
            <IconComponent sx={{ mb: 1, mr: 1, color: '#8600e1' }} />
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
          </Box>
          {renderSupervisionFields(fields, fullWidthFields)}
        </Stack>
      </CardContent>
    </Card>
  );

  const renderParaprofessionalSupervisionStep = () => {
    const supervisionCompleted = fieldValueEquals(formData.supervisionCompleted, 'Yes');
    const supervisionDetailsFields = [
      'nameOfProfessional',
      'patientInitials',
      'identificationBadge',
      'uniformScrubAdequateAttire',
      'changeInPocNeeded',
    ];
    const performanceFields = [
      'supervisionBathing',
      'supervisionGrooming',
      'groomingOther',
      'supervisionToileting',
      'toiletingOther',
      'supervisionTransfers',
      'transfersOther',
      'supervisionAmbulation',
      'ambulationOther',
      'otherTasks',
      'otherTasksOther',
      'vitalSignsReadRecord',
    ];
    const supervisionFields = [
      'instructionProvided',
      'useOfDmeSpecify',
      'instructionProvidedOther',
      'paraprofessionalResponse',
      'hhaPcaCompliesWithPoc',
      'pcaHhaCommunicatesEffectively',
      'patientFamilyCooperative',
      'patientSatisfiedWithServices',
      'areServicesContinuing',
      'patientTreatedWithRespect',
      'aideRequiresAgencyFollowUp',
    ];
    const commentsRequired = [
      'hhaPcaCompliesWithPoc',
      'pcaHhaCommunicatesEffectively',
      'patientFamilyCooperative',
      'patientSatisfiedWithServices',
      'areServicesContinuing',
      'patientTreatedWithRespect',
    ].some((fieldName) => fieldValueEquals(formData[fieldName], 'No') || fieldValueEquals(formData[fieldName], 'No (Explain Below)'));

    return (
      <>
        <Card variant="outlined" sx={{ mb: 2, mt: 3 }}>
          <CardContent>
            <Stack spacing={2}>
              <Box display="flex" alignItems="center" mb={2}>
                <VisibilityIcon sx={{ mb: 1, mr: 1, color: '#8600e1' }} />
                <Typography variant="h6" gutterBottom>
                  Supervision Completed
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                  gap: 2,
                }}
              >
                <Box>{renderField('supervisionCompleted')}</Box>
                <TextField
                  label="Aide Flu Status"
                  value={caregiverFluStatus(selectedCaregiver)}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={readOnlyTextFieldSx}
                />
                <Box sx={{ gridColumn: '1 / -1' }}>{renderField('maskMandateCompliance')}</Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        {supervisionCompleted && (
          <>
            {renderSupervisionCard('Supervision Details', PersonIcon, supervisionDetailsFields)}
            {renderSupervisionCard('Performance Observation', AssessmentIcon, performanceFields, new Set([
              'supervisionBathing',
              'supervisionGrooming',
              'supervisionToileting',
              'supervisionTransfers',
              'supervisionAmbulation',
              'otherTasks',
              'vitalSignsReadRecord',
            ]))}
            {renderSupervisionCard('Supervision', SupervisorAccountIcon, supervisionFields, new Set([
              'instructionProvided',
              'paraprofessionalResponse',
              'aideRequiresAgencyFollowUp',
            ]))}
            <Card variant="outlined" sx={{ mb: 2, mt: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <ChatBubbleOutlineIcon sx={{ mb: 1, mr: 1, color: '#8600e1' }} />
                    <Typography variant="h6" gutterBottom>
                      Comments
                    </Typography>
                  </Box>
                  <TextField
                    label="Comments"
                    value={String(formData.comments || '')}
                    error={Boolean(fieldErrors.comments)}
                    helperText={fieldErrors.comments || (commentsRequired ? 'Required when a supervision rating is No.' : undefined)}
                    fullWidth
                    multiline
                    rows={4}
                    inputProps={{ maxLength: 2000 }}
                    required={commentsRequired}
                    onChange={(event) => setField('comments', event.target.value)}
                  />
                </Stack>
              </CardContent>
            </Card>
          </>
        )}
      </>
    );
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh"><CircularProgress /></Box>;
  }

  if (!hasRole(user, 'nurse') && !hasRole(user, 'nurse-admin') && !hasRole(user, 'admin')) {
    return <Container sx={{ mt: 4 }}><Alert severity="error">Nursing access is required.</Alert></Container>;
  }

  if (assessment && showConsentPrerequisite) {
    return renderConsentPrerequisite();
  }

  if (assessment && isSubmitted) {
    return (
      <>
        {renderCompletedSummary()}
        {renderRecordingTextDialog()}
        {renderHandOffNoteDialog()}
        {renderTicketDialog()}
        <ReauthDialog
          open={reauthOpen}
          user={user}
          message={reauthMessage}
          onAuthenticated={completeReauth}
        />
      </>
    );
  }

  const activeStepDefinition = metadata?.steps[activeStep];
  const activeStepIndex = visibleSteps.findIndex(([key]) => key === activeStep);
  const isFirstVisibleStep = activeStepIndex <= 0;
  const isLastVisibleStep = activeStepIndex === visibleSteps.length - 1;

  const canLoginAsNurse = hasRole(user, 'nurse-admin') || hasRole(user, 'admin');

  return (
    <Container
      maxWidth={assessment ? 'xl' : 'lg'}
      sx={{
        px: { xs: 2, sm: 3 },
        py: assessment ? 0 : { xs: 3, sm: 6 },
        mt: assessment ? 3 : { xs: 2, sm: 4 },
        mb: 5,
        display: assessment ? 'block' : 'flex',
        flexDirection: 'column',
        alignItems: assessment ? 'initial' : 'center',
        justifyContent: assessment ? 'initial' : 'center',
      }}
    >
      <Dialog open={completedDialogOpen} onClose={cancelCompletedAssessmentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Last Completed Assessment</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            A nursing assessment was previously completed for this client. Would you like to review it or create a new one?
          </Typography>
          {completedAssessment && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="caption" display="block" gutterBottom>
                <strong>Client:</strong> {patientLabel(selectedPatient || {})}
              </Typography>
              <Typography variant="caption" display="block" gutterBottom>
                <strong>Visit Type:</strong> {String(completedAssessment.visitType || 'N/A')}
              </Typography>
              <Typography variant="caption" display="block" gutterBottom>
                <strong>Visit Date:</strong> {displayDate(completedAssessment.visitDate) || 'N/A'}
              </Typography>
              <Typography variant="caption" display="block" gutterBottom>
                <strong>Submission Date:</strong> {displayDate(completedAssessment.submissionDate) || 'N/A'}
              </Typography>
              <Typography variant="caption" display="block">
                <strong>Status:</strong> {String(completedAssessment.status || 'Completed')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
          <Button onClick={cancelCompletedAssessmentDialog} variant="outlined">
            Cancel
          </Button>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button onClick={reviewCompletedAssessment} variant="outlined" disabled={!completedAssessment || saving}>
              Review
            </Button>
            <Button onClick={createNewAfterCompleted} variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={18} color="inherit" /> : 'Create New'}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {!assessment ? (
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 3, md: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: { xs: '95%', sm: '85%', md: '65%', lg: '50%' },
            maxWidth: 600,
            mx: 'auto',
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' },
              textAlign: 'center',
              mb: { xs: 2, sm: 3 },
            }}
          >
            Nursing Assessment Setup
          </Typography>

          {canLoginAsNurse && !nurseAdminBackup && (
            <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={setupTab}
                onChange={(_, nextValue) => setSetupTab(nextValue)}
                centered
                sx={{
                  '& .MuiTab-root': {
                    color: 'text.secondary',
                    '&.Mui-selected': { color: '#8900e1' },
                  },
                  '& .MuiTabs-indicator': { backgroundColor: '#8900e1' },
                }}
              >
                <Tab label="Start Assessment" />
                <Tab label="Login As Nurse" />
              </Tabs>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {setupTab === 0 && (
            <Box sx={{ width: '100%', mt: { xs: 2, sm: 3 } }}>
              <Card variant="outlined" sx={{ mb: { xs: 2, sm: 3 }, p: { xs: 2.5, sm: 3 }, borderRadius: 1 }}>
                <CardContent sx={{ p: 0 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#8900e1', fontWeight: 600 }}>
                    Client Selection
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Please select a client to begin the nursing assessment setup.
                  </Typography>
                  <Autocomplete
                    options={patients}
                    getOptionLabel={patientLabel}
                    value={selectedPatient}
                    onChange={(_, value) => setSelectedPatient(value)}
                    loading={saving}
                    noOptionsText="No clients found"
                    renderOption={(props, option) => {
                      const visitInfoLines = assignmentPrimaryLines(option);
                      return (
                        <li {...props} key={String(option.id)}>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>{patientLabel(option)}</Typography>
                            {visitInfoLines.map((line) => (
                              <Typography key={line} variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                {line}
                              </Typography>
                            ))}
                          </Box>
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Client"
                        variant="outlined"
                        fullWidth
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {saving ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, mt: { xs: 2, sm: 1 } }}>
                <Button
                  variant="contained"
                  onClick={() => loadAssessment()}
                  disabled={!selectedPatient || saving}
                  size="large"
                  sx={{
                    bgcolor: '#8900e1',
                    py: { xs: 1.2, sm: 1 },
                    px: { xs: 2.5, sm: 3 },
                    fontSize: { xs: '0.95rem', sm: '0.875rem' },
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': { bgcolor: '#6b00b3' },
                    '&.Mui-disabled': {
                      backgroundColor: '#8900e1',
                      color: 'white',
                      opacity: 0.7,
                    },
                  }}
                >
                  {saving ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading...
                    </>
                  ) : (
                    'Continue to Assessment'
                  )}
                </Button>
              </Box>
            </Box>
          )}

          {setupTab === 1 && canLoginAsNurse && !nurseAdminBackup && (
            <Box sx={{ width: '100%', mt: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#8900e1', fontWeight: 600 }}>
                Login As Another Nurse
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select a nurse user to log in as. You will be able to return to your admin account using the banner at the top of the screen.
              </Typography>
              <Box>
                {nurseUsersLoading && (
                  <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                    <CircularProgress />
                  </Box>
                )}
                {!nurseUsersLoading && nurseUsers.length === 0 && (
                  <Typography color="text.secondary" textAlign="center" py={4}>
                    No other nurse users found
                  </Typography>
                )}
                {!nurseUsersLoading && nurseUsers.length > 0 && (
                  <Paper variant="outlined">
                    <List disablePadding>
                      {nurseUsers.map((nurse, index) => (
                        <ListItem
                          key={nurse.id}
                          divider={index < nurseUsers.length - 1}
                          sx={{ py: 1.5, pr: 16 }}
                        >
                          <ListItemText
                            primary={nurseUserLabel(nurse)}
                            primaryTypographyProps={{ fontWeight: 500 }}
                          />
                          <ListItemSecondaryAction>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={
                                impersonatingNurseId === nurse.id
                                  ? <CircularProgress size={16} color="inherit" />
                                  : <LoginIcon />
                              }
                              onClick={() => loginAsNurse(nurse)}
                              disabled={impersonatingNurseId !== null}
                            >
                              {impersonatingNurseId === nurse.id ? 'Logging in...' : 'Login As'}
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      ) : (
        <>
          {renderPinnedAssessmentHeader()}
          <Container id="c1" maxWidth="lg" sx={{ mt: 5, px: { xs: 0, sm: 3 } }}>
            <Paper
              elevation={3}
              sx={{
                p: 1,
                mb: 10,
                bgcolor: 'rgba(255,255,255,1.00)',
                paddingRight: { xs: '16px', sm: 1 },
                paddingLeft: { xs: '3px', sm: 1 },
              }}
            >
              <Box className="mt-3 c2 p-1">
                {renderNursingNavStatusBar()}

                {error && (
                  <Alert severity="error" sx={{ m: { xs: 1, sm: 2 } }} action={saveErrorStep && activeStep !== saveErrorStep ? (
                    <Button color="inherit" size="small" onClick={() => {
                      setActiveStep(saveErrorStep);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}>Go to {statusForStep(saveErrorStep)}</Button>
                  ) : undefined}>
                    {error}
                  </Alert>
                )}

                <Box
                  sx={{
                    m: { xs: 0.5, sm: 1 },
                    ml: { xs: 1 },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >

          <Dialog
            open={recordingConsentOpen}
            maxWidth="sm"
            fullWidth
            scroll="paper"
            disableEscapeKeyDown
            onClose={(_event, reason) => {
              if (reason !== 'backdropClick') setRecordingConsentOpen(false);
            }}
            PaperProps={{
              sx: {
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                width: { xs: 'calc(100% - 16px)', sm: 'auto' },
                maxHeight: { xs: 'calc(100svh - 16px)', sm: 'calc(100dvh - 64px)' },
                m: { xs: 1, sm: 2 },
              },
            }}
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: { xs: 1.5, sm: 2 }, px: { xs: 2, sm: 3 } }}>
              <MicIcon sx={{ color: '#8600e1' }} />
              Audio Recording Notice
            </DialogTitle>
            <DialogContent
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                px: { xs: 2, sm: 3 },
                py: { xs: 1, sm: 1.5 },
                pb: { xs: 1.5, sm: 2 },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: { xs: 2, sm: 2.5 },
                  fontWeight: 700,
                  color: 'text.primary',
                  lineHeight: 1.35,
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                }}
              >
                New Feature: The AI Scribe is available during and after assessments. The AI Scribe will create a clear summary for you that makes it easy to fill out the assessment!
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: { xs: 2, sm: 2.5 } }}>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  Audio will be captured from your device&apos;s microphone
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  Recordings are securely stored and HIPAA-compliant
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  You can pause or stop the recording at any time
                </Typography>
                <Typography component="li" variant="body2">
                  Recordings may be used for transcription and documentation
                </Typography>
              </Box>
              {(!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Audio recording is not supported in your current browser. You can still proceed with the assessment without recording.
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                By clicking &quot;Enable Recording&quot;, you confirm that you consent and have verbal consent from everyone present to the audio recording of this session.
              </Typography>
              <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={recordingDeclineVisible}
                      onChange={(event) => setRecordingDeclineVisible(event.target.checked)}
                      size="small"
                      sx={{ py: 0 }}
                    />
                  )}
                  label={<Typography variant="body2" color="text.secondary">View additional options</Typography>}
                  sx={{ m: 0 }}
                />
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                px: { xs: 2, sm: 3 },
                pb: { xs: 'calc(10px + env(safe-area-inset-bottom))', sm: 2 },
                pt: 1.25,
                flexDirection: 'column',
                alignItems: 'stretch',
                flexShrink: 0,
                borderTop: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              }}
            >
              <Button
                onClick={acceptRecordingConsent}
                variant="contained"
                startIcon={<MicIcon />}
                disabled={!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined'}
                fullWidth
                size="large"
              >
                Enable Recording
              </Button>
              {recordingDeclineVisible && (
                <Button onClick={declineRecordingConsent} variant="text" size="small" sx={{ mt: 1, color: 'text.secondary', textTransform: 'uppercase' }}>
                  Continue Without Recording
                </Button>
              )}
            </DialogActions>
          </Dialog>

          <Stack spacing={2}>
            {activeStepNumber(activeStep) === 1 && (
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <CalendarTodayIcon sx={{ mb: 1, mr: 1, color: '#8600e1' }} />
                    <Typography variant="h6" gutterBottom>
                      Visit Details
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                      gap: 2,
                    }}
                  >
                    {[
                      'visitDate',
                      'visitStartTime',
                      'visitType',
                      'assessmentNurse',
                      'visitReason',
                      'evaluationLocation',
                      'serviceStartDate',
                      'contract',
                      'contractTypes',
                      'certificationPeriod',
                    ]
                      .filter((fieldName) => (
                        ['visitDate', 'visitStartTime', 'visitType', 'assessmentNurse', 'visitReason', 'evaluationLocation', 'serviceStartDate', 'contract', 'contractTypes', 'certificationPeriod'].includes(fieldName)
                        || hasStoredValue(formData[fieldName])
                        || metadata?.fields[fieldName]
                      ))
                      .filter((fieldName) => isFieldVisible(fieldName, formData))
                      .map((fieldName) => (
                        <Box key={fieldName} sx={fieldGridSx('visitDetails', fieldName, metadata?.fields[fieldName], formData)}>
                          {renderVisitDetailField(fieldName)}
                        </Box>
                      ))}
                  </Box>
                </CardContent>
              </Card>
            )}
            {activeStepNumber(activeStep) === 11 && !fieldValueEquals(formData.visitType, 'Patient Evaluation') && (
              <Card
                variant="outlined"
                sx={{
                  mb: { xs: 2, sm: 3 },
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: 1,
                  bgcolor: formData.caregiver ? 'rgba(134, 0, 225, 0.04)' : 'background.paper',
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Stack spacing={0}>
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ color: '#8600e1', fontWeight: 600 }}>
                        Caregiver Selection
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {caregiverSearchInput.trim().length < 2
                          ? 'Start typing a caregiver name to search active caregivers.'
                          : selectedCaregiverId || caregiverOptions.length > 0
                            ? 'Please select a caregiver for this client. This is required to complete supervision.'
                            : 'No matching active caregivers found. Please adjust the search or add a caregiver contact before proceeding.'}
                      </Typography>
                    </Box>

                    {caregiversError && <Alert severity="warning">{caregiversError}</Alert>}

                    <Autocomplete
                      options={visibleCaregiverOptions}
                      getOptionLabel={caregiverOptionLabel}
                      filterOptions={(options) => options}
                      value={selectedCaregiver}
                      inputValue={caregiverSearchInput}
                      loading={caregiversLoading}
                      disabled={noCaregiverPresent}
                      isOptionEqualToValue={(option, value) => caregiverId(option) === caregiverId(value)}
                      noOptionsText={caregiverSearchInput.trim().length < 2 ? 'Type at least 2 characters' : 'No matching caregivers'}
                      onInputChange={(_, value, reason) => {
                        if (reason === 'input' || reason === 'clear') setCaregiverSearchInput(value);
                      }}
                      onChange={(_, nextCaregiver) => handleCaregiverChange(nextCaregiver)}
                      renderOption={(props, option) => (
                        <li {...props} key={caregiverId(option)}>
                          <Box>
                            <Typography variant="body1">{caregiverName(option)}</Typography>
                            {caregiverDetailParts(option).length > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                {caregiverDetailParts(option).join(' • ')}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Caregiver"
                          error={caregiverSearchInput.trim().length >= 2 && caregiverOptions.length === 0 && !caregiversLoading && !formData.caregiver}
                          helperText={caregiverSearchInput.trim().length >= 2 && caregiverOptions.length === 0 && !caregiversLoading && !formData.caregiver ? 'No matching active caregivers' : ''}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {caregiversLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      sx={{ mt: 1 }}
                    />

                    {selectedCaregiverId && !noCaregiverPresent && caregiverIdPictureLoading && (
                      <Box
                        aria-live="polite"
                        sx={{ mt: 2, width: 88, height: 88, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <CircularProgress size={28} />
                      </Box>
                    )}

                    {selectedCaregiverId
                      && !noCaregiverPresent
                      && caregiverIdPicture
                      && isDisplayableImageContentType(caregiverIdPicture.contentType)
                      && !caregiverIdPictureImageError && (
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          aria-live="polite"
                          sx={{ mt: 2, width: 'fit-content' }}
                        >
                          <Box
                            component="img"
                            src={caregiverIdPicture.url}
                            alt={`ID Picture for ${caregiverName(selectedCaregiver)}`}
                            onError={() => setCaregiverIdPictureImageError(true)}
                            sx={{
                              display: 'block',
                              width: 88,
                              height: 88,
                              flexShrink: 0,
                              borderRadius: '50%',
                              objectFit: 'cover',
                              bgcolor: 'common.white',
                              border: '3px solid',
                              borderColor: 'rgba(134, 0, 225, 0.28)',
                              boxShadow: 1,
                            }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight={700} color="#6800ad">
                              ID Picture
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Verify the caregiver present matches this photo.
                            </Typography>
                          </Box>
                        </Stack>
                      )}

                    {selectedCaregiverId && !noCaregiverPresent && caregiverIdPictureError && (
                      <Typography variant="caption" color="warning.main" sx={{ mt: 1.5 }} aria-live="polite">
                        The caregiver ID picture could not be loaded.
                      </Typography>
                    )}

                    <Box sx={{ mt: 2 }}>
                      <FormControlLabel
                        control={(
                          <Checkbox
                            checked={noCaregiverPresent}
                            disabled={caregiversLoading}
                            onChange={(event) => handleNoCaregiverChange(event.target.checked)}
                            sx={{
                              color: '#8600e1',
                              '&.Mui-checked': { color: '#8600e1' },
                            }}
                          />
                        )}
                        label="No Caregiver Present"
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.875rem',
                            color: 'text.primary',
                          },
                        }}
                      />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            )}
            {activeStepNumber(activeStep) === 9 && fieldValueEquals(formData.visitType, 'Revisit') ? (
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Narrative / Notes
                    </Typography>
                    {renderField('nursingProgressNote')}
                    {renderField('handOffNote')}
                  </Stack>
                </CardContent>
              </Card>
            ) : activeStepNumber(activeStep) === 11 && fieldValueEquals(formData.visitType, 'Patient Evaluation') ? (
              renderPatientEvaluationOutcome()
            ) : activeStepNumber(activeStep) === 11 ? (
              renderParaprofessionalSupervisionStep()
            ) : activeStepNumber(activeStep) === 12 ? (
              renderHandOffStep()
            ) : orderedCardEntries(activeStep, activeStepDefinition?.cards ?? {}).map(([cardKey, card]) => {
              const revisitGateField = revisitGateForCard(card);
              const hasConsentEditor = card.fields.includes('consent');
              const hasFallRiskEditor = card.fields.includes('fallRiskFactors');
              const visibleFields = orderedFieldNames(cardKey, card.fields, formData).filter((fieldName) => (
                isFieldVisible(fieldName, formData) && !(hasConsentEditor && CONSENT_EMBEDDED_FIELDS.has(fieldName))
                && !(hasFallRiskEditor && FALL_RISK_EMBEDDED_FIELDS.has(fieldName))
                && !(cardKey === 'integument' && INTEGUMENT_WOUND_DETAIL_FIELDS.has(fieldName))
                && !(cardKey === 'painManagement' && PAIN_LOCATION_DETAIL_FIELDS.has(fieldName))
                && !(cardKey === 'planOfCare' && PLAN_OF_CARE_SCHEDULE_FIELDS.has(fieldName))
                && !(cardKey === 'iadls' && IADL_FOLLOWUP_FIELDS.has(fieldName))
              ));
              const iadlFollowUpFields = cardKey === 'iadls'
                ? orderedFieldNames(cardKey, card.fields, formData).filter((fieldName) => IADL_FOLLOWUP_FIELDS.has(fieldName) && isFieldVisible(fieldName, formData))
                : [];
              if (visibleFields.length === 0 && !revisitGateField) return null;
              const CardIcon = iconForCard(cardKey);
              if (cardKey === 'medications') {
                return (
                  <Card
                    key={cardKey}
                    variant="outlined"
                    sx={{
                      mb: 2,
                      mt: 3,
                      ...(revisitGateField && !fieldValueEquals(formData[revisitGateField], 'Yes') ? {
                        bgcolor: 'rgba(0, 0, 0, 0.035)',
                        borderColor: 'rgba(0, 0, 0, 0.2)',
                      } : {}),
                    }}
                  >
                    <CardContent>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        justifyContent={{ xs: 'flex-start', md: 'space-between' }}
                        sx={{ mb: 2 }}
                      >
                        <Box display="flex" alignItems="center" mt={{ xs: 2, md: 0 }} mb={{ xs: 0, md: -1 }}>
                          <CardIcon sx={{ mb: 1, mr: 1, color: '#8600e1' }} />
                          <Typography variant="h6" gutterBottom>
                            {cardDisplayTitle(cardKey, card.title)}
                          </Typography>
                        </Box>
                        {renderRevisitGate(revisitGateField)}
                      </Stack>
                      {renderMedications(isSubmitted, revisitGateField ? !fieldValueEquals(formData[revisitGateField], 'Yes') : false)}
                    </CardContent>
                  </Card>
                );
              }
              return (
                <Card
                  key={cardKey}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    mt: 3,
                    ...(revisitGateField && !fieldValueEquals(formData[revisitGateField], 'Yes') ? {
                      bgcolor: 'rgba(0, 0, 0, 0.035)',
                      borderColor: 'rgba(0, 0, 0, 0.2)',
                    } : {}),
                  }}
                >
                  <CardContent>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1.5}
                      alignItems={{ xs: 'flex-start', md: 'center' }}
                      justifyContent={{ xs: 'flex-start', md: 'space-between' }}
                      sx={{ mb: 2 }}
                    >
                      <Box display="flex" alignItems="center" mt={{ xs: 2, md: 0 }} mb={{ xs: 0, md: -1 }}>
                        <CardIcon sx={{ mb: 1, mr: 1, color: '#8600e1' }} />
                        <Typography variant="h6" gutterBottom>
                          {cardDisplayTitle(cardKey, card.title)}
                        </Typography>
                      </Box>
                      {renderRevisitGate(revisitGateField)}
                    </Stack>
                    {cardKey === 'emergencyContact' && !isCheckedValue(formData.hasNoEmergencyContact) && fieldValueEquals(formData.patientSelfDirecting, 'No') && (
                      <Typography color="warning.main" variant="body2" sx={{ mb: 2 }}>
                        Patient is not self-directing. Fill Emergency Contact 1 with proxy information.
                      </Typography>
                    )}
                    {cardKey === 'planOfCare' && renderPlanOfCareSchedule()}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: cardGridTemplateColumns(cardKey),
                        gap: 2,
                      }}
                    >
                      {visibleFields.map((fieldName) => (
                        <Box key={fieldName} sx={fieldGridSx(cardKey, fieldName, metadata?.fields[fieldName], formData)}>
                          {renderField(fieldName)}
                        </Box>
                      ))}
                    </Box>
                    {iadlFollowUpFields.length > 0 && (
                      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                          Follow-up Questions
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: cardGridTemplateColumns(cardKey),
                            gap: 2,
                          }}
                        >
                          {iadlFollowUpFields.map((fieldName) => (
                            <Box key={fieldName} sx={fieldGridSx(cardKey, fieldName, metadata?.fields[fieldName], formData)}>
                              {renderField(fieldName)}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            {activeStepNumber(activeStep) === 2 && fieldValueEquals(formData.visitType, 'Patient Evaluation') && renderPatientEvaluationNeeds()}
            {activeStepNumber(activeStep) === 3 && fieldValueEquals(formData.visitType, 'Patient Evaluation') && renderPatientEvaluationAdlPace()}
            {activeStepNumber(activeStep) === 3 && isRevisitReason(formData, 'post-hospital', 'post hospital') && renderRevisitPostHospitalization()}
            {activeStepNumber(activeStep) === 6 && isRevisitReason(formData, 'post fall') && renderRevisitPostFall()}
            {activeStepNumber(activeStep) === 8 && renderLegacyStep8DetailCards()}
            {activeStepNumber(activeStep) === 11 && !fieldValueEquals(formData.visitType, 'Patient Evaluation') && renderManualNursingDocuments(true)}
          </Stack>

          {fieldValueEquals(formData.visitType, 'Triage Assessment') && (
            <Paper
              elevation={2}
              sx={{
                mt: 4,
                mb: 2,
                p: 2,
                bgcolor: isStepReviewed(formData, activeStepNumber(activeStep))
                  ? '#e8f5e9'
                  : fieldErrors[`step_${activeStepNumber(activeStep)}_reviewed`]
                    ? '#ffebee'
                    : '#fff3e0',
                border: '2px solid',
                borderColor: isStepReviewed(formData, activeStepNumber(activeStep))
                  ? '#4caf50'
                  : fieldErrors[`step_${activeStepNumber(activeStep)}_reviewed`]
                    ? '#f44336'
                    : '#ff9800',
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircleOutlineIcon
                  sx={{
                    fontSize: 32,
                    color: isStepReviewed(formData, activeStepNumber(activeStep))
                      ? '#4caf50'
                      : fieldErrors[`step_${activeStepNumber(activeStep)}_reviewed`]
                        ? '#f44336'
                        : '#ff9800',
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700} color={fieldErrors[`step_${activeStepNumber(activeStep)}_reviewed`] ? 'error' : 'text.primary'}>
                    Triage Assessment Review Confirmation
                  </Typography>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={isStepReviewed(formData, activeStepNumber(activeStep))}
                        onChange={(event) => handleStepReviewChange(event.target.checked)}
                        color={isStepReviewed(formData, activeStepNumber(activeStep)) ? 'success' : 'warning'}
                        sx={{
                          '& .MuiSvgIcon-root': { fontSize: 28 },
                          color: fieldErrors[`step_${activeStepNumber(activeStep)}_reviewed`] ? '#f44336' : undefined,
                        }}
                      />
                    )}
                    label={(
                      <Typography variant="body1">
                        I have reviewed all information on this page and confirm it is accurate
                      </Typography>
                    )}
                  />
                  {fieldErrors[`step_${activeStepNumber(activeStep)}_reviewed`] && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      Please confirm you have reviewed this page before proceeding
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
              <Button
                variant="text"
                disabled={saving}
                onClick={() => setShowExitDialog(true)}
                sx={{
                  color: '#8600e1',
                  minWidth: { xs: 'auto', sm: undefined },
                  px: { xs: 1, sm: 2 },
                  '&:hover': {
                    color: '#6a00b3',
                    backgroundColor: 'rgba(134, 0, 225, 0.04)',
                  },
                }}
              >
                Exit
              </Button>
              <Button
                variant="text"
                disabled={saving}
                onClick={handleManualSave}
                sx={{
                  color: '#8600e1',
                  minWidth: { xs: 'auto', sm: undefined },
                  px: { xs: 1, sm: 2 },
                  '&:hover': {
                    color: '#6a00b3',
                    backgroundColor: 'rgba(134, 0, 225, 0.04)',
                  },
                }}
              >
                Save
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
              {!isFirstVisibleStep && (
                <Button
                  variant="outlined"
                  disabled={saving}
                  onClick={goToPreviousStep}
                  sx={{
                    borderColor: '#8600e1',
                    color: '#8600e1',
                    minWidth: { xs: 'auto', sm: undefined },
                    px: { xs: 1.5, sm: 2 },
                    '&:hover': {
                      borderColor: '#6a00b3',
                      color: '#6a00b3',
                    },
                  }}
                >
                  Back
                </Button>
              )}
              <Button
                variant="contained"
                disabled={saving}
                onClick={goToNextStep}
                sx={{
                  minWidth: { xs: 'auto', sm: undefined },
                  px: { xs: 1.5, sm: 2 },
                }}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : isLastVisibleStep ? 'Complete Assessment' : 'Next'}
              </Button>
            </Box>
          </Box>
                </Box>
              </Box>
            </Paper>
          </Container>

        </>
      )}
      {renderTopNavTools()}
      {renderDocumentPreviewDialog()}
      {renderRecordingTextDialog()}
      {renderHandOffNoteDialog()}
      {renderTicketDialog()}
      {renderPcpAddressDialog()}
      <ReauthDialog
        open={reauthOpen}
        user={user}
        message={reauthMessage}
        onAuthenticated={completeReauth}
      />
      <Dialog open={showExitDialog} onClose={() => setShowExitDialog(false)}>
        <DialogTitle>Exit Assessment?</DialogTitle>
        <DialogContent>
          Would you like to save your changes before exiting?
        </DialogContent>
        <DialogActions sx={{ alignItems: 'stretch' }}>
          <Button
            onClick={() => setShowExitDialog(false)}
            variant="outlined"
            sx={{
              borderColor: '#8600e1',
              color: '#8600e1',
              '&:hover': {
                borderColor: '#6a00b3',
                bgcolor: 'rgba(134, 0, 225, 0.04)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExitWithoutSaving}
            variant="outlined"
            sx={{
              borderColor: '#8600e1',
              color: '#8600e1',
              '&:hover': {
                borderColor: '#6a00b3',
                bgcolor: 'rgba(134, 0, 225, 0.04)',
              },
            }}
          >
            Exit Without Saving
          </Button>
          <Button
            onClick={handleSaveAndExit}
            variant="contained"
          >
            Save and Exit
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
